import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import WorkspaceLink from '../../components/workspace/WorkspaceLink';
import useWorkspaceNavigate from '../../hooks/useWorkspaceNavigate';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import ComplianceOverview from '../../components/portal/ComplianceOverview';
import DocumentPreviewModal from '../../components/portal/DocumentPreviewModal';
import RecurringComplianceSection from '../../components/portal/RecurringComplianceSection';
import LifecycleReasonDialog from '../../components/portal/LifecycleReasonDialog';
import DocumentExtractionReview from '../../components/documents/DocumentExtractionReview';
import TrainingIntakeWizard from '../../components/training/TrainingIntakeWizard';
import TrainingRequestDialog from '../../components/training/TrainingRequestDialog';
import AuditReadyTrainingMatrix from '../../components/training/AuditReadyTrainingMatrix';
import TrainingAuditHub from '../../components/training/TrainingAuditHub';
import TrainingAdminWorkspaceSwitcher from '../../components/training/TrainingAdminWorkspaceSwitcher';
// EnhancedTrainingTab removed - functionality consolidated into AuditReadyTrainingMatrix
import { DualRowComplianceSection, EmploymentPackPanel, FixItWizard, PersonFileHeroCard, RecordCheckDialog, TrainingSummaryCard, ReferencesPanel, AuditTrailPanel, InterviewFormPanel } from '../../components/compliance';
import AssistedOnboardingWorkspace from '../../components/assisted/AssistedOnboardingWorkspace';
import AdminRTWShareCodeSubmission from '../../components/compliance/AdminRTWShareCodeSubmission';
import ApplicantQuickSwitch from '../../components/employee/ApplicantQuickSwitch';
import InterviewSchedulingPanel from '../../components/compliance/InterviewSchedulingPanel';
import CvEmploymentAlignmentPanel from '../../components/compliance/CvEmploymentAlignmentPanel';
import EmploymentReviewExceptionQueue from '../../components/compliance/EmploymentReviewExceptionQueue';
import CvRequestReasonPicker from '../../components/compliance/CvRequestReasonPicker';
import TenantSetupGateNotice from '../../components/compliance/TenantSetupGateNotice';
import { getCvRequestTemplateById } from '../../config/cvRequestTemplates';
import { extractTenantSetupGate, tenantSetupGateToastMessage } from '../../utils/tenantSetupGate';

import EmploymentGapPanel from '../../components/compliance/EmploymentGapPanel';
import CompetencyAssessmentsPanel from '../../components/compliance/CompetencyAssessmentsPanel';
import SpotChecksPanel from '../../components/compliance/SpotChecksPanel';
import SupervisionsPanel from '../../components/compliance/SupervisionsPanel';
import AppraisalsPanel from '../../components/compliance/AppraisalsPanel';
import PendingVerificationBanner from '../../components/compliance/PendingVerificationBanner';
import AdminOnboardingSyncPanel from '../../components/portal/AdminOnboardingSyncPanel';
import ApplicantKidPanel from '../../components/compliance/ApplicantKidPanel';
import { SendReminderButton, RequestRenewalButton } from '../../components/admin/AdminActionButtons';
import EditPersonalDetailsDialog from '../../components/admin/EditPersonalDetailsDialog';
import EditEmploymentHistoryDialog from '../../components/admin/EditEmploymentHistoryDialog';
import EditReferenceDialog from '../../components/admin/EditReferenceDialog';
import EditDeclarationsDialog from '../../components/admin/EditDeclarationsDialog';
import ReviewDeclarationsDialog from '../../components/admin/ReviewDeclarationsDialog';
import SupersedeContractDialog from '../../components/admin/SupersedeContractDialog';
import DocumentVerificationModal from '../../components/admin/DocumentVerificationModal';
import DocumentViewerModal from '../../components/admin/DocumentViewerModal';
import InlineDocumentViewer from '../../components/shared/InlineDocumentViewer';
import ApplicationFormViewer from '../../components/compliance/ApplicationFormViewer';
import EvidenceReviewViewerDialog from '../../components/compliance/EvidenceReviewViewerDialog';
import { 
  InductionChecklistPanel, 
  CompetencyRecordsPanel, 
  PreEmploymentGatesPanel, 
  ReferenceEmploymentComparison,
  PoliciesTabContent,
  TrainingTabContent,
  AuditTabContent,
  ReferencesTabContent
} from '../../components/employee';
import {
  ArrowLeft, Upload, FileText, Mail, Phone, Calendar,
  CheckCircle, Clock, AlertTriangle, AlertCircle, XCircle, Loader2, FileCheck,
  GraduationCap, ClipboardList, History, User, FolderUp, Eye, Shield,
  MoreHorizontal, MoreVertical, Edit, Archive, Trash2, RotateCcw, FileDown, Save,
  Download, RefreshCw, FileArchive, FileSpreadsheet, Printer, FileSearch,
  Camera, Replace, FileX, ClipboardCheck, FormInput, ChevronRight,
  Briefcase, UserCheck, FileWarning, CalendarClock, Send, MessageCircle
} from 'lucide-react';
import { FileUploaderInline } from '../../components/ui/file-uploader';
import { formatBackendDate, formatBackendDateTime, parseBackendDate } from '../../lib/dateUtils';
import { normalizeGapRecord } from '../../lib/gapStatusUtils';
import {
  areApplicationGapsVerified,
  cvApprovalWithNoteBlockedReason,
} from '../../lib/applicationGapReadiness';
import {
  canonicalDocumentIdentity,
  getCvReviewState,
  getDocumentFileFormat,
  isActiveDocumentCandidate,
  isCvLikeDocument,
  isInlinePreviewableCvDocument,
  isPdfLikeDocument,
  shouldOfferServerCvLinkRecovery,
  verifyLinkedCvRefresh,
} from './employeeCvReviewState';
import { getCanonicalPersonStage, isActiveLifecycleStatus, normalizeLifecycleStatus } from '../../lib/lifecycle';
import API_BASE from '../../utils/apiBase';
import { resolveApplicantProfileProgress } from '../../lib/profileProgress';
import { getActivationPresentation, canActivateFromSnapshot } from '../../utils/activationReadiness';
import { CARE_ROLE_OPTIONS } from '../../config/careRoles';
import { resolveDocumentFetchUrl } from '../../lib/resolveDocumentFetchUrl';
import { resolveFormSubmission } from '../../lib/formSubmissions';
import {
  fetchProtectedFileBlob,
  openBlobUrlInNewTab,
  revokeBlobUrlLater,
} from '../../lib/protectedFiles';
import {
  getCvExtractedEmploymentRecords,
  getEditableEmploymentHistory,
  getEmploymentHistoryEmptyState,
} from '../../lib/employmentHistoryDisplay';
import { deriveApplicantNextAction, presentApplicantNextAction } from '../../lib/applicantNextAction';
import { explainRecruitmentItem } from '../../lib/recruitmentActionGuide';
import { deriveEmploymentReviewNextAction } from '../../lib/employmentReviewNextAction';
import EmploymentComparisonPendingNotice, { employmentComparisonSourceState } from '../../components/compliance/EmploymentComparisonPendingNotice';
import { formatEmploymentBoundary } from '../../lib/employmentTimeline';
import { deriveApplicantDeclarationDisplayState } from '../../lib/applicantDeclarationStatus';
import { getEnhancedDbsConsentDisplay } from '../../lib/dbsConsentPresentation';
import { blobToDataUrl, downloadStaffIdPrintSheet, fetchImageDataUrl } from '../../lib/staffIdPrint';

const API = API_BASE;

/**
 * Readable, recursive renderer for a structured application submission so
 * "View Application" always shows the applicant's submitted answers in a usable
 * form (nested sections/lists rendered as labelled fields — never raw JSON and
 * never a dead/empty modal). Works entirely from the canonical structured
 * submission and does not depend on any PDF artifact.
 */
const humanizeSubmissionKey = (key) =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

function SubmissionObjectView({ data }) {
  const entries = Object.entries(data || {}).filter(
    ([key]) => !String(key).startsWith('_') && key !== 'schema_version',
  );
  if (entries.length === 0) {
    return <p className="text-gray-400 text-sm">Not provided</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key} className="border-b border-gray-100 pb-2 last:border-0">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {humanizeSubmissionKey(key)}
          </p>
          <div className="mt-1 text-sm text-gray-800">
            <SubmissionValueView value={value} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmissionValueView({ value }) {
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">Not provided</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">None</span>;
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-md border border-gray-100 bg-gray-50 p-2">
            {item !== null && typeof item === 'object' ? (
              <SubmissionObjectView data={item} />
            ) : (
              <span className="text-gray-800">{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    return <SubmissionObjectView data={value} />;
  }
  return <span className="text-gray-800 whitespace-pre-wrap break-words">{String(value)}</span>;
}

/**
 * Pick the richest structured payload for an application submission. Prefers the
 * object with the most keys between form_data and data so a thin/legacy `data`
 * object can never mask the real structured answers.
 */
const pickSubmissionData = (submission) => {
  if (!submission) return {};
  const fd = submission.form_data && typeof submission.form_data === 'object' ? submission.form_data : null;
  const dd = submission.data && typeof submission.data === 'object' ? submission.data : null;
  const fdCount = fd ? Object.keys(fd).length : 0;
  const ddCount = dd ? Object.keys(dd).length : 0;
  return fdCount >= ddCount ? (fd || dd || {}) : (dd || fd || {});
};

/** Applicant recruitment review tabs in workflow order (left â†’ right). */
const APPLICANT_RECRUITMENT_TAB_FLOW = [
  'employment',
  'references',
  'forms',
  'checklist',
  'training',
  'work_readiness',
  'audit',
];

const RECRUITMENT_TAB_SIGNOFF_LABELS = {
  references: 'References',
  forms: 'Application & Interview',
  checklist: 'Recruitment Checks',
  training: 'Mandatory Training',
};

// C3E: read-only stage-summary rows for the applicant Approval (Tab 6) readiness
// panel. Derived entirely from existing canonical sign-off state
// (recruitmentTabSignoffs / employmentSignedOff); this is presentation only and
// does NOT introduce a competing approval gate.
const APPROVAL_STAGE_SUMMARY_ITEMS = [
  { key: 'employment', label: 'Recruitment Review' },
  { key: 'references', label: 'References' },
  { key: 'forms', label: 'Application & Interview' },
  { key: 'checklist', label: 'Recruitment Checks' },
  { key: 'training', label: 'Mandatory Training' },
];

function getApplicantRecruitmentTabLabel(tab, employeeStatus) {
  if (tab === 'forms') {
    return ['new', 'screening', 'interview'].includes(normalizeLifecycleStatus(employeeStatus))
      ? '3. Application & Interview'
      : '3. Pre-employment Forms';
  }
  const labels = {
    employment: '1. Recruitment Review',
    references: '2. References',
    checklist: '4. Recruitment Checks',
    training: '5. Training',
    work_readiness: '6. Approval',
    audit: 'Audit',
  };
  return labels[tab] || tab;
}

export function ApplicantRecruitmentTabFooter({
  currentTab,
  employeeStatus,
  onContinue,
  canContinue = true,
  blockers = [],
  signoff = null,
  signoffRequired = false,
  onSignOff,
  signingOff = false,
  isAuditor = false,
}) {
  const flowIndex = APPLICANT_RECRUITMENT_TAB_FLOW.indexOf(currentTab);
  const nextTab = flowIndex >= 0 ? APPLICANT_RECRUITMENT_TAB_FLOW[flowIndex + 1] : null;
  if (!nextTab || nextTab === 'audit') return null;

  const blockerList = Array.isArray(blockers) ? blockers.filter(Boolean) : [];
  const sectionReady = canContinue !== false && blockerList.length === 0;
  const isChecklistTab = currentTab === 'checklist';
  const signedOff = Boolean(signoff?.signed_off);
  const invalidated = signoff?.status === 'invalidated' || Boolean(signoff?.invalidated_at);
  const ready = sectionReady && (!signoffRequired || signedOff);
  const signedOffAt = signoff?.signed_off_at
    ? new Date(signoff.signed_off_at).toLocaleDateString('en-GB')
    : null;
  const invalidatedAt = signoff?.invalidated_at
    ? new Date(signoff.invalidated_at).toLocaleDateString('en-GB')
    : null;

  return (
    <div
      className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4"
      data-testid={`applicant-tab-footer-${currentTab}`}
    >
      {signoffRequired && (
        <div className={`rounded-lg border px-3 py-2 ${
          signedOff
            ? 'border-green-200 bg-green-50 text-green-800'
            : sectionReady
              ? 'border-blue-200 bg-blue-50 text-blue-800'
              : 'border-slate-200 bg-slate-50 text-slate-600'
        }`}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-sm">
              {signedOff ? (
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              ) : (
                <Shield className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <div>
                <p className="font-medium">
                  {signedOff
                    ? `${RECRUITMENT_TAB_SIGNOFF_LABELS[currentTab]} signed off`
                    : invalidated
                      ? `${RECRUITMENT_TAB_SIGNOFF_LABELS[currentTab]} sign-off expired`
                    : isChecklistTab && sectionReady
                      ? 'Recruitment Checks ready for sign-off'
                      : `${RECRUITMENT_TAB_SIGNOFF_LABELS[currentTab]} sign-off required`}
                </p>
                <p className="text-xs opacity-80">
                  {signedOff
                    ? `Recorded by ${signoff.signed_off_by_name || 'admin'}${signedOffAt ? ` on ${signedOffAt}` : ''}.`
                    : invalidated
                      ? `Evidence changed${invalidatedAt ? ` on ${invalidatedAt}` : ''}; sign off this tab again.`
                    : sectionReady
                      ? (isChecklistTab
                        ? 'All recruitment checks required for this stage have been reviewed. Training and employment documents are handled in the following stages.'
                        : 'Final admin review stamp for this recruitment section.')
                      : 'Clear the section items above before sign-off is available.'}
                </p>
              </div>
            </div>
            {!signedOff && !isAuditor && (
              <Button
                type="button"
                size="sm"
                variant={sectionReady ? 'default' : 'outline'}
                disabled={!sectionReady || signingOff}
                onClick={() => sectionReady && onSignOff?.(currentTab)}
              >
                {signingOff ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileCheck className="mr-1 h-4 w-4" />}
                {isChecklistTab ? 'Sign off Recruitment Checks' : 'Sign off tab'}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          {ready
            ? 'Section complete — continue when convenient'
            : 'Keep recruitment moving: you can work on the next section in parallel while this one is completed.'}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onContinue(nextTab)}
        >
          Continue to {getApplicantRecruitmentTabLabel(nextTab, employeeStatus)}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      {/* Outstanding checks are already shown once at the top of this tab.
          Keep the footer for navigation/sign-off, not a second checklist. */}
    </div>
  );
}

function RecruitmentGuidanceLine({ item, reminder = false }) {
  const explanation = explainRecruitmentItem(item);
  return (
    <li className="rounded-lg border border-amber-200/70 bg-white/75 px-3 py-2 text-sm"
      data-testid="recruitment-guidance-item">
      <strong className="block text-slate-900">{explanation.title}</strong>
      <span className="mt-1 block text-slate-700"><strong>Who:</strong> {explanation.owner}</span>
      <span className="mt-1 block text-slate-700"><strong>What to do:</strong> {explanation.step}</span>
      {explanation.note ? <span className="mt-1 block text-slate-500">{explanation.note}</span> : null}
      {reminder ? <span className="mt-1 block text-blue-800">Reminder — other tasks can continue.</span> : null}
    </li>
  );
}

function ApplicantTabReadinessBanner({ readiness, completeMessage, incompleteMessage, systemCheckPending = false }) {
  if (!readiness) return null;
  const ready = readiness.can_continue === true;
  const blockers = Array.isArray(readiness.blockers) ? readiness.blockers : [];
  const warnings = Array.isArray(readiness.warnings) ? readiness.warnings : [];
  const hasWarnings = warnings.length > 0;
  return (
    <div
      className={`rounded-xl border p-4 ${ready && !hasWarnings ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}
      data-testid={`${readiness.tab}-tab-readiness-banner`}
    >
      <div className="flex items-start gap-3">
        {ready && !hasWarnings
          ? <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          : <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
        <div className="flex-1">
          <p className={`font-medium ${ready && !hasWarnings ? 'text-green-800' : 'text-amber-800'}`}>
            {ready ? completeMessage : systemCheckPending ? 'Recruitment Review: comparison needs refreshing' : incompleteMessage}
          </p>
          <p className="mt-1 text-xs text-slate-700">
            CareDock keeps one application and recruitment record. The care provider makes the hiring decision.
            You can work on other available checks while waiting for an applicant or colleague.
          </p>
          {!ready && systemCheckPending && (
            <p className="mt-2 text-sm text-slate-700" data-testid="employment-system-only-notice">
              The saved application, work history, gaps, CV and declarations remain on file. CareDock needs a current comparison before sign-off. This is not a new applicant work-history task.
            </p>
          )}
          {!ready && !systemCheckPending && blockers.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-amber-950">What needs to happen next</p>
              <ul className="mt-2 space-y-2">
                {blockers.slice(0, 3).map((blocker, index) => (
                  <RecruitmentGuidanceLine key={`${blocker.requirement_key || blocker.label || 'check'}-${index}`} item={blocker} />
                ))}
              </ul>
              {blockers.length > 3 ? (
                <details className="mt-2 rounded-lg border border-amber-200 bg-white/60 p-3">
                  <summary className="cursor-pointer text-xs font-medium text-amber-950">
                    Show {blockers.length - 3} more checks
                  </summary>
                  <ul className="mt-3 space-y-2">
                    {blockers.slice(3).map((blocker, index) => (
                      <RecruitmentGuidanceLine key={`${blocker.requirement_key || blocker.label || 'check'}-${index + 3}`} item={blocker} />
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          )}
          {hasWarnings && (
            <details className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3"
              data-testid={`${readiness.tab}-tab-readiness-warnings`}>
              <summary className="cursor-pointer text-xs font-medium text-blue-900">
                Notes and things not due yet ({warnings.length})
              </summary>
              <ul className="mt-3 space-y-2">
                {warnings.map((warning, index) => (
                  <RecruitmentGuidanceLine key={`${warning.requirement_key || warning.label || 'note'}-${index}`} item={warning} reminder />
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function EmploymentReviewCockpit({
  snapshot,
  onOpenEvidence,
  onRetry,
  retrying = false,
  state,
  signedOffBy,
  signedOffAt,
  items,
  nextAction,
  blockers = [],
  warnings = [],
  canSignOff,
  onSignOff,
  signingOff,
  isAuditor,
  attentionCount = 0,
  systemCheckPending = false,
}) {
  const isComplete = state === 'Signed off';
  const isReady = state === 'Ready for sign-off';
  const isBlocked = state === 'Blocked';
  const statusStyles = isComplete
    ? {
        panel: 'border-green-200 bg-green-50',
        icon: 'text-green-700 bg-green-100',
        heading: 'text-green-900',
        badge: 'bg-green-100 text-green-800 border-green-200',
        label: 'Signed off',
      }
    : isReady
      ? {
          panel: 'border-blue-200 bg-blue-50',
          icon: 'text-blue-700 bg-blue-100',
          heading: 'text-blue-900',
          badge: 'bg-blue-100 text-blue-800 border-blue-200',
          label: 'Ready to sign off',
        }
      : isBlocked
        ? {
            panel: 'border-amber-200 bg-amber-50',
            icon: 'text-amber-700 bg-amber-100',
            heading: 'text-amber-900',
            badge: 'bg-amber-100 text-amber-800 border-amber-200',
            label: 'Needs a few checks',
          }
        : {
            panel: 'border-red-200 bg-red-50',
            icon: 'text-red-700 bg-red-100',
            heading: 'text-red-900',
            badge: 'bg-red-100 text-red-800 border-red-200',
            label: 'Needs review',
          };
  const signedDate = signedOffAt ? new Date(signedOffAt).toLocaleDateString('en-GB') : null;

  return (
    <Card className={`shadow-sm ${statusStyles.panel}`} data-testid="employment-review-cockpit">
      <CardContent className="p-4 md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className={`rounded-full p-2 ${statusStyles.icon}`}>
              {isComplete || isReady ? <CheckCircle className="h-5 w-5" /> : <ClipboardCheck className="h-5 w-5" />}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={`font-heading text-lg ${statusStyles.heading}`}>Employment review</h3>
                <Badge variant="outline" className={statusStyles.badge}>{systemCheckPending ? 'Comparison needs refreshing' : statusStyles.label}</Badge>
              </div>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                {isComplete
                  ? `Signed off${signedOffBy ? ` by ${signedOffBy}` : ''}${signedDate ? ` on ${signedDate}` : ''}.`
                  : isReady
                    ? 'Everything needed for this section is checked. Record the sign-off when you are happy to proceed.'
                    : systemCheckPending
                      ? 'The five source records are on file, but the stored comparison needs a fresh run before sign-off. No new applicant evidence is requested.'
                    : attentionCount > 0
                      ? `${attentionCount} thing${attentionCount === 1 ? '' : 's'} need${attentionCount === 1 ? 's' : ''} attention. Work through the items below — sign-off unlocks once they are done.`
                      : 'Work through the checks below. The final sign-off stays locked until the essentials are complete.'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            {canSignOff && snapshot?.sign_off?.ready === true && !isAuditor ? (
              <Button
                size="sm"
                onClick={onSignOff}
                disabled={signingOff}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="employment-review-cockpit-signoff"
              >
                {signingOff ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Sign off
              </Button>
            ) : null}
            {!canSignOff && nextAction && !isAuditor && snapshot?.summary?.items ? (
              <details className="rounded-lg border border-slate-200 bg-white/70 px-3 py-2 text-sm">
                <summary className="cursor-pointer font-medium text-slate-700">More options</summary>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 min-h-11"
                  onClick={nextAction.onClick}
                  disabled={nextAction.disabled}
                  data-testid="employment-review-cockpit-next-action"
                >
                  {nextAction.icon ? React.createElement(nextAction.icon, { className: 'h-4 w-4 mr-1' }) : null}
                  {nextAction.label}
                </Button>
              </details>
            ) : null}
          </div>
        </div>

        {!systemCheckPending && <div className="mt-4">
          <EmploymentReviewExceptionQueue
            snapshot={snapshot}
            onOpenEvidence={onOpenEvidence}
            onRetry={onRetry}
            retrying={retrying}
            isAuditor={isAuditor}
          />
        </div>}

        {/* Five canonical statuses are already in the existing evidence view; no duplicate cards. */}

        {!systemCheckPending && !isReady && !isComplete && blockers.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-white/70 p-3">
            <p className="text-sm font-medium text-amber-900">Still needed</p>
            <ul className="mt-2 grid gap-1 text-sm text-amber-800 md:grid-cols-2">
              {blockers.slice(0, 6).map((blocker) => (
                <li key={blocker} className="flex gap-2">
                  <span aria-hidden="true">-</span>
                  <span>{blocker}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!isComplete && warnings.length > 0 ? (
          <div className="mt-3 rounded-lg border border-blue-100 bg-white/70 p-3 text-sm text-blue-800">
            {warnings[0]}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

const applicantNextActionToneClasses = {
  danger: {
    shell: 'border-red-200 bg-red-50',
    icon: 'bg-red-100 text-red-700',
    badge: 'bg-red-100 text-red-700 border-red-200',
    text: 'text-red-900',
    muted: 'text-red-700',
  },
  warning: {
    shell: 'border-amber-200 bg-amber-50',
    icon: 'bg-amber-100 text-amber-700',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    text: 'text-amber-900',
    muted: 'text-amber-700',
  },
  info: {
    shell: 'border-blue-200 bg-blue-50',
    icon: 'bg-blue-100 text-blue-700',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    text: 'text-blue-900',
    muted: 'text-blue-700',
  },
  success: {
    shell: 'border-green-200 bg-green-50',
    icon: 'bg-green-100 text-green-700',
    badge: 'bg-green-100 text-green-700 border-green-200',
    text: 'text-green-900',
    muted: 'text-green-700',
  },
};

function ApplicantNextActionPanel({ action }) {
  if (!action) return null;
  const classes = applicantNextActionToneClasses[action.tone] || applicantNextActionToneClasses.warning;
  const staffView = presentApplicantNextAction(action, 'admin');
  const applicantView = presentApplicantNextAction(action, 'applicant');
  const Icon = action.owner === 'applicant'
    ? Send
    : action.owner === 'complete'
      ? CheckCircle
      : ClipboardCheck;

  return (
    <div className={`rounded-xl border p-4 ${classes.shell}`} data-testid="applicant-next-action-panel">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <div className={`rounded-full p-2 ${classes.icon}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className={`font-heading text-base font-semibold ${classes.text}`}>{action.title}</p>
              <Badge variant="outline" className={classes.badge}>{action.badge}</Badge>
            </div>
            <p className={`mt-1 text-sm ${classes.muted}`}>{action.description}</p>
          </div>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-2 lg:min-w-[420px]">
          <div className="rounded-lg border border-white/70 bg-white/70 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{staffView.label}</p>
            <p className="mt-1 text-slate-800">{staffView.description}</p>
          </div>
          <div className="rounded-lg border border-white/70 bg-white/70 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{applicantView.label}</p>
            <p className="mt-1 text-slate-800">{applicantView.title}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

class ComplianceTabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Checks & Compliance tab render failure:', error, info);
    this.setState({ error, info });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-medium">Compliance temporarily unavailable</p>
          <p className="mt-1">Checks & Compliance data could not be rendered right now. Please retry.</p>
          {this.state.error?.message && (
            <p className="mt-2 text-xs text-amber-700">Error: {this.state.error.message}</p>
          )}
          <details className="mt-3 text-xs text-amber-700 whitespace-pre-wrap">
            <summary className="cursor-pointer font-medium">Technical details</summary>
            {this.state.error?.stack ? `\n${this.state.error.stack}\n` : ''}
            {this.state.info?.componentStack ? `\n${this.state.info.componentStack}` : ''}
          </details>
          <Button
            className="mt-3"
            variant="outline"
            size="sm"
            data-testid="compliance-tab-retry"
            onClick={() => this.setState({ hasError: false, error: null, info: null })}
          >
            Retry render
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Form-based requirements (open modal instead of file upload)
const FORM_BASED_REQUIREMENTS = [
  // 'health_screening' - ARCHIVED: replaced by staff_health_questionnaire
  'induction', 
  'interview_record', 
  'recruitment_checklist', 
  'equal_opportunities',
  'hmrc_starter_checklist',
  'staff_personal_info',
  'staff_health_questionnaire',
  // Worker-submittable forms — must open form viewer, not file upload
  'emergency_contacts',
  'conflict_of_interest',
  'pre_interview_questionnaire',
  'fit_proper_persons',
];

const statusIcons = {
  not_started: Clock,
  requested: Mail,
  uploaded: Upload,
  under_review: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  expired: AlertTriangle,
  not_applicable: XCircle
};

const statusColors = {
  not_started: 'status-neutral',
  requested: 'status-info',
  uploaded: 'status-info',
  under_review: 'status-warning',
  approved: 'status-success',
  rejected: 'status-error',
  expired: 'status-error',
  not_applicable: 'status-neutral'
};

const getComplianceFileWorkflowStatus = (complianceFile, sectionKey) => {
  const section = complianceFile?.sections?.[sectionKey];
  if (!section) return null;

  const evidenceRow = section.rows?.find((row) => row.row_type === 'evidence');
  const checkRow = section.rows?.find((row) => row.row_type === 'check');
  const evidenceFiles = evidenceRow?.documents_preview || [];
  const checkRecord = checkRow?.check_data || null;
  const acceptedFiles = evidenceFiles.filter((file) =>
    file.verified ||
    file.status === 'verified' ||
    file.status === 'approved' ||
    file.status === 'accepted'
  );
  const hasEvidence = evidenceFiles.length > 0;
  const hasAcceptedEvidence = acceptedFiles.length > 0;
  const hasCheck = !!checkRecord;
  const checkVerified = checkRecord?.outcome === 'verified';
  const checkFailed = checkRecord?.outcome === 'failed';
  const proofDocumentId = checkRecord?.proof_document_id || checkRecord?.evidence_document_id || null;
  const isDbs = sectionKey === 'dbs';
  const isRtw = sectionKey === 'right_to_work';
  const proofRequired = isRtw || (isDbs && ['dbs_update_service_check', 'update_service_check', 'dbs_update_service'].includes(checkRecord?.method));
  const reviewDueAt = checkRecord?.review_due_at || checkRecord?.next_recheck_date || null;
  const reviewDueDate = reviewDueAt ? parseBackendDate(reviewDueAt) : null;
  const isDbsOverdue = isDbs && reviewDueDate && reviewDueDate < new Date();
  const permissionEndDate = checkRecord?.permission_end_date || null;
  const permissionDate = permissionEndDate ? parseBackendDate(permissionEndDate) : null;
  const isRtwExpired = isRtw && permissionDate && permissionDate < new Date();

  if (!hasEvidence) {
    return { label: 'Missing', tone: 'gray', isBlocking: true };
  }
  if (!hasAcceptedEvidence) {
    return { label: 'Awaiting admin review', tone: 'amber', isBlocking: true };
  }
  if (!hasCheck) {
    return { label: 'Awaiting admin review', tone: 'amber', isBlocking: true };
  }
  if (checkFailed) {
    return { label: 'Rejected / action required', tone: 'red', isBlocking: true };
  }
  if (!checkVerified) {
    return { label: 'Awaiting admin review', tone: 'amber', isBlocking: true };
  }
  if (proofRequired && !proofDocumentId) {
    return { label: 'Awaiting admin review', tone: 'amber', isBlocking: true };
  }
  if (isDbsOverdue) {
    return { label: 'Rejected / action required', tone: 'red', isBlocking: true, reviewDueAt };
  }
  if (isRtwExpired) {
    return { label: 'Rejected / action required', tone: 'red', isBlocking: true, permissionEndDate };
  }

  return { label: 'Verified', tone: 'green', isBlocking: false, reviewDueAt, permissionEndDate };
};

const quickViewToneClasses = {
  red: {
    card: 'border-red-200 bg-red-50',
    icon: 'text-red-600',
    text: 'text-red-700'
  },
  amber: {
    card: 'border-amber-200 bg-amber-50',
    icon: 'text-amber-600',
    text: 'text-amber-700'
  },
  green: {
    card: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-700'
  },
  gray: {
    card: 'border-gray-200 bg-gray-50',
    icon: 'text-gray-500',
    text: 'text-gray-700'
  },
  blue: {
    card: 'border-blue-200 bg-blue-50',
    icon: 'text-blue-600',
    text: 'text-blue-700'
  }
};

const asArray = (value, keys = []) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(value[key])) return value[key];
  }
  return [];
};

const normalizeComplianceSections = (sections) => {
  if (!sections) return {};
  if (Array.isArray(sections)) {
    return sections.reduce((acc, section, index) => {
      const key = section?.key || section?.id || section?.section_key || section?.requirement_id || `section_${index}`;
      if (key) acc[String(key)] = section;
      return acc;
    }, {});
  }
  if (typeof sections === 'object') return sections;
  return {};
};

export default function EmployeeProfilePage() {
  const { employeeId } = useParams();
  const navigate = useWorkspaceNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const applicantNavigationContext = location.state?.applicantNavigation;
  
  // Route context detection - URL is only an initial hint until canonical stage is loaded
  const urlIsRecruitmentView = location.pathname.startsWith('/portal/recruitment/');
  
  // Initialize active tab from URL for navigation state persistence
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'employment');
  const [queueTrainingFocus, setQueueTrainingFocus] = useState(() => searchParams.get('action') === 'review_training');
  const [trainingEvidenceRefreshKey, setTrainingEvidenceRefreshKey] = useState(0);
  const [employee, setEmployee] = useState(null);
  const isActiveEmployee =
    employee?.is_active_employee ||
    isActiveLifecycleStatus(employee?.employee_status) ||
    isActiveLifecycleStatus(employee?.status);
  const canonicalStage = getCanonicalPersonStage(employee);
  const isRecruitmentView = employee
    ? (canonicalStage === 'applicant')
    : urlIsRecruitmentView;
  const profileMode = isRecruitmentView ? 'applicant' : 'employee';
  const isPreEmploymentEmployee =
    !isActiveEmployee && (
      canonicalStage === 'employee' ||
      employee?.is_approved ||
      employee?.recruitment_approved ||
      normalizeLifecycleStatus(employee?.employee_status) === 'onboarding' ||
      normalizeLifecycleStatus(employee?.status) === 'onboarding'
    );
  const lifecycleStage = isActiveEmployee
    ? 'active'
    : isPreEmploymentEmployee
      ? 'pre_employment'
      : 'recruitment';
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [training, setTraining] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [generatedForms, setGeneratedForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [complianceFile, setComplianceFile] = useState(null);
  const [employmentReview, setEmploymentReview] = useState(null);
  const [employmentReviewPersisted, setEmploymentReviewPersisted] = useState(false);
  const [employmentReviewError, setEmploymentReviewError] = useState(false);
  // ER-S1: canonical single read model for the whole Employment Review page.
  const [employmentSnapshot, setEmploymentSnapshot] = useState(null);
  // ER-S1: ambiguous historical application review dialog.
  const [applicationCandidates, setApplicationCandidates] = useState([]);
  const [applicationCandidatesOpen, setApplicationCandidatesOpen] = useState(false);
  const [applicationCandidatesLoading, setApplicationCandidatesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [generateFormsOpen, setGenerateFormsOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkDocTypes, setBulkDocTypes] = useState({});
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [importAppOpen, setImportAppOpen] = useState(false);
  const [importAppFile, setImportAppFile] = useState(null);
  const [importCvFile, setImportCvFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [complianceRequirements, setComplianceRequirements] = useState(null);
  const [unifiedProgress, setUnifiedProgress] = useState(null);
  // Canonical activation-readiness snapshot (single source of truth for the
  // activation controls). Fetched from /employees/{id}/activation-readiness.
  const [activationSnapshot, setActivationSnapshot] = useState(null);
  const [employeeCanonicalReadiness, setEmployeeCanonicalReadiness] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [documentLabel, setDocumentLabel] = useState('');
  
  // Edit dialog states for universal editability
  const [editPersonalOpen, setEditPersonalOpen] = useState(false);
  const [editEmploymentOpen, setEditEmploymentOpen] = useState(false);
  const [editReferenceOpen, setEditReferenceOpen] = useState(false);
  const [editDeclarationsOpen, setEditDeclarationsOpen] = useState(false);
  const [reviewDeclarationsOpen, setReviewDeclarationsOpen] = useState(false);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);
  const [selectedReferenceData, setSelectedReferenceData] = useState(null);
  const [referencesTabRefreshKey, setReferencesTabRefreshKey] = useState(0);
  const [interviewSchedulingRefreshKey, setInterviewSchedulingRefreshKey] = useState(0);
  const [supersedeContractOpen, setSupersedeContractOpen] = useState(false);
  const [currentContract, setCurrentContract] = useState(null);
  
  // Import document dialog states
  const [importDocOpen, setImportDocOpen] = useState(false);
  const [importDocType, setImportDocType] = useState('');
  const [importDocFile, setImportDocFile] = useState(null);
  const [importDocNotes, setImportDocNotes] = useState('');
  
  // Training completion dialog states
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [selectedTrainingReq, setSelectedTrainingReq] = useState(null);
  const [trainingExpiryDate, setTrainingExpiryDate] = useState('');
  const [isCompletingTraining, setIsCompletingTraining] = useState(false);
  
  // Training certificate upload states
  const [trainingCertDialogOpen, setTrainingCertDialogOpen] = useState(false);
  const [trainingCertFile, setTrainingCertFile] = useState(null);
  const [isUploadingCert, setIsUploadingCert] = useState(false);
  
  // Form submissions state
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [formSubmissionsError, setFormSubmissionsError] = useState(false);
  const [formsTabReadiness, setFormsTabReadiness] = useState(null);
  const [employmentTabReadiness, setEmploymentTabReadiness] = useState(null);
  const [referencesTabReadiness, setReferencesTabReadiness] = useState(null);
  const [checklistTabReadiness, setChecklistTabReadiness] = useState(null);
  const [trainingTabReadiness, setTrainingTabReadiness] = useState(null);
  const [recruitmentTabSignoffs, setRecruitmentTabSignoffs] = useState({});
  const [recruitmentTabSigningOff, setRecruitmentTabSigningOff] = useState(null);
  const [viewFormSubmission, setViewFormSubmission] = useState(null);
  const [formReviewViewer, setFormReviewViewer] = useState(null);
  const [formCorrectionDialog, setFormCorrectionDialog] = useState(null);
  const [formCorrectionReason, setFormCorrectionReason] = useState('');
  const [isReopeningFormCorrection, setIsReopeningFormCorrection] = useState(false);
  const [isVerifyingTraining, setIsVerifyingTraining] = useState(false);
  
  // Training correction/history dialog states
  const [trainingCorrectionDialogOpen, setTrainingCorrectionDialogOpen] = useState(false);
  const [editingTrainingRecord, setEditingTrainingRecord] = useState(null);
  const [trainingCorrectionField, setTrainingCorrectionField] = useState('expiry_date');
  const [trainingCorrectionValue, setTrainingCorrectionValue] = useState('');
  const [trainingCorrectionReason, setTrainingCorrectionReason] = useState('');
  
  // Recruitment Checks states (Reference Integrity, CV Gaps, Proof of Address)
  const [recruitmentStatus, setRecruitmentStatus] = useState(null);
  const [onboardingSync, setOnboardingSync] = useState(null);
  const onboardingSyncRequestSeq = useRef(0);
  const [loadingRecruitment, setLoadingRecruitment] = useState(false);
  const [fixItWizardOpen, setFixItWizardOpen] = useState(false);
  const [assistedOnboardingOpen, setAssistedOnboardingOpen] = useState(false);
  const [fixItWizardKey, setFixItWizardKey] = useState(0);
  const [personFileRefreshKey, setPersonFileRefreshKey] = useState(0);
  const [verifyRefDialogOpen, setVerifyRefDialogOpen] = useState(false);
  const [selectedRefNum, setSelectedRefNum] = useState(null);
  const [refFromCv, setRefFromCv] = useState(true);
  const [refOverrideReason, setRefOverrideReason] = useState('');
  const [isVerifyingRef, setIsVerifyingRef] = useState(false);
  const [explainGapDialogOpen, setExplainGapDialogOpen] = useState(false);
  const [selectedGap, setSelectedGap] = useState(null);
  const [gapExplanation, setGapExplanation] = useState('');
  const [isExplainingGap, setIsExplainingGap] = useState(false);
  const [trainingHistoryDialogOpen, setTrainingHistoryDialogOpen] = useState(false);
  const [trainingHistory, setTrainingHistory] = useState([]);
  
  // CV Review states (Admin reviews CV, triggers AI extraction)
  const [cvReviewDialogOpen, setCvReviewDialogOpen] = useState(false);
  const [cvReviewLoading, setCvReviewLoading] = useState(false);
  const [cvExtractionResult, setCvExtractionResult] = useState(null);
  const [cvRejectDialogOpen, setCvRejectDialogOpen] = useState(false);
  const [cvRejectReason, setCvRejectReason] = useState('');
  const [cvRejectLoading, setCvRejectLoading] = useState(false);
  const [cvApproveConfirmOpen, setCvApproveConfirmOpen] = useState(false);
  const [cvApprovalNote, setCvApprovalNote] = useState('');
  const [cvRequestDialogOpen, setCvRequestDialogOpen] = useState(false);
  const [cvRequestMode, setCvRequestMode] = useState('upload');
  const [cvRequestReason, setCvRequestReason] = useState('');
  const [cvRequestLoading, setCvRequestLoading] = useState(false);
  const [gapRerunLoading, setGapRerunLoading] = useState(false);

  // Inline document viewer state (replaces window.open / new-tab PDF viewing)
  const [inlineViewerOpen, setInlineViewerOpen] = useState(false);
  const [inlineViewerUrl, setInlineViewerUrl] = useState(null);
  const [inlineViewerTitle, setInlineViewerTitle] = useState('Document');
  const [inlineViewerFilename, setInlineViewerFilename] = useState('document.pdf');
  const [inlineViewerFallback, setInlineViewerFallback] = useState(null);

  // Employment Review sign-off state
  const [employmentSignOffLoading, setEmploymentSignOffLoading] = useState(false);
  const [employmentConfirmationSending, setEmploymentConfirmationSending] = useState('');
  const [employmentIntegrityRecheckLoading, setEmploymentIntegrityRecheckLoading] = useState(false);
  const [employmentReviewRebuildLoading, setEmploymentReviewRebuildLoading] = useState(false);
  const [employmentReviewRetryLoading, setEmploymentReviewRetryLoading] = useState(false);
  
  // Delete training record states
  const [deleteTrainingDialogOpen, setDeleteTrainingDialogOpen] = useState(false);
  const [deletingTrainingRecord, setDeletingTrainingRecord] = useState(null);
  const [deleteTrainingReason, setDeleteTrainingReason] = useState('');
  const [isDeletingTraining, setIsDeletingTraining] = useState(false);
  
  // Training evaluation state (canonical evaluator result)
  const [trainingEvaluation, setTrainingEvaluation] = useState(null);
  const [loadingTrainingEvaluation, setLoadingTrainingEvaluation] = useState(false);
  const trainingEvaluationLoadedRef = useRef(false);
  const employeeFetchRequestIdRef = useRef(0);
  // Latest canonical compliance-file read wins after back-to-back check/upload events.
  const complianceFileRequestSeqRef = useRef(0);
  const [inductionChecklist, setInductionChecklist] = useState(null);
  
  // Acknowledgement states (for Contract/Handbook acknowledgement flow)
  const [acknowledgementDialogOpen, setAcknowledgementDialogOpen] = useState(false);
  const [workApprovalDialogOpen, setWorkApprovalDialogOpen] = useState(false);
  const [lifecycleReasonDialogOpen, setLifecycleReasonDialogOpen] = useState(false);
  const [lifecycleNextStatus, setLifecycleNextStatus] = useState('');
  const [lifecycleActionLabel, setLifecycleActionLabel] = useState('');
  const [isLifecycleSaving, setIsLifecycleSaving] = useState(false);
  const [workApprovalOutcome, setWorkApprovalOutcome] = useState('ready');
  const [workApprovalRationale, setWorkApprovalRationale] = useState('');
  const [workApprovalConditions, setWorkApprovalConditions] = useState('');
  const [isSavingWorkApproval, setIsSavingWorkApproval] = useState(false);
  const [scopeOfPracticeDraft, setScopeOfPracticeDraft] = useState('');
  const [isSavingScopeOfPractice, setIsSavingScopeOfPractice] = useState(false);
  const [acknowledgingRequirement, setAcknowledgingRequirement] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [acknowledgementConfirmed, setAcknowledgementConfirmed] = useState(false);
  
  // Profile photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [profilePhotoBlob, setProfilePhotoBlob] = useState(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const photoInputRef = useRef(null);
  
  // Evidence edit state
  const [editEvidenceOpen, setEditEvidenceOpen] = useState(false);
  const [editEvidenceData, setEditEvidenceData] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isEditingEvidence, setIsEditingEvidence] = useState(false);
  const [editForm, setEditForm] = useState({
    issue_date: '',
    expiry_date: '',
    notes: '',
    file_label: '',
    reason: ''
  });

  const getSafeErrorMessage = async (error, fallbackMessage) => {
    const data = error?.response?.data;

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            return parsed?.detail || parsed?.message || fallbackMessage;
          } catch {
            return text;
          }
        }
      } catch {
        // Ignore blob parsing failures and fall through to fallback handling.
      }
      return fallbackMessage;
    }

    if (typeof data === 'string' && data.trim()) {
      return data;
    }

    const detail = data?.detail;
    if (detail && typeof detail === 'object') {
      return detail.message || detail.msg || JSON.stringify(detail);
    }
    return detail || data?.message || fallbackMessage;
  };
  
  // File management state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [requirementHistoryOpen, setRequirementHistoryOpen] = useState(false);
  const [selectedFileForAction, setSelectedFileForAction] = useState(null);
  const [selectedRequirementForAction, setSelectedRequirementForAction] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [replaceReason, setReplaceReason] = useState('');
  const [replaceFile, setReplaceFile] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [requirementHistory, setRequirementHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form submission modal state (for structured forms)
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [viewFormOpen, setViewFormOpen] = useState(false);
  const [viewFormData, setViewFormData] = useState(null);
  
  // Document request modal state
  const [requestDocDialogOpen, setRequestDocDialogOpen] = useState(false);
  const [requestingRequirement, setRequestingRequirement] = useState(null);
  const [requestDocMessage, setRequestDocMessage] = useState('');
  const [isRequestingDoc, setIsRequestingDoc] = useState(false);
  
  // Send Form state
  const [sendFormDialogOpen, setSendFormDialogOpen] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState('');
  const [sendFormMessage, setSendFormMessage] = useState('');
  const [sendFormSetupGate, setSendFormSetupGate] = useState(null);
  const [isSendingForm, setIsSendingForm] = useState(false);
  
  // Reference Request state (NHS-Level Workflow)
  const [referenceStatus, setReferenceStatus] = useState(null);
  const [loadingReferenceStatus, setLoadingReferenceStatus] = useState(false);
  const [requestReferenceDialogOpen, setRequestReferenceDialogOpen] = useState(false);
  const [selectedRefForRequest, setSelectedRefForRequest] = useState(null);
  const [referenceRequestMessage, setReferenceRequestMessage] = useState('');
  const [isRequestingReference, setIsRequestingReference] = useState(false);
  const [reviewReferenceDialogOpen, setReviewReferenceDialogOpen] = useState(false);
  const [selectedRefForReview, setSelectedRefForReview] = useState(null);
  const [reviewMismatchNotes, setReviewMismatchNotes] = useState('');
  const [isReviewingReference, setIsReviewingReference] = useState(false);
  const [isVerifyingReferenceStrict, setIsVerifyingReferenceStrict] = useState(false);
  
  // Profile extraction from application form state
  const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionFailed, setExtractionFailed] = useState(null); // For graceful failure handling
  const [isExtracting, setIsExtracting] = useState(false);
  const [fieldsToApply, setFieldsToApply] = useState({});
  const [isApplyingExtraction, setIsApplyingExtraction] = useState(false);
  
  // Employment History Mismatch State (CV vs Structured)
  const [employmentMismatch, setEmploymentMismatch] = useState(null);
  const [loadingMismatch, setLoadingMismatch] = useState(false);
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [mismatchReviewNote, setMismatchReviewNote] = useState('');
  const [isSubmittingMismatchNote, setIsSubmittingMismatchNote] = useState(false);
  const [selectedCvRolesForApply, setSelectedCvRolesForApply] = useState({});
  const [reconcileReason, setReconcileReason] = useState('');
  const [isApplyingReconcile, setIsApplyingReconcile] = useState(false);
  const [isReextractingFromCv, setIsReextractingFromCv] = useState(false);
  
  // Document Correction State (Step 8)
  const [docCorrectionDialogOpen, setDocCorrectionDialogOpen] = useState(false);
  const [docCorrectionType, setDocCorrectionType] = useState(null); // 'uploaded_in_error' | 'supersede' | 'move_category' | 'reopen_review'
  const [docCorrectionTarget, setDocCorrectionTarget] = useState(null);
  const [docCorrectionReason, setDocCorrectionReason] = useState('');
  const [docCorrectionNewCategory, setDocCorrectionNewCategory] = useState('');
  const [isSubmittingDocCorrection, setIsSubmittingDocCorrection] = useState(false);
  
  // Document Extraction Review State (Phase 2 - DBS, RTW, ID)
  const [docExtractionReviewOpen, setDocExtractionReviewOpen] = useState(false);
  const [docExtractionDocumentId, setDocExtractionDocumentId] = useState(null);
  const [docExtractionDocumentName, setDocExtractionDocumentName] = useState('');
  const [docExtractionContext, setDocExtractionContext] = useState(null); // Full context for modal header
  
  // Document Verification & Viewer Modal States (CQC P0 Compliance)
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationDocument, setVerificationDocument] = useState(null);
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [viewerDocument, setViewerDocument] = useState(null);
  
  const { token, isAuditor, isAdmin, isManagerOrAdmin, user } = useAuth();

  useEffect(() => {
    if (!employee) return;
    const targetBase = canonicalStage === 'applicant' ? '/portal/recruitment' : '/portal/employees';
    const inRecruitmentPath = location.pathname.startsWith('/portal/recruitment/');
    const inEmployeesPath = location.pathname.startsWith('/portal/employees/');
    const shouldGoRecruitment = canonicalStage === 'applicant' && !inRecruitmentPath;
    const shouldGoEmployees = canonicalStage !== 'applicant' && !inEmployeesPath;
    if (!shouldGoRecruitment && !shouldGoEmployees) return;
    navigate(`${targetBase}/${employeeId}${location.search || ''}`, { replace: true });
  }, [employee, canonicalStage, location.pathname, location.search, navigate, employeeId]);
  
  // Document preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]); // For multi-file navigation
  
  // Name Mismatch State (Phase 4 - Cross-Document Intelligence)
  const [nameMismatch, setNameMismatch] = useState(null);
  const [loadingNameMismatch, setLoadingNameMismatch] = useState(false);
  const [nameMismatchExpanded, setNameMismatchExpanded] = useState(false);
  
  // Training Intake Wizard State (Step 10)
  const [trainingIntakeOpen, setTrainingIntakeOpen] = useState(false);
  const [trainingRequestOpen, setTrainingRequestOpen] = useState(false);
  const [proposedTrainingItems, setProposedTrainingItems] = useState([]);
  const [loadingProposedItems, setLoadingProposedItems] = useState(false);
  const [lazyLoadedTabs, setLazyLoadedTabs] = useState({});
  const [lazyLoadingTabs, setLazyLoadingTabs] = useState({});
  
  // Dual-Row Compliance Model State (Step 11)
  const [recordCheckDialogOpen, setRecordCheckDialogOpen] = useState(false);
  const [recordCheckType, setRecordCheckType] = useState(null);
  
  // Refs for scrolling to compliance sections
  const complianceSectionRef = useRef(null);
  const trainingSectionRef = useRef(null);
  
  /** Force-remount wizard so a stuck invisible overlay cannot block repeat clicks. */
  const handleOpenFixItWizard = useCallback(() => {
    setFixItWizardOpen(false);
    setFixItWizardKey((k) => k + 1);
    window.requestAnimationFrame(() => setFixItWizardOpen(true));
  }, []);
  
  // Fetch recruitment context plus canonical Recruitment-verified progress.
  // Applicant Progress comes from onboarding-sync; Recruitment Progress comes from
  // recruitment-approval-check. Keep the two metrics separate rather than deriving
  // either percentage in the browser.
  const fetchRecruitmentStatus = async () => {
    if (!employeeId) return;
    setLoadingRecruitment(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const statusPromise = axios.get(`${API}/employees/${employeeId}/recruitment-status`, { headers });
      const approvalPromise = profileMode === 'applicant'
        ? axios.get(`${API}/employees/${employeeId}/recruitment-approval-check`, { headers })
        : Promise.resolve(null);

      const [statusResult, approvalResult] = await Promise.allSettled([
        statusPromise,
        approvalPromise,
      ]);

      if (statusResult.status !== 'fulfilled') {
        throw statusResult.reason;
      }

      const statusPayload = statusResult.value.data || {};
      const approvalPayload = approvalResult.status === 'fulfilled'
        ? approvalResult.value?.data
        : null;

      setRecruitmentStatus({
        ...statusPayload,
        recruitment_progress: approvalPayload?.recruitment_progress || null,
      });
    } catch (err) {
      console.error('Failed to fetch recruitment status:', err);
    } finally {
      setLoadingRecruitment(false);
    }
  };
  
  const fetchOnboardingSync = async () => {
    if (!employeeId || profileMode === 'employee') return;
    // A slow earlier request must not replace a more recent signed-off state.
    const requestSeq = ++onboardingSyncRequestSeq.current;
    try {
      const response = await axios.get(`${API}/admin/employees/${employeeId}/onboarding-sync`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (requestSeq === onboardingSyncRequestSeq.current) {
        setOnboardingSync(response.data?.onboarding_sync || null);
      }
    } catch (err) {
      if (requestSeq === onboardingSyncRequestSeq.current) setOnboardingSync(null);
      console.error('Failed to fetch onboarding sync:', err);
    }
  };

  // Fetch Employment History Mismatch Status (CV vs Structured)
  const fetchEmploymentMismatch = async () => {
    if (!employeeId) return;
    setLoadingMismatch(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/employment-mismatch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmploymentMismatch(response.data);
    } catch (err) {
      console.error('Failed to fetch employment mismatch:', err);
    } finally {
      setLoadingMismatch(false);
    }
  };
  
  // Fetch Name Mismatch Status (Phase 4 - Cross-Document Intelligence)
  const fetchNameMismatch = async () => {
    if (!employeeId) return;
    setLoadingNameMismatch(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/name-mismatches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNameMismatch(response.data);
    } catch (err) {
      console.error('Failed to fetch name mismatch:', err);
    } finally {
      setLoadingNameMismatch(false);
    }
  };
  
  // Fetch proposed training items (Step 10)
  const fetchProposedTrainingItems = async () => {
    if (!employeeId) return;
    setLoadingProposedItems(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/training/proposed-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProposedTrainingItems(response.data.proposed_items || []);
    } catch (err) {
      console.error('Failed to fetch proposed training items:', err);
    } finally {
      setLoadingProposedItems(false);
    }
  };
  
  // Re-extract employment history from CV (Admin action)
  const handleReextractFromCv = async () => {
    const recruitmentLocked = String(employee?.cv_status || '').toLowerCase() === 'approved'
      || Boolean(employee?.employment_review_signed_off);
    if (recruitmentLocked) {
      toast.error(
        'CV comparison is locked after CV approval or Employment Review sign-off.',
      );
      return;
    }

    // The legacy compliance payload is expensive and is only needed for this
    // explicit recovery action. Do not fetch it during every profile render.
    let complianceForAction = compliance;
    if (!complianceForAction) {
      try {
        const response = await axios.get(`${API}/employees/${employeeId}/compliance`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        });
        complianceForAction = response.data;
        setCompliance(response.data);
      } catch {
        toast.error('Could not load CV evidence for re-extraction. Please try again.');
        return;
      }
    }

    const cvDoc = (complianceForAction?.evidence || []).find(e =>
      e.document_type_name?.toLowerCase().includes('cv') ||
      e.document_type_name?.toLowerCase().includes('resume')
    );
    
    if (!cvDoc?.file_id && !cvDoc?.id) {
      toast.error('No CV document found. Please upload a CV first.');
      return;
    }
    
    setIsReextractingFromCv(true);
    try {
      // Step 1: Extract from CV
      const extractResponse = await axios.post(
        `${API}/cv/extract-employment-history?file_id=${cvDoc.file_id || cvDoc.id}&employee_id=${employeeId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (extractResponse.data.status !== 'success' || !extractResponse.data.extracted_roles?.length) {
        toast.warning('No employment history could be extracted from CV');
        return;
      }
      
      // Step 2: Compare with structured history
      const compareResponse = await axios.post(
        `${API}/employees/${employeeId}/compare-employment-history`,
        extractResponse.data.extracted_roles,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`CV re-extracted. ${extractResponse.data.extracted_roles.length} roles found.`);
      fetchEmploymentMismatch();
      fetchRecruitmentStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-extract from CV');
    } finally {
      setIsReextractingFromCv(false);
    }
  };
  
  // NEW: Admin reviews CV - triggers AI extraction
  const handleReviewCv = async () => {
    setCvReviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/employees/${employeeId}/cv/review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCvExtractionResult(response.data.extraction);
      setCvReviewDialogOpen(true);

      const alignment = response.data.alignment;
      if (alignment?.status === 'action_required') {
        toast.warning(alignment.summary || 'CV does not align with application employment history');
        const suggested = getCvRequestTemplateById(alignment.suggested_template_id);
        if (suggested) {
          setCvRequestMode('replacement');
          setCvRequestReason(suggested.reason);
        }
      } else if (response.data.requires_action) {
        toast.warning('CV reviewed — a material difference needs checking against the official work history');
      } else if (response.data.read_only) {
        toast.info('CV is approved and locked — showing saved review only');
      } else if (alignment?.status === 'aligned') {
        toast.success('CV aligns with application employment history');
      } else {
        toast.success(`CV reviewed — ${response.data.extraction?.jobs_found || 0} roles extracted`);
      }
      
      await Promise.all([
        fetchEmployee(),
        fetchRecruitmentStatus(),
        fetchCompliance(),
        fetchComplianceFile(),
        fetchEmploymentReview(),
        fetchEmploymentMismatch(),
      ]);
    } catch (err) {
      const rawDetail = err.response?.data?.detail;
      const detail = Array.isArray(rawDetail)
        ? rawDetail.map((item) => item?.msg || item).filter(Boolean).join(', ')
        : rawDetail;
      if (detail === 'No CV uploaded for this employee' || detail === 'No CV document found for this employee') {
        toast.error('No CV uploaded yet. Worker needs to upload their CV first.');
      } else if (detail === 'Storage not initialized' || String(detail || '').includes('Could not load CV file')) {
        toast.error(
          'CareOps could not open this CV file. Try Review CV again; if the file cannot be recovered, request a replacement.',
        );
      } else {
        toast.error(detail || 'Failed to review CV');
      }
    } finally {
      setCvReviewLoading(false);
    }
  };

  // Open the active CV PDF in inline viewer (admin view)
  const handleStaleEvidence = async (kind) => {
    // ER-S1: evidence became unretrievable between snapshot fetch and click. Refresh
    // the canonical snapshot and drop into the repair state — no fallback search.
    toast.error(kind === 'cv'
      ? 'This CV file could not be opened — refreshing. The record is here but the file needs recovery.'
      : 'This application file could not be opened — refreshing. The record is here but the evidence needs recovery.');
    await fetchEmploymentReview();
  };

  const handleViewApplication = async () => {
    // ER-S1: View Application uses the EXACT evidence id from the canonical snapshot
    // with a stale-evidence precheck (no second independent search).
    const snapAppState = employmentSnapshot?.application || null;
    const evId = snapAppState?.view_available ? snapAppState.evidence_id : null;
    if (!evId) return;
    try {
      const res = await axios.get(
        `${API}/employees/${employeeId}/employment-review/evidence/application/${evId}/resolve`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.data?.available) { await handleStaleEvidence('application'); return; }
    } catch (e) { await handleStaleEvidence('application'); return; }
    setInlineViewerUrl(`${API}/employees/${employeeId}/requirements/application_form_pdf/evidence/${evId}/view`);
    setInlineViewerTitle('Application Record');
    setInlineViewerFilename('application_form.pdf');
    setInlineViewerFallback(null);
    setInlineViewerOpen(true);
  };

  const handleViewCv = async () => {
    // ER-S1: View uses the EXACT document id from the canonical snapshot — no
    // second independent search. If the snapshot says the file is not viewable,
    // do nothing (the button should not be shown in that state).
    const snapCvState = employmentSnapshot?.cv || null;
    const docId = snapCvState
      ? (snapCvState.view_available ? snapCvState.document_id : null)
      : (employee?.cv_document_id || cvDocument?.id);
    if (!docId) {
      if (snapCvState && !snapCvState.view_available) {
        toast.error(snapCvState.reason || 'No retrievable CV file — recover the file or request a replacement.');
      }
      return;
    }
    // Stale-evidence precheck: confirm the exact snapshot doc is still retrievable.
    if (snapCvState) {
      try {
        const res = await axios.get(
          `${API}/employees/${employeeId}/employment-review/evidence/cv/${docId}/resolve`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.data?.available) { await handleStaleEvidence('cv'); return; }
      } catch (e) { await handleStaleEvidence('cv'); return; }
    }
    setInlineViewerUrl(`${API}/employees/${employeeId}/requirements/cv/evidence/${docId}/view`);
    setInlineViewerTitle(cvDocument?.original_filename || cvDocument?.file_name || 'CV / Resume');
    setInlineViewerFilename(cvDocument?.original_filename || 'cv.pdf');
    setInlineViewerFallback(null);
    setInlineViewerOpen(true);
  };

  // Securely open/download the ORIGINAL CV file (used for Word CVs, or as a
  // fallback when inline PDF preview is not available). Goes through the
  // authenticated, tenant-scoped document download endpoint — never a public URL.
  const handleOpenCvDocument = async () => {
    const docId = employmentSnapshot?.cv?.document_id || employee?.cv_document_id || cvDocument?.id;
    if (!docId) {
      toast.error('No CV document is on file to open.');
      return;
    }
    try {
      const res = await axios.get(`${API}/employee-documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const contentType = res.headers?.['content-type'] || 'application/octet-stream';
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
      openBlobUrlInNewTab(blobUrl, cvDocument?.original_filename || cvDocument?.file_name || 'cv');
      revokeBlobUrlLater(blobUrl);
    } catch (err) {
      toast.error(await getSafeErrorMessage(err, 'Could not open the CV file — please try again.'));
    }
  };

  // Link an existing CV document as the active review CV
  const handleRecoverCv = async () => {
    setCvReviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/employees/${employeeId}/cv/recover`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(response.data?.message || 'CV recovered from file');
      await Promise.allSettled([fetchEmployee(), fetchCompliance(), fetchComplianceFile(), fetchEmploymentReview()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No recoverable CV found in the system');
    } finally {
      setCvReviewLoading(false);
    }
  };

  const handleRecoverApplication = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/employees/${employeeId}/application/recover`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.status === 'recovered') {
        toast.success('Historical application recovered and attached.');
      } else if (response.data?.status === 'confirmation_required') {
        toast.info('A historical application was found — the applicant must confirm it.');
      } else {
        toast.info('No unambiguous historical application to recover.');
      }
      await Promise.allSettled([fetchEmployee(), fetchCompliance(), fetchComplianceFile(), fetchEmploymentReview()]);
    } catch (err) {
      if (err?.response?.status === 409) {
        // Ambiguous — open the review dialog so an admin can pick the right one.
        await handleOpenApplicationCandidates();
      } else {
        toast.error(err?.response?.data?.detail || 'No recoverable application found.');
      }
    }
  };

  const handleOpenApplicationCandidates = async () => {
    setApplicationCandidatesLoading(true);
    setApplicationCandidatesOpen(true);
    try {
      const response = await axios.get(
        `${API}/admin/employees/${employeeId}/application/candidates`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setApplicationCandidates(response.data?.candidates || []);
    } catch (err) {
      toast.error('Could not load possible historical applications.');
      setApplicationCandidates([]);
    } finally {
      setApplicationCandidatesLoading(false);
    }
  };

  const handleSelectApplicationCandidate = async (applicationId) => {
    try {
      const response = await axios.post(
        `${API}/admin/employees/${employeeId}/application/recover`,
        { application_id: applicationId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.status === 'recovered') {
        toast.success('Selected historical application recovered and attached.');
      } else if (response.data?.status === 'confirmation_required') {
        toast.info('This application still needs the applicant to confirm it.');
      } else {
        toast.info('That application could not be attached.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not attach the selected application.');
    } finally {
      setApplicationCandidatesOpen(false);
      setApplicationCandidates([]);
      await Promise.allSettled([fetchEmployee(), fetchCompliance(), fetchComplianceFile(), fetchEmploymentReview()]);
    }
  };

  const handleLinkCv = async (preferredDocumentId = null, { reviewAfterLink = false } = {}) => {
    setCvReviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/employees/${employeeId}/cv/link`,
        preferredDocumentId ? { document_id: preferredDocumentId } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const headers = { Authorization: `Bearer ${token}` };
      const [employeeRes, docsRes] = await Promise.all([
        axios.get(`${API}/employees/${employeeId}`, { headers }),
        axios.get(`${API}/employee-documents?employee_id=${employeeId}`, { headers }),
      ]);
      const refreshedEmployee = employeeRes.data;
      const refreshedDocuments = asArray(docsRes.data, ['items', 'records', 'documents']);
      let refreshedLinkedDocument = null;
      const linkedDocumentId = String(response.data?.cv_document_id || '').trim();
      if (linkedDocumentId) {
        const listedLinkedDocument = refreshedDocuments.find(
          (document) => canonicalDocumentIdentity(document) === linkedDocumentId,
        );
        if (!listedLinkedDocument) {
          try {
            const linkedDocRes = await axios.get(`${API}/employee-documents/${linkedDocumentId}`, { headers });
            refreshedLinkedDocument = linkedDocRes.data;
          } catch (_linkedDocErr) {
            refreshedLinkedDocument = null;
          }
        }
      }
      verifyLinkedCvRefresh({
        employeeId,
        postResponse: response.data,
        refreshedEmployee,
        refreshedDocuments,
        refreshedDocument: refreshedLinkedDocument,
      });
      employeeFetchRequestIdRef.current += 1;
      setEmployee(refreshedEmployee);
      setScopeOfPracticeDraft((prev) => refreshedEmployee?.scope_of_practice_notes ?? prev);
      setDocuments(refreshedLinkedDocument ? [...refreshedDocuments, refreshedLinkedDocument] : refreshedDocuments);
      if (!reviewAfterLink) {
        toast.success('CV prepared for review');
      }
      await Promise.allSettled([
        fetchCompliance(),
        fetchComplianceFile(),
        fetchEmploymentReview(),
      ]);
      if (reviewAfterLink) {
        await handleReviewCv();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to verify the linked CV');
      await Promise.allSettled([fetchEmployee()]);
    } finally {
      setCvReviewLoading(false);
    }
  };

  const handleReviewCvSimple = async () => {
    if (cvReviewLoading || isAuditor()) return;

    if (cvReviewReady) {
      await handleReviewCv();
      return;
    }

    if (cvLinkRecoveryAvailable || cvServerLinkRecoveryAvailable || cvHasCanonicalEvidence) {
      await handleLinkCv(linkablePdfCvDocumentId || null, { reviewAfterLink: true });
      return;
    }

    if (cvLegacyNonPdfOnly || cvFileExists) {
      setCvReviewLoading(true);
      try {
        const response = await axios.post(
          `${API}/admin/employees/${employeeId}/cv/recover`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data?.status === 'recovered' || response.data?.success !== false) {
          await Promise.allSettled([fetchEmployee(), fetchComplianceFile(), fetchEmploymentReview()]);
          await handleReviewCv();
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'CareOps could not prepare this CV for review');
      } finally {
        setCvReviewLoading(false);
      }
      return;
    }

    openCvRequestDialog('upload');
  };

  const complianceGapRowForCv = complianceFile?.sections?.employment_history?.rows?.[0] || null;
  const applicationGapsVerifiedForCv = useMemo(
    () => areApplicationGapsVerified({
      employmentReview,
      employmentReviewPersisted,
      complianceEmploymentHistoryGapRow: complianceGapRowForCv,
    }),
    [employmentReview, employmentReviewPersisted, complianceGapRowForCv],
  );
  const activeCvAlignment = employmentMismatch?.alignment || employee?.cv_employment_alignment || null;
  const cvAlignmentActionRequired = activeCvAlignment?.status === 'action_required';
  const cvApproveWithNoteBlockedReason = cvApprovalWithNoteBlockedReason({
    alignmentStatus: activeCvAlignment?.status,
    applicationGapsVerified: applicationGapsVerifiedForCv,
  });
  const cvApproveNeedsAcknowledgment = Boolean(
    cvAlignmentActionRequired && applicationGapsVerifiedForCv,
  );
  const cvApproveWithNoteBlocked = Boolean(cvApproveWithNoteBlockedReason);

  const handleApproveCvClick = () => {
    if (cvApproveWithNoteBlocked) {
      toast.error(cvApproveWithNoteBlockedReason);
      return;
    }
    if (cvApproveNeedsAcknowledgment) {
      setCvApprovalNote('');
      setCvApproveConfirmOpen(true);
      return;
    }
    handleApproveCv();
  };

  // Admin approves CV after review
  const handleApproveCv = async (approvalNote = '') => {
    setCvReviewLoading(true);
    try {
      const note = (approvalNote || '').trim();
      await axios.post(
        `${API}/admin/employees/${employeeId}/cv/approve`,
        note ? { approval_note: note } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        note
          ? 'CV approved with documented alignment note'
          : 'CV approved — extraction accepted for this applicant.'
      );
      setCvReviewDialogOpen(false);
      setCvApproveConfirmOpen(false);
      setCvApprovalNote('');
      setCvExtractionResult(null);

      await Promise.all([
        fetchEmployee(),
        fetchRecruitmentStatus(),
        fetchCompliance(),
        fetchComplianceFile(),
        fetchEmploymentReview(),
        fetchEmploymentMismatch(),
        fetchOnboardingSync(),
      ]);
    } catch (err) {
      const rawDetail = err.response?.data?.detail;
      const detail = Array.isArray(rawDetail)
        ? rawDetail.map((item) => item?.msg || item).filter(Boolean).join(', ')
        : rawDetail;
      toast.error(detail || 'Failed to approve CV');
    } finally {
      setCvReviewLoading(false);
    }
  };

  const handleApproveForWork = async () => {
    if (!canRecordWorkApproval()) return;
    if (!canRecordFitForWorkNow) {
      toast.error('A new Fit-for-Work decision is only available for an approved Onboarding employee whose readiness checks currently pass.');
      return;
    }

    const rationale = workApprovalRationale.trim();
    if (rationale.length < 3) {
      toast.error('Please add a short rationale for the approval decision.');
      return;
    }

    const conditions = workApprovalConditions
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

    try {
      setIsSavingWorkApproval(true);
      await axios.post(
        `${API}/employees/${employeeId}/work-readiness/approve`,
        {
          outcome: workApprovalOutcome,
          rationale,
          conditions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fit-for-work approval recorded.');
      setWorkApprovalDialogOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record work approval');
    } finally {
      setIsSavingWorkApproval(false);
    }
  };

  // This evaluates the latest CV/history; it never confirms for the applicant.
  const handleAdminRecheckEmploymentIntegrity = async () => {
    setEmploymentIntegrityRecheckLoading(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/employment-integrity/recheck`, {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      await Promise.allSettled([
        fetchEmploymentReview({ refreshReadiness: false }),
        fetchEmployee(),
        fetchApplicantTabReadiness(),
      ]);
      const state = response.data?.state;
      if (state === 'confirmation_required') {
        toast.success('Check complete. The applicant must now review and confirm their employment history in CareDock.');
      } else if (state === 'awaiting_admin_review') {
        toast.success('Applicant confirmation recorded. Refreshing the admin sign-off checks.');
      } else if (state === 'action_required') {
        toast.info('Check complete. The applicant has a work-history item to resolve.');
      } else {
        toast.info(response.data?.reason || 'Employment-history check refreshed.');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not refresh employment-history comparison.');
    } finally {
      setEmploymentIntegrityRecheckLoading(false);
    }
  };

  // A dedicated secure-link CTA for the one applicant-only action that
  // prevents final Employment Review. It never confirms or signs off for them.
  const handleEmploymentConfirmationReminder = async (channel) => {
    if (employmentConfirmationSending) return;
    // Reserve the popup during the user's click, before the async secure-link read.
    const windowForWhatsApp = channel === 'whatsapp'
      ? window.open('about:blank', '_blank') : null;
    if (windowForWhatsApp) windowForWhatsApp.opener = null;
    setEmploymentConfirmationSending(channel);
    try {
      if (channel === 'email') {
        await axios.post(
          `${API}/employees/${employeeId}/communications/send`,
          { template_key: 'employment_history_confirmation' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('A secure confirmation link has been emailed to the applicant.');
        return;
      }
      const response = await axios.post(
        `${API}/employees/${employeeId}/communications/prepare-chase`,
        { action_key: 'employment_integrity_confirm' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const message = String(response.data?.message || '').trim();
      if (!message) throw new Error('Could not prepare the secure confirmation link');
      const phone = String(response.data?.whatsapp_recipient || '').replace(/\D/g, '');
      if (phone && windowForWhatsApp) {
        windowForWhatsApp.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        toast.success('WhatsApp opened. Check the recipient, then press Send.');
      } else {
        if (windowForWhatsApp) windowForWhatsApp.close();
        await navigator.clipboard.writeText(message);
        toast.success(phone ? 'Your browser blocked WhatsApp. Message copied with a fresh link.' : 'No WhatsApp number saved. Message copied with a fresh secure link.');
      }
    } catch (error) {
      if (windowForWhatsApp) windowForWhatsApp.close();
      toast.error(
        error?.response?.data?.detail
        || error?.message
        || 'Cannot prepare confirmation. Check the applicant status and try again.'
      );
    } finally {
      setEmploymentConfirmationSending('');
    }
  };

  // Admin signs off Employment Review
  const handleSignOffEmploymentReview = async () => {
    if (!employmentReadyForSignOff) {
      toast.error('Employment Review cannot be signed off until all blockers are cleared.');
      return;
    }
    setEmploymentSignOffLoading(true);
    try {
      const res = await axios.post(
        `${API}/employees/${employeeId}/employment-review/sign-off`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Employment review signed off.');
      const autoRef = res.data?.auto_reference_send;
      if (autoRef?.sent_count > 0) {
        toast.success(autoRef.message || `Sent ${autoRef.sent_count} reference request(s) automatically.`);
      } else if (autoRef?.failed_count > 0) {
        toast.warning(autoRef.message || 'Reference requests could not be emailed — send manually from the References tab.');
      }
      await Promise.all([fetchEmployee(), fetchEmploymentReview(), fetchOnboardingSync(), fetchApplicantTabReadiness()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-off failed. Check all gaps are resolved.');
    } finally {
      setEmploymentSignOffLoading(false);
    }
  };

  // Admin rejects CV with reason
  const handleRejectCv = async () => {
    if (cvRejectReason.length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters)');
      return;
    }
    
    setCvRejectLoading(true);
    try {
      await axios.post(
        `${API}/admin/employees/${employeeId}/cv/reject`,
        { reason: cvRejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('CV rejected. Worker has been notified and asked to take action.');
      setCvRejectDialogOpen(false);
      setCvReviewDialogOpen(false);
      setCvRejectReason('');
      setCvExtractionResult(null);
      
      // Refresh data
      fetchEmployee();
      fetchRecruitmentStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject CV');
    } finally {
      setCvRejectLoading(false);
    }
  };

  const handleRequestCvFromApplicant = async (reason) => {
    await axios.post(
      `${API}/employees/${employeeId}/request-cv-reupload`,
      { reason, notify_employee: true },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success('CV upload requested — applicant will be notified');
    await Promise.all([fetchEmployee(), fetchComplianceFile(), fetchRecruitmentStatus(), fetchOnboardingSync()]);
  };

  const handleRequestCvReplacement = async (reason) => {
    await axios.post(
      `${API}/admin/employees/${employeeId}/cv/request-replacement`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success('Replacement CV requested — applicant will be notified');
    await Promise.all([fetchEmployee(), fetchComplianceFile(), fetchRecruitmentStatus(), fetchOnboardingSync()]);
  };

  const openCvRequestDialog = (mode, prefillReason = '') => {
    setCvRequestMode(mode);
    const alignment = employmentMismatch?.alignment;
    let reason = prefillReason;
    if (!reason && mode === 'replacement' && alignment?.suggested_template_id) {
      reason = getCvRequestTemplateById(alignment.suggested_template_id)?.reason || '';
    }
    if (!reason && mode === 'upload') {
      reason = getCvRequestTemplateById('initial_upload')?.reason || '';
    }
    setCvRequestReason(reason);
    setCvRequestDialogOpen(true);
  };

  const handleSubmitCvRequest = async () => {
    const reason = cvRequestReason.trim();
    if (reason.length < 10) {
      toast.error('Please provide a clear reason (at least 10 characters)');
      return;
    }
    setCvRequestLoading(true);
    try {
      if (cvRequestMode === 'replacement') {
        await handleRequestCvReplacement(reason);
      } else {
        await handleRequestCvFromApplicant(reason);
      }
      setCvRequestDialogOpen(false);
      setCvRequestReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send CV request');
    } finally {
      setCvRequestLoading(false);
    }
  };
  
  // Add mismatch review note
  const handleAddMismatchNote = async () => {
    if (mismatchReviewNote.length < 10) {
      toast.error('Note must be at least 10 characters');
      return;
    }
    
    setIsSubmittingMismatchNote(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/employment-mismatch/add-note`,
        { note: mismatchReviewNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review note saved. Verify application gaps before approving the CV with alignment issues.');
      setMismatchDialogOpen(false);
      setMismatchReviewNote('');
      await Promise.all([fetchEmployee(), fetchEmploymentMismatch(), fetchOnboardingSync()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setIsSubmittingMismatchNote(false);
    }
  };

  const handleApplyReconcile = async () => {
    const cvReconcileLocked = String(employee?.cv_status || '').toLowerCase() === 'approved'
      || Boolean(employee?.employment_review_signed_off);
    if (cvReconcileLocked) {
      toast.error('CV roles cannot be applied after the CV is approved or Employment Review is signed off.');
      return;
    }

    const rolesToApply = (employmentMismatch?.cv_extracted_roles || []).filter(
      (_, i) => selectedCvRolesForApply[i]
    );
    if (!rolesToApply.length) { toast.error('Select at least one CV role to apply'); return; }
    if (!reconcileReason || reconcileReason.trim().length < 5) {
      toast.error('Enter a reason (at least 5 characters) before applying');
      return;
    }
    const rolesPayload = rolesToApply.map((role) => ({
      employer: role.employer || role.employer_name || role.company || '',
      job_title: role.job_title || role.position || role.role || '',
      start_date: role.start_date || '',
      end_date: role.end_date || null,
      responsibilities: role.responsibilities || '',
      is_current: Boolean(role.is_current || !role.end_date),
    }));
    setIsApplyingReconcile(true);
    try {
      const res = (await axios.post(
        `${API}/employees/${employeeId}/employment-history/apply-from-cv`,
        { roles_to_apply: rolesPayload, edit_reason: reconcileReason },
        { headers: { Authorization: `Bearer ${token}` } }
      )).data;
      toast.success(`Added ${res.applied_count} CV role(s) to employment history. Gaps recalculated.${
        res.skipped_duplicates?.length ? ` ${res.skipped_duplicates.length} duplicate(s) skipped.` : ''
      }`);
      setSelectedCvRolesForApply({});
      setReconcileReason('');
      setMismatchDialogOpen(false);
      await Promise.allSettled([
        fetchEmployee(),
        fetchComplianceFile(),
        fetchEmploymentMismatch(),
        fetchEmploymentReview({ rebuild: true }),
        fetchOnboardingSync(),
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => item?.msg || item?.message || String(item)).join(' ')
        : detail;
      toast.error(message || 'Could not update employment history from the CV. Check the selected roles and try again.');
    } finally {
      setIsApplyingReconcile(false);
    }
  };
  
  // Fetch training evaluation (canonical evaluator)
  const fetchTrainingEvaluation = async (options = {}) => {
    const { force = false } = options;
    if (!employeeId) return;
    if (!force && (loadingTrainingEvaluation || trainingEvaluationLoadedRef.current)) return;
    setLoadingTrainingEvaluation(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/training`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingEvaluation(response.data);
      trainingEvaluationLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch training evaluation:', err);
    } finally {
      setLoadingTrainingEvaluation(false);
    }
  };
  
  // Verify reference with integrity check
  const handleVerifyReference = async () => {
    if (!refFromCv && refOverrideReason.length < 10) {
      toast.error('Override reason must be at least 10 characters');
      return;
    }
    
    setIsVerifyingRef(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/verify-reference`, {
        reference_num: selectedRefNum,
        from_cv: refFromCv,
        override_reason: refFromCv ? null : refOverrideReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Reference ${selectedRefNum} verified successfully`);
      setVerifyRefDialogOpen(false);
      setRefFromCv(true);
      setRefOverrideReason('');
      fetchRecruitmentStatus();
      fetchEmployee();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify reference');
    } finally {
      setIsVerifyingRef(false);
    }
  };
  
  // Explain CV gap
  const handleExplainGap = async () => {
    if (gapExplanation.length < 10) {
      toast.error('Explanation must be at least 10 characters');
      return;
    }
    
    setIsExplainingGap(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/explain-cv-gap`, {
        gap_id: selectedGap?.gap_id,
        explanation: gapExplanation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Gap explanation recorded');
      setExplainGapDialogOpen(false);
      setGapExplanation('');
      setSelectedGap(null);
      fetchRecruitmentStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record explanation');
    } finally {
      setIsExplainingGap(false);
    }
  };
  
  // Fetch reference status (NHS-Level strict workflow)
  const fetchReferenceStatus = async () => {
    if (!employeeId) return;
    setLoadingReferenceStatus(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/reference-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferenceStatus(asArray(response.data, ['references', 'items', 'records']));
    } catch (err) {
      console.error('Failed to fetch reference status:', err);
    } finally {
      setLoadingReferenceStatus(false);
    }
  };
  
  // Send reference request to referee (NHS-Level Step 1: Request)
  const handleSendReferenceRequest = async () => {
    if (!selectedRefForRequest) return;
    
    setIsRequestingReference(true);
    try {
      // Detect if this is a resend (reference already sent/requested/awaiting response)
      const isResend = ['sent', 'requested', 'awaiting_response'].includes(selectedRefForRequest.request_status);
      
      const response = await axios.post(
        `${API}/employees/${employeeId}/send-reference-request?reference_num=${selectedRefForRequest.reference_num}&force_resend=${isResend}${referenceRequestMessage ? `&message=${encodeURIComponent(referenceRequestMessage)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'duplicate') {
        toast.info('Reference request already sent and awaiting response');
      } else if (response.data.status === 'success') {
        toast.success(`Reference request sent to ${response.data.referee_email}`);
      } else if (response.data.status === 'email_failed') {
        toast.warning('Request created but email failed to send');
      }
      
      setRequestReferenceDialogOpen(false);
      setReferenceRequestMessage('');
      setSelectedRefForRequest(null);
      fetchReferenceStatus();
      fetchEmployee();
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reference request');
    } finally {
      setIsRequestingReference(false);
    }
  };
  
  // Review reference (NHS-Level Step 2: Review with mismatch documentation)
  const handleReviewReference = async () => {
    if (!selectedRefForReview) return;
    
    // If mismatch detected, require notes
    if (selectedRefForReview.mismatch_detected && reviewMismatchNotes.length < 10) {
      toast.error('Mismatch detected - please provide at least 10 characters explaining the mismatch');
      return;
    }
    
    setIsReviewingReference(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/review-reference?reference_num=${selectedRefForReview.reference_num}${reviewMismatchNotes ? `&mismatch_notes=${encodeURIComponent(reviewMismatchNotes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reference ${selectedRefForReview.reference_num} reviewed. Ready for final verification.`);
      setReviewReferenceDialogOpen(false);
      setReviewMismatchNotes('');
      setSelectedRefForReview(null);
      fetchReferenceStatus();
      fetchEmployee();
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review reference');
    } finally {
      setIsReviewingReference(false);
    }
  };
  
  // Verify reference (NHS-Level Step 3: Final Verification - Admin Only)
  const handleVerifyReferenceStrict = async (refNum) => {
    setIsVerifyingReferenceStrict(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/verify-reference-strict?reference_num=${refNum}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reference ${refNum} verified (2-step verification complete)`);
      fetchReferenceStatus();
      fetchEmployee();
      fetchCompliance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify reference');
    } finally {
      setIsVerifyingReferenceStrict(false);
    }
  };
  
  // Sync tab changes to URL
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value }, { replace: true });
  };
  
  // Open document in preview modal - supports single file or array
  const handlePreviewDocument = (url, name, filename) => {
    setPreviewFile({ url, name, filename });
    setPreviewFiles([]); // Clear multi-file array
    setPreviewOpen(true);
  };
  
  // Open multiple files in preview modal with navigation
  const handlePreviewMultipleFiles = (files, requirementId) => {
    if (!files || files.length === 0) return;
    
    // Build array of file objects for the modal
    const fileArray = files.map(f => ({
      url: `${API}/employees/${employeeId}/requirements/${requirementId}/evidence/${f.file_id}/view`,
      filename: f.file_label || f.original_filename || 'Document',
      content_type: f.content_type,
      file_id: f.file_id
    }));
    
    setPreviewFiles(fileArray);
    setPreviewFile(fileArray[0]); // Set first file as initial
    setPreviewOpen(true);
  };

  const roles = CARE_ROLE_OPTIONS;

  const statuses = [
    { value: 'new', label: 'New' },
    { value: 'screening', label: 'Screening' },
    { value: 'interview', label: 'Interview' },
    { value: 'compliance_review', label: 'Compliance Review' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const onboardingStatuses = [
    'New',
    'Recruitment File: Incomplete',
    'Under Review',
    'Ready for Placement',
    'Active',
    'Archived'
  ];

  const isSuperAdmin = () => user?.role === 'super_admin';
  const canRecordWorkApproval = () => user?.role === 'admin' || user?.role === 'super_admin';
  const latestWorkReadinessDecision = employee?.latest_work_readiness_decision || null;
  const latestHealthDeclaration = employee?.latest_health_declaration || null;

  const formatWorkApprovalOutcome = (outcome) => {
    if (outcome === 'ready_with_conditions') return 'Ready with conditions';
    if (outcome === 'ready') return 'Ready for work';
    if (outcome === 'not_ready') return 'Not ready';
    return 'No decision recorded';
  };

  const getWorkApprovalBadgeClass = (outcome) => {
    if (outcome === 'ready') return 'bg-green-100 text-green-700';
    if (outcome === 'ready_with_conditions') return 'bg-amber-100 text-amber-700';
    if (outcome === 'not_ready') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatHealthOutcomeLabel = (outcome) => {
    if (outcome === 'fit') return 'Fit';
    if (outcome === 'conditional') return 'Conditional';
    if (outcome === 'requires_review') return 'Requires review';
    if (outcome === 'pending') return 'Pending';
    if (outcome === 'rejected') return 'Rejected';
    if (outcome === 'missing') return 'Missing';
    if (outcome === 'not_fit') return 'Not fit';
    return 'Missing';
  };

  const getHealthOutcomeBadgeClass = (outcome) => {
    if (outcome === 'fit') return 'bg-green-100 text-green-700';
    if (outcome === 'conditional') return 'bg-amber-100 text-amber-700';
    if (outcome === 'requires_review' || outcome === 'pending') return 'bg-blue-100 text-blue-700';
    if (outcome === 'rejected' || outcome === 'not_fit') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  // Canonical activation truth: the promote CTA is driven ONLY by the backend
  // activation-readiness snapshot (lifecycle + UCE + latest valid Fit-for-Work
  // decision). Fails closed when the snapshot is unavailable.
  const activationView = getActivationPresentation(activationSnapshot);
  const canPromoteToActiveNow = canActivateFromSnapshot(activationSnapshot);
  const canRecordFitForWorkNow = Boolean(
    profileMode === 'employee'
    && employee?.status === 'onboarding'
    && employee?.recruitment_approved === true
    && activationSnapshot?.is_work_ready === true
    && (activationView.needsFitForWork || activationView.isBlocked)
  );

  const openWorkApprovalDialog = () => {
    setWorkApprovalOutcome('ready');
    setWorkApprovalRationale('');
    setWorkApprovalConditions('');
    setWorkApprovalDialogOpen(true);
  };

  const handlePromoteToActive = async () => {
    try {
      const response = await axios.post(`${API}/employees/${employeeId}/auto-promote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.promoted) {
        toast.success('Employee promoted to active');
      } else {
        toast.error(response.data?.message || 'Promotion is not currently allowed');
      }
      await fetchData();
      await fetchComplianceFile();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to promote employee'));
    }
  };

  const handleLifecycleStatusChange = async (nextStatus, actionLabel) => {
    setLifecycleNextStatus(nextStatus);
    setLifecycleActionLabel(actionLabel);
    setLifecycleReasonDialogOpen(true);
  };

  const submitLifecycleStatusChange = async (reason) => {
    if (!lifecycleNextStatus) return;
    setIsLifecycleSaving(true);
    try {
      await axios.put(
        `${API}/employees/${employeeId}`,
        { status: lifecycleNextStatus, status_change_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Employee updated to ${lifecycleNextStatus}`);
      setLifecycleReasonDialogOpen(false);
      setLifecycleNextStatus('');
      setLifecycleActionLabel('');
      await fetchData();
      await fetchComplianceFile();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update lifecycle status');
    } finally {
      setIsLifecycleSaving(false);
    }
  };

  const handleCompliancePreviewFile = (fileObj) => {
    const name = fileObj?.file_name || fileObj?.name || 'Document';
    const url = resolveDocumentFetchUrl({
      document_id: fileObj?.file_id || fileObj?.document_id,
      file_id: fileObj?.file_id,
      file_url: fileObj?.file_url || fileObj?.url,
      stamped_file_url: fileObj?.stamped_file_url,
      verified: fileObj?.verified,
      verification_stamp: fileObj?.verification_stamp,
      view_url: fileObj?.view_url,
      download_url: fileObj?.download_url,
    }, { apiBase: API });

    if (url) {
      setPreviewFile({
        url,
        name,
        filename: name,
        stampedFileUrl: null,
        verificationStampByName: fileObj?.verification_stamp_by_name || null,
        verificationStampAt: fileObj?.verification_stamp_at || null,
      });
      setPreviewFiles([]);
      setPreviewOpen(true);
    } else {
      toast.error('Document preview is not available yet');
    }
  };

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const CRITICAL_TIMEOUT_MS = 45000;
    // First-render critical path: identify the person and load canonical progress.
    // Detailed compliance requirements are deliberately loaded after the shell;
    // they previously added several seconds to the blocking spinner.
    const criticalRequests = [
      axios.get(`${API}/employees/${employeeId}`, { headers, timeout: CRITICAL_TIMEOUT_MS }),
      axios.get(`${API}/employees/${employeeId}/unified-progress`, { headers, timeout: CRITICAL_TIMEOUT_MS }),
    ];
    if (profileMode === 'employee') {
      criticalRequests.push(
        axios.get(`${API}/employees/unified-progress-summary`, {
          params: { employee_ids: employeeId },
          headers,
          timeout: CRITICAL_TIMEOUT_MS,
        })
      );
      criticalRequests.push(
        axios.get(`${API}/employees/${employeeId}/activation-readiness`, {
          headers,
          timeout: CRITICAL_TIMEOUT_MS,
        })
      );
    }
    const criticalResults = await Promise.allSettled(criticalRequests);

    // Process results - extract data or use defaults
    // Order must match:
    // critical: employee, unified-progress
    // employee mode additionally: unified-progress-summary, activation-readiness
    const empRes = criticalResults[0];
    const unifiedProgressRes = criticalResults[1];
    const listReadinessRes = profileMode === 'employee' ? criticalResults[2] : null;
    const activationReadinessRes = profileMode === 'employee' ? criticalResults[3] : null;
    
    let hasError = false;
    
    // Employee data is critical - if it fails, show error
    if (empRes.status === 'fulfilled') {
      setEmployee(empRes.value.data);
    } else {
      // Recruitment route resilience: promoted/manual mixed states may not resolve on /employees/{id}
      if (isRecruitmentView) {
        try {
          const fallback = await axios.get(`${API}/recruitment/resolve-profile/${employeeId}`, { headers });
          setEmployee(fallback.data);
        } catch (fallbackErr) {
          console.error('Failed to fetch employee and recruitment fallback:', empRes.reason, fallbackErr);
          hasError = true;
        }
      } else {
        console.error('Failed to fetch employee:', empRes.reason);
        hasError = true;
      }
    }
    
    // Other data can fail gracefully with defaults
    const documentsData = [];
    const documentTypesData = [];
    const policiesData = [];
    const trainingData = [];
    const auditLogsData = [];
    const generatedFormsData = [];
    const templatesData = [];
    setDocuments(documentsData);
    setDocumentTypes(documentTypesData);
    setPolicies(policiesData);
    setTraining(trainingData);
    setAuditLogs(auditLogsData);
    setGeneratedForms(generatedFormsData);
    setTemplates(templatesData);
    setUnifiedProgress(unifiedProgressRes.status === 'fulfilled' ? unifiedProgressRes.value.data : null);
    if (profileMode === 'employee') {
      const listSummary = listReadinessRes?.status === 'fulfilled'
        ? (Array.isArray(listReadinessRes.value?.data) ? listReadinessRes.value.data[0] : null)
        : null;
      setEmployeeCanonicalReadiness(listSummary || null);
      // Fail closed: if the activation-readiness endpoint failed, keep null so
      // the UI treats activation status as unavailable (never a percentage guess).
      setActivationSnapshot(
        activationReadinessRes?.status === 'fulfilled' ? (activationReadinessRes.value?.data || null) : null
      );
    } else {
      setEmployeeCanonicalReadiness(null);
      setActivationSnapshot(null);
    }
    // Optional documents are loaded in the non-blocking batch below.
    
    if (hasError) {
      toast.error('Failed to load employee data');
    }

    setLoading(false);

    // Tab-specific optional datasets are lazy-loaded when their tabs are opened.
  };

  const handleApproveToOnboarding = async () => {
    try {
      setIsSavingWorkApproval(true);
      await axios.post(
        `${API}/employees/${employeeId}/approve-recruitment`,
        {
          notes: workApprovalRationale?.trim() || 'Approved via profile summary'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Applicant approved to onboarding.');
      setWorkApprovalDialogOpen(false);
      await fetchData();
      await fetchComplianceFile();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Failed to approve applicant to onboarding');
      const blockerList = Array.isArray(detail?.blockers) ? detail.blockers : [];
      toast.error(
        blockerList.length > 0
          ? `${message}: ${blockerList.slice(0, 3).join('; ')}`
          : message,
      );
    } finally {
      setIsSavingWorkApproval(false);
    }
  };

  const fetchInductionChecklist = async () => {
    if (!employeeId || !token) return;
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/induction-checklist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response?.data && typeof response.data === 'object') {
        setInductionChecklist(response.data);
      }
    } catch (err) {
      console.warn('Failed to fetch induction checklist:', err?.response?.data?.detail || err?.message);
    }
  };

  const markLazyTabLoaded = (tabKey) => {
    setLazyLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
  };

  const setLazyTabLoading = (tabKey, value) => {
    setLazyLoadingTabs((prev) => ({ ...prev, [tabKey]: value }));
  };

  const loadLazyTabData = async (tabKey) => {
    if (!employeeId || !token || lazyLoadedTabs[tabKey] || lazyLoadingTabs[tabKey]) return;
    const headers = { Authorization: `Bearer ${token}` };
    const OPTIONAL_TIMEOUT_MS = 8000;
    setLazyTabLoading(tabKey, true);
    try {
      if (tabKey === 'employment') {
        await Promise.allSettled([
          fetchEmploymentReview({ refreshReadiness: false }),
          fetchFormSubmissions({ refreshReadiness: false }),
          fetchRecruitmentStatus(),
          fetchReferenceStatus(),
          fetchEmploymentMismatch(),
          fetchNameMismatch(),
          fetchComplianceFile({ refreshReadiness: false }),
          axios.get(`${API}/employee-documents?employee_id=${employeeId}`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
          axios.get(`${API}/document-types`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
        ]).then((results) => {
          const docsRes = results[7];
          const typesRes = results[8];
          if (docsRes?.status === 'fulfilled') setDocuments(asArray(docsRes.value.data, ['items', 'records', 'documents']));
          if (typesRes?.status === 'fulfilled') setDocumentTypes(asArray(typesRes.value.data, ['items', 'records']));
        });
      } else if (tabKey === 'forms') {
        await Promise.allSettled([
          fetchFormSubmissions({ refreshReadiness: false }),
          axios.get(`${API}/generated-forms?employee_id=${employeeId}`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
          axios.get(`${API}/templates`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
        ]).then((results) => {
          const formsRes = results[1];
          const templatesRes = results[2];
          if (formsRes?.status === 'fulfilled') setGeneratedForms(asArray(formsRes.value.data, ['forms', 'items', 'records']));
          if (templatesRes?.status === 'fulfilled') setTemplates(asArray(templatesRes.value.data, ['items', 'records']));
        });
      } else if (tabKey === 'training') {
        await Promise.allSettled([
          fetchTrainingEvaluation(),
          fetchProposedTrainingItems(),
          axios.get(`${API}/training-records?employee_id=${employeeId}`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
        ]).then((results) => {
          const trainingRes = results[2];
          if (trainingRes?.status === 'fulfilled') setTraining(asArray(trainingRes.value.data, ['items', 'records']));
        });
      } else if (tabKey === 'references') {
        await Promise.allSettled([fetchReferenceStatus()]);
      } else if (tabKey === 'policies') {
        await Promise.allSettled([
          axios.get(`${API}/policy-assignments?employee_id=${employeeId}&include_inactive=true`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
        ]).then((results) => {
          const policiesRes = results[0];
          if (policiesRes?.status === 'fulfilled') setPolicies(asArray(policiesRes.value.data, ['items', 'records']));
        });
      } else if (tabKey === 'audit') {
        await Promise.allSettled([
          axios.get(`${API}/audit-logs?entity_id=${employeeId}&compliance_only=true`, { headers, timeout: OPTIONAL_TIMEOUT_MS }),
        ]).then((results) => {
          const logsRes = results[0];
          if (logsRes?.status === 'fulfilled') setAuditLogs(asArray(logsRes.value.data, ['items', 'records']));
        });
      } else if (tabKey === 'checklist') {
        await Promise.allSettled([fetchComplianceFile()]);
      } else if (tabKey === 'work_readiness' && profileMode === 'applicant') {
        // C3D: the Approval tab presents Employment documents & agreements from the
        // same canonical compliance file (read-only fetch, no new truth).
        await Promise.allSettled([fetchComplianceFile()]);
      }
      markLazyTabLoaded(tabKey);
    } finally {
      setLazyTabLoading(tabKey, false);
    }
  };

  const fetchComplianceRequirements = async () => {
    if (!employeeId || !token) return;
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/compliance-requirements`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      const payload = (response?.data && typeof response.data === 'object')
        ? response.data
        : { requirements: [] };
      payload.requirements = asArray(payload.requirements, ['items', 'records', 'requirements']);
      setComplianceRequirements(payload);
    } catch (error) {
      console.error('Failed to fetch compliance requirements:', error);
      // Keep any last valid payload rather than replacing it with a false-empty state.
    }
  };

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/compliance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompliance(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
    }
  };

  const fetchEmployee = async () => {
    const requestId = ++employeeFetchRequestIdRef.current;
    try {
      const response = await axios.get(`${API}/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (requestId !== employeeFetchRequestIdRef.current) {
        return;
      }
      setEmployee(response.data);
      setScopeOfPracticeDraft(response.data?.scope_of_practice_notes || '');
    } catch (error) {
      console.error('Failed to fetch employee:', error);
    }
  };

  const handleSaveScopeOfPractice = async () => {
    setIsSavingScopeOfPractice(true);
    try {
      await axios.put(
        `${API}/employees/${employeeId}`,
        { scope_of_practice_notes: scopeOfPracticeDraft || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scope of practice notes updated');
      await fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update scope of practice notes');
    } finally {
      setIsSavingScopeOfPractice(false);
    }
  };

  const fetchComplianceFile = async ({ refreshReadiness = true } = {}) => {
    if (!employeeId || !token) {
      console.debug('Skipping compliance-file fetch until employeeId and auth token are available');
      return;
    }

    const requestSeq = ++complianceFileRequestSeqRef.current;
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/compliance-file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // An older in-flight response must not repaint the check as incomplete
      // after the newer post-verification request has loaded fresh source truth.
      if (requestSeq !== complianceFileRequestSeqRef.current) return;
      const payload = (response?.data && typeof response.data === 'object') ? response.data : {};
      const normalizedSections = normalizeComplianceSections(payload.sections);
      setComplianceFile((prev) => {
        const prevSections = normalizeComplianceSections(prev?.sections);
        const nextHasSections = Object.keys(normalizedSections).length > 0;
        const prevHasSections = Object.keys(prevSections).length > 0;
        const shouldStickyPreserve =
          prevHasSections &&
          (!nextHasSections || payload?.status_unavailable === true);
        if (payload?.serializer_version === 'dual_row_v1' && !nextHasSections && prevHasSections) {
          console.warn('dual_row_v1 returned empty sections; preserving previous valid compliance sections', {
            employeeId,
            previousSectionCount: Object.keys(prevSections).length
          });
          return {
            ...payload,
            sections: prevSections,
            stale_data_preserved: true,
            stale_data_message: 'Latest refresh failed; showing last valid compliance data.',
            summary: (payload.summary && typeof payload.summary === 'object')
              ? payload.summary
              : { status_unavailable: Boolean(payload.status_unavailable), overall_status: 'unavailable', ready_for_work: false },
          };
        }
        if (shouldStickyPreserve) {
          return {
            ...payload,
            sections: prevSections,
            stale_data_preserved: true,
            stale_data_message: 'Latest refresh failed; showing last valid compliance data.',
            summary: (payload.summary && typeof payload.summary === 'object')
              ? payload.summary
              : { status_unavailable: Boolean(payload.status_unavailable), overall_status: 'unavailable', ready_for_work: false },
          };
        }
        return {
          ...payload,
          sections: normalizedSections,
          stale_data_preserved: false,
          stale_data_message: null,
          summary: (payload.summary && typeof payload.summary === 'object')
            ? payload.summary
            : { status_unavailable: Boolean(payload.status_unavailable), overall_status: 'unavailable', ready_for_work: false },
        };
      });
      if (refreshReadiness) {
        await fetchApplicantTabReadiness();
      }
    } catch (error) {
      if (requestSeq !== complianceFileRequestSeqRef.current) return;
      console.error('Failed to fetch compliance file:', error);
      setComplianceFile((prev) => {
        const prevSections = normalizeComplianceSections(prev?.sections);
        const prevHasSections = Object.keys(prevSections).length > 0;
        if (prevHasSections) {
          return {
            ...(prev || {}),
            employee_id: employeeId,
            status_unavailable: true,
            stale_data_preserved: true,
            stale_data_message: 'Latest refresh failed; showing last valid compliance data.',
            sections: prevSections,
            errors: [{
              code: 'compliance_file_fetch_failed',
              message: error?.response?.data?.message || error?.message || 'Failed to load compliance file'
            }]
          };
        }
        return {
          employee_id: employeeId,
          status_unavailable: true,
          message: 'Compliance temporarily unavailable',
          stale_data_preserved: false,
          stale_data_message: null,
          sections: {},
          summary: {
            status_unavailable: true,
            overall_status: 'unavailable',
            ready_for_work: false
          },
          errors: [{
            code: 'compliance_file_fetch_failed',
            message: error?.response?.data?.message || error?.message || 'Failed to load compliance file'
          }]
        };
      });
    }
  };

  // Every evidence/check/proof write must re-read the SAME canonical
  // compliance-file payload that RequirementWorkflowCard renders. The old
  // handlers refreshed profile/progress only, leaving green ticks and status
  // stale until a full browser reload. Prioritise the check card, then refresh
  // the slower secondary summaries without holding up the visible update.
  const refreshChecksAfterMutation = async () => {
    await fetchComplianceFile({ refreshReadiness: false });
    void Promise.allSettled([
      fetchData(),
      fetchCompliance(),
      fetchOnboardingSync(),
      fetchApplicantTabReadiness(),
    ]);
  };

  const fetchEmploymentReview = async ({ rebuild = false, showToast = false, refreshReadiness = true } = {}) => {
    if (!employeeId || !token) {
      console.debug('Skipping employment-review fetch until employeeId and auth token are available');
      return;
    }

    try {
      setEmploymentReviewError(false);
      // Never display a previously loaded snapshot as fresh while this check is pending.
      setEmploymentSnapshot(null);
      const response = await axios.get(`${API}/employees/${employeeId}/employment-review`, {
        headers: { Authorization: `Bearer ${token}` },
        params: rebuild ? { rebuild: true } : {}
      });
      setEmploymentReview(response.data?.employment_review || null);
      setEmploymentReviewPersisted(Boolean(response.data?.persisted && response.data?.employment_review));
      // ER-S1: fetch the canonical snapshot (side-effect free) — single source of
      // truth for the five cards, their actions and sign-off eligibility.
      try {
        const snapRes = await axios.get(`${API}/employees/${employeeId}/employment-review/snapshot`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmploymentSnapshot(snapRes.data || null);
      } catch (snapErr) {
        console.error('Failed to fetch employment review snapshot:', snapErr);
        setEmploymentSnapshot(null);
      }
      if (refreshReadiness) {
        await fetchApplicantTabReadiness();
      }
      if (showToast && response.data?.rebuilt) {
        toast.success('Work history review updated.');
      }
    } catch (error) {
      console.error('Failed to fetch employment review:', error);
      setEmploymentReview(null);
      setEmploymentReviewPersisted(false);
      // A failed base read must never leave stale sign-off evidence on screen.
      setEmploymentSnapshot(null);
      setEmploymentReviewError(true);
      if (showToast) {
        toast.error(error?.response?.data?.detail || 'Failed to rebuild employment review');
      }
    }
  };

  const handleRetryEmploymentReview = async () => {
    setEmploymentReviewRetryLoading(true);
    try {
      await fetchEmploymentReview({ refreshReadiness: false });
    } finally {
      setEmploymentReviewRetryLoading(false);
    }
  };

  const handleRebuildEmploymentReview = async () => {
    setEmploymentReviewRebuildLoading(true);
    try {
      await fetchEmploymentReview({ rebuild: true, showToast: true });
    } finally {
      setEmploymentReviewRebuildLoading(false);
    }
  };

  useEffect(() => {
    if (!employeeId || !token) {
      console.debug('Skipping employee profile protected fetches until employeeId and auth token are available');
      return undefined;
    }

    let cancelled = false;
    trainingEvaluationLoadedRef.current = false;

    // Render the profile shell from the smallest critical read set first.
    // Heavy/detail reads begin afterwards so they cannot hold the page spinner.
    (async () => {
      await fetchData();
      if (cancelled) return;
      void Promise.allSettled([
        fetchComplianceRequirements(),
        fetchTrainingEvaluation(),
        fetchOnboardingSync(),
        fetchApplicantTabReadiness(),
      ]);
    })();

    return () => {
      cancelled = true;
    };
  }, [employeeId, token]);

  useEffect(() => {
    if (!employeeId || !token || loading) return;
    loadLazyTabData(activeTab);
  }, [activeTab, employeeId, token, loading]);
  
  const fetchApplicantTabReadiness = async () => {
    if (!employeeId || !token || profileMode !== 'applicant') {
      setFormsTabReadiness(null);
      setEmploymentTabReadiness(null);
      setReferencesTabReadiness(null);
      setChecklistTabReadiness(null);
      setTrainingTabReadiness(null);
      setRecruitmentTabSignoffs({});
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    const base = `${API}/employees/${employeeId}/recruitment-tabs`;
    try {
      const [formsRes, employmentRes, referencesRes, checklistRes, trainingRes, signoffsRes] = await Promise.all([
        axios.get(`${base}/forms/readiness`, { headers }),
        axios.get(`${base}/employment/readiness`, { headers }),
        axios.get(`${base}/references/readiness`, { headers }),
        axios.get(`${base}/checklist/readiness`, { headers }),
        axios.get(`${base}/training/readiness`, { headers }),
        axios.get(`${base}/sign-offs`, { headers }),
      ]);
      setFormsTabReadiness(formsRes.data || null);
      setEmploymentTabReadiness(employmentRes.data || null);
      setReferencesTabReadiness(referencesRes.data || null);
      setChecklistTabReadiness(checklistRes.data || null);
      setTrainingTabReadiness(trainingRes.data || null);
      setRecruitmentTabSignoffs(signoffsRes.data?.signoffs || {});
    } catch {
      setFormsTabReadiness(null);
      setEmploymentTabReadiness(null);
      setReferencesTabReadiness(null);
      setChecklistTabReadiness(null);
      setTrainingTabReadiness(null);
      setRecruitmentTabSignoffs({});
    }
  };

  const handleSignOffRecruitmentTab = async (tab) => {
    if (!RECRUITMENT_TAB_SIGNOFF_LABELS[tab]) return;
    setRecruitmentTabSigningOff(tab);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/recruitment-tabs/${tab}/sign-off`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        tab === 'training'
          ? 'Mandatory Training sign-off recorded.'
          : `${RECRUITMENT_TAB_SIGNOFF_LABELS[tab]} signed off.`
      );
      setRecruitmentTabSignoffs((prev) => ({
        ...prev,
        [tab]: response.data?.signoff || { signed_off: true },
      }));
      await Promise.allSettled([
        fetchApplicantTabReadiness(),
        fetchRecruitmentStatus(),
        fetchOnboardingSync(),
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const blockers = Array.isArray(detail?.blockers) ? detail.blockers : [];
      toast.error(
        blockers.length
          ? `${detail.message || 'Tab cannot be signed off'}: ${blockers.slice(0, 2).map((b) => b.reason || b.label).join('; ')}`
          : (detail?.message || detail || 'Failed to sign off tab')
      );
    } finally {
      setRecruitmentTabSigningOff(null);
    }
  };

  // Fetch form submissions for the Forms tab
  const fetchFormSubmissions = async ({ refreshReadiness = true } = {}) => {
    try {
      setFormSubmissionsError(false);
      const response = await axios.get(
        `${API}/form-submissions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employee_id: employeeId }
        }
      );
      setFormSubmissions(asArray(response.data, ['forms', 'items', 'records']));
      if (refreshReadiness) {
        await fetchApplicantTabReadiness();
      }
    } catch {
      setFormSubmissions([]);
      setFormSubmissionsError(true);
      setFormsTabReadiness(null);
      setEmploymentTabReadiness(null);
      setReferencesTabReadiness(null);
      setChecklistTabReadiness(null);
    }
  };

  // Handle URL parameters from email action links
  useEffect(() => {
    const action = searchParams.get('action');
    const requirement = searchParams.get('requirement');
    const section = searchParams.get('section');
    const requestId = searchParams.get('request_id');
    const emailToken = searchParams.get('token');
    
    if (action && employee) {
      // Track that user clicked the email link
      if (requestId && emailToken) {
        trackEmailClick(requestId, emailToken);
      }
      
      // Handle different action types
      if (action.includes('upload') || action === 'upload_document') {
        // Set the requirement if provided, then open upload dialog
        if (requirement) {
          setSelectedDocType(requirement);
          setSelectedRequirement(requirement);
        }
        setUploadDialogOpen(true);
        
        // Clear URL params after handling
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('action');
          newParams.delete('requirement');
          newParams.delete('request_id');
          newParams.delete('token');
          return newParams;
        });
      }

      if (action === 'review_cv') {
        setActiveTab('employment');
        setTimeout(() => {
          const target = document.querySelector('[data-testid="section-employment-cv"]');
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            target.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => target.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 2400);
          }
          const cvLocked = String(employee?.cv_status || '').toLowerCase() === 'approved'
            || Boolean(employee?.employment_review_signed_off);
          if (cvLocked) {
            toast.info('CV is already approved and locked for recruitment.');
          }
        }, 180);

        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', 'employment');
          newParams.delete('action');
          newParams.delete('requirement');
          newParams.delete('document_id');
          newParams.delete('section');
          newParams.delete('request_id');
          newParams.delete('token');
          return newParams;
        });
      }

      if (
        action === 'review_document'
        || action === 'open_document'
        || action === 'review_training'
        || action === 'review_reference'
        || action === 'open_spot_check'
        || action === 'open_competency'
        || action === 'review_followup'
      ) {
        const targetTab = searchParams.get('tab') || 'checklist';
        if (action === 'review_training') setQueueTrainingFocus(true);
        setActiveTab(targetTab);
        setTimeout(() => {
          const sectionId = section || (requirement ? `section-${String(requirement).replace(/_/g, '-')}` : null);
          const target = sectionId ? document.querySelector(`[data-testid="${sectionId}"]`) : null;
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            target.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => target.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 2400);
          }
        }, 180);

        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', targetTab);
          newParams.delete('action');
          newParams.delete('document_id');
          newParams.delete('section');
          newParams.delete('request_id');
          newParams.delete('token');
          return newParams;
        });
      }
    }
  }, [searchParams, employee, setSearchParams]);

  useEffect(() => {
    if (!employee || activeTab !== 'spot_checks' || isActiveEmployee) {
      return;
    }

    setActiveTab('employment');
    setSearchParams({ tab: 'employment' }, { replace: true });
  }, [activeTab, employee, isActiveEmployee, setSearchParams]);

  useEffect(() => {
    if (!employee) {
      return;
    }
    const applicantHiddenTabs = ['competencies', 'spot_checks', 'supervisions', 'appraisals', 'policies'];
    const activeOnlyTabs = ['competencies', 'spot_checks', 'supervisions', 'appraisals'];
    const adminOnlyTabs = ['spot_checks', 'supervisions', 'competencies'];
    if (profileMode === 'applicant' && applicantHiddenTabs.includes(activeTab)) {
      setActiveTab('employment');
      setSearchParams({ tab: 'employment' }, { replace: true });
      return;
    }
    if ((profileMode !== 'employee' || lifecycleStage !== 'active') && activeOnlyTabs.includes(activeTab)) {
      setActiveTab('employment');
      setSearchParams({ tab: 'employment' }, { replace: true });
      return;
    }
    if (adminOnlyTabs.includes(activeTab) && !isAdmin()) {
      setActiveTab('employment');
      setSearchParams({ tab: 'employment' }, { replace: true });
    }
  }, [activeTab, employee, profileMode, lifecycleStage, setSearchParams, user?.role]);

  useEffect(() => {
    fetchInductionChecklist();
  }, [employeeId, token, profileMode, lifecycleStage]);

  // Track email click event
  const trackEmailClick = async (requestId, emailToken) => {
    try {
      await axios.post(
        `${API}/email-requests/${requestId}/track-click`,
        { token: emailToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to track email click:', error);
    }
  };

  // Protected photo fetch. Revoke only the URL created by this request and
  // ignore late responses when switching applicants (never show the wrong photo).
  useEffect(() => {
    let cancelled = false;
    let activePhotoUrl = null;
    setProfilePhotoBlob(null);
    setPhotoPreviewOpen(false);

    if (employee?.profile_photo_url && employeeId && token) {
      const fetchProfilePhoto = async () => {
        try {
          const response = await axios.get(
            `${API}/employees/${employeeId}/profile-photo/view`,
            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
          );
          if (cancelled) return;
          activePhotoUrl = URL.createObjectURL(response.data);
          setProfilePhotoBlob(activePhotoUrl);
        } catch (error) {
          if (cancelled) return;
          if (error?.response?.status !== 404) {
            console.error('Failed to fetch profile photo:', error);
          }
          setProfilePhotoBlob(null);
        }
      };
      fetchProfilePhoto();
    }
    return () => {
      cancelled = true;
      if (activePhotoUrl) URL.revokeObjectURL(activePhotoUrl);
    };
  }, [employee?.profile_photo_url, employeeId, token]);

  const handleRefreshStatus = async () => {
    setIsRefreshingStatus(true);
    try {
      const response = await axios.post(`${API}/employees/${employeeId}/refresh-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status_changed) {
        toast.success(`Status updated to: ${response.data.new_status}`);
      } else {
        toast.info('Status is already up to date');
      }
      fetchData();
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh status');
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const handleExportFile = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/export-file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
        || `${employee?.employee_code || employee?.applicant_reference || 'export'}_File.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Employee file exported successfully');
    } catch (error) {
      toast.error(await getSafeErrorMessage(error, 'Failed to export file'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportComplianceSummary = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/export-compliance-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert JSON to downloadable file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${employee?.employee_code || employee?.applicant_reference || 'export'}_Compliance_Summary.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Compliance summary exported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export compliance summary');
    }
  };

  const handleExportCompliancePDF = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/export-compliance-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
        || `${employee?.employee_code || employee?.applicant_reference || 'export'}_Compliance_Summary.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Compliance PDF exported successfully');
    } catch (error) {
      toast.error(await getSafeErrorMessage(error, 'Failed to export PDF'));
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrintCompliancePDF = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/export-compliance-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Open in new tab for printing
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to open PDF for printing');
    }
  };

  // Phase 4A - Unified export handler
  const handleExportComplianceFile = async () => {
    setIsExporting(true);
    try {
      // Default to PDF export
      const response = await axios.get(`${API}/employees/${employeeId}/export-compliance-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
        || `${employee?.employee_code || employee?.applicant_reference || 'export'}_Compliance_File.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Compliance file exported');
    } catch (error) {
      toast.error(await getSafeErrorMessage(error, 'Failed to export compliance file'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!selectedRequirement || !uploadFile) {
      toast.error('Please select a requirement and choose a file to upload');
      return;
    }

    if (selectedRequirement === 'cv') {
      const lower = uploadFile.name?.toLowerCase() || '';
      const allowedCv = lower.endsWith('.pdf') || lower.endsWith('.doc') || lower.endsWith('.docx') || uploadFile.type?.includes('word') || uploadFile.type === 'application/pdf';
      if (!allowedCv) {
        toast.error('Upload CV as PDF or Word (.pdf, .doc, .docx).');
        return;
      }
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (documentLabel) {
        formData.append('file_label', documentLabel);
      }
      
      // Use the unified evidence upload endpoint for all documents
      const uploadResponse = await axios.post(`${API}/employees/${employeeId}/requirements/${selectedRequirement}/evidence`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (selectedRequirement === 'cv') {
        console.debug('CV_LINK_DIAGNOSTIC upload_response', {
          employeeId,
          requirement_id: selectedRequirement,
          response: uploadResponse.data
        });
      }
      
      // POST-UPLOAD FEEDBACK - Clear guidance on next step
      toast.success('Document uploaded — please review and approve', {
        duration: 5000,
        description: 'Check the document is clear and correct, then mark as approved.'
      });
      
      setUploadDialogOpen(false);
      setSelectedRequirement('');
      setSelectedDocType('');
      setDocumentLabel('');
      setUploadFile(null);
      const refreshTasks = [
        fetchData(),
        fetchCompliance()
      ];
      if (token) {
        refreshTasks.push(fetchComplianceFile());
      } else {
        console.debug('Skipping post-upload compliance-file refresh because auth token is not available');
      }
      await Promise.all(refreshTasks);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed — please try again');
    } finally {
      setIsUploading(false);
    }
  };

  const openCvRecoveryUpload = () => {
    setSelectedRequirement('cv');
    setSelectedDocType('cv');
    setDocumentLabel(cvFileExists ? 'Replacement CV' : 'CV');
    setUploadFile(null);
    setUploadDialogOpen(true);
  };

  const handleUpdateDocumentStatus = async (docId, status) => {
    try {
      await axios.put(`${API}/employee-documents/${docId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Document ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleVerifyDocument = async (docId, fileUrl) => {
    try {
      await axios.post(`${API}/employee-documents/${docId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify document');
    }
  };

  const handleUnverifyDocument = async (docId) => {
    try {
      await axios.post(`${API}/employee-documents/${docId}/unverify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove verification');
    }
  };

  const handleSaveFormAsDocument = async (formId, e) => {
    e.stopPropagation(); // Prevent navigation to form editor
    try {
      toast.loading('Saving form as document...');
      const response = await axios.post(`${API}/generated-forms/${formId}/save-as-document`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.dismiss();
      toast.success(`Saved to ${response.data.folder}`);
      fetchData();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to save form as document');
    }
  };

  // Verify all documents under a requirement
  const handleVerifyRequirement = async (requirementId) => {
    try {
      // Get the requirement data
      const req = complianceRequirements?.requirements?.find(r => r.id === requirementId);
      if (!req) {
        toast.error('Requirement not found');
        return;
      }
      
      // Get evidence files
      const evidenceFiles = req.evidence_files || [];
      if (evidenceFiles.length === 0) {
        toast.error('Cannot verify - no evidence file uploaded');
        return;
      }
      
      // Proceed with verification - backend will handle file validation
      await axios.post(`${API}/employees/${employeeId}/requirements/${requirementId}/verify-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Requirement approved');
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify requirement');
    }
  };
  
  // Open Document Verification Modal with evidence upload (CQC P0)
  const handleOpenVerificationModal = (document, requirementId = null) => {
    const docData = {
      ...document,
      document_type: document.document_type || document.requirement_id || requirementId,
      id: document.id || document.document_id
    };
    setVerificationDocument(docData);
    setVerificationModalOpen(true);
  };
  
  // Open Document Viewer Modal (CQC P0)
  const handleOpenViewerModal = (document) => {
    setViewerDocument(document);
    setViewerModalOpen(true);
  };
  
  // Handle successful verification from modal
  const handleVerificationComplete = async () => {
    await fetchData();
    await fetchCompliance();
  };

  // Delete a specific document (for multi-file requirements)
  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/employee-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  // Open remove file dialog
  const openRemoveDialog = (file, requirementId) => {
    setSelectedFileForAction(file);
    setSelectedRequirementForAction(requirementId);
    setRemoveReason('');
    setRemoveDialogOpen(true);
  };

  // Handle permanent delete file (removes from active use, keeps audit trail)
  const handleDeleteFile = async () => {
    setIsRemoving(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/requirements/${selectedRequirementForAction}/evidence/${selectedFileForAction.file_id}/delete`,
        { reason: removeReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('File deleted successfully');
      setRemoveDialogOpen(false);
      setSelectedFileForAction(null);
      setSelectedRequirementForAction(null);
      setRemoveReason('');
      // CRITICAL: await fetchData to ensure UI syncs immediately
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    } finally {
      setIsRemoving(false);
    }
  };

  // Open replace file dialog
  const openReplaceDialog = (file, requirementId) => {
    setSelectedFileForAction(file);
    setSelectedRequirementForAction(requirementId);
    setReplaceReason('');
    setReplaceFile(null);
    setReplaceDialogOpen(true);
  };

  // Handle replace file
  const handleReplaceFile = async () => {
    if (!replaceReason.trim() || replaceReason.trim().length < 3) {
      toast.error('Please provide a reason (minimum 3 characters)');
      return;
    }
    if (!replaceFile) {
      toast.error('Please select a replacement file');
      return;
    }

    setIsReplacing(true);
    try {
      const formData = new FormData();
      formData.append('file', replaceFile);
      formData.append('reason', replaceReason.trim());
      
      await axios.post(
        `${API}/employees/${employeeId}/requirements/${selectedRequirementForAction}/evidence/${selectedFileForAction.file_id}/replace`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('File replaced successfully');
      setReplaceDialogOpen(false);
      setSelectedFileForAction(null);
      setSelectedRequirementForAction(null);
      setReplaceReason('');
      setReplaceFile(null);
      // CRITICAL: await fetchData to ensure UI syncs immediately
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace file');
    } finally {
      setIsReplacing(false);
    }
  };

  // Fetch requirement history
  const fetchRequirementHistory = async (requirementId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/requirements/${requirementId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequirementHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      setRequirementHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Open requirement history dialog
  const openHistoryDialog = (requirementId) => {
    setSelectedRequirementForAction(requirementId);
    setRequirementHistoryOpen(true);
    fetchRequirementHistory(requirementId);
  };

  // ========================================
  // DOCUMENT CORRECTION ACTIONS (Step 8)
  // ========================================
  
  // Mark document as uploaded in error
  const handleMarkUploadedInError = async (documentId, reason) => {
    if (!reason || reason.trim().length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return false;
    }
    
    try {
      await axios.post(
        `${API}/documents/${documentId}/mark-uploaded-in-error`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document marked as uploaded in error');
      await fetchData();
      await fetchCompliance();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark document');
      return false;
    }
  };

  // Supersede document with newer version
  const handleSupersedeDocument = async (documentId, replacementId, reason) => {
    try {
      await axios.post(
        `${API}/documents/${documentId}/supersede`,
        { 
          replacement_document_id: replacementId || null,
          reason: reason || 'Replaced by newer document'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document marked as superseded');
      await fetchData();
      await fetchCompliance();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to supersede document');
      return false;
    }
  };

  // Move document to different requirement category
  const handleMoveDocumentCategory = async (documentId, newRequirementId, reason) => {
    if (!reason || reason.trim().length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return false;
    }
    
    try {
      await axios.post(
        `${API}/documents/${documentId}/move-category`,
        { 
          new_requirement_id: newRequirementId,
          reason: reason.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document moved to new category');
      await fetchData();
      await fetchCompliance();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move document');
      return false;
    }
  };

  // Reopen document for review (undo verification/rejection)
  const handleReopenDocumentReview = async (documentId, reason) => {
    if (!reason || reason.trim().length < 10) {
      toast.error('Please provide a reason (minimum 10 characters)');
      return false;
    }
    
    try {
      await axios.post(
        `${API}/documents/${documentId}/reopen-review`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document reopened for review');
      await fetchData();
      await fetchCompliance();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen document');
      return false;
    }
  };

  // Open document correction dialog
  const openDocCorrectionDialog = (type, document) => {
    setDocCorrectionType(type);
    setDocCorrectionTarget(document);
    setDocCorrectionReason('');
    setDocCorrectionNewCategory('');
    setDocCorrectionDialogOpen(true);
  };

  // Submit document correction
  const handleSubmitDocCorrection = async () => {
    setIsSubmittingDocCorrection(true);
    try {
      let success = false;
      switch (docCorrectionType) {
        case 'uploaded_in_error':
          success = await handleMarkUploadedInError(docCorrectionTarget.file_id || docCorrectionTarget.id, docCorrectionReason);
          break;
        case 'supersede':
          success = await handleSupersedeDocument(docCorrectionTarget.file_id || docCorrectionTarget.id, null, docCorrectionReason);
          break;
        case 'move_category':
          success = await handleMoveDocumentCategory(docCorrectionTarget.file_id || docCorrectionTarget.id, docCorrectionNewCategory, docCorrectionReason);
          break;
        case 'reopen_review':
          success = await handleReopenDocumentReview(docCorrectionTarget.file_id || docCorrectionTarget.id, docCorrectionReason);
          break;
        default:
          toast.error('Unknown correction type');
      }
      
      if (success) {
        setDocCorrectionDialogOpen(false);
        setDocCorrectionTarget(null);
        setDocCorrectionType(null);
        setDocCorrectionReason('');
        setDocCorrectionNewCategory('');
      }
    } finally {
      setIsSubmittingDocCorrection(false);
    }
  };

  // ========================================
  // DOCUMENT EXTRACTION REVIEW (Phase 2)
  // ========================================
  
  // Requirement IDs that support extraction
  const EXTRACTABLE_REQUIREMENTS = [
    'dbs_certificate', 'dbs_check',
    'right_to_work_documents', 'right_to_work_check',
    'id_document', 'passport', 'driving_licence',
    'proof_of_address', 'proof_of_address_1', 'proof_of_address_2'
  ];
  
  // Check if a requirement supports extraction
  const isExtractableRequirement = (requirementId) => {
    return EXTRACTABLE_REQUIREMENTS.includes(String(requirementId || '').toLowerCase());
  };
  
  // Open document extraction review
  // BUGFIX: Must receive actual document_id, not file_id from evidence_files
  const openDocExtraction = (document, requirementName = '') => {
    // Validate we have a proper document ID
    const documentId = document?.id || document?.document_id;
    
    if (!documentId) {
      toast.error('No document selected for extraction. Please select a specific file.');
      console.error('openDocExtraction called without valid document ID:', document);
      return;
    }
    
    // Build context for modal header
    const context = {
      documentId,
      fileName: document.file_label || document.original_filename || document.file_name || 'Document',
      requirementName: requirementName,
      documentType: document.document_type || document.requirement_id || '',
      uploadedAt: document.uploaded_at
    };
    
    setDocExtractionDocumentId(documentId);
    setDocExtractionDocumentName(context.fileName);
    setDocExtractionContext(context);
    setDocExtractionReviewOpen(true);
  };
  
  // Handle extraction review complete
  const handleDocExtractionComplete = () => {
    setDocExtractionReviewOpen(false);
    setDocExtractionDocumentId(null);
    setDocExtractionDocumentName('');
    setDocExtractionContext(null);
    fetchData();
    fetchCompliance();
    toast.success('Document extraction reviewed successfully');
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    
    // Check all files have doc type assigned
    const missingTypes = bulkFiles.filter((_, i) => !bulkDocTypes[i]);
    if (missingTypes.length > 0) {
      toast.error('Please assign document types to all files');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      bulkFiles.forEach((file) => formData.append('files', file));
      const typeIds = bulkFiles.map((_, i) => bulkDocTypes[i]).join(',');
      
      const response = await axios.post(
        `${API}/employees/${employeeId}/bulk-upload?document_type_ids=${typeIds}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`Uploaded ${response.data.successful} documents`);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      
      setBulkUploadOpen(false);
      setBulkFiles([]);
      setBulkDocTypes({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateForms = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const response = await axios.post(
        `${API}/generated-forms/bulk`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            employee_id: employeeId,
            template_ids: selectedTemplates
          },
          paramsSerializer: params => {
            return Object.keys(params).map(key => {
              if (Array.isArray(params[key])) {
                return params[key].map(v => `${key}=${v}`).join('&');
              }
              return `${key}=${params[key]}`;
            }).join('&');
          }
        }
      );
      
      toast.success(`Generated ${response.data.created} forms`);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      setGenerateFormsOpen(false);
      setSelectedTemplates([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate forms');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImportApplication = async () => {
    if (!importAppFile) {
      toast.error('Please select an application form to upload');
      return;
    }
    
    setIsImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('employee_id', employeeId);
      formData.append('application_file', importAppFile);
      if (importCvFile) {
        formData.append('cv_file', importCvFile);
      }
      
      const response = await axios.post(
        `${API}/generated-forms/import-application`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(response.data.message || 'Application imported successfully');
      setImportAppOpen(false);
      setImportAppFile(null);
      setImportCvFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import application');
    } finally {
      setIsImporting(false);
    }
  };

  // Import document for Reference, Health Screening, Contract, etc.
  const handleImportDocument = async () => {
    if (!importDocFile || !importDocType) {
      toast.error('Please select document type and file');
      return;
    }
    
    setIsImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('employee_id', employeeId);
      formData.append('form_type', importDocType);
      formData.append('document_file', importDocFile);
      if (importDocNotes) {
        formData.append('notes', importDocNotes);
      }
      
      const response = await axios.post(
        `${API}/generated-forms/import-document`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(response.data.message || 'Document imported successfully');
      setImportDocOpen(false);
      setImportDocType('');
      setImportDocFile(null);
      setImportDocNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import document');
    } finally {
      setIsImporting(false);
    }
  };

  // Handle completing a training requirement
  const handleCompleteTraining = async () => {
    if (!selectedTrainingReq) {
      toast.error('No training requirement selected');
      return;
    }
    
    setIsCompletingTraining(true);
    
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/complete-training`,
        {
          requirement_id: selectedTrainingReq.id,
          expiry_date: trainingExpiryDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Training marked as complete');
      setTrainingDialogOpen(false);
      setSelectedTrainingReq(null);
      setTrainingExpiryDate('');
      fetchData();
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete training');
    } finally {
      setIsCompletingTraining(false);
    }
  };
  
  // Open training completion dialog
  const openTrainingDialog = (requirement) => {
    setSelectedTrainingReq(requirement);
    setTrainingExpiryDate('');
    setTrainingDialogOpen(true);
  };
  
  // Open training certificate upload dialog
  const openTrainingCertDialog = (requirement) => {
    setSelectedTrainingReq(requirement);
    setTrainingExpiryDate('');
    setTrainingCertFile(null);
    setTrainingCertDialogOpen(true);
  };
  
  // Handle uploading training certificate
  const handleUploadTrainingCertificate = async () => {
    if (!selectedTrainingReq || !trainingCertFile) {
      toast.error('Please select a certificate file');
      return;
    }
    
    setIsUploadingCert(true);
    
    try {
      const formData = new FormData();
      formData.append('file', trainingCertFile);
      if (trainingExpiryDate) {
        formData.append('expiry_date', trainingExpiryDate);
      }
      
      const response = await axios.post(
        `${API}/employees/${employeeId}/training/${selectedTrainingReq.id}/upload-certificate`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success(response.data.message || 'Certificate uploaded successfully');
      setTrainingCertDialogOpen(false);
      setSelectedTrainingReq(null);
      setTrainingCertFile(null);
      setTrainingExpiryDate('');
      fetchData();
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload certificate');
    } finally {
      setIsUploadingCert(false);
    }
  };
  
  // Handle verifying training
  const handleVerifyTraining = async (trainingId) => {
    setIsVerifyingTraining(true);
    try {
      await axios.post(
        `${API}/training-records/${trainingId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Training verified successfully');
      fetchData();
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify training');
    } finally {
      setIsVerifyingTraining(false);
    }
  };
  
  // Handle unverifying training
  const handleUnverifyTraining = async (trainingId) => {
    try {
      await axios.post(
        `${API}/training-records/${trainingId}/unverify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Training verification removed');
      fetchData();
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove verification');
    }
  };
  
  // View training certificate
  const handleViewTrainingCertificate = (trainingId, filename) => {
    if (!trainingId) {
      toast.error('Training record not found');
      return;
    }
    const url = `${API}/training-records/${trainingId}/certificate/file`;
    setPreviewFile({ url, name: filename || 'Certificate', filename: filename || 'Certificate' });
    setPreviewOpen(true);
  };
  
  // Download training certificate
  const handleDownloadTrainingCertificate = async (trainingId, filename) => {
    if (!trainingId) {
      toast.error('Training record not found');
      return;
    }
    try {
      const response = await axios.get(
        `${API}/training-records/${trainingId}/certificate/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'training_certificate';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.status === 404 ? 'Certificate file not found' : 'Failed to download certificate');
    }
  };
  
  // Training correction handler
  const handleTrainingCorrection = async () => {
    if (!trainingCorrectionReason || trainingCorrectionReason.trim().length < 3) {
      toast.error('Please provide a reason for this correction (minimum 3 characters)');
      return;
    }
    
    try {
      await axios.post(
        `${API}/training-records/${editingTrainingRecord.id}/correct`,
        {
          field: trainingCorrectionField,
          old_value: editingTrainingRecord[trainingCorrectionField],
          new_value: trainingCorrectionValue,
          reason: trainingCorrectionReason.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Training record corrected');
      setTrainingCorrectionDialogOpen(false);
      setEditingTrainingRecord(null);
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to correct training record');
    }
  };

  // Delete training record handler
  const handleDeleteTrainingRecord = async () => {
    setIsDeletingTraining(true);
    try {
      await axios.delete(
        `${API}/training-records/${deletingTrainingRecord.id}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { reason: deleteTrainingReason.trim() || undefined }
        }
      );
      toast.success('Training record deleted');
      setDeleteTrainingDialogOpen(false);
      setDeletingTrainingRecord(null);
      setDeleteTrainingReason('');
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete training record');
    } finally {
      setIsDeletingTraining(false);
    }
  };

  // Open training correction dialog from What's Needed tab
  // This reuses the same dialog as the Training tab for consistency
  const openTrainingCorrectionFromWhatsNeeded = (requirement) => {
    if (!requirement.training?.id) {
      toast.error('No training record found for this requirement');
      return;
    }
    
    // Build a training record object compatible with the correction dialog
    const trainingRecord = {
      id: requirement.training.id,
      training_name: requirement.name,
      status: requirement.training.status,
      expiry_date: requirement.training.expiry_date,
      completion_date: requirement.training.completed_at,
      verified: requirement.training.verified
    };
    
    setEditingTrainingRecord(trainingRecord);
    setTrainingCorrectionField('expiry_date');
    setTrainingCorrectionValue(trainingRecord.expiry_date?.split('T')[0] || '');
    setTrainingCorrectionReason('');
    setTrainingCorrectionDialogOpen(true);
  };

  // Open delete training dialog from What's Needed tab
  const openDeleteTrainingFromWhatsNeeded = (requirement) => {
    if (!requirement.training?.id) {
      toast.error('No training record found for this requirement');
      return;
    }
    
    // Build a training record object compatible with the delete dialog
    const trainingRecord = {
      id: requirement.training.id,
      training_name: requirement.name,
      status: requirement.training.status,
      verified: requirement.training.verified
    };
    
    setDeletingTrainingRecord(trainingRecord);
    setDeleteTrainingReason('');
    setDeleteTrainingDialogOpen(true);
  };

  // Handle requirement acknowledgement (Contract/Handbook)
  const handleAcknowledgeRequirement = async () => {
    if (!acknowledgingRequirement) return;
    
    setIsAcknowledging(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/requirements/${acknowledgingRequirement.id}/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${acknowledgingRequirement.name} acknowledged and completed`);
      setAcknowledgementDialogOpen(false);
      setAcknowledgingRequirement(null);
      setAcknowledgementConfirmed(false);
      await fetchData();
      await fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit acknowledgement');
    } finally {
      setIsAcknowledging(false);
    }
  };

  // Profile photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and WEBP images are allowed');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setIsUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/employees/${employeeId}/profile-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Profile photo uploaded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  // Print the same canonical staff ID the worker sees in CareOps.
  const handlePrintStaffId = async () => {
    try {
      const idResponse = await axios.get(
        `${API}/employees/${employeeId}/staff-id`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const staffId = idResponse.data || {};
      if (!staffId.issued) {
        toast.error('Staff ID is issued after recruitment approval and a permanent employee code are in place.');
        return;
      }
      if (!staffId.photo_present) {
        toast.error('Add a profile photo before printing the staff ID.');
        return;
      }

      const photoResponse = await axios.get(
        `${API}/employees/${employeeId}/profile-photo/view`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const photoDataUrl = await blobToDataUrl(photoResponse.data);

      const rawLogoUrl = staffId?.branding?.logo_url || '/osabea_logo.png';
      const logoUrl = rawLogoUrl.startsWith('http')
        ? rawLogoUrl
        : `${window.location.origin}${rawLogoUrl.startsWith('/') ? rawLogoUrl : `/${rawLogoUrl}`}`;
      const logoDataUrl = await fetchImageDataUrl(logoUrl);

      await downloadStaffIdPrintSheet({ staffId, photoDataUrl, logoDataUrl });
      toast.success('Staff ID print sheet created — print at Actual Size / 100%.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Could not create staff ID');
    }
  };

  // Remove profile photo handler
  const handleRemovePhoto = async () => {
    try {
      await axios.delete(
        `${API}/employees/${employeeId}/profile-photo`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotoPreviewOpen(false);
      toast.success('Profile photo removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Open edit evidence modal
  const openEditEvidence = (reqId, fileData) => {
    setEditEvidenceData({ 
      requirementId: reqId, 
      file: fileData 
    });
    setEditForm({
      issue_date: fileData.issue_date || '',
      expiry_date: fileData.expiry_date || '',
      notes: fileData.notes || '',
      file_label: fileData.file_label || fileData.original_filename || '',
      reason: ''
    });
    setEditEvidenceOpen(true);
  };

  // Save evidence edits
  const handleSaveEvidenceEdit = async () => {
    if (!editForm.reason || editForm.reason.trim().length < 3) {
      toast.error('Please provide a reason for this change (min 3 characters)');
      return;
    }
    
    setIsEditingEvidence(true);
    try {
      await axios.put(
        `${API}/employees/${employeeId}/requirements/${editEvidenceData.requirementId}/evidence/${editEvidenceData.file.file_id}`,
        {
          issue_date: editForm.issue_date || null,
          expiry_date: editForm.expiry_date || null,
          notes: editForm.notes || null,
          file_label: editForm.file_label || null,
          reason: editForm.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document details updated');
      setEditEvidenceOpen(false);
      // Force refresh data immediately after edit to ensure expiry status is recalculated
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update details');
    } finally {
      setIsEditingEvidence(false);
    }
  };

  // Load evidence edit history
  const loadEditHistory = async (reqId, fileId) => {
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/requirements/${reqId}/evidence/${fileId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditHistory(response.data);
      setHistoryOpen(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const openEditDialog = () => {
    setEditForm({
      first_name: employee?.first_name || '',
      last_name: employee?.last_name || '',
      email: employee?.email || '',
      phone: employee?.phone || '',
      role: employee?.role || '',
      status: employee?.status || '',
      onboarding_status: employee?.onboarding_status || 'New',
      start_date: employee?.start_date || '',
      notes: employee?.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEmployee = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/employees/${employeeId}/update`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee details updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveEmployee = async () => {
    try {
      await axios.post(`${API}/employees/${employeeId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee archived successfully');
      setArchiveDialogOpen(false);
      navigate(isRecruitmentView ? '/portal/recruitment' : '/portal/employees');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || JSON.stringify(detail));
      } else {
        toast.error(detail || 'Failed to archive employee');
      }
    }
  };

  const handleRestoreEmployee = async () => {
    try {
      await axios.post(`${API}/employees/${employeeId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee restored successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore employee');
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await axios.delete(`${API}/employees/${employeeId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee permanently deleted');
      setDeleteDialogOpen(false);
      navigate(isRecruitmentView ? '/portal/recruitment' : '/portal/employees');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || JSON.stringify(detail));
      } else {
        toast.error(detail || 'Failed to delete employee');
      }
    }
  };

  // ========== Document Request Handlers ==========
  
  // Open request document dialog
  const openRequestDocDialog = (requirement, isResend = false) => {
    setRequestingRequirement(requirement);
    setRequestDocMessage('');
    setIsResendMode(isResend);
    setRequestDocDialogOpen(true);
  };
  
  // State for resend mode
  const [isResendMode, setIsResendMode] = useState(false);
  const [duplicateBlockedInfo, setDuplicateBlockedInfo] = useState(null);
  
  // Send document request email
  const handleRequestDocument = async (forceResend = false) => {
    if (!requestingRequirement) return;
    
    setIsRequestingDoc(true);
    setDuplicateBlockedInfo(null);
    
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/request-document`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            requirement_id: requestingRequirement.id,
            message: requestDocMessage || undefined,
            due_days: 14,
            force_resend: forceResend || isResendMode
          }
        }
      );
      
      const status = response.data.status;
      
      if (status === 'sent') {
        toast.success(response.data.message || 'Request sent successfully');
        setRequestDocDialogOpen(false);
        setRequestingRequirement(null);
        setRequestDocMessage('');
        setIsResendMode(false);
      } else if (status === 'resent') {
        toast.success(response.data.message || 'Request resent successfully');
        setRequestDocDialogOpen(false);
        setRequestingRequirement(null);
        setRequestDocMessage('');
        setIsResendMode(false);
      } else if (status === 'duplicate_blocked') {
        // Show duplicate blocked info and offer resend option
        setDuplicateBlockedInfo({
          message: response.data.message,
          existingRequestId: response.data.existing_request_id
        });
        toast.warning('An active request already exists. Click "Resend" to send a new email.');
      } else {
        toast.info(response.data.message || 'Request processed');
        setRequestDocDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setIsRequestingDoc(false);
    }
  };
  
  // Handle resend action
  const handleResendRequest = () => {
    handleRequestDocument(true);
  };

  // ========== Send Form via Email Handlers ==========
  
  const FORM_OPTIONS = [
    { value: 'staff_health_questionnaire', label: 'Health Questionnaire' },
    { value: 'staff_personal_info', label: 'Personal Details Form' },
    { value: 'hmrc_starter_checklist', label: 'HMRC Starter Checklist' },
    { value: 'interview_record', label: 'Interview Assessment Record (Admin Only)' }
  ];
  
  const openSendFormDialog = (formType) => {
    setSelectedFormType(formType || '');
    setSendFormMessage('');
    setSendFormSetupGate(null);
    setSendFormDialogOpen(true);
  };
  
  const handleSendForm = async () => {
    if (!selectedFormType) {
      toast.error('Please select a form type');
      return;
    }
    
    setIsSendingForm(true);
    setSendFormSetupGate(null);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/send-form`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            form_type: selectedFormType,
            message: sendFormMessage || undefined
          }
        }
      );
      
      if (response.data.status === 'duplicate') {
        toast.info(response.data.message || 'Form request already pending');
      } else {
        toast.success(response.data.message || 'Form request sent');
      }
      
      setSendFormDialogOpen(false);
      setSelectedFormType('');
      setSendFormMessage('');
    } catch (error) {
      const gate = extractTenantSetupGate(error);
      if (gate) {
        setSendFormSetupGate(gate);
        toast.error(tenantSetupGateToastMessage(gate));
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send form request');
      }
    } finally {
      setIsSendingForm(false);
    }
  };

  // ========== Application Form Extraction Handlers ==========
  
  // Start extraction from application form
  const handleExtractFromApplication = async () => {
    setIsExtracting(true);
    setExtractionDialogOpen(true);
    setExtractionResult(null);
    setExtractionFailed(null);
    setFieldsToApply({});
    
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/extract-from-application`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if extraction failed gracefully (returns extraction_failed: true)
      if (response.data.extraction_failed) {
        setExtractionFailed(response.data);
        // Don't show error toast - show the options modal instead
      } else {
        setExtractionResult(response.data);
        
        // Initialize fields to apply based on extraction result
        const initialFields = {};
        response.data.fields.forEach(field => {
          // Default: apply if field is empty in profile OR if extracted value differs
          initialFields[field.field_name] = field.apply;
        });
        setFieldsToApply(initialFields);
        
        toast.success(`Extracted ${response.data.fields.length} fields from application form`);
      }
    } catch (error) {
      // Only show toast for actual API errors (not graceful failures)
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && errorDetail.includes('No application form found')) {
        toast.error('No application form found. Please upload an application form first.');
        setExtractionDialogOpen(false);
      } else {
        // For unexpected errors, show failure options
        setExtractionFailed({
          extraction_failed: true,
          message: errorDetail || 'An unexpected error occurred during extraction.',
          options: [
            { action: 'fill_manually', label: 'Fill form manually', description: 'Enter profile data manually' },
            { action: 'retry', label: 'Retry extraction', description: 'Try extracting again' }
          ]
        });
      }
    } finally {
      setIsExtracting(false);
    }
  };
  
  // Handle extraction failure options
  const handleExtractionOption = async (action) => {
    switch (action) {
      case 'fill_manually':
        setExtractionDialogOpen(false);
        setExtractionFailed(null);
        // Switch to forms tab for manual entry
        setActiveTab('forms');
        toast.info('You can manually enter profile data using the forms below.');
        break;
      case 'view_document':
        if (extractionFailed?.file_url) {
          try {
            const { blobUrl } = await fetchProtectedFileBlob(extractionFailed.file_url, token);
            openBlobUrlInNewTab(blobUrl, 'extraction_source');
            revokeBlobUrlLater(blobUrl);
          } catch (error) {
            toast.error('Failed to open extraction source document');
          }
        }
        break;
      case 'retry':
        setExtractionFailed(null);
        await handleExtractFromApplication();
        break;
      default:
        break;
    }
  };
  
  // Toggle a field for applying
  const toggleFieldToApply = (fieldName) => {
    setFieldsToApply(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };
  
  // Apply selected extracted fields to profile
  const handleApplyExtraction = async () => {
    if (!extractionResult) return;
    
    const selectedFields = Object.entries(fieldsToApply)
      .filter(([_, apply]) => apply)
      .map(([fieldName]) => fieldName);
    
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to apply');
      return;
    }
    
    setIsApplyingExtraction(true);
    try {
      const response = await axios.post(
        `${API}/extractions/${extractionResult.extraction_id}/apply`,
        { extraction_id: extractionResult.extraction_id, fields_to_apply: selectedFields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      // Show success with details
      if (result.applied_fields && result.applied_fields.length > 0) {
        toast.success(`Profile updated: ${result.applied_fields.length} field(s) applied`);
      }
      
      // Show warnings for failed fields
      if (result.warnings?.failed_fields?.length > 0) {
        const failedNames = result.warnings.failed_fields.map(f => f.field).join(', ');
        toast.warning(`Some fields could not be applied: ${failedNames}`);
      }
      
      // Show info for unsupported fields
      if (result.unsupported?.fields?.length > 0) {
        const unsupportedNames = result.unsupported.fields.map(f => f.field).join(', ');
        toast.info(`Unsupported fields skipped: ${unsupportedNames}`);
      }
      
      setExtractionDialogOpen(false);
      setExtractionResult(null);
      
      // Refresh employee data
      try {
        await fetchData();
      } catch (refreshError) {
        console.error('Error refreshing data after apply:', refreshError);
        // Don't show error toast - the apply was successful
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      if (typeof errorDetail === 'object') {
        // Structured error response
        const message = errorDetail.message || 'Failed to apply extracted data';
        const failedFields = errorDetail.failed_fields || [];
        const unsupportedFields = errorDetail.unsupported_fields || [];
        
        if (failedFields.length > 0) {
          const failedInfo = failedFields.map(f => `${f.field}: ${f.reason}`).join('\n');
          toast.error(`${message}\n${failedInfo}`);
        } else if (unsupportedFields.length > 0) {
          toast.error(`${message}: ${unsupportedFields.map(f => f.field).join(', ')}`);
        } else {
          toast.error(message);
        }
      } else {
        toast.error(errorDetail || 'Failed to apply extracted data');
      }
    } finally {
      setIsApplyingExtraction(false);
    }
  };
  
  // Discard extraction without applying
  const handleDiscardExtraction = async () => {
    // If there's a failed extraction, just close the dialog
    if (extractionFailed) {
      setExtractionDialogOpen(false);
      setExtractionFailed(null);
      return;
    }
    
    if (!extractionResult) {
      setExtractionDialogOpen(false);
      return;
    }
    
    try {
      await axios.post(
        `${API}/extractions/${extractionResult.extraction_id}/discard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Extraction discarded');
    } catch (error) {
      // Ignore discard errors
    }
    
    setExtractionDialogOpen(false);
    setExtractionResult(null);
    setExtractionFailed(null);
  };
  
  // Human-readable field name mapping
  const FIELD_LABELS = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email Address',
    phone: 'Phone Number',
    address_line_1: 'Address Line 1',
    address_line_2: 'Address Line 2',
    city: 'City',
    county: 'County',
    postcode: 'Postcode',
    country: 'Country',
    ni_number: 'NI Number',
    date_of_birth: 'Date of Birth',
    next_of_kin_name: 'Next of Kin Name',
    next_of_kin_relationship: 'Next of Kin Relationship',
    next_of_kin_phone: 'Next of Kin Phone',
    next_of_kin_address: 'Next of Kin Address',
    emergency_contact_name: 'Emergency Contact Name',
    emergency_contact_phone: 'Emergency Contact Phone',
    emergency_contact_relationship: 'Emergency Contact Relationship',
    reference_1_name: 'Reference 1 Name',
    reference_1_company: 'Reference 1 Company',
    reference_1_phone: 'Reference 1 Phone',
    reference_1_email: 'Reference 1 Email',
    reference_2_name: 'Reference 2 Name',
    reference_2_company: 'Reference 2 Company',
    reference_2_phone: 'Reference 2 Phone',
    reference_2_email: 'Reference 2 Email',
    has_driving_licence: 'Has Driving Licence',
    driving_licence_type: 'Driving Licence Type',
    has_own_vehicle: 'Has Own Vehicle',
    vehicle_registration: 'Vehicle Registration'
  };

  // ========== Form Submission Handlers ==========
  
  // Open form modal for a specific requirement
  const openFormModal = async (requirementId) => {
    try {
      const response = await axios.get(`${API}/form-submissions/template/${requirementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormTemplate(response.data);
      
      // Check if there's an existing submission to pre-fill
      const existingResponse = await axios.get(`${API}/form-submissions?employee_id=${employeeId}&requirement_id=${requirementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get today's date in YYYY-MM-DD format for auto-fill
      const today = new Date().toISOString().split('T')[0];
      
      if (existingResponse.data && existingResponse.data.length > 0) {
        // Use existing submission data
        setFormData(existingResponse.data[0].data || {});
      } else {
        // Fetch auto-fill data from backend based on employee profile
        try {
          const autoFillResponse = await axios.get(
            `${API}/form-submissions/auto-fill/${requirementId}/${employeeId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Add today's date for signature_date if not already set
          const autoFillData = autoFillResponse.data.auto_fill_data || {};
          if (!autoFillData.signature_date) {
            autoFillData.signature_date = today;
          }
          setFormData(autoFillData);
        } catch (autoFillError) {
          // Fallback to basic employee data if auto-fill endpoint fails
          setFormData({
            employee_name: `${employee.first_name} ${employee.last_name}`,
            full_name: `${employee.first_name} ${employee.last_name}`,
            candidate_name: `${employee.first_name} ${employee.last_name}`,
            signature_date: today
          });
        }
      }
      
      setFormModalOpen(true);
    } catch (error) {
      toast.error('Failed to load form template');
    }
  };
  
  // Submit structured form
  const handleFormSubmit = async () => {
    if (!formTemplate) return;
    
    setIsSubmittingForm(true);
    try {
      await axios.post(`${API}/form-submissions`, {
        employee_id: employeeId,
        requirement_id: formTemplate.requirement_id,
        form_type: formTemplate.form_type,
        data: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${formTemplate.name} submitted successfully`);
      setFormModalOpen(false);
      setFormTemplate(null);
      setFormData({});
      fetchData(); // Refresh all data including compliance requirements
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setIsSubmittingForm(false);
    }
  };
  
  // View submitted form
  const openViewForm = (requirement) => {
    if (requirement.form_submission) {
      setViewFormData({
        ...requirement.form_submission,
        requirementName: requirement.name
      });
      setViewFormOpen(true);
    }
  };
  
  // Verify form submission
  const handleVerifyFormSubmission = async (submissionId) => {
    if ((viewFormData?.form_type || '').toLowerCase() === 'staff_health_questionnaire') {
      toast.error('Select a health outcome in the Review workflow before approving this questionnaire.');
      return;
    }
    try {
      await axios.post(`${API}/form-submissions/${submissionId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form verified successfully');
      setViewFormOpen(false);
      fetchData(); // Refresh all data including compliance requirements
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify form');
    }
  };

  // Generate PDF from form submission (Template-Backed Forms Architecture)
  const handleGenerateFormPDF = async (submissionId, formType) => {
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${API}/form-submissions/${submissionId}/generate-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.file_url) {
        const { blobUrl } = await fetchProtectedFileBlob(response.data.file_url, token);
        openBlobUrlInNewTab(blobUrl, `${formType || 'form'}_${submissionId}.pdf`);
        revokeBlobUrlLater(blobUrl);
        toast.success('PDF generated successfully');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download existing PDF export or generate new one
  const handleDownloadFormPDF = async (submissionId) => {
    try {
      // Use responseType: 'blob' to receive the actual PDF file bytes
      const response = await axios.get(
        `${API}/form-submissions/${submissionId}/download-pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'form.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(await getSafeErrorMessage(error, 'Failed to download PDF'));
    }
  };

  // View PDF in a new tab
  const handleViewFormPDF = async (submissionId) => {
    setInlineViewerUrl(`${API}/form-submissions/${submissionId}/view-pdf`);
    setInlineViewerTitle('Application Form');
    setInlineViewerFilename('application-form.pdf');
    setInlineViewerFallback(null);
    setInlineViewerOpen(true);
  };

  const handleReopenFormForCorrection = async () => {
    const reason = formCorrectionReason.trim();
    if (!formCorrectionDialog?.submission?.id || !reason) {
      toast.error('Correction reason is required');
      return;
    }

    setIsReopeningFormCorrection(true);
    try {
      await axios.post(
        `${API}/form-submissions/${formCorrectionDialog.submission.id}/reopen-for-correction`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Form reopened for worker correction');
      setFormCorrectionDialog(null);
      setFormCorrectionReason('');
      fetchFormSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen form for correction');
    } finally {
      setIsReopeningFormCorrection(false);
    }
  };

  const openFormSubmissionPdfViewer = (submission, form) => {
    if (!submission?.id) {
      setViewFormSubmission({
        isOpen: true,
        formType: form?.key,
        formName: form?.name,
        submissionId: submission?.id,
        data: submission?.data || submission?.form_data
      });
      return;
    }
    setInlineViewerUrl(`${API}/form-submissions/${submission.id}/view-pdf`);
    setInlineViewerTitle(form?.name || 'Form Submission');
    setInlineViewerFilename(`${form?.key || 'form'}_${employee?.first_name || ''}_${employee?.last_name || ''}.pdf`);
    setInlineViewerFallback({
      isOpen: true,
      formType: form?.key,
      formName: form?.name,
      submissionId: submission?.id,
      data: submission?.data || submission?.form_data
    });
    setInlineViewerOpen(true);
  };

  const groupedTemplates = asArray(templates).reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    acc[template.category].push(template);
    return acc;
  }, {});

  const groupedDocs = asArray(documentTypes).reduce((acc, type) => {
    if (!acc[type.category]) acc[type.category] = [];
    const doc = documents.find(d => d.document_type_id === type.id);
    acc[type.category].push({ ...type, document: doc });
    return acc;
  }, {});
  const applicationSubmission =
    formSubmissions.find((submission) => {
      const requirementId = String(submission?.requirement_id || '').toLowerCase();
      const templateId = String(submission?.template_id || '').toLowerCase();
      const formType = String(submission?.form_type || '').toLowerCase();
      const key = String(submission?.key || '').toLowerCase();
      return (
        requirementId === 'application_form'
        || templateId === 'structured_application_form'
        || formType === 'application_form'
        || key === 'application_form'
      );
    }) || null;
  const declarationSubmission =
    formSubmissions.find((submission) => {
      const requirementId = String(submission?.requirement_id || '').toLowerCase();
      const formType = String(submission?.form_type || '').toLowerCase();
      const key = String(submission?.key || '').toLowerCase();
      return requirementId === 'applicant_declarations'
        || formType === 'applicant_declarations'
        || key === 'applicant_declarations';
    }) || null;
  const applicationPdfDocument = documents.find((document) => {
    const requirementId = String(document?.requirement_id || '').toLowerCase();
    const documentType = String(document?.document_type || document?.document_type_name || '').toLowerCase();
    const label = String(document?.title || document?.file_name || document?.original_filename || '').toLowerCase();
    return (
      requirementId === 'application_form_pdf' ||
      documentType === 'application_form' ||
      (label.includes('application') && label.includes('.pdf'))
    );
  }) || null;
  const applicationEmbeddedRecord = employee?.application_pdf_url
    || employee?.application_form_url
    || employee?.application_submission_id
    || employee?.application_id
    ? employee
    : null;
  const recruitmentSectionRows = asArray(complianceFile?.sections?.recruitment_record?.rows);
  const applicationEvidenceRow = recruitmentSectionRows.find((row) => {
    const key = String(row?.requirement_id || row?.requirement_key || row?.key || row?.id || '').toLowerCase();
    const label = String(row?.title || row?.name || row?.label || '').toLowerCase();
    return key.includes('application_form') || label.includes('application form');
  }) || null;
  const cvEvidenceRow = recruitmentSectionRows.find((row) => {
    const key = String(row?.requirement_id || row?.requirement_key || row?.key || row?.id || '').toLowerCase();
    const label = String(row?.title || row?.name || row?.label || '').toLowerCase();
    return key === 'cv' || key.includes('resume') || key.includes('curriculum_vitae') || label.includes('cv') || label.includes('resume');
  }) || null;
  const rtwSummary = complianceRequirements?.rtw_summary || {};
  const cvReviewState = getCvReviewState(employee, documents);
  const {
    cvDocuments,
    activePdfCvCandidates,
    exactLinkedReviewDocument,
    linkablePdfCvDocument: linkedOrFallbackPdfCvDocument,
    cvReviewReady,
    cvLinkRecoveryAvailable,
  } = cvReviewState;
  const activeCvDocument = exactLinkedReviewDocument || null;
  const linkedDocumentId = String(employee?.cv_document_id || '').trim();
  const isLinkedActiveCvDocument = (document) => Boolean(
    linkedDocumentId &&
    activeCvDocument &&
    document === activeCvDocument
  );
  // If cv_document_id is set but no document matches, do NOT fall back to cvDocuments[0].
  // Opening the wrong file would silently mismatch what the backend's Review CV operates on.
  const cvDocumentIdIsSet = Boolean(employee?.cv_document_id);
  const cvDocument = activeCvDocument || (cvDocumentIdIsSet ? null : cvDocuments[0] || null);
  const activeNonPdfCvCandidates = cvDocuments.filter((document) => isActiveDocumentCandidate(document) && !isPdfLikeDocument(document));
  const complianceEmploymentHistoryGapRow = complianceFile?.sections?.employment_history?.rows?.[0] || null;
  // Canonical admin read source: persisted employment_review first, compliance-file row fallback during rollout.
  const hasPersistedEmploymentReview = Boolean(employmentReviewPersisted && employmentReview?.id);
  const employmentReviewSegments = Array.isArray(employmentReview?.segments) ? employmentReview.segments : [];
  const persistedEmploymentSegments = employmentReviewSegments.filter((segment) => segment?.type === 'employment');
  const persistedGapSegments = employmentReviewSegments.filter((segment) => segment?.type === 'gap');
  const canonicalEmploymentRecords = hasPersistedEmploymentReview
    ? persistedEmploymentSegments.map((segment) => ({
        ...(segment.raw_record || {}),
        id: segment.id,
        employer: segment.employer || segment.organisation,
        employer_name: segment.employer || segment.organisation,
        company: segment.organisation || segment.employer,
        job_title: segment.title || segment.role,
        role: segment.role || segment.title,
        start_date: segment.start_date,
        end_date: segment.end_date === 'present' ? null : segment.end_date,
        source: segment.source,
      }))
    : Array.isArray(complianceEmploymentHistoryGapRow?.employment_records)
      ? complianceEmploymentHistoryGapRow.employment_records
    : [];
  const cvExtractedEmploymentRecords = getCvExtractedEmploymentRecords(employee, cvExtractionResult);
  const editableEmploymentHistory = getEditableEmploymentHistory(
    employee?.employment_history,
    canonicalEmploymentRecords,
  );
  const employmentHistoryEmptyState = getEmploymentHistoryEmptyState({
    canonicalEmploymentRecords,
    cvExtractedEmploymentRecords,
  });
  const mapPersistedGapSegment = (gap) => normalizeGapRecord({
    ...gap,
    id: gap.id || gap.gap_id || gap.explanation?.matched_gap_id || gap.explanation?.source_gap_id,
    gap_id: gap.gap_id || gap.id,
    canonical_review_segment_id: gap.id,
    canonical_review_gap_id: gap.gap_id,
    canonical_review_source_gap_id: gap.explanation?.source_gap_id || gap.explanation?.matched_gap_id,
    gap_start: gap.start_date,
    gap_end: gap.end_date,
    raw_status: gap.raw_status || gap.status,
    explanation: gap.explanation?.text || gap.explanation?.explanation,
    reason_type: gap.explanation?.reason_type,
    explanation_provided_at: gap.explanation?.submitted_at,
    verified_by: gap.admin_review?.reviewed_by,
    verified_by_name: gap.admin_review?.reviewed_by_name,
    verified_at: gap.admin_review?.reviewed_at,
    rejection_reason: gap.admin_review?.rejection_reason,
  });
  const legacyEmploymentGaps = Array.isArray(complianceEmploymentHistoryGapRow?.canonical_gaps)
    ? complianceEmploymentHistoryGapRow.canonical_gaps
    : (complianceEmploymentHistoryGapRow?.gaps || []);
  const canonicalEmploymentGaps = hasPersistedEmploymentReview
    ? persistedGapSegments.map(mapPersistedGapSegment)
    : legacyEmploymentGaps.map(normalizeGapRecord);
  const employmentReviewReferenceDate =
    employmentReview?.employment_window_reference_date
    || employmentReview?.coverage?.coverage_end
    || null;
  const formatEmploymentReviewDate = (value) => {
    const text = String(value || '').trim().toLowerCase();
    if (['present', 'current', 'ongoing', 'now', 'to date'].includes(text) && employmentReviewReferenceDate) {
      return formatEmploymentBoundary(employmentReviewReferenceDate, { fallback: 'Review date' });
    }
    return formatEmploymentBoundary(value, { fallback: 'Date not available' });
  };
  const matchedApplicantGapExplanations = hasPersistedEmploymentReview
    ? persistedGapSegments
        .filter((gap) => gap?.explanation)
        .map((gap) => ({
          ...(gap.explanation || {}),
          explanation: gap.explanation?.text || gap.explanation?.explanation,
          reason_type: gap.explanation?.reason_type,
          gap_id: gap.gap_id || gap.id,
          matched_gap_id: gap.gap_id || gap.id,
          gap_start: gap.start_date,
          gap_end: gap.end_date,
          duration_days: gap.duration_days,
          duration_months: gap.duration_months,
          matched_by: gap.explanation?.matched_by,
        }))
    : (complianceEmploymentHistoryGapRow?.matched_applicant_explanations || []);
  const unmatchedApplicantGapExplanations = hasPersistedEmploymentReview
    ? (employmentReview?.unmatched_applicant_notes || [])
    : (complianceEmploymentHistoryGapRow?.unmatched_applicant_explanations || []);
  const invalidEmploymentEntries = hasPersistedEmploymentReview
    ? (employmentReview?.invalid_entries || [])
    : (complianceEmploymentHistoryGapRow?.invalid_employment_entries || []);
  // A canonical cannot_assess status often means bad input, NOT a crashed analysis.
  // Valid rows produce provisional gaps; excluded source rows still need correction.
  const employmentHistoryNeedsCorrection = invalidEmploymentEntries.length > 0;
  const canonicalGapSegmentCount = Number(
    employmentReview?.top_summary?.gap_segments ?? persistedGapSegments.length
  ) || 0;
  const canonicalMissingGapCount = Number(employmentReview?.top_summary?.missing_gaps || 0) || 0;
  const employmentGapEvaluation = hasPersistedEmploymentReview
    ? {
        has_gaps: canonicalGapSegmentCount > 0,
        total_gaps: canonicalGapSegmentCount,
        verified_count: employmentReview?.top_summary?.verified_gaps || 0,
        pending_count: employmentReview?.top_summary?.missing_gaps || 0,
        explained_count: employmentReview?.top_summary?.explained_gaps || 0,
        rejected_count: employmentReview?.top_summary?.rejected_gaps || 0,
        needs_info_count: 0,
        is_complete: canonicalGapSegmentCount > 0
          && canonicalGapSegmentCount === (employmentReview?.top_summary?.verified_gaps || 0),
      }
    : (complianceEmploymentHistoryGapRow?.gap_evaluation || null);
  const gapAnalysisRun = hasPersistedEmploymentReview || Boolean(complianceEmploymentHistoryGapRow?.gap_analysis_run);
  const employmentHistoryGapRow = hasPersistedEmploymentReview
    ? {
        has_gaps: canonicalGapSegmentCount > 0,
        status_summary: employmentReview?.status_reason,
        gap_evaluation: employmentGapEvaluation,
        gap_analysis_run: true,
        gap_analysis_status: employmentReview?.diagnostics?.analysis_status,
        gap_analysis_error: employmentReview?.diagnostics?.analysis_error,
      }
    : complianceEmploymentHistoryGapRow;
  const showEmploymentGapPanel = Boolean(
    employmentHistoryGapRow?.has_gaps || (hasPersistedEmploymentReview && canonicalMissingGapCount > 0)
  );
  // ER-S1: the canonical snapshot is the SINGLE source of truth for the five
  // Employment Review cards, their actions and sign-off. When it is loaded we
  // consume it authoritatively and stop relying on the older local heuristics.
  const snap = employmentSnapshot || null;
  const snapApp = snap?.application || null;
  const snapCv = snap?.cv || null;
  // Multi-format CV: does the linked/active CV support an inline PDF preview
  // (PDF, or a Word CV that has a generated PDF preview)? Word CVs without a
  // preview use a secure Open/Download action instead of a fake inline view.
  const cvFileFormat = snapCv?.file_format || getDocumentFileFormat(activeCvDocument || cvDocument);
  const cvInlinePreviewable = snapCv
    ? Boolean(snapCv.is_pdf_viewable)
    : isInlinePreviewableCvDocument(activeCvDocument || cvDocument);
  const snapWork = snap?.work_history || null;
  const snapGaps = snap?.gaps || null;
  const snapDecl = snap?.declarations || null;
  const snapIntegrity = snap?.employment_integrity || null;
  const snapItems = Array.isArray(snap?.summary?.items) ? snap.summary.items : null;
  const snapSignOff = snap?.sign_off || null;
  // ER-S1: View CV visibility + target come ONLY from the snapshot when loaded.
  const cvViewAvailable = snapCv ? Boolean(snapCv.view_available) : null;
  // Canonical application-on-file decision comes from the backend snapshot
  // (services/employment_review_snapshot -> services/application_source) and is the
  // single source of truth shared with Employment Review sign-off. Fall back to the
  // legacy union only when the snapshot has not loaded yet.
  const applicationAvailable = snapApp
    ? Boolean(snapApp.on_file)
    : (typeof employee?.application_on_file === 'boolean'
        ? employee.application_on_file
        : Boolean(
            applicationSubmission ||
            applicationPdfDocument ||
            applicationEmbeddedRecord ||
            applicationEvidenceRow
          ));
  const cvEvidenceUrl =
    cvEvidenceRow?.file_url
    || cvEvidenceRow?.download_url
    || cvEvidenceRow?.view_url
    || cvEvidenceRow?.document_url
    || cvEvidenceRow?.files?.[0]?.file_url
    || cvEvidenceRow?.files?.find((f) => f?.file_url)?.file_url
    || null;
  const cvEmbeddedUrl = employee?.cv_url || employee?.cv_file_url || employee?.resume_url || null;
  const cvDocumentUrl =
    cvDocument?.file_url
    || cvDocument?.download_url
    || cvDocument?.view_url
    || cvDocument?.document_url
    || null;
  const cvFileExists = Boolean(
    activeCvDocument ||
    cvDocumentUrl ||
    cvEmbeddedUrl ||
    cvEvidenceUrl
  );
  const cvDocumentName = cvDocument?.original_filename || cvDocument?.file_name || cvDocument?.file_url || '';
  const cvDocumentMimeType = (cvDocument?.mime_type || cvDocument?.content_type || cvDocument?.file_type || '').toLowerCase();
  const cvIsPdf = Boolean(
    cvDocument && (cvDocumentMimeType === 'application/pdf' || /\.pdf(?:$|\?)/i.test(cvDocumentName))
  );
  const cvEvidencePdfFile = (cvEvidenceRow?.files || []).find((file) => {
    const name = file?.file_name || file?.original_filename || file?.file_url || '';
    const mime = (file?.mime_type || file?.content_type || file?.file_type || '').toLowerCase();
    return mime === 'application/pdf' || /\.pdf(?:$|\?)/i.test(name);
  }) || null;
  const linkablePdfCvDocument =
    linkedOrFallbackPdfCvDocument ||
    (cvEvidencePdfFile?.id && !cvDocumentIdIsSet
      ? {
          id: cvEvidencePdfFile.id,
          file_name: cvEvidencePdfFile.file_name || cvEvidencePdfFile.original_filename,
          original_filename: cvEvidencePdfFile.original_filename || cvEvidencePdfFile.file_name,
        }
      : null);
  const linkablePdfCvDocumentId = canonicalDocumentIdentity(linkablePdfCvDocument);
  const cvLinkedForReview = Boolean(employee?.cv_document_id);
  const cvLegacyNonPdfOnly = Boolean(!cvReviewReady && !cvLinkRecoveryAvailable && activeNonPdfCvCandidates.length > 0);
  const cvHasCanonicalEvidence = Boolean(
    activeCvDocument ||
    cvDocumentUrl ||
    cvEvidenceUrl ||
    cvEmbeddedUrl
  );
  const cvCanViewFromFallback = !cvReviewReady && Boolean(cvEvidenceUrl);
  // Display-only: surface the admin-requested replacement state alongside the
  // "On file" badge so the admin view does not silently contradict the
  // readiness blocker ("CV was rejected — please upload a replacement") and
  // the worker dashboard ("CV on file — replacement requested"). No new
  // truth: reads employee.cv_status directly from the backend record.
  const cvReplacementPending = [
    'rejected',
    'replacement_requested',
    'replacement_required',
    'reupload_required',
  ].includes((employee?.cv_status || '').toLowerCase());
  const cvApproved = String(employee?.cv_status || '').toLowerCase() === 'approved';
  const cvServerLinkRecoveryAvailable = shouldOfferServerCvLinkRecovery({
    cvReviewReady,
    cvLinkRecoveryAvailable,
    cvHasCanonicalEvidence,
    cvReplacementPending,
    cvApproved,
  });
  const cvStatusLabel = snapCv
    ? (snapCv.label || 'Missing')
    : cvApproved && cvReviewReady
    ? 'Approved'
    : cvReviewReady && cvReplacementPending
    ? 'Replacement requested'
    : cvReviewReady
      ? 'On file'
      : cvHasCanonicalEvidence
        ? 'On file'
      : cvLinkRecoveryAvailable
        ? 'On file — awaiting review'
        : cvLegacyNonPdfOnly
          ? 'On file — awaiting review'
          : 'Missing';
  const snapCvBadgeClass = snapCv ? ({
    approved: 'bg-green-100 text-green-800 border-green-200',
    under_review: 'bg-amber-100 text-amber-700 border-amber-200',
    recovered: 'bg-blue-100 text-blue-700 border-blue-200',
    replacement_required: 'bg-amber-100 text-amber-700 border-amber-200',
    repair_required: 'bg-amber-100 text-amber-700 border-amber-200',
    missing: 'bg-gray-100 text-gray-600 border-gray-200',
  }[snapCv.state] || 'bg-gray-100 text-gray-600 border-gray-200') : null;
  const cvStatusBadgeClass = snapCvBadgeClass
    ? snapCvBadgeClass
    : cvApproved && cvReviewReady
    ? 'bg-green-100 text-green-800 border-green-200'
    : cvReviewReady && cvReplacementPending
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : cvReviewReady
      ? 'bg-green-100 text-green-700 border-green-200'
      : cvHasCanonicalEvidence
        ? 'bg-green-100 text-green-700 border-green-200'
      : cvLinkRecoveryAvailable
        ? 'bg-blue-100 text-blue-700 border-blue-200'
        : cvLegacyNonPdfOnly
          ? 'bg-amber-100 text-amber-700 border-amber-200'
          : 'bg-gray-100 text-gray-600 border-gray-200';
  const gapVerifiedCount = employmentGapEvaluation?.verified_count;
  const gapNeedsReviewCount = employmentGapEvaluation
    ? (employmentGapEvaluation?.pending_count || 0) +
      (employmentGapEvaluation?.explained_count || 0) +
      (employmentGapEvaluation?.rejected_count || 0) +
      (employmentGapEvaluation?.needs_info_count || 0)
    : null;
  const employmentHistoryExists = Boolean(canonicalEmploymentRecords.length > 0);
  const employmentHistoryHasDatedRows = Boolean(
    canonicalEmploymentRecords.some((job) => job?.start_date)
  );
  const employmentCoverage = hasPersistedEmploymentReview
    ? (employmentReview?.coverage || null)
    : (employmentHistoryGapRow?.employment_coverage || null);
  const coveragePercent = Number(employmentCoverage?.coverage_percent ?? employmentCoverage?.percent);
  const accountedPercent = Number(
    employmentCoverage?.accounted_percent
    ?? employmentCoverage?.coverage_accounted_percent
    ?? employmentCoverage?.percent_accounted
  );
  const legacyPercent = Number(employmentCoverage?.percent);
  const displayCoveragePercentRaw = Number.isFinite(accountedPercent)
    ? accountedPercent
    : (Number.isFinite(coveragePercent) ? coveragePercent : legacyPercent);
  const displayCoveragePercent = Number.isFinite(displayCoveragePercentRaw)
    ? Math.min(displayCoveragePercentRaw, 100)
    : displayCoveragePercentRaw;
  const directCoveragePercent = Number.isFinite(coveragePercent) ? Math.min(coveragePercent, 100) : coveragePercent;
  const coverageHasNumericPercent = Number.isFinite(displayCoveragePercent);
  const coverageTotalDaysRequired = Number(employmentCoverage?.total_days_required);
  const coverageTotalDaysCovered = Number(employmentCoverage?.total_days_covered);
  const coverageTotalDaysExplained = Number(
    employmentCoverage?.total_days_explained
    ?? employmentCoverage?.days_explained
    ?? employmentCoverage?.explained_days
  );
  const coverageInformationalOnly = Boolean(employmentCoverage?.informational_only);
  const coverageInfoMessage = employmentCoverage?.message || employmentCoverage?.note || null;
  const coverageHasUsableSummary = Boolean(
    employmentCoverage &&
    coverageHasNumericPercent &&
    Number.isFinite(coverageTotalDaysRequired) &&
    coverageTotalDaysRequired > 0 &&
    Number.isFinite(coverageTotalDaysCovered)
  );
  const coverageDisplayPercent = coverageHasUsableSummary ? displayCoveragePercent : 0;
  const coverageLooksStaleOrUnusable = Boolean(
    employmentCoverage &&
    employmentHistoryHasDatedRows &&
    coverageHasNumericPercent &&
    displayCoveragePercent === 0 &&
    !employmentCoverage?.earliest_entry_date &&
    !employmentCoverage?.latest_entry_date
  );
  const coverageAssessed = Boolean(coverageHasUsableSummary && !coverageLooksStaleOrUnusable);
  const coverageMet = Boolean(
    employmentCoverage?.meets_10_year_requirement && coverageAssessed
  );
  const employmentCannotAssess = !hasPersistedEmploymentReview && !complianceFile;
  const employmentSignedOff = hasPersistedEmploymentReview
    ? Boolean(employmentReview?.sign_off?.signed_off)
    : Boolean(employee?.employment_review_signed_off);
  const cvReconcileBlocked = cvApproved || employmentSignedOff;
  const employmentSignedOffBy = hasPersistedEmploymentReview
    ? (employmentReview?.sign_off?.signed_off_by_name || employmentReview?.sign_off?.signed_off_by || null)
    : (employee?.employment_review_signed_off_by_name || employee?.employment_review_signed_off_by || null);
  const employmentSignedOffAt = hasPersistedEmploymentReview
    ? employmentReview?.sign_off?.signed_off_at
    : employee?.employment_review_signed_off_at || null;
  const gapAnalysisFailed = hasPersistedEmploymentReview
    ? employmentReview?.status === 'cannot_assess'
    : employmentHistoryGapRow?.gap_analysis_status === 'failed';
  const gapAnalysisError = employmentHistoryGapRow?.gap_analysis_error;
  const employmentGapsCannotAssess = Boolean(
    hasPersistedEmploymentReview
      ? employmentReview?.status === 'cannot_assess'
      : employmentHistoryExists && (gapAnalysisFailed || !employmentHistoryGapRow || !gapAnalysisRun || !employmentGapEvaluation)
  );
  const allGapsResolved = Boolean(
    hasPersistedEmploymentReview
      ? employmentGapEvaluation?.is_complete
      : employmentHistoryExists &&
        gapAnalysisRun &&
        employmentGapEvaluation &&
        employmentGapEvaluation.is_complete &&
        gapNeedsReviewCount === 0
  );
  // Applicant declarations may be in the structured application before the
  // employee review summary has been backfilled.
  const declarationSummaryOnFile = Boolean(
    employee?.declarations && 'dbs_consent_given' in employee.declarations
  );
  const submissionHasDeclarationAnswers = (submission) => {
    const data = submission?.form_data || submission?.data || {};
    if (!data || typeof data !== 'object') return false;
    if (submission === declarationSubmission) {
      return [
        'dbs_consent_given',
        'information_accurate_declared',
        'consents_to_reference_checks',
        'consents_to_background_checks',
        'consents_to_data_processing',
      ].some((key) => data[key] !== undefined && data[key] !== null && data[key] !== '');
    }
    return ['criminal_declaration', 'health_declaration', 'right_to_work', 'declarations']
      .some((key) => data[key] && typeof data[key] === 'object' && Object.keys(data[key]).length > 0);
  };
  const declarationsAvailableFromApplication = Boolean(
    submissionHasDeclarationAnswers(declarationSubmission)
    || submissionHasDeclarationAnswers(applicationSubmission)
    || employee?.applicant_declaration_source
    || employee?.applicant_declaration_submission_id
    || employee?.declarations?.applicant_declaration_source
    || employee?.declarations?.applicant_declaration_submission_id
  );
  const declarationsOnFile = Boolean(declarationSummaryOnFile || declarationsAvailableFromApplication);
  const declarationsReviewRecorded = Boolean(
    employee?.declarations_reviewed_at ||
    employee?.declarations_reviewed_by ||
    employee?.declarations?.reviewed_at ||
    employee?.declarations?.reviewed_by ||
    employee?.declarations?.review_status === 'reviewed' ||
    employee?.declarations?.review_status === 'signed_off' ||
    employee?.declarations?.reviewed === true
  );
  const declarationsAdequatelyReviewed = Boolean(
    declarationsOnFile && declarationsReviewRecorded
  );
  const declarationDisplayState = deriveApplicantDeclarationDisplayState({
    declarationsOnFile,
    declarationsAdequatelyReviewed,
    declarationSubmission,
    employee,
  });
  const declarationsReadyForReview = ['awaiting_review', 'reviewed'].includes(
    declarationDisplayState.key
  );
  const declarationsApplicantComplete =
    declarationsReadyForReview || declarationDisplayState.key === 'submitted_unlinked';
  // Regulation 19 / Schedule 3 item 7: full employment history must be evidenced.
  // Tenant policy requires an active CV/resume on file before Employment Review sign-off.
  const cvOnFileForSignOff = Boolean(
    cvReviewReady
    && cvApproved
    && (
      !hasPersistedEmploymentReview
      || (
        employmentReview?.cv_evidence?.on_file
        && employmentReview.cv_evidence.admin_approved !== false
      )
    ),
  );
  const cvLinkBlocksReview = Boolean(cvFileExists && !cvReviewReady);
  // Pre-conditions gate: all data requirements satisfied, ready for admin sign-off.
  // ER-S1: when the canonical snapshot is loaded, sign-off eligibility is driven
  // by snap.sign_off.ready — the SAME decision the sign-off endpoint enforces.
  const legacyEmploymentReadyForSignOff = hasPersistedEmploymentReview
    ? Boolean(
        !employmentSignedOff &&
        !employmentCannotAssess &&
        applicationAvailable &&
        declarationsAdequatelyReviewed &&
        employmentHistoryExists &&
        !employmentGapsCannotAssess &&
        employmentReview?.gap_actions?.can_sign_off &&
        cvOnFileForSignOff
      )
    : Boolean(
        !employmentSignedOff &&
        !employmentCannotAssess &&
        applicationAvailable &&
        declarationsAdequatelyReviewed &&
        employmentHistoryExists &&
        coverageAssessed &&
        coverageMet &&
        !employmentGapsCannotAssess &&
        allGapsResolved &&
        cvOnFileForSignOff
      );
  const employmentReadyForSignOff = snapSignOff
    ? Boolean(snapSignOff.ready && !employmentSignedOff)
    : legacyEmploymentReadyForSignOff;
  // Final "Complete" requires the persisted sign-off, not just derived conditions
  const employmentDecisionState = employmentSignedOff
    ? 'Signed off'
    : employmentCannotAssess || employmentGapsCannotAssess || (!hasPersistedEmploymentReview && !coverageAssessed)
      ? 'Cannot assess'
      : employmentReadyForSignOff
        ? 'Ready for sign-off'
        : 'Blocked';
  const legacyEmploymentStatusBlockers = [
    employmentCannotAssess ? 'Compliance file unavailable' : null,
    !applicationAvailable ? 'Application evidence missing' : null,
    !declarationsAdequatelyReviewed ? 'Declarations not verified' : null,
    !employmentHistoryExists ? 'Employment history missing' : null,
    !employmentCoverage ? 'Coverage not assessed — run gap analysis' : null,
    (employmentCoverage && !coverageAssessed) ? 'Cannot assess 10-year coverage from current data' : null,
    (coverageAssessed && !coverageMet) ? '10-year coverage not met' : null,
    employmentGapsCannotAssess ? 'Unable to confirm gaps from the current history' : null,
    (!employmentGapsCannotAssess && employmentHistoryExists && !allGapsResolved) ? 'Detected gaps unresolved' : null,
  ].filter(Boolean);
  const canonicalReviewBlockers = hasPersistedEmploymentReview
    ? (employmentReview?.gap_actions?.blocked_sign_off_reasons || [])
    : [];
  const employmentStatusBlockers = hasPersistedEmploymentReview
    ? [
        employmentReviewError ? 'Canonical employment review unavailable' : null,
        !applicationAvailable ? 'Application evidence missing' : null,
        !declarationsAdequatelyReviewed ? 'Declarations not verified' : null,
        !employmentHistoryExists ? 'Employment history missing' : null,
        !cvOnFileForSignOff
          ? (cvFileExists && !cvApproved
            ? 'CV must be reviewed and approved by admin'
            : 'CV / resume not on file')
          : null,
        employmentGapsCannotAssess ? 'Unable to confirm gaps from the current history' : null,
        ...canonicalReviewBlockers,
      ].filter(Boolean)
    : [
        ...legacyEmploymentStatusBlockers,
        !cvOnFileForSignOff
          ? (cvFileExists && !cvApproved
            ? 'CV must be reviewed and approved by admin'
            : 'CV / resume not on file')
          : null,
      ].filter(Boolean);
  const employmentStatusWarnings = [
    cvFileExists && !cvApproved && !cvReplacementPending
      ? 'Review and approve the CV before Employment Review sign-off'
      : null,
    cvFileExists && !cvOnFileForSignOff && cvLinkRecoveryAvailable
      ? 'CV needs checking before Employment Review can be signed off'
      : null,
  ].filter(Boolean);
  const openEmploymentSection = (selector) => {
    window.requestAnimationFrame(() => {
      const target = document.querySelector(selector);
      if (target) {
        // Opening the canonical action must also reveal any folded source record.
        const disclosure = target.matches('details') ? target : target.closest('details');
        if (disclosure) disclosure.open = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };
  const openEmploymentReviewEvidence = (key) => {
    // Navigate to the existing evidence, not an alternative approval/write path.
    switch (key) {
      case 'application':
        setActiveTab('forms');
        break;
      case 'work_history':
        openEmploymentSection('[data-testid="section-employment-history"]');
        break;
      case 'gaps':
        openEmploymentSection('[data-testid="section-employment-gaps"]');
        break;
      case 'cv':
        openEmploymentSection('[data-testid="section-employment-cv"]');
        break;
      case 'declarations':
        openEmploymentSection('[data-testid="section-employment-declarations"]');
        break;
      default:
        // Unknown canonical actions never invoke a mutation or approval.
        break;
    }
  };
  const displayEmploymentBlocker = (blocker) => {
    const text = String(blocker || '');
    if (/canonical employment review/i.test(text)) return 'Work history review is not available yet';
    if (/declarations not verified/i.test(text)) return 'Declarations need checking';
    if (/employment history missing/i.test(text)) return 'Work history needs adding';
    if (/cv must be reviewed/i.test(text)) return 'CV needs checking';
    if (/cv \/ resume not on file/i.test(text)) return 'CV is missing';
    if (/unable to confirm gaps/i.test(text)) return 'Work history gaps need checking';
    if (/detected gaps unresolved/i.test(text)) return 'Work history gaps need checking';
    if (/employment-history integrity check/i.test(text)) return 'CV & work history check is not complete';
    if (/coverage/i.test(text)) return '10-year work history needs checking';
    return text;
  };
  const legacyEmploymentCockpitBlockers = Array.from(new Set(employmentStatusBlockers.map(displayEmploymentBlocker)));
  // ER-S1: when the canonical snapshot is loaded, the sign-off blockers come
  // straight from it (deduped) so the summary never repeats or contradicts the
  // detailed sections' canonical blockers.
  const employmentCockpitBlockers = snapSignOff
    ? Array.from(new Set((snapSignOff.blockers || []).map(displayEmploymentBlocker)))
    : legacyEmploymentCockpitBlockers;
  const snapItemIcons = { application: FileText, work_history: Briefcase, gaps: CalendarClock, cv: FileCheck, declarations: Shield };
  const legacyEmploymentCockpitItems = [
    {
      id: 'application',
      label: 'Application',
      status: applicationAvailable ? 'done' : 'action',
      detail: applicationAvailable ? 'On file' : 'Missing',
      icon: FileText,
    },
    {
      id: 'work-history',
      label: 'Work history',
      status: employmentHistoryExists ? 'done' : 'action',
      detail: employmentHistoryExists
        ? `${canonicalEmploymentRecords.length} role${canonicalEmploymentRecords.length === 1 ? '' : 's'} saved`
        : 'Needs adding',
      icon: Briefcase,
    },
    {
      id: 'gaps',
      label: 'Gaps',
      status: employmentHistoryExists && !employmentGapsCannotAssess && allGapsResolved ? 'done' : 'action',
      detail: employmentHistoryExists
        ? allGapsResolved
          ? 'Checked'
          : 'Needs checking'
        : 'Waiting for work history',
      icon: CalendarClock,
    },
    {
      id: 'cv',
      label: 'CV',
      status: cvOnFileForSignOff ? 'done' : 'action',
      detail: cvOnFileForSignOff
        ? 'Approved'
        : cvFileExists
          ? 'Needs checking'
          : 'Missing',
      icon: FileCheck,
    },
    {
      id: 'declarations',
      label: 'Declarations',
      status: declarationsAdequatelyReviewed ? 'done' : 'action',
      detail: declarationsAdequatelyReviewed
        ? 'Checked'
        : declarationDisplayState.key === 'incomplete'
          ? 'Incomplete'
          : declarationDisplayState.key === 'submitted_unlinked'
            ? 'System check'
            : declarationsReadyForReview
              ? 'Needs checking'
              : 'Not submitted',
      icon: Shield,
    },
  ];
  // ER-S1: the five summary items are driven by the SAME canonical snapshot
  // objects that drive the detailed cards and the sign-off gate.
  const employmentClassificationLabels = {
    valid_on_file: 'Valid / on file',
    recoverable: 'On file — CareOps can prepare it',
    applicant_action_required: 'Applicant action needed',
    admin_action_required: 'Osabea action needed',
    genuinely_missing: 'Missing',
  };
  const employmentCockpitItems = snapItems
    ? snapItems.map((item) => ({
        id: item.key === 'work_history' ? 'work-history' : item.key,
        label: item.title,
        status: item.blocking ? 'action' : 'done',
        detail: item.label,
        classification: item.classification,
        classificationLabel: employmentClassificationLabels[item.classification] || item.label,
        owner: item.owner,
        nextActionLabel: item.next_action_label,
        icon: snapItemIcons[item.key] || FileText,
      }))
    : legacyEmploymentCockpitItems;
  const integrityState = String(snapIntegrity?.state || '').toLowerCase();
  const integritySourceState = employmentComparisonSourceState(snap);
  const integrityUnresolvedDifferences = Array.isArray(snapIntegrity?.unresolved_material_discrepancies)
    ? snapIntegrity.unresolved_material_discrepancies
    : [];
  const integrityResolvedDifferences = Array.isArray(snapIntegrity?.resolved_material_discrepancies)
    ? snapIntegrity.resolved_material_discrepancies
    : [];
  // Submitted evidence and the latest integrity scan are different things.
  // A pending system check must not become a false applicant work-history task.
  const employmentCoreEvidenceOnFile = Array.isArray(snapItems)
    && snapItems.length >= 5
    && snapItems.every((item) => !item?.blocking);
  const employmentSystemOnlyPending = Boolean(!employmentSignedOff
    && employmentCoreEvidenceOnFile
    && ['checking', 'error'].includes(integrityState));
  const integrityApplicantGapActions = Number(snapIntegrity?.gap_summary?.worker_action || 0);
  const integrityAdminGapReviews = Number(snapIntegrity?.gap_summary?.admin_review || 0);
  const integrityNeedsAttention = Boolean(
    snapIntegrity
    && !employmentSignedOff
    && !['awaiting_admin_review', 'approved'].includes(integrityState)
  );
  const employmentAttentionCount = snap?.summary
    ? Number(snap.summary.attention_count || 0) + (integrityNeedsAttention ? 1 : 0)
    : employmentCockpitItems.filter((it) => it.status === 'action').length;
  const legacyEmploymentCockpitNextAction = employmentReadyForSignOff || employmentSignedOff
    ? null
    : !applicationAvailable
      ? {
          label: 'Open application forms',
          icon: FileText,
          variant: 'outline',
          onClick: () => setActiveTab('forms'),
        }
      : !employmentHistoryExists
        ? {
            label: 'Add work history',
            icon: Briefcase,
            variant: 'outline',
            onClick: () => setEditEmploymentOpen(true),
          }
        : employmentGapsCannotAssess
          ? {
              label: 'Rebuild work history',
              icon: RefreshCw,
              variant: 'outline',
              disabled: employmentReviewRebuildLoading,
              onClick: handleRebuildEmploymentReview,
            }
          : !allGapsResolved
            ? {
                label: 'Check gaps',
                icon: CalendarClock,
                variant: 'outline',
                onClick: () => openEmploymentSection('[data-testid="section-employment-gaps"]'),
              }
            : !cvOnFileForSignOff
              ? cvReviewReady && !cvApproved
                ? {
                    label: 'Review CV',
                    icon: FileCheck,
                    variant: 'outline',
                    disabled: cvReviewLoading,
                    onClick: handleReviewCv,
                  }
                : cvServerLinkRecoveryAvailable || cvLinkRecoveryAvailable || cvFileExists
                  ? {
                      label: 'Review CV',
                      icon: FileCheck,
                      variant: 'outline',
                      disabled: cvReviewLoading,
                      onClick: handleReviewCvSimple,
                    }
                  : {
                      label: 'Request CV',
                      icon: Send,
                      variant: 'outline',
                      onClick: () => openCvRequestDialog('upload'),
                    }
              : !declarationsAdequatelyReviewed && declarationsReadyForReview
                ? {
                    label: 'Review declarations',
                    icon: Shield,
                    variant: 'outline',
                    onClick: () => setReviewDeclarationsOpen(true),
                  }
                : null;

  // When the canonical Employment Review snapshot is available, its ordered
  // blocker list owns the cockpit CTA. This prevents a stale local heuristic
  // from sending staff to the wrong place (for example opening blank application
  // forms when the backend says a historical application can be recovered).
  const canonicalEmploymentAction = deriveEmploymentReviewNextAction(
    snap,
    { signedOff: employmentSignedOff },
  );
  const canonicalEmploymentCockpitNextAction = (() => {
    if (!canonicalEmploymentAction) return null;

    switch (canonicalEmploymentAction.key) {
      case 'recover_application':
        return {
          label: 'Use saved application',
          icon: FileText,
          variant: 'outline',
          onClick: handleRecoverApplication,
        };
      case 'review_possible_historical_application':
        return {
          label: 'Choose saved application',
          icon: Eye,
          variant: 'outline',
          onClick: handleOpenApplicationCandidates,
        };
      case 'confirm_recovered_application':
        return {
          label: 'Applicant confirmation needed',
          icon: FileText,
          variant: 'outline',
          onClick: () => setActiveTab('forms'),
        };
      case 'complete_application':
        return {
          label: 'Application needed',
          icon: FileText,
          variant: 'outline',
          onClick: () => setActiveTab('forms'),
        };
      case 'add_work_history':
        return {
          label: 'Work history needed',
          icon: Briefcase,
          variant: 'outline',
          onClick: () => openEmploymentSection('[data-testid="section-employment-history"]'),
        };
      case 'rebuild_work_history':
        return {
          label: 'Refresh work history checks',
          icon: RefreshCw,
          variant: 'outline',
          disabled: employmentReviewRebuildLoading,
          onClick: handleRebuildEmploymentReview,
        };
      case 'review_employment_gaps':
        return {
          label: 'Check employment gaps',
          icon: CalendarClock,
          variant: 'outline',
          onClick: () => openEmploymentSection('[data-testid="section-employment-gaps"]'),
        };
      case 'recover_cv':
        return {
          label: 'Review CV',
          icon: FileCheck,
          variant: 'outline',
          disabled: cvReviewLoading,
          onClick: handleReviewCvSimple,
        };
      case 'review_cv':
        return {
          label: 'Review CV',
          icon: FileCheck,
          variant: 'outline',
          disabled: cvReviewLoading,
          onClick: handleReviewCv,
        };
      case 'request_cv_replacement':
        return {
          label: 'Request replacement CV',
          icon: Send,
          variant: 'outline',
          onClick: () => openCvRequestDialog('replacement'),
        };
      case 'request_cv_upload':
        return {
          label: 'Request CV',
          icon: Send,
          variant: 'outline',
          onClick: () => openCvRequestDialog('upload'),
        };
      case 'wait_for_cv_replacement':
        return {
          label: 'Replacement CV pending',
          icon: Clock,
          variant: 'outline',
          onClick: () => openEmploymentSection('[data-testid="section-employment-cv"]'),
        };
      case 'review_declarations':
        return {
          label: 'Review declarations',
          icon: Shield,
          variant: 'outline',
          onClick: () => setReviewDeclarationsOpen(true),
        };
      case 'complete_declarations':
        return {
          label: 'Declarations needed',
          icon: Shield,
          variant: 'outline',
          onClick: () => setActiveTab('forms'),
        };
      default:
        return {
          label: 'Review blockers',
          icon: AlertTriangle,
          variant: 'outline',
          onClick: () => openEmploymentSection('[data-testid="employment-review-cockpit"]'),
        };
    }
  })();

  const employmentCockpitNextAction = snap
    ? canonicalEmploymentCockpitNextAction
    : legacyEmploymentCockpitNextAction;
  const applicantNextAction = deriveApplicantNextAction({
    applicationOnFile: employee?.application_on_file !== false,
    cvFileExists,
    cvApproved,
    cvReplacementPending,
    cvLinkedForReview,
    cvReviewReady,
    cvLinkRecoveryAvailable: cvLinkRecoveryAvailable || cvServerLinkRecoveryAvailable,
    declarationsOnFile: declarationsApplicantComplete,
    declarationsAdequatelyReviewed,
    employmentHistoryExists,
    employmentReadyForSignOff,
    employmentSignedOff,
    employmentIntegrity: snapIntegrity,
    employmentGapEvaluation,
    employmentStatusBlockers,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">{isRecruitmentView ? 'Applicant' : 'Employee'} not found</p>
        <WorkspaceLink to={isRecruitmentView ? '/portal/recruitment' : '/portal/employees'}>
          <Button className="mt-4">{isRecruitmentView ? 'Back to Recruitment Pipeline' : 'Back to Staff'}</Button>
        </WorkspaceLink>
      </div>
    );
  }

  // UCE is the ONLY source of readiness truth. No fallback to the embedded copy.
  const canonicalProgress = profileMode === 'employee'
    ? (employeeCanonicalReadiness || null)
    : (unifiedProgress || null);
  const canonicalProgressPct = canonicalProgress?.overall_percentage ?? 0;
  const canonicalBlockers = canonicalProgress?.blockers || [];
  const canonicalBlockerDetails = canonicalProgress?.blocker_details || [];
  const canonicalBlockerObjects = canonicalBlockerDetails.length > 0
    ? canonicalBlockerDetails
    : canonicalBlockers.map((blocker) => (
        typeof blocker === 'string'
          ? { reason: blocker, label: blocker, severity: 'critical' }
          : blocker
      ));
  const canonicalIsWorkReady = canonicalProgress?.is_work_ready === true;
  const recruitmentTabsSignedOff = Object.keys(RECRUITMENT_TAB_SIGNOFF_LABELS)
    .every((tab) => Boolean(recruitmentTabSignoffs?.[tab]?.signed_off));
  const applicantReadyForRecruitmentApproval = profileMode === 'applicant'
    ? (canonicalIsWorkReady && employmentSignedOff && recruitmentTabsSignedOff)
    : canonicalIsWorkReady;
  const canonicalCanPromote = canonicalProgress?.can_promote === true;
  const canonicalCompletedRequirements = canonicalProgress?.completed_requirements ?? canonicalProgress?.progress?.completed ?? null;
  const canonicalTotalRequirements = canonicalProgress?.total_requirements ?? canonicalProgress?.progress?.total ?? null;
  const canonicalRequirementCountAvailable = Number.isFinite(canonicalTotalRequirements) && canonicalTotalRequirements > 0;
  const complianceSections = complianceFile?.sections && typeof complianceFile.sections === 'object' ? complianceFile.sections : {};
  const isDualRowCompliance = complianceFile?.serializer_version === 'dual_row_v1';
  const sectionRows = Object.values(complianceSections).flatMap((section) => (
    Array.isArray(section?.rows) ? section.rows.filter((r) => r && typeof r === 'object') : []
  ));
  const sectionRowsByType = sectionRows.reduce((acc, row) => {
    const t = String(row?.row_type || '').toLowerCase();
    if (!acc[t]) acc[t] = [];
    acc[t].push(row);
    return acc;
  }, {});
  const compliancePendingAdminReviewCount = sectionRows.filter((row) => (
    row?.row_type === 'evidence' && (
      (row?.counts?.awaiting_verification || 0) > 0
      || row?.status === 'submitted'
      || row?.status === 'awaiting_review'
      || row?.status === 'pending'
      || row?.requires_admin_review
    )
  )).length;
  const complianceReadinessSummary = {
    blockingCount: canonicalBlockerObjects.filter(
      (b) => String(b?.severity || '').toLowerCase() !== 'info',
    ).length,
    pendingAdminReviewCount: compliancePendingAdminReviewCount,
    checksComplete: canonicalCompletedRequirements,
    checksTotal: canonicalTotalRequirements,
    isWorkReady: canonicalIsWorkReady || canonicalCanPromote,
  };
  const sectionTotalRows = sectionRows.length;
  const sectionCompletedRows = sectionRows.filter((row) => {
    const s = String(row?.status || '').toLowerCase();
    return row?.is_verified === true || row?.verified === true || ['verified', 'complete', 'completed', 'accepted', 'approved', 'recorded'].includes(s);
  }).length;
  const sectionProgressAvailable = sectionTotalRows > 0;
  const useComplianceSectionsAsPrimary = isDualRowCompliance && sectionProgressAvailable;
  const complianceSummary = (complianceFile?.summary && typeof complianceFile.summary === 'object') ? complianceFile.summary : {};
  const requirementsSummary = (complianceRequirements?.summary && typeof complianceRequirements.summary === 'object')
    ? complianceRequirements.summary
    : {};
  const complianceSummaryCompleted = Number(complianceSummary.section_completed_rows);
  const complianceSummaryTotal = Number(complianceSummary.section_total_rows);
  const complianceSummaryPct = Number(complianceSummary.section_completion_percentage);
  const complianceSummaryAvailable = Number.isFinite(complianceSummaryTotal) && complianceSummaryTotal > 0;
  const requirementsSummaryCompleted = Number(requirementsSummary.completed);
  const requirementsSummaryTotal = Number(requirementsSummary.total);
  const requirementsSummaryPct = Number(requirementsSummary.completion_percentage);
  const requirementsSummaryAvailable = Number.isFinite(requirementsSummaryTotal) && requirementsSummaryTotal > 0;

  let summarySource = profileMode === 'employee'
    ? 'canonical_uce'
    : 'unified_progress';
  let effectiveCompletedRequirements = canonicalCompletedRequirements ?? 0;
  let effectiveTotalRequirements = canonicalTotalRequirements ?? 0;
  const clampCompletionPct = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, Math.round(numeric)));
  };

  let effectiveProgressPct = clampCompletionPct(canonicalProgressPct);
  const canonicalUCEApplicable = canonicalProgress?.not_applicable !== true;
  const canonicalUCECountAvailable = Number.isFinite(canonicalTotalRequirements) && canonicalTotalRequirements > 0;
  const complianceFileCountAvailable = sectionProgressAvailable
    || complianceSummaryAvailable
    || requirementsSummaryAvailable;

  if (profileMode === 'employee') {
    if (!canonicalUCEApplicable) {
      if (sectionProgressAvailable) {
        summarySource = 'compliance_file_sections_fallback';
        effectiveCompletedRequirements = sectionCompletedRows;
        effectiveTotalRequirements = sectionTotalRows;
        effectiveProgressPct = clampCompletionPct((sectionCompletedRows / sectionTotalRows) * 100);
      } else if (complianceSummaryAvailable) {
        summarySource = 'compliance_file_summary_fallback';
        effectiveCompletedRequirements = complianceSummaryCompleted;
        effectiveTotalRequirements = complianceSummaryTotal;
        effectiveProgressPct = clampCompletionPct(complianceSummaryPct);
      } else if (requirementsSummaryAvailable) {
        summarySource = 'compliance_requirements_summary_fallback';
        effectiveCompletedRequirements = requirementsSummaryCompleted;
        effectiveTotalRequirements = requirementsSummaryTotal;
        effectiveProgressPct = clampCompletionPct(requirementsSummaryPct);
      } else {
        summarySource = 'canonical_uce_not_applicable_unavailable';
        effectiveCompletedRequirements = 0;
        effectiveTotalRequirements = 0;
        effectiveProgressPct = 0;
      }
    }
  } else if (profileMode === 'applicant') {
    // Applicant Progress is intentionally different from UCE Staff File / Work Readiness.
    // The applicant gets credit when their own work is completed or submitted for Osabea
    // review. UCE remains the truth for blockers/readiness/approval, but must not be
    // presented as "File completion" on a recruitment profile.
    const applicantProgress = resolveApplicantProfileProgress(onboardingSync);

    if (applicantProgress) {
      summarySource = applicantProgress.source;
      effectiveCompletedRequirements = applicantProgress.completed;
      effectiveTotalRequirements = applicantProgress.total;
      effectiveProgressPct = applicantProgress.percentage;
    } else {
      // No applicant-progress snapshot is NOT zero work. Compliance/UCE and
      // verified recruitment requirements measure different things; never paint
      // either of their percentages as the applicant's own submitted progress.
      summarySource = 'applicant_progress_unavailable';
      effectiveCompletedRequirements = null;
      effectiveTotalRequirements = null;
      effectiveProgressPct = null;
    }
  }

  const isSectionUnavailable = Boolean(complianceFile?.status_unavailable);
  const requirementCountUnavailable = profileMode === 'employee'
    ? (!(canonicalUCECountAvailable || complianceFileCountAvailable))
    : effectiveProgressPct === null;
  const completionHeadline = (profileMode === 'employee' && !canonicalUCEApplicable)
    ? (complianceFileCountAvailable
      ? `Compliance File: ${effectiveCompletedRequirements}/${effectiveTotalRequirements} verified`
      : 'Compliance File: unavailable')
    : effectiveProgressPct === null ? 'Progress unavailable' : `${effectiveProgressPct}% Complete`;
  const canonicalReadinessLabel = profileMode === 'applicant'
    ? (applicantReadyForRecruitmentApproval
        ? 'Ready for Recruitment Approval'
        : 'Not ready for recruitment approval')
    : (lifecycleStage === 'active'
        ? (canonicalIsWorkReady ? 'Ready for work' : 'Not ready for work')
        // Onboarding staff: the canonical activation snapshot decides the label
        // (distinguishes "onboarding in progress" vs "Fit-for-Work required" vs
        // "Ready to activate" vs "unavailable") — never a percentage guess.
        : activationView.label);
  const canonicalProgressComplete = effectiveProgressPct === 100;

  if (process.env.NODE_ENV === 'development') {
    const endpointSet = [
      '/employees/{id}',
      '/employees/{id}/compliance-file',
      '/employees/{id}/compliance-requirements',
      profileMode === 'applicant' ? '/employees/{id}/unified-progress' : '/employees/{id}/unified-progress (legacy only)',
      profileMode === 'applicant' ? '/employees/{id}/recruitment-status' : '/employees/{id}/recruitment-status (context only)',
    ];
    console.debug('PROFILE_MODE_DIAGNOSTIC', {
      employeeId,
      pathname: location.pathname,
      profileMode,
      endpointsUsed: endpointSet,
      summarySource,
      complianceSectionCount: Object.keys(complianceSections).length,
      complianceRowCount: sectionRows.length
    });
  }

  return (
    <div className="space-y-6" data-testid="employee-profile">
      {/* Back Link - Returns to correct section based on route context */}
      <button 
        onClick={() => navigate(isRecruitmentView ? '/portal/recruitment' : '/portal/employees', {
          state: isRecruitmentView ? { recruitmentFilters: applicantNavigationContext?.filters || {} } : undefined,
        })} 
        className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors"
        data-testid="back-link"
      >
        <ArrowLeft className="h-4 w-4" />
        {isRecruitmentView ? 'Back to Recruitment Pipeline' : 'Back to Staff'}
      </button>

      {/* Header Card */}
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex items-start gap-4 flex-1">
              {/* Applicant/staff photo: larger identity anchor; opens an authenticated, read-only preview. */}
              <div className="relative group shrink-0" data-testid="person-profile-photo-controls">
                {profilePhotoBlob ? (
                  <button
                    type="button"
                    onClick={() => setPhotoPreviewOpen(true)}
                    className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                    title="View larger photo"
                    aria-label={`View larger photo of ${employee.first_name} ${employee.last_name}`}
                    data-testid="profile-photo-enlarge"
                  >
                    <img
                      src={profilePhotoBlob}
                      alt={`${employee.first_name} ${employee.last_name}`}
                      className="h-24 w-24 rounded-2xl border-2 border-[#E4E8EB] object-cover"
                      data-testid="profile-photo"
                    />
                  </button>
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-accent">
                    <span className="font-heading text-2xl font-bold text-primary" aria-label="Profile initials">
                      {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                    </span>
                  </div>
                )}
                {!isAuditor() && (
                  <div className="absolute bottom-1 right-1 flex items-center gap-0.5 rounded-lg bg-slate-950/85 p-0.5 text-white shadow-sm transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                    {profilePhotoBlob && (
                      <button
                        type="button"
                        onClick={() => setPhotoPreviewOpen(true)}
                        className="rounded-md p-1.5 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        title="View larger photo"
                        aria-label="View larger photo"
                        data-testid="profile-photo-view-action"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                    <label
                      className="cursor-pointer rounded-md p-1.5 hover:bg-white/20 focus-within:ring-2 focus-within:ring-white"
                      title={profilePhotoBlob ? 'Replace photo' : 'Add photo'}
                    >
                      <span className="sr-only">{profilePhotoBlob ? 'Replace photo' : 'Add photo'}</span>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                        className="sr-only"
                        aria-label={profilePhotoBlob ? 'Replace photo' : 'Add photo'}
                        disabled={isUploadingPhoto}
                      />
                      {isUploadingPhoto
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Camera className="h-4 w-4" />}
                    </label>
                    {employee.profile_photo_url && (
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="rounded-md p-1.5 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        title="Remove photo"
                        aria-label="Remove photo"
                        data-testid="profile-photo-remove-action"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* Preview uses the already-authorised in-memory blob, never a public storage URL. */}
              <Dialog open={photoPreviewOpen && Boolean(profilePhotoBlob)} onOpenChange={setPhotoPreviewOpen}>
                <DialogContent className="w-[min(94vw,720px)] max-w-3xl p-4 sm:p-6" data-testid="profile-photo-preview-dialog">
                  <DialogHeader>
                    <DialogTitle>{employee.first_name} {employee.last_name}</DialogTitle>
                    <DialogDescription>Profile photo — original proportions preserved.</DialogDescription>
                  </DialogHeader>
                  {profilePhotoBlob && (
                    <img
                      src={profilePhotoBlob}
                      alt={`Enlarged profile photo of ${employee.first_name} ${employee.last_name}`}
                      className="mx-auto max-h-[75vh] w-full rounded-lg object-contain"
                      data-testid="profile-photo-preview-image"
                    />
                  )}
                </DialogContent>
              </Dialog>
              <div className="min-w-0">
                <h1 className="font-heading text-2xl font-bold text-text-primary">
                  {employee.first_name} {employee.last_name}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="person-identity-metadata">
                  {profileMode === 'applicant' ? (
                    <span
                      className="inline-flex flex-wrap items-baseline gap-x-2 text-sm"
                      title="Applicant reference used to identify this application"
                      data-testid="applicant-identity-reference"
                    >
                      <span className="font-medium text-slate-600">Applicant ID</span>
                      <span className="break-all font-mono font-semibold text-slate-900">
                        {employee.applicant_reference || employee.employee_code || employeeId}
                      </span>
                    </span>
                  ) : (
                    <span className="inline-flex items-baseline gap-2 text-sm" data-testid="employee-identity-code">
                      <span className="font-medium text-slate-600">Employee code</span>
                      <span className="font-mono font-semibold text-slate-900">{employee.employee_code || '—'}</span>
                    </span>
                  )}
                  <span className="text-sm text-slate-500">{String(employee.role || 'Role not set').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {profileMode !== 'applicant' ? (
                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      Staff
                    </span>
                  ) : null}
                  <span className={`status-chip ${
                    employee.status === 'active' ? 'status-success' :
                    employee.status === 'onboarding' ? 'status-info' :
                    employee.status === 'screening' || employee.status === 'interview' || employee.status === 'compliance_review' ? 'status-warning' :
                    'status-neutral'
                  }`}>
                    {employee.status === 'compliance_review' ? 'Awaiting Approval' : employee.status?.replace('_', ' ')}
                  </span>
                  {profileMode === 'applicant' ? null : (
                  /* Canonical activation truth: badges derive from the activation
                     snapshot (lifecycle + UCE + latest valid Fit-for-Work). */
                  (() => {
                    if (employee.status === 'active' || employee.status === 'active_employee') {
                      return (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-800" data-testid="active-employee-badge">
                          Active Employee
                        </span>
                      );
                    }
                    if (activationView.canActivate) {
                      return (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-800" data-testid="ready-badge">
                          Ready to activate
                        </span>
                      );
                    }
                    if (activationView.needsFitForWork) {
                      return (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-800" data-testid="fit-for-work-required-badge">
                          Fit-for-Work decision required
                        </span>
                      );
                    }
                    if (activationView.isBlocked) {
                      return (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-800" data-testid="fit-for-work-blocked-badge">
                          Not ready for activation
                        </span>
                      );
                    }
                    if (activationView.isUnavailable) {
                      return (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700" data-testid="activation-unavailable-badge">
                          Activation status unavailable
                        </span>
                      );
                    }
                    return (
                      <span className="px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-800" data-testid="awaiting-approval-badge">
                        Onboarding in progress
                      </span>
                    );
                  })()
                  )}
                  {canonicalStage === 'employee' && (
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      employee.status === 'active' || employee.status === 'active_employee'
                        ? 'bg-green-100 text-green-800'
                        : canPromoteToActiveNow
                          ? 'bg-emerald-100 text-emerald-800'
                          : employee.status === 'onboarding'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                      {employee.status === 'active' || employee.status === 'active_employee'
                        ? 'Lifecycle: Active Workforce'
                        : canPromoteToActiveNow
                  ? 'Lifecycle: Activation Eligible'
                          : employee.status === 'onboarding'
                            ? 'Lifecycle: Onboarding'
                            : `Lifecycle: ${(employee.status || 'unknown').replace(/_/g, ' ')}`}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              {profileMode === 'applicant' && (
                <ApplicantQuickSwitch
                  employeeId={employeeId}
                  navigationContext={applicantNavigationContext}
                  onSwitch={(nextId, nextContext) => {
                    // Preserve the current tab, not a person-specific action or message.
                    const tabQuery = activeTab ? '?tab=' + encodeURIComponent(activeTab) : '';
                    navigate('/portal/recruitment/' + encodeURIComponent(nextId) + tabQuery, {
                      state: { applicantNavigation: nextContext },
                    });
                  }}
                />
              )}
              {!isAuditor() ? (
                <div className="flex flex-wrap items-center justify-end gap-2" data-testid="employee-profile-actions">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl gap-1.5"
                    onClick={openEditDialog}
                    data-testid="employee-edit-btn"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0 rounded-xl"
                    onClick={handleRefreshStatus}
                    disabled={isRefreshingStatus}
                    title="Refresh file status"
                    data-testid="employee-refresh-btn"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshingStatus ? 'animate-spin' : ''}`} />
                  </Button>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-xl gap-1.5"
                        onPointerDown={(e) => e.stopPropagation()}
                        data-testid="employee-export-menu-btn"
                      >
                        <FileDown className="h-4 w-4" />
                        Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 z-[120]">
                      <DropdownMenuItem onClick={handleExportFile} disabled={isExporting}>
                        <FileArchive className="h-4 w-4 mr-2" />
                        {profileMode === 'applicant' ? 'Applicant file (ZIP)' : 'Employee file (ZIP)'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportCompliancePDF} disabled={isExporting} data-testid="download-compliance-pdf-btn">
                        <FileDown className="h-4 w-4 mr-2" />
                        Compliance PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handlePrintCompliancePDF} data-testid="print-compliance-pdf-btn">
                        <Printer className="h-4 w-4 mr-2" />
                        Print compliance PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handlePrintStaffId} data-testid="print-staff-id-btn">
                        <Printer className="h-4 w-4 mr-2" />
                        Print staff ID
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {(profileMode === 'employee' || employee.status === 'archived' || isSuperAdmin()) ? (
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 rounded-xl"
                          onPointerDown={(e) => e.stopPropagation()}
                          title="More actions"
                          data-testid="employee-actions-btn"
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 z-[120]">
                        {employee.status === 'active' && (
                          <DropdownMenuItem
                            onClick={() => handleLifecycleStatusChange('inactive', 'Deactivation')}
                            className="text-amber-700"
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Set inactive
                          </DropdownMenuItem>
                        )}
                        {employee.status === 'inactive' && (
                          <DropdownMenuItem
                            onClick={() => handleLifecycleStatusChange('onboarding', 'Reactivation')}
                            className="text-blue-700"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reactivate to onboarding
                          </DropdownMenuItem>
                        )}
                        {(employee.status === 'active' || employee.status === 'inactive') ? (
                          <DropdownMenuSeparator />
                        ) : null}
                        {employee.status === 'archived' ? (
                          <DropdownMenuItem onClick={handleRestoreEmployee}>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restore {profileMode === 'applicant' ? 'applicant' : 'employee'}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => setArchiveDialogOpen(true)}
                            className="text-warning"
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive {profileMode === 'applicant' ? 'applicant' : 'employee'}
                          </DropdownMenuItem>
                        )}
                        {isSuperAdmin() ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteDialogOpen(true)}
                              className="text-error"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete permanently
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-xl text-amber-800 hover:text-amber-900"
                      onClick={() => setArchiveDialogOpen(true)}
                    >
                      <Archive className="h-4 w-4 mr-1" />
                      Archive
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {profileMode === 'employee' && complianceFile?.stale_data_preserved && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {complianceFile?.stale_data_message || 'Latest refresh failed; showing last valid compliance data.'}
            </div>
          )}

                    {/* AUDIT QUICK VIEW - staff files only; applicants use hero + numbered tabs */}
          {profileMode !== 'applicant' && (() => {
            const categories = unifiedProgress?.categories || {};
            const trainingCategory = categories?.training || {};
            const agreementsCategory = categories?.agreements || {};
            const inductionCategory = categories?.induction || {};
            const workplaceReadinessCategory = categories?.workplace_readiness || {};
            const dbsWorkflow = getComplianceFileWorkflowStatus(complianceFile, 'dbs') || {};
            const rtwWorkflow = getComplianceFileWorkflowStatus(complianceFile, 'right_to_work') || {};
            const dbsCheckRow = asArray(complianceSections?.dbs?.rows).find((row) => row?.row_type === 'check') || {};
            const rtwCheckRow = asArray(complianceSections?.right_to_work?.rows).find((row) => row?.row_type === 'check') || {};
            const trainingSectionRows = [
              ...asArray(sectionRowsByType?.training),
              ...asArray(sectionRowsByType?.training_record)
            ];
            const trainingOperationalRows = trainingSectionRows;
            const agreementsSectionRows = [
              ...asArray(sectionRowsByType?.form_acknowledgement),
              ...asArray(sectionRowsByType?.agreement),
              ...asArray(complianceSections?.agreements?.rows),
            ];
            const inductionSectionRows = asArray(complianceSections?.induction?.rows).filter((row) => {
              const key = String(row?.id || row?.code || row?.requirement_id || row?.key || '').toLowerCase();
              return key !== 'shadow_shift';
            });

            const toDate = (value) => (value ? formatBackendDate(value) : 'Unavailable');
            // Day-count formatter. Adds qualifier text so the audit value is
            // unambiguous: "(due in)", "(overdue by)", "today".
            const toDays = (value) => {
              if (typeof value !== 'number' || !Number.isFinite(value)) return 'Unavailable';
              if (value === 0) return '0d (due today)';
              if (value < 0) return `${Math.abs(value)}d (overdue)`;
              return `${value}d`;
            };
            // Fallback: when backend doesn't precompute days_until_*, derive
            // from the date itself so the Audit Quick View never shows
            // "Unavailable" for a verified check that has a known
            // expiry/review date. Returns null only when there is no date.
            const daysUntil = (dateValue) => {
              if (!dateValue) return null;
              const target = new Date(dateValue);
              if (Number.isNaN(target.getTime())) return null;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              target.setHours(0, 0, 0, 0);
              return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            };
            const tone = (status, daysRemaining) => {
              const s = String(status || '').toLowerCase();
              if (s.includes('expired') || s.includes('missing') || s.includes('rejected')) return 'red';
              if (s.includes('pending') || s.includes('awaiting') || s.includes('required') || s.includes('review')) return 'amber';
              if (typeof daysRemaining === 'number' && daysRemaining < 0) return 'red';
              if (typeof daysRemaining === 'number' && daysRemaining <= 60) return 'amber';
              if (s.includes('verified') || s.includes('complete')) return 'green';
              return 'gray';
            };

            const dbsCheckData = dbsCheckRow?.check_data || {};
            const dbsCheckOutcome = String(dbsCheckData?.outcome || dbsCheckRow?.status || '').toLowerCase();
            const dbsIsVerified = dbsCheckRow?.is_verified === true
              || dbsCheckRow?.verified === true
              || dbsCheckOutcome === 'verified';
            const dbsStatus = dbsIsVerified
              ? 'Verified'
              : (dbsWorkflow?.label || dbsCheckRow?.status_summary || (complianceSections?.dbs ? 'Awaiting review' : 'Unavailable'));
            const dbsChecked = dbsCheckRow?.check_data?.checked_at || dbsCheckRow?.verified_at || null;
            // DBS review fallback (Tier 4 fix): when no explicit review date
            // is captured, use tenant policy of "1 year after the check
            // date". Update Service is checked annually, so the operational
            // review cadence is always 12 months. Without this fallback the
            // Audit Quick View was showing "Review date: Unavailable" /
            // "Days remaining: Unavailable" for every verified DBS that
            // hadn't been hand-stamped with a review date.
            const addYears = (dateValue, years) => {
              if (!dateValue) return null;
              const d = new Date(dateValue);
              if (Number.isNaN(d.getTime())) return null;
              d.setFullYear(d.getFullYear() + years);
              return d.toISOString();
            };
            const dbsReview = dbsCheckRow?.check_data?.review_due_at
              || dbsCheckRow?.check_data?.next_recheck_date
              || addYears(dbsChecked, 1);
            const dbsDays = Number.isFinite(dbsCheckRow?.check_data?.days_until_review)
              ? dbsCheckRow.check_data.days_until_review
              : daysUntil(dbsReview);

            const rtwCheckData = rtwCheckRow?.check_data || {};
            const rtwCheckOutcome = String(rtwCheckData?.outcome || rtwCheckRow?.status || '').toLowerCase();
            const rtwIsVerified = rtwCheckRow?.is_verified === true
              || rtwCheckRow?.verified === true
              || rtwCheckOutcome === 'verified';
            const rtwStatus = rtwIsVerified
              ? 'Verified'
              : (rtwWorkflow?.label || rtwCheckRow?.status_summary || (complianceSections?.right_to_work ? 'Awaiting review' : 'Unavailable'));
            const rtwChecked = rtwCheckRow?.check_data?.checked_at || rtwCheckRow?.verified_at || null;
            const rtwExpiry = rtwCheckRow?.check_data?.permission_end_date || null;
            const rtwDays = Number.isFinite(rtwCheckRow?.check_data?.days_until_expiry)
              ? rtwCheckRow.check_data.days_until_expiry
              : daysUntil(rtwExpiry);

            const trainingItems = asArray(trainingCategory?.items);
            const trainingSectionRequired = trainingOperationalRows.length > 0 ? trainingOperationalRows.length : null;
            const trainingSectionCompleted = trainingOperationalRows.length > 0
              ? trainingOperationalRows.filter((row) => {
                  const s = String(row?.status || '').toLowerCase();
                  return row?.is_verified === true || row?.verified === true || ['verified', 'complete', 'completed', 'accepted', 'approved', 'recorded'].includes(s);
                }).length
              : null;
            const trainingEvalSummary = (trainingEvaluation && typeof trainingEvaluation === 'object')
              ? (trainingEvaluation.summary || trainingEvaluation.top_summary || trainingEvaluation)
              : {};
            const trainingEvalItems = asArray(trainingEvaluation?.items);
            const trainingEvalItemsRequired = trainingEvalItems.length;
            const getTrainingEvalTimingStatus = (item) => String(
              item?.status_band ||
              item?.renewal_status ||
              item?.timing_status ||
              item?.renewalStatus ||
              item?.status ||
              ''
            ).toLowerCase();
            const isTrainingEvalExpiryPast = (item) => {
              const expiryValue = item?.expires_at || item?.expiry_date;
              if (!expiryValue) return false;
              const expiry = new Date(expiryValue);
              if (Number.isNaN(expiry.getTime())) return false;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              expiry.setHours(0, 0, 0, 0);
              return expiry < today;
            };
            const isTrainingEvalExpired = (item) => (
              ['expired', 'overdue'].includes(getTrainingEvalTimingStatus(item)) ||
              isTrainingEvalExpiryPast(item)
            );
            const trainingEvalItemsCurrent = trainingEvalItems.filter((item) => {
              const status = getTrainingEvalTimingStatus(item);
              const isHistoricallyVerified = item?.verified === true
                || item?.is_verified === true
                || ['verified', 'current'].includes(String(item?.status || '').toLowerCase());
              const timingIsCurrent = ['current', 'verified', 'valid', 'no_expiry', 'due_soon', 'expiring_soon'].includes(status);
              return isHistoricallyVerified && timingIsCurrent && !isTrainingEvalExpired(item);
            }).length;
            const trainingEvalItemsExpiring = trainingEvalItems.filter((item) => (
              !isTrainingEvalExpired(item) &&
              ['due_soon', 'expiring_soon'].includes(getTrainingEvalTimingStatus(item))
            )).length;
            const trainingEvalItemsExpired = trainingEvalItems.filter(isTrainingEvalExpired).length;
            const trainingEvalItemsAwaitingReview = trainingEvalItems.filter((item) => String(item?.status || '').toLowerCase() === 'awaiting_review').length;
            const trainingEvalItemsMissing = trainingEvalItems.filter((item) => String(item?.status || '').toLowerCase() === 'missing').length;
            const trainingEvalRequired = Number(
              trainingEvalSummary?.required_total
              ?? trainingEvalSummary?.mandatory_total
              ?? trainingEvalSummary?.required
            );
            const trainingEvalVerified = Number(
              trainingEvalSummary?.verified_total
              ?? trainingEvalSummary?.mandatory_verified
              ?? trainingEvalSummary?.verified
              ?? trainingEvalSummary?.completed
            );
            const canonicalTrainingCompleted = Number(
              trainingCategory?.completed
              ?? trainingCategory?.verified
              ?? canonicalProgress?.categories?.training?.completed
            );
            const canonicalTrainingRequired = Number(
              trainingCategory?.total
              ?? trainingCategory?.required
              ?? canonicalProgress?.categories?.training?.total
            );
            // The canonical training evaluator owns CURRENT mandatory-training truth.
            // Compliance-section rows are useful evidence/history rows, but a verified
            // historical certificate can remain verified after it expires. Therefore
            // never let those rows inflate the Quick View "current/required" count.
            const trainingRequired = trainingEvalItemsRequired > 0
              ? trainingEvalItemsRequired
              : (trainingSectionRequired ?? (
                  Number.isFinite(trainingEvalRequired)
                    ? trainingEvalRequired
                    : (Number.isFinite(canonicalTrainingRequired) ? canonicalTrainingRequired : null)
                ));
            const trainingCompleted = trainingEvalItemsRequired > 0
              ? trainingEvalItemsCurrent
              : (trainingSectionCompleted ?? (
                  Number.isFinite(trainingEvalVerified)
                    ? trainingEvalVerified
                    : (Number.isFinite(canonicalTrainingCompleted) ? canonicalTrainingCompleted : null)
                ));
            const trainingExpiring = trainingEvalItemsRequired > 0
              ? trainingEvalItemsExpiring
              : (
                  trainingOperationalRows.length > 0
                    ? trainingOperationalRows.filter((row) => String(row?.status || '').toLowerCase() === 'expiring_soon').length
                    : (!useComplianceSectionsAsPrimary ? trainingItems.filter((i) => String(i?.status || '').toLowerCase() === 'expiring_soon').length : 0)
                );
            const trainingExpired = trainingEvalItemsRequired > 0
              ? trainingEvalItemsExpired
              : (
                  trainingOperationalRows.length > 0
                    ? trainingOperationalRows.filter((row) => String(row?.status || '').toLowerCase() === 'expired').length
                    : (!useComplianceSectionsAsPrimary ? trainingItems.filter((i) => String(i?.status || '').toLowerCase() === 'expired').length : 0)
                );
            const trainingTone = trainingRequired == null || trainingCompleted == null
              ? 'gray'
              : (trainingExpired > 0 ? 'red' : (trainingCompleted < trainingRequired || trainingExpiring > 0 ? 'amber' : 'green'));

            const agreementItems = agreementsSectionRows.length > 0
              ? agreementsSectionRows
              : asArray(agreementsCategory?.items);
            const canonicalAgreementRows = asArray(complianceSections?.agreements?.rows).filter((row) => row?.row_type === 'form_acknowledgement');
            const selectLatestAgreement = (matcher) => {
              const scoped = (canonicalAgreementRows.length > 0 ? canonicalAgreementRows : agreementItems).filter((item) =>
                matcher(String(item?.id || item?.requirement_id || item?.agreement_type || ''))
              );
              if (!scoped.length) return null;
              const preferred = scoped.find((item) => item?.latest_active === true);
              if (preferred) return preferred;
              const byState = [...scoped].sort((a, b) => {
                const rank = (val) => {
                  const s = String(val || '').toLowerCase();
                  if (s.includes('fully_executed') || s.includes('verified')) return 4;
                  if (s.includes('awaiting_company_countersignature') || s.includes('signed')) return 3;
                  if (s.includes('pending') || s.includes('awaiting')) return 2;
                  if (s.includes('rejected') || s.includes('unavailable')) return 0;
                  return 1;
                };
                return rank(b?.status) - rank(a?.status);
              });
              return byState[0];
            };
            const contractAgreement = selectLatestAgreement((id) => id.includes('contract'));
            const handbookAgreement = selectLatestAgreement((id) => id.includes('handbook'));
            const contractStatus = contractAgreement?.state_label || contractAgreement?.status || 'Unavailable';
            const handbookStatus = handbookAgreement?.state_label || handbookAgreement?.status || 'Unavailable';
            const inductionSectionRequired = inductionSectionRows.length > 0 ? inductionSectionRows.length : null;
            const inductionSectionCompleted = inductionSectionRows.length > 0
              ? inductionSectionRows.filter((row) => {
                  const s = String(row?.status || '').toLowerCase();
                  return row?.is_verified === true || row?.verified === true || ['verified', 'complete', 'completed', 'accepted', 'approved', 'recorded'].includes(s);
                }).length
              : null;
            const canonicalInductionItems = asArray(inductionChecklist?.items).filter((item) => {
              const key = String(item?.code || item?.id || '').toLowerCase();
              return key !== 'shadow_shift';
            });
            const canonicalCareCertificate = inductionChecklist?.care_certificate || {};
            const inductionFromChecklist = inductionChecklist
              || employee?.induction_checklist_summary
              || employee?.induction_summary
              || {};
            const inductionChecklistCompleted = Number(
              canonicalCareCertificate?.completed
              ?? (canonicalInductionItems.length > 0
                ? canonicalInductionItems.filter((item) => String(item?.status || '').toLowerCase() === 'completed').length
                : undefined)
              ?? inductionFromChecklist?.completed
              ?? inductionFromChecklist?.completed_count
              ?? inductionFromChecklist?.verified_count
            );
            const inductionChecklistTotal = Number(
              canonicalCareCertificate?.total
              ?? (canonicalInductionItems.length > 0 ? canonicalInductionItems.length : undefined)
              ?? inductionFromChecklist?.total
              ?? inductionFromChecklist?.total_count
              ?? inductionFromChecklist?.required_count
            );
            const inductionCompleted = inductionSectionCompleted ?? (
              Number.isFinite(inductionCategory?.completed)
                ? inductionCategory.completed
                : (Number.isFinite(inductionChecklistCompleted) ? inductionChecklistCompleted : null)
            );
            const inductionRequired = inductionSectionRequired ?? (
              Number.isFinite(inductionCategory?.total)
                ? inductionCategory.total
                : (Number.isFinite(inductionChecklistTotal) ? inductionChecklistTotal : null)
            );
            const inductionDisplayCompleted = Number.isFinite(inductionCompleted) && Number.isFinite(inductionRequired)
              ? Math.min(inductionCompleted, inductionRequired)
              : inductionCompleted;
            const agreementsTone = tone(`${contractStatus} ${handbookStatus}`, null);
            const shadowShiftRequired = Number(workplaceReadinessCategory?.total) > 0;
            const shadowShiftComplete = Number(workplaceReadinessCategory?.completed) >= Number(workplaceReadinessCategory?.total || 1);
            const careCertificateKnown = Number.isFinite(inductionDisplayCompleted)
              && Number.isFinite(inductionRequired)
              && inductionRequired > 0;
            const careCertificateComplete = careCertificateKnown
              && inductionDisplayCompleted >= inductionRequired;
            const readinessIncomplete = !careCertificateComplete || (shadowShiftRequired && !shadowShiftComplete);
            const agreementsReadinessTone = agreementsTone === 'red'
              ? 'red'
              : (agreementsTone === 'amber'
                  ? 'amber'
                  : (!careCertificateKnown ? 'gray' : (readinessIncomplete ? 'amber' : agreementsTone)));

            const cardClass = (t) => (quickViewToneClasses[t] || quickViewToneClasses.gray).card;
            const textClass = (t) => (quickViewToneClasses[t] || quickViewToneClasses.gray).text;
            const iconClass = (t) => (quickViewToneClasses[t] || quickViewToneClasses.gray).icon;

            return (
              <div className="mt-6 pt-6 border-t border-[#E4E8EB]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Audit Quick View</h3>
                  <p className="text-xs text-text-muted">Canonical compliance summary for checker review</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="audit-quick-view">
                  <div className={`rounded-xl border p-3 ${cardClass(tone(dbsStatus, dbsDays))}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-text-primary">DBS</span>
                      <Shield className={`h-4 w-4 ${iconClass(tone(dbsStatus, dbsDays))}`} />
                    </div>
                    <p className={`text-sm font-semibold mt-1 ${textClass(tone(dbsStatus, dbsDays))}`}>{dbsStatus}</p>
                    <p className="text-xs text-text-muted mt-1">Check date: {toDate(dbsChecked)}</p>
                    <p className="text-xs text-text-muted">Review date: {toDate(dbsReview)}</p>
                    <p className="text-xs text-text-muted">Days remaining: {toDays(dbsDays)}</p>
                  </div>

                  <div className={`rounded-xl border p-3 ${cardClass(tone(rtwStatus, rtwDays))}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-text-primary">Right to Work</span>
                      <FileCheck className={`h-4 w-4 ${iconClass(tone(rtwStatus, rtwDays))}`} />
                    </div>
                    <p className={`text-sm font-semibold mt-1 ${textClass(tone(rtwStatus, rtwDays))}`}>{rtwStatus}</p>
                    <p className="text-xs text-text-muted mt-1">Check date: {toDate(rtwChecked)}</p>
                    <p className="text-xs text-text-muted">Expiry date: {toDate(rtwExpiry)}</p>
                    <p className="text-xs text-text-muted">Days remaining: {toDays(rtwDays)}</p>
                  </div>

                  <div className={`rounded-xl border p-3 ${cardClass(trainingTone)}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-text-primary">Mandatory Training Current</span>
                      <GraduationCap className={`h-4 w-4 ${iconClass(trainingTone)}`} />
                    </div>
                    <p className={`text-sm font-semibold mt-1 ${textClass(trainingTone)}`}>
                      {trainingCompleted == null || trainingRequired == null ? 'Unavailable' : `${trainingCompleted}/${trainingRequired}`}
                    </p>
                    <p className="text-xs text-text-muted mt-1">Expiring soon: {trainingCompleted == null ? 'Unavailable' : trainingExpiring}</p>
                    <p className="text-xs text-text-muted">Expired: {trainingCompleted == null ? 'Unavailable' : trainingExpired}</p>
                  </div>

                  <div className={`rounded-xl border p-3 ${cardClass(agreementsReadinessTone)}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-text-primary">Agreements & Readiness</span>
                      <ClipboardList className={`h-4 w-4 ${iconClass(agreementsReadinessTone)}`} />
                    </div>
                    <p className={`text-sm font-semibold mt-1 ${textClass(agreementsTone)}`}>Contract: {contractStatus}</p>
                    <p className="text-xs text-text-muted mt-1">Handbook: {handbookStatus}</p>
                    <p className={`text-xs ${!careCertificateKnown ? 'text-text-muted' : (careCertificateComplete ? 'text-green-700' : 'text-amber-700')}`}>
                      Care Certificate: {inductionCompleted == null || inductionRequired == null
                        ? 'Unavailable'
                        : `${inductionDisplayCompleted}/${inductionRequired}`}
                    </p>
                    {shadowShiftRequired && (
                      <p className={`text-xs ${shadowShiftComplete ? 'text-green-700' : 'text-amber-700'}`}>
                        {shadowShiftComplete ? 'Shadow shift sign-off complete' : 'Shadow shift sign-off required'}
                      </p>
                    )}
                  </div>
                </div>

              </div>
            );
          })()}
{/* Status Strip - Replaces contact row */}
          {profileMode !== 'applicant' ? (
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#E4E8EB]" data-testid="status-strip">
            {/* Business-facing identifier — label adapts to person stage */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-500">{canonicalStage === 'applicant' ? 'Applicant Reference' : 'Employee Code'}:</span>
              <span className="font-mono text-sm font-semibold text-slate-900 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                {employee.employee_code || employee.applicant_reference || 'Not assigned'}
              </span>
            </div>
            
            {/* Key Expiry - Show most critical */}
            {(() => {
              const statusDbsCheckRow = asArray(complianceSections?.dbs?.rows).find((row) => row?.row_type === 'check') || {};
              const statusRtwCheckRow = asArray(complianceSections?.right_to_work?.rows).find((row) => row?.row_type === 'check') || {};
              const dbsSummary = complianceRequirements?.dbs_summary || {};
              const rtwSummary = complianceRequirements?.rtw_summary || {};
              const rtwExpiryDate = statusRtwCheckRow?.check_data?.permission_end_date || rtwSummary.expiry_date;
              const rtwDaysRemaining = Number.isFinite(statusRtwCheckRow?.check_data?.days_until_expiry)
                ? statusRtwCheckRow.check_data.days_until_expiry
                : rtwSummary.days_until_expiry;
              const dbsReviewDueDate = statusDbsCheckRow?.check_data?.review_due_at || statusDbsCheckRow?.check_data?.next_recheck_date || dbsSummary.next_dbs_review_due;
              const dbsDaysRemaining = Number.isFinite(statusDbsCheckRow?.check_data?.days_until_review)
                ? statusDbsCheckRow.check_data.days_until_review
                : dbsSummary.days_until_review;
              
              // Check RTW expiry first (more critical)
              if (rtwExpiryDate) {
                const days = rtwDaysRemaining;
                if (days !== undefined && days <= 30) {
                  return (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      days <= 0 ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      <Calendar className={`h-4 w-4 ${days <= 0 ? 'text-red-600' : 'text-amber-600'}`} />
                      <span className={`text-sm font-medium ${days <= 0 ? 'text-red-700' : 'text-amber-700'}`}>
                        RTW {days <= 0 ? 'Expired' : `Expires ${days}d`}
                      </span>
                    </div>
                  );
                }
              }
              
              // Check DBS expiry — use only backend-computed days_until_review (safety engine).
              // No local date arithmetic: if backend doesn't supply days_until_review, skip rendering.
              if (dbsReviewDueDate && dbsDaysRemaining != null) {
                const days = dbsDaysRemaining;
                if (days <= 30) {
                  return (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      days <= 0 ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      <Calendar className={`h-4 w-4 ${days <= 0 ? 'text-red-600' : 'text-amber-600'}`} />
                      <span className={`text-sm font-medium ${days <= 0 ? 'text-red-700' : 'text-amber-700'}`}>
                        DBS {days <= 0 ? 'Overdue' : `Review ${days}d`}
                      </span>
                    </div>
                  );
                }
              }
              
              return null;
            })()}
            
          </div>
          ) : null}

          {/* Blocker summary intentionally lives only in PersonFileHeroCard above.
              Employee and applicant profiles share that one canonical surface to avoid
              repeating the same readiness blockers in multiple lists. */}

          {/* Note: Global Upload Document button removed. */}
          {/* All upload actions now live INSIDE each compliance requirement card. */}
          {/* Workflow: See issue â†’ Scroll to section â†’ Upload/Request/Verify there. */}

          {!isAuditor() && (
            <div className="flex flex-wrap gap-3 mt-6">
              {/* Generate Blank Forms Dialog */}
              <Dialog open={generateFormsOpen} onOpenChange={setGenerateFormsOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Generate Compliance Forms</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
                    <p className="text-sm text-text-muted">
                      Select templates to generate for <strong>{employee?.first_name} {employee?.last_name}</strong>. 
                      Employee details will be auto-filled.
                    </p>
                    
                    {templates.length === 0 ? (
                      <div className="text-center py-8 text-text-muted">
                        <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>No templates available. Load templates first.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
                          <div key={category} className="space-y-2">
                            <h4 className="font-medium text-text-primary text-sm">{category}</h4>
                            <div className="space-y-2">
                              {categoryTemplates.map((template) => {
                                const existingForm = generatedForms.find(
                                  f => f.template_id === template.id && !['archived', 'signed_off'].includes(f.status)
                                );
                                const isSelected = selectedTemplates.includes(template.id);
                                
                                return (
                                  <div 
                                    key={template.id}
                                    className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                                      existingForm 
                                        ? 'bg-gray-50 border-gray-200 opacity-60' 
                                        : isSelected 
                                          ? 'bg-primary/5 border-primary' 
                                          : 'bg-[#F8FAFA] border-[#E4E8EB] hover:border-primary/30'
                                    }`}
                                  >
                                    <Checkbox
                                      id={template.id}
                                      checked={isSelected}
                                      disabled={!!existingForm}
                                      onCheckedChange={() => toggleTemplateSelection(template.id)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <label 
                                        htmlFor={template.id}
                                        className={`text-sm font-medium cursor-pointer ${existingForm ? 'text-text-muted' : 'text-text-primary'}`}
                                      >
                                        {template.name}
                                      </label>
                                      {template.description && (
                                        <p className="text-xs text-text-muted mt-0.5 line-clamp-1">{template.description}</p>
                                      )}
                                      {existingForm && (
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className="text-xs text-warning">Form exists ({existingForm.status})</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 text-xs"
                                            onClick={() => navigate(`/portal/forms/${existingForm.id}`)}
                                          >
                                            <Eye className="h-3 w-3 mr-1" />
                                            View
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      {template.requires_employee_signature && (
                                        <span className="text-xs bg-accent text-primary px-2 py-0.5 rounded">Emp Sign</span>
                                      )}
                                      {template.requires_admin_signature && (
                                        <span className="text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded">Admin Sign</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center gap-3 pt-4 border-t border-[#E4E8EB] mt-4">
                    <span className="text-sm text-text-muted">
                      {selectedTemplates.length} template{selectedTemplates.length !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setGenerateFormsOpen(false)} className="rounded-xl">
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleGenerateForms}
                        disabled={isGenerating || selectedTemplates.length === 0}
                        className="bg-primary hover:bg-primary-hover text-white rounded-xl"
                        data-testid="generate-forms-submit"
                      >
                        {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : `Generate ${selectedTemplates.length} Forms`}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Import Existing Application Dialog */}
              <Dialog open={importAppOpen} onOpenChange={setImportAppOpen}>
                <DialogContent className="max-w-lg bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create from Existing Application</DialogTitle>
                    <DialogDescription>
                      Upload a completed application form and optionally a CV. The form will be stored as uploaded evidence and linked to the employee's compliance checklist.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Application Form <span className="text-red-500">*</span>
                      </Label>
                      <FileUploaderInline
                        onFileSelect={(file) => setImportAppFile(file)}
                        selectedFile={importAppFile}
                        onClear={() => setImportAppFile(null)}
                        acceptedTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                        placeholder="Drop application form here or click to browse"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        CV / Resume <span className="text-text-muted">(optional)</span>
                      </Label>
                      <FileUploaderInline
                        onFileSelect={(file) => {
                          if (file) {
                            const lower = file.name?.toLowerCase() || '';
                            const allowedCv = lower.endsWith('.pdf') || lower.endsWith('.doc') || lower.endsWith('.docx') || file.type?.includes('word') || file.type === 'application/pdf';
                            if (!allowedCv) {
                              toast.error('Upload CV as PDF or Word (.doc, .docx).');
                              return;
                            }
                          }
                          setImportCvFile(file);
                        }}
                        selectedFile={importCvFile}
                        onClear={() => setImportCvFile(null)}
                        acceptedTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                        placeholder="Drop CV here or click to browse"
                      />
                    </div>

                    <div className="bg-[#F8FAFA] rounded-xl p-4 space-y-2">
                      <h4 className="text-sm font-medium text-text-primary">What happens next:</h4>
                      <ul className="text-xs text-text-muted space-y-1">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Application Form marked as "Completed (Imported)"
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Document stored in employee's A_Application folder
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Checklist item evidence uploaded automatically
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Form fields locked (read-only) unless manually edited
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#E4E8EB] mt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => { setImportAppOpen(false); setImportAppFile(null); setImportCvFile(null); }} 
                      className="rounded-xl"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleImportApplication}
                      disabled={isImporting || !importAppFile}
                      className="bg-primary hover:bg-primary-hover text-white rounded-xl"
                      data-testid="import-application-submit"
                    >
                      {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import Application'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Import Other Document Dialog (Reference, Health Screening, Contract) */}
              <Dialog open={importDocOpen} onOpenChange={setImportDocOpen}>
                <DialogContent className="max-w-lg bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Import Existing Document</DialogTitle>
                    <DialogDescription>
                      Upload an existing completed document (Reference letter, Health form, Contract, etc.) to add evidence to the corresponding compliance requirement.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Document Type <span className="text-red-500">*</span>
                      </Label>
                      <Select value={importDocType} onValueChange={setImportDocType}>
                        <SelectTrigger className="rounded-xl" data-testid="import-doc-type-select">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recruitment_checklist">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-primary" />
                              Recruitment Compliance Checklist
                            </div>
                          </SelectItem>
                          <SelectItem value="personal_info">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-info" />
                              Personal Information Form
                            </div>
                          </SelectItem>
                          <SelectItem value="interview_record">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-primary" />
                              Interview Record Form
                            </div>
                          </SelectItem>
                          <SelectItem value="equal_opportunities">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-success" />
                              Equal Opportunities Monitoring
                            </div>
                          </SelectItem>
                          <SelectItem value="reference_1">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-warning" />
                              Reference 1
                            </div>
                          </SelectItem>
                          <SelectItem value="reference_2">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-warning" />
                              Reference 2
                            </div>
                          </SelectItem>
                          <SelectItem value="health_screening">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-info" />
                              Health Screening Questionnaire
                            </div>
                          </SelectItem>
                          <SelectItem value="contract">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-success" />
                              Contract / Offer Letter
                            </div>
                          </SelectItem>
                          <SelectItem value="induction">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-primary" />
                              Induction & Competency
                            </div>
                          </SelectItem>
                          <SelectItem value="handbook">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-gray-400" />
                              Employee Handbook Acknowledgement
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Document File <span className="text-red-500">*</span>
                      </Label>
                      <FileUploaderInline
                        onFileSelect={(file) => setImportDocFile(file)}
                        selectedFile={importDocFile}
                        onClear={() => setImportDocFile(null)}
                        acceptedTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png']}
                        placeholder="Drop document here or click to browse"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Notes <span className="text-text-muted">(optional)</span>
                      </Label>
                      <Textarea 
                        value={importDocNotes}
                        onChange={(e) => setImportDocNotes(e.target.value)}
                        placeholder="e.g., Reference from John Smith, previous employer at ABC Company"
                        className="rounded-xl resize-none"
                        rows={2}
                      />
                    </div>

                    <div className="bg-[#F8FAFA] rounded-xl p-4 space-y-2">
                      <h4 className="text-sm font-medium text-text-primary">What happens:</h4>
                      <ul className="text-xs text-text-muted space-y-1">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Form marked as "Completed (Imported)"
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Document stored in employee's compliance folder
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Checklist requirement evidence uploaded
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                          Ready for verification
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#E4E8EB] mt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => { setImportDocOpen(false); setImportDocType(''); setImportDocFile(null); setImportDocNotes(''); }} 
                      className="rounded-xl"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleImportDocument}
                      disabled={isImporting || !importDocFile || !importDocType}
                      className="bg-primary hover:bg-primary-hover text-white rounded-xl"
                      data-testid="import-document-submit"
                    >
                      {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import Document'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </CardContent>
      </Card>

      <PersonFileHeroCard
        employeeId={employeeId}
        employee={employee}
        profileMode={profileMode}
        progressPct={effectiveProgressPct}
        completedRequirements={effectiveCompletedRequirements}
        totalRequirements={effectiveTotalRequirements}
        readinessLabel={canonicalReadinessLabel}
        isReady={profileMode === 'applicant'
          ? applicantReadyForRecruitmentApproval
          : (lifecycleStage !== 'active' ? activationView.canActivate : canonicalIsWorkReady)}
        blockers={canonicalBlockerObjects}
        onFixIssues={handleOpenFixItWizard}
        onGoToCompliance={() => setActiveTab('checklist')}
        onRecordExistingEvidence={
          ['admin', 'super_admin', 'manager', 'branch_manager'].includes(user?.role) && !isAuditor()
            ? () => setAssistedOnboardingOpen(true)
            : undefined
        }
        isAuditor={isAuditor()}
        refreshTrigger={personFileRefreshKey}
      />

      <FixItWizard
        key={`fix-it-${employeeId}-${fixItWizardKey}`}
        open={fixItWizardOpen}
        onOpenChange={setFixItWizardOpen}
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        blockers={canonicalBlockerObjects}
        onRefresh={() => {
          setPersonFileRefreshKey((k) => k + 1);
          fetchData();
          fetchCompliance();
          fetchOnboardingSync();
        }}
        onNavigateToCompliance={() => setActiveTab('checklist')}
        onRequestDocument={() => setActiveTab('checklist')}
        isAuditor={isAuditor()}
      />

      <AssistedOnboardingWorkspace
        employeeId={employeeId}
        open={assistedOnboardingOpen}
        onClose={() => {
          setAssistedOnboardingOpen(false);
          setPersonFileRefreshKey((k) => k + 1);
          fetchData();
          fetchCompliance();
          fetchOnboardingSync();
        }}
      />

      {profileMode === 'applicant' && onboardingSync ? (
        <AdminOnboardingSyncPanel
          onboardingSync={onboardingSync}
          onRefresh={fetchOnboardingSync}
          onJumpToTab={setActiveTab}
          employmentSignedOff={employmentSignedOff || Boolean(recruitmentTabSignoffs?.employment?.signed_off)}
          recruitmentApproved={Boolean(recruitmentStatus?.recruitment_approved)}
          recruitmentProgress={recruitmentStatus?.recruitment_progress}
        />
      ) : null}

      {/* Early KID issue is available in Recruitment, BEFORE Recruitment Approval.
          Does not change applicant progress or any canonical sign-off. */}
      {profileMode === 'applicant' && (
        <ApplicantKidPanel employeeId={employeeId} isAuditor={isAuditor()} />
      )}

      {/* Pending Verification Banner - Shows items needing admin attention */}
      {isAuditor() && (
        <PendingVerificationBanner
          employeeId={employeeId}
          employeeName={`${employee?.first_name} ${employee?.last_name}`}
          onNavigateToItem={(tab) => {
            setActiveTab(tab === 'compliance' ? 'work_readiness' : tab);
          }}
        />
      )}

      {/* RTW applicant-provided share code — pending admin online check (always visible above tabs) */}
      {!isAuditor() && (
        <AdminRTWShareCodeSubmission employeeId={employeeId} />
      )}

      {/* Tabs - workflow order for applicants; functional grouping for employees */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="bg-white border border-[#E4E8EB] p-1 rounded-xl flex-wrap">
          {profileMode === 'applicant' ? (
            <>
              <TabsTrigger value="employment" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <Briefcase className="h-4 w-4 mr-2" />
                1. Recruitment Review
              </TabsTrigger>
              <TabsTrigger value="references" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <UserCheck className="h-4 w-4 mr-2" />
                2. References
              </TabsTrigger>
              <TabsTrigger value="forms" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <FileText className="h-4 w-4 mr-2" />
                {['new', 'screening', 'interview'].includes(normalizeLifecycleStatus(employee?.status))
                  ? '3. Application & Interview'
                  : '3. Pre-employment Forms'}
              </TabsTrigger>
              <TabsTrigger value="checklist" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <CheckCircle className="h-4 w-4 mr-2" />
                4. Recruitment Checks
              </TabsTrigger>
              <TabsTrigger value="training" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <GraduationCap className="h-4 w-4 mr-2" />
                5. Training
              </TabsTrigger>
              <TabsTrigger value="work_readiness" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <User className="h-4 w-4 mr-2" />
                6. Approval
              </TabsTrigger>
              <TabsTrigger value="audit" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
                <History className="h-4 w-4 mr-2" />
                Audit
              </TabsTrigger>
            </>
          ) : (
            <>
          <TabsTrigger value="employment" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <Briefcase className="h-4 w-4 mr-2" />
            Employment Review
          </TabsTrigger>
          <TabsTrigger value="forms" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Forms
          </TabsTrigger>
          <TabsTrigger value="references" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <UserCheck className="h-4 w-4 mr-2" />
            References
          </TabsTrigger>
          <TabsTrigger value="checklist" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <CheckCircle className="h-4 w-4 mr-2" />
            Checks & Evidence
          </TabsTrigger>
          <TabsTrigger value="training" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <GraduationCap className="h-4 w-4 mr-2" />
            Training
          </TabsTrigger>
          <TabsTrigger value="work_readiness" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <User className="h-4 w-4 mr-2" />
            Profile Summary
          </TabsTrigger>
          {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
            <TabsTrigger value="competencies" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Competencies
            </TabsTrigger>
          )}
          {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
            <TabsTrigger value="spot_checks" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Eye className="h-4 w-4 mr-2" />
              Spot Checks
            </TabsTrigger>
          )}
          {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
            <TabsTrigger value="supervisions" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Calendar className="h-4 w-4 mr-2" />
              Supervisions
            </TabsTrigger>
          )}
          {profileMode === 'employee' && lifecycleStage === 'active' && (
            <TabsTrigger value="appraisals" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <CalendarClock className="h-4 w-4 mr-2" />
              Appraisals
            </TabsTrigger>
          )}
          {profileMode === 'employee' && (
            <TabsTrigger value="policies" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Shield className="h-4 w-4 mr-2" />
              Policies
            </TabsTrigger>
          )}
          <TabsTrigger value="audit" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <History className="h-4 w-4 mr-2" />
            Audit
          </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* ========== TAB 1: PROFILE SUMMARY ========== */}
        {/* Stable profile / core-record tab. Readiness logic lives above the tabs only. */}
        <TabsContent value="work_readiness">
          <div className="space-y-6">
            {/* C3E: applicant-mode compact identity + canonical recruitment
                readiness summary. Presentation only — reuses recruitmentTabSignoffs,
                employmentSignedOff and complianceReadinessSummary.blockingCount so no
                second approval gate is introduced. */}
            {profileMode === 'applicant' && (
              <Card className="border-[#E4E8EB] shadow-sm" data-testid="approval-applicant-summary">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Recruitment readiness</CardTitle>
                  <p className="mt-1 text-sm text-text-muted">
                    This is the final recruitment decision workspace. Review the remaining
                    blockers below, confirm employment documents and agreements, then approve
                    the applicant to onboarding once all required checks are complete.
                  </p>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="approval-applicant-identity">
                    <div>
                      <p className="text-xs text-text-muted">Applicant</p>
                      <p className="font-medium text-text-primary">{employee?.first_name} {employee?.last_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Applicant reference</p>
                      <p className="font-medium text-text-primary">{employee?.applicant_reference || employee?.employee_code || 'Not assigned'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Role</p>
                      <p className="font-medium text-text-primary">{employee?.role || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Recruitment status</p>
                      <Badge className="bg-gray-100 text-gray-700">
                        {employee?.status?.replace(/_/g, ' ') || 'Unknown'}
                      </Badge>
                    </div>
                  </div>

                  <div className="rounded-lg border border-[#E4E8EB] p-3" data-testid="approval-recruitment-readiness">
                    <p className="text-xs uppercase tracking-wider text-text-muted mb-2">Recruitment readiness</p>
                    <div className="space-y-1.5">
                      {APPROVAL_STAGE_SUMMARY_ITEMS.map(({ key, label }) => {
                        const done = key === 'employment'
                          ? (employmentSignedOff || Boolean(recruitmentTabSignoffs?.employment?.signed_off))
                          : Boolean(recruitmentTabSignoffs?.[key]?.signed_off);
                        return (
                          <div key={key} className="flex items-center justify-between text-sm" data-testid={`approval-stage-${key}`}>
                            <span className="text-text-primary">{label}</span>
                            <span className={done ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                              {done ? 'Complete' : 'Outstanding'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#E4E8EB] flex items-center justify-between text-sm" data-testid="approval-final-blockers">
                      <span className="font-medium text-text-primary">Final blockers</span>
                      <span className={complianceReadinessSummary.blockingCount > 0 ? 'text-red-600 font-semibold' : 'text-green-700 font-semibold'}>
                        {complianceReadinessSummary.blockingCount}
                      </span>
                    </div>
                    {!applicantReadyForRecruitmentApproval && (
                      <p className="mt-2 text-xs text-amber-700" data-testid="approval-readiness-hint">
                        Final approval stays locked until every recruitment stage is signed off and all blockers are cleared.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {canRecordWorkApproval() && (
              <Card className="border-[#E4E8EB] shadow-sm">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">
                      {profileMode === 'applicant' ? 'Final recruitment approval' : 'Fit for Work Approval'}
                    </CardTitle>
                    <p className="mt-1 text-sm text-text-muted">
                      {profileMode === 'applicant'
                        ? 'Review the remaining recruitment blockers, employment documents and agreements, then approve the applicant to onboarding when all required checks are complete.'
                        : 'Record the approval decision once readiness checks are clear.'}
                    </p>
                  </div>
                  {profileMode === 'applicant' ? (
                    <Button
                      onClick={openWorkApprovalDialog}
                      disabled={!applicantReadyForRecruitmentApproval}
                      className="w-full sm:w-auto"
                      title={
                        !employmentSignedOff
                          ? 'Employment Review sign-off is required first'
                          : !recruitmentTabsSignedOff
                            ? 'Each recruitment tab must be signed off first'
                            : undefined
                      }
                    >
                      Approve to Onboarding
                    </Button>
                  ) : employee?.status === 'onboarding' ? (
                    <Button
                      onClick={openWorkApprovalDialog}
                      disabled={!canRecordFitForWorkNow}
                      className="w-full sm:w-auto"
                      title={!canRecordFitForWorkNow
                        ? 'A new Fit-for-Work decision is available only when approved Onboarding readiness checks pass and a decision is required or blocked.'
                        : undefined}
                    >
                      {activationView.isBlocked ? 'Review Fit-for-Work Decision' : 'Record Fit-for-Work Decision'}
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent className="space-y-4">
                  {profileMode === 'employee' && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-text-muted">Promotion to active</p>
                      <p className="mt-1 text-sm text-text-primary" data-testid="activation-status-message">
                        {canPromoteToActiveNow
                          ? 'This employee can now be promoted to active.'
                          : (activationView.message
                              || 'Promotion stays locked until readiness and fit-for-work approval are both complete.')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handlePromoteToActive}
                      disabled={!canPromoteToActiveNow}
                      className="w-full sm:w-auto"
                    >
                    Activate Employee
                    </Button>
                  </div>
                  )}

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-text-muted">Latest decision</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge className={getWorkApprovalBadgeClass(latestWorkReadinessDecision?.outcome)}>
                          {formatWorkApprovalOutcome(latestWorkReadinessDecision?.outcome)}
                        </Badge>
                        {!canonicalIsWorkReady && (
                          <span className="text-xs text-red-600">
                            Approval is locked until readiness checks pass.
                          </span>
                        )}
                      </div>
                    </div>
                    {latestWorkReadinessDecision?.decided_at && (
                      <div className="text-sm text-text-muted sm:text-right">
                        <p>Approved by {latestWorkReadinessDecision?.decided_by_name || latestWorkReadinessDecision?.decided_by || 'Unknown'}</p>
                        <p>{formatBackendDateTime(latestWorkReadinessDecision.decided_at)}</p>
                      </div>
                    )}
                  </div>

                  {latestWorkReadinessDecision?.rationale ? (
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase tracking-wider text-text-muted">Rationale</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-text-primary">
                        {latestWorkReadinessDecision.rationale}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-text-muted">No explicit fit-for-work decision has been recorded yet.</p>
                  )}

                  {latestWorkReadinessDecision?.conditions?.length > 0 && (
                    <div className="rounded-lg bg-amber-50 p-3">
                      <p className="text-xs uppercase tracking-wider text-amber-700">Conditions</p>
                      <ul className="mt-2 space-y-1 text-sm text-amber-900">
                        {latestWorkReadinessDecision.conditions.map((condition, index) => (
                          <li key={`${condition}-${index}`}>- {condition}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* â”€â”€ Personal Details â”€â”€ */}
            {/* C3D: Employment documents & agreements — presentation relocated from the
                Recruitment Checks tab. Reads the same canonical agreement/contract state;
                nothing in this section blocks Recruitment Checks sign-off. */}
            {profileMode === 'applicant' && (
              <Card className="border-[#E4E8EB] shadow-sm" data-testid="approval-employment-documents-section">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Employment documents & agreements</CardTitle>
                  <p className="mt-1 text-sm text-text-muted">
                    Contract and handbook preparation, worker acceptance, and acknowledgements are
                    managed here. These do not block Recruitment Checks sign-off.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ComplianceTabErrorBoundary>
                    <EmploymentPackPanel
                      employeeId={employeeId}
                      isAuditor={isAuditor()}
                      onRefresh={refreshChecksAfterMutation}
                    />
                  </ComplianceTabErrorBoundary>
                  <ComplianceTabErrorBoundary>
                    <DualRowComplianceSection
                      surface="agreements_only"
                      employeeId={employeeId}
                      employeeEmail={employee?.email}
                      employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
                      employeeData={employee}
                      complianceFile={complianceFile}
                      parentManagesFetch={true}
                      onPreviewFile={handleCompliancePreviewFile}
                      onReissueContract={(contract) => {
                        setCurrentContract(contract || null);
                        setSupersedeContractOpen(true);
                      }}
                      isAuditor={isAuditor()}
                      onRefresh={refreshChecksAfterMutation}
                    />
                  </ComplianceTabErrorBoundary>
                </CardContent>
              </Card>
            )}

            {employee?.person_stage === 'employee' && (
              <Card className="border-[#E4E8EB] shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Occupational Health</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-text-muted">Current outcome</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge className={getHealthOutcomeBadgeClass(latestHealthDeclaration?.status)}>
                          {formatHealthOutcomeLabel(latestHealthDeclaration?.status || 'missing')}
                        </Badge>
                        <span className={`text-xs ${latestHealthDeclaration?.satisfies_readiness ? 'text-green-700' : 'text-red-600'}`}>
                          {latestHealthDeclaration?.satisfies_readiness
                            ? 'Satisfies readiness'
                            : 'Does not satisfy readiness'}
                        </span>
                      </div>
                    </div>
                    {(latestHealthDeclaration?.reviewed_at || latestHealthDeclaration?.reviewed_by) && (
                      <div className="text-sm text-text-muted sm:text-right">
                        <p>Reviewed by {latestHealthDeclaration?.reviewed_by || 'Unknown'}</p>
                        {latestHealthDeclaration?.reviewed_at && (
                          <p>{formatBackendDateTime(latestHealthDeclaration.reviewed_at)}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {latestHealthDeclaration?.review_notes && (
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase tracking-wider text-text-muted">Review notes</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-text-primary">
                        {latestHealthDeclaration.review_notes}
                      </p>
                    </div>
                  )}

                  {latestHealthDeclaration?.adjustments_required && (
                    <div className="rounded-lg bg-amber-50 p-3">
                      <p className="text-xs uppercase tracking-wider text-amber-700">Adjustments required</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900">
                        {latestHealthDeclaration.adjustments_required}
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg bg-gray-50 p-3 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-text-muted">Scope of practice notes</p>
                    <Textarea
                      value={scopeOfPracticeDraft}
                      onChange={(e) => setScopeOfPracticeDraft(e.target.value)}
                      placeholder="Record clinical scope limits, delegated tasks, and escalation boundaries"
                      rows={4}
                      disabled={isAuditor() || isSavingScopeOfPractice}
                    />
                    {!isAuditor() && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={handleSaveScopeOfPractice}
                          disabled={isSavingScopeOfPractice}
                        >
                          {isSavingScopeOfPractice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Scope Notes
                        </Button>
                      </div>
                    )}
                  </div>

                  {!latestHealthDeclaration && (
                    <p className="text-sm text-text-muted">
                      No reviewed health declaration is on file yet.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* C3E: Personal/Contact/Emergency/Employment History/References are
                duplicate general-profile cards on the applicant Approval tab. Their
                canonical review surfaces live on Tabs 1-3 (Recruitment Review,
                References, Application & Interview) and the person hero header, so
                they are scoped OUT of applicant Approval and retained for
                employee/active profiles only. Presentation change only — no records,
                edit routes or payload fields are removed. */}
            {profileMode !== 'applicant' && (
            <>
            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Personal Details</CardTitle>
                {!isAuditor() && (
                  <Button variant="outline" size="sm" onClick={() => setEditPersonalOpen(true)} data-testid="edit-personal-btn">
                    <Edit className="h-4 w-4 mr-1" />Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-text-muted">Full Name</p>
                    <p className="font-medium text-text-primary">{employee?.first_name} {employee?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">{employee?.person_stage === 'applicant' ? 'Applicant Reference' : 'Employee ID'}</p>
                    <p className="font-medium text-text-primary">{employee?.employee_code || employee?.applicant_reference || 'Not assigned'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Role</p>
                    <p className="font-medium text-text-primary">{employee?.role || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Status</p>
                    <Badge className={
                      employee?.status === 'active_employee' ? 'bg-green-100 text-green-700' :
                      employee?.status === 'onboarding' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {employee?.status === 'active_employee' ? 'Active employee'
                        : employee?.status === 'onboarding' ? 'Onboarding'
                        : employee?.status?.replace(/_/g, ' ') || 'Unknown'}
                    </Badge>
                  </div>
                  {employee?.date_of_birth && (
                    <div>
                      <p className="text-xs text-text-muted">Date of Birth</p>
                      <p className="font-medium text-text-primary">{formatBackendDate(employee.date_of_birth)}</p>
                    </div>
                  )}
                  {employee?.national_insurance_number && (
                    <div>
                      <p className="text-xs text-text-muted">NI Number</p>
                      <p className="font-medium text-text-primary">{employee.national_insurance_number}</p>
                    </div>
                  )}
                  {employee?.nationality && (
                    <div>
                      <p className="text-xs text-text-muted">Nationality</p>
                      <p className="font-medium text-text-primary">{employee.nationality}</p>
                    </div>
                  )}
                  {employee?.start_date && (
                    <div>
                      <p className="text-xs text-text-muted">Start Date</p>
                      <p className="font-medium text-text-primary">{formatBackendDate(employee.start_date)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* â”€â”€ Contact Details â”€â”€ */}
            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Contact Details</CardTitle>
                {!isAuditor() && (
                  <Button variant="outline" size="sm" onClick={() => setEditPersonalOpen(true)} data-testid="edit-contact-btn">
                    <Edit className="h-4 w-4 mr-1" />Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-text-muted">Email</p>
                    <p className="font-medium text-text-primary break-all">{employee?.email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">Phone</p>
                    <p className="font-medium text-text-primary">{employee?.phone || employee?.phone_number || '—'}</p>
                  </div>
                  {employee?.mobile && (
                    <div>
                      <p className="text-xs text-text-muted">Mobile</p>
                      <p className="font-medium text-text-primary">{employee.mobile}</p>
                    </div>
                  )}
                </div>
                {/* Address */}
                {(employee?.address_line_1 || employee?.city || employee?.postcode) && (
                  <div className="mt-4 pt-4 border-t border-[#E4E8EB]">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Address</p>
                    <address className="not-italic text-sm text-text-primary leading-relaxed">
                      {employee?.address_line_1 && <span className="block">{employee.address_line_1}</span>}
                      {employee?.address_line_2 && <span className="block">{employee.address_line_2}</span>}
                      {(employee?.city || employee?.county) && (
                        <span className="block">{[employee?.city, employee?.county].filter(Boolean).join(', ')}</span>
                      )}
                      {employee?.postcode && <span className="block">{employee.postcode}</span>}
                      {employee?.country && employee?.country !== 'United Kingdom' && (
                        <span className="block">{employee.country}</span>
                      )}
                    </address>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* â”€â”€ Emergency Contact / Next of Kin â”€â”€ */}
            {(employee?.emergency_contact_name || employee?.next_of_kin_name) && (
              <Card className="border-[#E4E8EB] shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Emergency Contact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-text-muted">Name</p>
                      <p className="font-medium text-text-primary">
                        {employee?.emergency_contact_name || employee?.next_of_kin_name || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Relationship</p>
                      <p className="font-medium text-text-primary">
                        {employee?.emergency_contact_relationship || employee?.next_of_kin_relationship || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Phone</p>
                      <p className="font-medium text-text-primary">
                        {employee?.emergency_contact_phone || employee?.next_of_kin_phone || '—'}
                      </p>
                    </div>
                    {employee?.next_of_kin_address && (
                      <div className="col-span-2 sm:col-span-3">
                        <p className="text-xs text-text-muted">Address</p>
                        <p className="font-medium text-text-primary">{employee.next_of_kin_address}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* â”€â”€ Employment History â”€â”€ */}
            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Employment History</CardTitle>
                {!isAuditor() && (
                  <Button variant="outline" size="sm" onClick={() => setEditEmploymentOpen(true)} data-testid="edit-employment-btn">
                    <Edit className="h-4 w-4 mr-1" />Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {employee?.employment_history && employee.employment_history.length > 0 ? (
                  <div className="space-y-2">
                    {employee.employment_history.slice(0, 5).map((job, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{job.employer || job.company || job.employer_name}</p>
                          <p className="text-xs text-gray-500">{job.job_title || job.position}</p>
                        </div>
                        <p className="text-xs text-gray-400 shrink-0 ml-2">
                          {formatBackendDate(job.start_date, { format: 'short' })} - {job.end_date ? formatBackendDate(job.end_date, { format: 'short' }) : 'Present'}
                        </p>
                      </div>
                    ))}
                    {employee.employment_history.length > 5 && (
                      <p className="text-xs text-gray-500 text-center">+{employee.employment_history.length - 5} more — see Employment Review tab</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No employment history recorded. See the Employment Review tab to add history.</p>
                )}
              </CardContent>
            </Card>

            {/* â”€â”€ References â”€â”€ */}
            <Card className="border-[#E4E8EB] shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">References</CardTitle>
                {!isAuditor() && (
                  <Button variant="outline" size="sm" onClick={() => setEditReferenceOpen(true)} data-testid="edit-references-btn">
                    <Edit className="h-4 w-4 mr-1" />Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[1, 2].map((n) => {
                    const canonicalRefFromStatus = asArray(referenceStatus, ['items', 'records', 'references']).find((r) => {
                      const refNum = String(r?.reference_num || r?.reference_number || '').trim();
                      const reqId = String(r?.requirement_id || r?.id || '').toLowerCase();
                      return refNum === String(n) || reqId === `reference_${n}`;
                    });
                    const canonicalRefFromCompliance = asArray(complianceSections?.references?.rows).find((r) => {
                      const reqId = String(r?.requirement_id || r?.id || r?.key || '').toLowerCase();
                      const label = String(r?.label || r?.title || '').toLowerCase();
                      return reqId === `reference_${n}` || label.includes(`reference ${n}`);
                    });
                    const arrayFallbackRef = employee?.references?.[n - 1];
                    const legacyInlineRef = employee?.[`reference_${n}`];
                    const ref = canonicalRefFromStatus || canonicalRefFromCompliance || arrayFallbackRef || legacyInlineRef;

                    const status = String(
                      canonicalRefFromStatus?.verification_status
                      || canonicalRefFromStatus?.status
                      || canonicalRefFromCompliance?.verification_status
                      || canonicalRefFromCompliance?.status
                      || employee?.[`reference_${n}_status`]
                      || (arrayFallbackRef?.verified ? 'verified' : '')
                      || (legacyInlineRef?.verified ? 'verified' : '')
                      || ''
                    );
                    const isVerified = status.toLowerCase() === 'verified';
                    const name = ref?.name || ref?.referee_name || ref?.full_name || 'Name not recorded';
                    const email = ref?.email || ref?.referee_email || null;
                    const phone = ref?.phone || ref?.referee_phone || null;
                    const company = ref?.company || ref?.organisation || ref?.employer || ref?.referee_organisation || null;
                    const relationship = ref?.relationship || ref?.referee_relationship || null;
                    return (
                      <div key={n} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Reference {n}</p>
                        {ref ? (
                          <>
                            <p className="font-medium text-sm">{name}</p>
                            {email && <p className="text-xs text-gray-500">{email}</p>}
                            {phone && <p className="text-xs text-gray-500">{phone}</p>}
                            {company && <p className="text-xs text-gray-500">{company}</p>}
                            {relationship && <p className="text-xs text-gray-500">{relationship}</p>}
                            <Badge className={isVerified ? 'bg-green-100 text-green-700 mt-1' : 'bg-amber-100 text-amber-700 mt-1'}>
                              {isVerified ? 'Verified' : 'Awaiting verification'}
                            </Badge>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">Not yet provided</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            </>
            )}

            {/* â”€â”€ Recurring compliance (active workforce only, display-only surface of
                 canonical /employees/{id}/recurring-compliance; no new truth) â”€â”€ */}
            {lifecycleStage === 'active' && (
              <Card className="border-[#E4E8EB] shadow-sm" data-testid="section-recurring-compliance-root">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Recurring compliance</CardTitle>
                  <p className="mt-1 text-sm text-text-muted">
                    Overdue, due, and upcoming supervisions, competency assessments, spot checks, and training refreshes.
                  </p>
                </CardHeader>
                <CardContent>
                  <RecurringComplianceSection
                    employeeId={employeeId}
                    employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
                  />
                </CardContent>
              </Card>
            )}

            {profileMode === 'applicant' && (
              <ApplicantRecruitmentTabFooter
                currentTab="work_readiness"
                employeeStatus={employee?.status}
                onContinue={handleTabChange}
              />
            )}
          </div>
        </TabsContent>
        {/* Interview record + worker onboarding forms (health, personal, HMRC, declarations) */}
        <TabsContent value="forms">
          {/* â”€â”€ Top-level Forms & Interview Summary â”€â”€ */}
          {(() => {
            // Derive form-level counts — reused in summary banner and form cards
            const FORM_GROUPS = [
              {
                label: 'Pre-Employment Checks',
                sensitive: false,
                forms: [
                  { key: 'staff_health_questionnaire', name: 'Staff Health Questionnaire', description: 'Medical history and health declarations', prefill: true, sensitive: true },
                  { key: 'staff_personal_info', name: 'Staff Personal Information', description: 'Contact details, NI number, bank details', prefill: true },
                  { key: 'hmrc_starter_checklist', name: 'HMRC Starter Checklist', description: 'Tax code and employment status', sensitive: true },
                  { key: 'equal_opportunities', name: 'Equal Opportunities Monitoring', description: 'Equality & diversity monitoring (optional, non-blocking)', sensitive: true, optional: true },
                  { key: 'emergency_contacts', name: 'Emergency Contacts', description: 'Next of kin and emergency contact details', prefill: true },
                ]
              },
              {
                label: 'Declarations',
                sensitive: true,
                forms: [
                  { key: 'conflict_of_interest', name: 'Conflict of Interest Declaration', description: 'Secondary employment, relationships or financial interests (NHS standard)', sensitive: true },
                  ...(/manager|director/i.test(employee?.role || '') ? [{ key: 'fit_proper_persons', name: 'Fit and Proper Persons Declaration', description: 'CQC Regulation 5 — managers and directors only', sensitive: true }] : []),
                ]
              },
            ];
            const allFormDefinitions = FORM_GROUPS.flatMap((g) => g.forms);

            // Status derivation for each form
            const deriveFormStatus = (form) => {
              const submission = resolveFormSubmission(formSubmissions, form.key);
              // The server forbids work-health collection before selection.
              // A locked questionnaire must not appear as worker-neglected.
              if (!submission && form.key === 'staff_health_questionnaire'
                  && formsTabReadiness?.checks?.work_health_available === false) {
                return { status: 'not_yet_available', submission: null };
              }
              if (!submission) return { status: 'missing', submission: null };
              const raw = submission?.status || 'not_started';
              if (['returned_for_correction', 'reopened_for_worker_correction', 'amendment_requested'].includes(raw)) return { status: 'returned_for_correction', submission };
              if (raw === 'verified' || raw === 'signed_off' || submission?.verified === true) return { status: 'signed_off', submission };
              if (raw === 'rejected') return { status: 'rejected', submission };
              if (raw === 'approved' || raw === 'reviewed') return { status: 'reviewed', submission };
              if (raw === 'submitted') return { status: 'submitted', submission };
              if (raw === 'draft' || raw === 'in_progress') return { status: 'in_progress', submission };
              return { status: 'missing', submission };
            };

            const formStatuses = allFormDefinitions.map((f) => ({ ...f, ...deriveFormStatus(f) }));

            // Cannot-assess guard
            const cannotAssessForms = Boolean(formSubmissionsError);

            // Counts
            const missingCount = formStatuses.filter((f) => f.status === 'missing').length;
            const notYetAvailableCount = formStatuses.filter((f) => f.status === 'not_yet_available').length;
            const inProgressCount = formStatuses.filter((f) => f.status === 'in_progress').length;
            const submittedCount = formStatuses.filter((f) => f.status === 'submitted').length;
            const reviewedCount = formStatuses.filter((f) => f.status === 'reviewed').length;
            const signedOffCount = formStatuses.filter((f) => f.status === 'signed_off').length;
            const rejectedCount = formStatuses.filter((f) => f.status === 'rejected').length;
            const returnedForCorrectionCount = formStatuses.filter((f) => f.status === 'returned_for_correction').length;
            const totalForms = allFormDefinitions.length;
            const allSignedOff = signedOffCount === totalForms && !cannotAssessForms;

            // Interview record status (from formSubmissions — interview submission)
            const interviewSubmission = formSubmissions?.find(fs =>
              fs.form_type === 'interview_record' || fs.requirement_id === 'interview_record'
            );
            const interviewFormStatus = interviewSubmission?.status || null;
            const interviewExists = Boolean(interviewSubmission);
            const interviewDecision = interviewSubmission?.form_data?.decision || interviewSubmission?.data?.decision || interviewSubmission?.form_data?.overall_decision || null;
            const interviewScore = interviewSubmission?.form_data?.total_score || interviewSubmission?.data?.total_score || null;
            const interviewPassMark = interviewSubmission?.form_data?.pass_score
              || interviewSubmission?.data?.pass_score
              || interviewSubmission?.form_data?.pass_mark
              || interviewSubmission?.data?.pass_mark
              || 11;
            const interviewStoredPassed = interviewSubmission?.form_data?.passed ?? interviewSubmission?.data?.passed;
            const interviewPassed = interviewStoredPassed ?? (
              interviewScore !== null && interviewScore !== undefined
                ? Number(interviewScore) >= Number(interviewPassMark)
                : null
            );
            const interviewAdminStatus = interviewDecision
              ? (['Reject', 'Not Suitable'].includes(interviewDecision) ? 'reviewed_rejected' : 'reviewed_approved')
              : interviewPassed !== null
                ? (interviewPassed ? 'reviewed_passed' : 'reviewed_failed')
                : interviewFormStatus;

            // Banner colour
            const summaryBorderClass = cannotAssessForms
              ? 'border-red-200 bg-red-50'
              : allSignedOff
                ? 'border-green-200 bg-green-50'
                : (rejectedCount > 0 || returnedForCorrectionCount > 0)
                  ? 'border-red-200 bg-red-50'
                  : 'border-amber-200 bg-amber-50';
            const summaryTextClass = cannotAssessForms
              ? 'text-red-800'
              : allSignedOff
                ? 'text-green-800'
                : (rejectedCount > 0 || returnedForCorrectionCount > 0)
                  ? 'text-red-800'
                  : 'text-amber-800';

            // Status display config
            const STATUS_DISPLAY = {
              missing:     { label: 'Missing',                         color: 'bg-gray-100 text-gray-600',   icon: AlertCircle, iconColor: 'text-gray-400',   cardBorder: 'bg-gray-50 border-gray-200' },
              not_yet_available: { label: 'After interview & offer', color: 'bg-blue-100 text-blue-800', icon: Clock, iconColor: 'text-blue-500', cardBorder: 'bg-blue-50 border-blue-200' },
              in_progress: { label: 'Worker in progress',              color: 'bg-amber-100 text-amber-700', icon: Clock,       iconColor: 'text-amber-500',  cardBorder: 'bg-amber-50 border-amber-200' },
              submitted:   { label: 'Submitted, not reviewed',         color: 'bg-blue-100 text-blue-700',   icon: FileText,    iconColor: 'text-blue-500',   cardBorder: 'bg-blue-50 border-blue-200' },
              reviewed:    { label: 'Reviewed',                        color: 'bg-indigo-100 text-indigo-700',icon: Eye,         iconColor: 'text-indigo-500', cardBorder: 'bg-indigo-50 border-indigo-200' },
              signed_off:  { label: 'Signed off',                      color: 'bg-green-100 text-green-700', icon: CheckCircle,  iconColor: 'text-green-600',  cardBorder: 'bg-green-50 border-green-200' },
              rejected:    { label: 'Rejected / action required',      color: 'bg-red-100 text-red-700',     icon: XCircle,     iconColor: 'text-red-500',    cardBorder: 'bg-red-50 border-red-200' },
              returned_for_correction: { label: 'Returned for correction', color: 'bg-red-100 text-red-700', icon: RotateCcw, iconColor: 'text-red-500', cardBorder: 'bg-red-50 border-red-200' },
            };

            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', year: 'numeric'
            }) : null;

            return (
              <div className="space-y-6">
                {profileMode === 'applicant' && (
                  <ApplicantTabReadinessBanner
                    readiness={formsTabReadiness}
                    completeMessage="Application & Interview tab complete — you can continue to Recruitment Checks"
                    incompleteMessage="Application & Interview tab incomplete"
                  />
                )}

                {/* â”€â”€ Summary banner â”€â”€ */}
                <div className={`rounded-xl border p-4 ${summaryBorderClass}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {cannotAssessForms
                        ? <AlertTriangle className="h-5 w-5 text-red-600" />
                        : allSignedOff
                          ? <CheckCircle className="h-5 w-5 text-green-600" />
                          : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${summaryTextClass}`}>
                        {cannotAssessForms
                          ? 'Cannot assess forms — submission data unavailable'
                          : allSignedOff
                            ? `All ${totalForms} forms signed off`
                            : `Forms Signed Off: ${signedOffCount} / ${totalForms}`}
                      </p>
                      {!cannotAssessForms && !allSignedOff && (
                        <div className="mt-2 text-sm space-y-0.5">
                          {missingCount > 0 && <p>Missing: {missingCount}</p>}
                          {notYetAvailableCount > 0 && <p>Not yet due (post-interview): {notYetAvailableCount}</p>}
                          {inProgressCount > 0 && <p>Worker in progress: {inProgressCount}</p>}
                          {submittedCount > 0 && <p>Submitted, not reviewed: {submittedCount}</p>}
                          {reviewedCount > 0 && <p>Reviewed (not yet signed off): {reviewedCount}</p>}
                          {returnedForCorrectionCount > 0 && <p className="text-red-700 font-medium">Returned for correction: {returnedForCorrectionCount}</p>}
                          {rejectedCount > 0 && <p className="text-red-700 font-medium">Rejected / action required: {rejectedCount}</p>}
                        </div>
                      )}
                      {cannotAssessForms && (
                        <div className="mt-2">
                          <Button size="sm" variant="outline" onClick={fetchFormSubmissions}
                            className="text-red-600 border-red-200 hover:bg-red-50">
                            <RefreshCw className="h-3 w-3 mr-1" /> Retry
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {profileMode === 'applicant' ? (
                  <InterviewSchedulingPanel
                    key={interviewSchedulingRefreshKey}
                    hasApprovedInterview={interviewAdminStatus === 'reviewed_approved' || interviewAdminStatus === 'reviewed_passed'}
                    employeeId={employeeId}
                    onUpdated={() => {
                      fetchFormSubmissions();
                      fetchOnboardingSync();
                    }}
                    onRecordInterview={() => {
                      const assessment = document.getElementById('interview-assessment');
                      assessment?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      assessment?.focus({ preventScroll: true });
                    }}
                  />
                ) : null}

                <Card className={`shadow-sm ${
                  cannotAssessForms ? 'border-red-200'
                  : !interviewExists ? 'border-amber-200'
                  : 'border-[#E4E8EB]'
                }`} id="interview-assessment" tabIndex={-1} data-testid="section-forms-interview">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <ClipboardList className="h-5 w-5 text-primary" />
                          Interview assessment
                        </CardTitle>
                        <p className="text-xs text-text-muted mt-1">
                          Score the interview after the call. Scheduling is handled in the panel above.
                        </p>
                      </div>
                      {cannotAssessForms ? (
                        <Badge className="bg-red-100 text-red-700">Cannot assess</Badge>
                      ) : !interviewExists ? (
                        <Badge className="bg-gray-100 text-gray-600">Not recorded</Badge>
                      ) : (
                        <Badge className={
                          interviewPassed === true || ['Approve', 'Hire', 'Strong Hire'].includes(interviewDecision)
                            ? 'bg-green-100 text-green-700'
                            : interviewPassed === false || ['Reject', 'Not Suitable'].includes(interviewDecision)
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                        }>
                          {interviewDecision || (interviewPassed === true ? 'Passed' : interviewPassed === false ? 'Failed' : 'Draft / pending')}
                          {interviewScore != null ? ` · ${interviewScore}/${interviewSubmission?.form_data?.max_score || 24}` : ''}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {cannotAssessForms ? (
                      <div className="text-center py-6 text-red-700">
                        <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-red-500" />
                        <p className="text-sm font-medium">Cannot assess interview record</p>
                        <p className="text-xs text-red-600 mt-1">Form submissions unavailable. Review and sign-off actions are disabled.</p>
                      </div>
                    ) : (
                      <InterviewFormPanel
                        employeeId={employeeId}
                        employeeName={`${employee?.first_name} ${employee?.last_name}`}
                        employee={employee}
                        employeeRole={employee?.role || employee?.job_title || 'Healthcare Assistant'}
                        employeeJobTitle={employee?.role || employee?.job_title || ''}
                        onComplete={() => {
                          fetchCompliance();
                          fetchFormSubmissions();
                          fetchApplicantTabReadiness();
                          setInterviewSchedulingRefreshKey((k) => k + 1);
                        }}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* â”€â”€ Worker Onboarding Forms â”€â”€ */}
                <details className="rounded-xl border border-slate-200 bg-white" open={!allSignedOff}
                  data-testid="worker-onboarding-forms-disclosure">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-800">
                    {allSignedOff ? `${signedOffCount} signed-off forms · View records` : `Worker forms · ${signedOffCount}/${totalForms} signed off`}
                  </summary>
                <Card className="border-0 shadow-none" data-testid="section-forms-core">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Worker Onboarding Forms</CardTitle>
                    <p className="text-xs text-text-muted">
                      Forms submitted by the worker for onboarding and admin review. Sensitive records are marked.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {cannotAssessForms ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
                        <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-red-500" />
                        <p className="font-medium">Cannot assess forms</p>
                        <p className="text-sm text-red-600 mt-1">
                          Submission data unavailable. All review and sign-off actions are disabled until data loads.
                        </p>
                        <Button size="sm" variant="outline" onClick={fetchFormSubmissions}
                          className="mt-3 text-red-600 border-red-200 hover:bg-red-50">
                          <RefreshCw className="h-3 w-3 mr-1" /> Retry
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {FORM_GROUPS.map(group => (
                          <div key={group.label}>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
                              {group.label}
                              {group.sensitive && (
                                <span className="ml-2 text-slate-400 normal-case font-normal">— sensitive recruitment records</span>
                              )}
                            </p>
                            <div className="space-y-2">
                              {group.forms.map((form) => {
                                const derived = formStatuses.find((f) => f.key === form.key) || { status: 'missing', submission: null };
                                const cfg = STATUS_DISPLAY[derived.status] || STATUS_DISPLAY.missing;
                                const IconEl = cfg.icon;
                                const hasSubmission = Boolean(derived.submission);
                                const submission = derived.submission;

                                const submittedAt = fmtDate(submission?.submitted_at);
                                const reviewedAt = fmtDate(submission?.reviewed_at || submission?.verified_at || submission?.signed_off_at);
                                const reviewedBy = submission?.reviewed_by_name || submission?.signed_off_by_name || submission?.verified_by_name;

                                return (
                                  <div
                                    key={form.key}
                                    className={`p-4 rounded-lg border ${cfg.cardBorder}`}
                                    data-testid={`form-card-${form.key}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex items-start gap-3 min-w-0">
                                        <IconEl className={`h-5 w-5 mt-0.5 shrink-0 ${cfg.iconColor}`} />
                                        <div className="min-w-0">
                                          <p className="font-medium text-gray-800 text-sm">{form.name}</p>
                                          <p className="text-xs text-gray-500">{form.description}</p>
                                          {form.sensitive && (
                                            <Badge variant="outline" className="mt-0.5 text-xs bg-slate-50 text-slate-500 border-slate-200">
                                              Sensitive
                                            </Badge>
                                          )}
                                          {form.key === 'staff_health_questionnaire' && derived.status === 'not_yet_available' ? (
                                            <p className="mt-1 text-xs text-blue-900">
                                              This form is not available to the applicant yet. Complete and pass the interview,
                                              then record the provisional offer. CareDock will open the confidential questionnaire
                                              for the applicant to answer personally.
                                            </p>
                                          ) : form.key === 'staff_health_questionnaire' ? (
                                            <p className="mt-1 text-xs text-slate-500">
                                              Basic identity details can be pre-filled; health answers must come from the applicant.
                                            </p>
                                          ) : form.prefill ? (
                                            <p className="text-xs text-indigo-500 mt-0.5 italic">
                                              Auto-filled from worker's application
                                            </p>
                                          ) : null}
                                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                                            {submittedAt && (
                                              <span className="text-gray-400">
                                                Submitted: <span className="text-gray-600">{submittedAt}</span>
                                              </span>
                                            )}
                                            {reviewedAt ? (
                                              <span className="text-gray-400">
                                                Reviewed: <span className="text-gray-600">{reviewedAt}</span>
                                                {reviewedBy && <span className="text-gray-600"> by {reviewedBy}</span>}
                                              </span>
                                            ) : (derived.status === 'submitted' || derived.status === 'reviewed') ? (
                                              <span className="text-gray-400 italic">Not yet reviewed</span>
                                            ) : null}
                                            {derived.status === 'rejected' && submission?.rejection_reason && (
                                              <span className="text-red-600">
                                                Reason: {submission.rejection_reason}
                                              </span>
                                            )}
                                            {derived.status === 'returned_for_correction' && (
                                              <span className="text-red-600">
                                                Correction required: {submission?.correction_reason || submission?.review_reason || 'Worker correction requested'}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-2 shrink-0">
                                        <Badge className={cfg.color}>{cfg.label}</Badge>
                                        <div className="flex gap-1">
                                          {/* Signed off â†’ View */}
                                          {derived.status === 'signed_off' && hasSubmission && (
                                            <>
                                              <Button size="sm" variant="outline"
                                                onClick={() => openFormSubmissionPdfViewer(submission, form)}
                                                className="text-green-700 border-green-200 hover:bg-green-50"
                                                data-testid={`view-submission-${form.key}`}>
                                                <Eye className="h-3.5 w-3.5 mr-1" />View
                                              </Button>
                                              {!isAuditor() && (
                                                <Button size="sm" variant="outline"
                                                  onClick={() => {
                                                    setFormCorrectionDialog({ submission, form });
                                                    setFormCorrectionReason('');
                                                  }}
                                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                                  data-testid={`reopen-correction-${form.key}`}>
                                                  <RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen for correction
                                                </Button>
                                              )}
                                            </>
                                          )}

                                          {/* Reviewed â†’ View + Sign Off */}
                                          {derived.status === 'reviewed' && hasSubmission && (
                                            <>
                                              <Button size="sm" variant="outline"
                                                onClick={() => openFormSubmissionPdfViewer(submission, form)}
                                                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                                data-testid={`view-submission-${form.key}`}>
                                                <Eye className="h-3.5 w-3.5 mr-1" />View
                                              </Button>
                                              {!isAuditor() && (
                                                <>
                                                  <Button size="sm" variant="outline"
                                                    onClick={async () => {
                                                      if (form.key === 'staff_health_questionnaire') {
                                                        setFormReviewViewer({
                                                          isOpen: true,
                                                          formName: form.name,
                                                          submissionId: submission?.id,
                                                        });
                                                        return;
                                                      }
                                                      try {
                                                        await axios.post(
                                                          `${API}/form-submissions/${submission.id}/verify`, {},
                                                          { headers: { Authorization: `Bearer ${token}` } }
                                                        );
                                                        toast.success('Form signed off');
                                                        fetchFormSubmissions();
                                                      } catch { toast.error('Failed to sign off form'); }
                                                    }}
                                                    className="text-green-600 border-green-200 hover:bg-green-50"
                                                    data-testid={`sign-off-${form.key}`}>
                                                    <CheckCircle className="h-3.5 w-3.5 mr-1" />Sign Off
                                                  </Button>
                                                  <Button size="sm" variant="outline"
                                                    onClick={() => {
                                                      setFormCorrectionDialog({ submission, form });
                                                      setFormCorrectionReason('');
                                                    }}
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    data-testid={`reopen-correction-${form.key}`}>
                                                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen for correction
                                                  </Button>
                                                </>
                                              )}
                                            </>
                                          )}

                                          {/* Submitted â†’ Review & Approve (forced viewing) */}
                                          {derived.status === 'submitted' && hasSubmission && (
                                            <Button size="sm" variant="outline"
                                              onClick={() => setFormReviewViewer({
                                                isOpen: true,
                                                formName: form.name,
                                                submissionId: submission?.id,
                                              })}
                                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                              data-testid={`review-submission-${form.key}`}>
                                              <Eye className="h-3.5 w-3.5 mr-1" />Review Submission
                                            </Button>
                                          )}

                                          {/* Missing / in-progress â†’ passive label */}
                                          {(derived.status === 'missing' || derived.status === 'in_progress') && (
                                            <Badge
                                              className={derived.status === 'in_progress' ? 'bg-amber-50 text-amber-700 text-xs' : 'bg-slate-100 text-slate-600 text-xs'}
                                              data-testid={`form-status-${form.key}`}
                                            >
                                              {derived.status === 'in_progress' ? 'Worker in progress' : 'Missing'}
                                            </Badge>
                                          )}

                                          {/* Rejected â†’ View */}
                                          {derived.status === 'rejected' && hasSubmission && (
                                            <Button size="sm" variant="outline"
                                              onClick={() => openFormSubmissionPdfViewer(submission, form)}
                                              className="text-red-600 border-red-200 hover:bg-red-50">
                                              <Eye className="h-3.5 w-3.5 mr-1" />View
                                            </Button>
                                          )}

                                          {/* Returned for correction â†’ View only until worker resubmits */}
                                          {derived.status === 'returned_for_correction' && hasSubmission && (
                                            <Button size="sm" variant="outline"
                                              onClick={() => openFormSubmissionPdfViewer(submission, form)}
                                              className="text-red-600 border-red-200 hover:bg-red-50">
                                              <Eye className="h-3.5 w-3.5 mr-1" />View
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                </details>
              </div>
            );
          })()}
          
          {/* Form Submission View Dialog moved to component root (see below) so it
              is mounted regardless of the active tab. */}

          {/* Reopen signed-off/reviewed form for worker correction */}
          <Dialog open={!!formCorrectionDialog} onOpenChange={(open) => {
            if (!open) {
              setFormCorrectionDialog(null);
              setFormCorrectionReason('');
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Reopen for correction</DialogTitle>
                <DialogDescription>
                  Return {formCorrectionDialog?.form?.name || 'this form'} to the worker for correction. The previous signed-off version is kept in the audit history.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="form-correction-reason">Correction reason</Label>
                <Textarea
                  id="form-correction-reason"
                  value={formCorrectionReason}
                  onChange={(event) => setFormCorrectionReason(event.target.value)}
                  placeholder="Explain exactly what the worker needs to correct."
                  rows={4}
                  disabled={isReopeningFormCorrection}
                />
                <p className="text-xs text-text-muted">
                  The worker will be able to edit and resubmit this form. Admin sign-off will be required again.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFormCorrectionDialog(null);
                    setFormCorrectionReason('');
                  }}
                  disabled={isReopeningFormCorrection}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReopenFormForCorrection}
                  disabled={isReopeningFormCorrection || !formCorrectionReason.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isReopeningFormCorrection ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                  Reopen for correction
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Form Review Viewer (PDF viewer with forced viewing + approve/reject) */}
          <EvidenceReviewViewerDialog
            isOpen={!!formReviewViewer?.isOpen}
            onClose={() => { setFormReviewViewer(null); fetchFormSubmissions(); }}
            mode="form-review"
            formSubmissionId={formReviewViewer?.submissionId}
            formName={formReviewViewer?.formName}
            employeeId={employee?.id}
            employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
            onFormApproved={() => fetchFormSubmissions()}
            onFormRejected={() => fetchFormSubmissions()}
          />
          {profileMode === 'applicant' && (
              <ApplicantRecruitmentTabFooter
                currentTab="forms"
                employeeStatus={employee?.status}
                onContinue={handleTabChange}
                canContinue={formsTabReadiness?.can_continue}
                blockers={formsTabReadiness?.blockers}
                signoff={recruitmentTabSignoffs.forms}
                signoffRequired
                onSignOff={handleSignOffRecruitmentTab}
                signingOff={recruitmentTabSigningOff === 'forms'}
                isAuditor={isAuditor()}
              />
          )}
        </TabsContent>

        {/* ========== TAB 6: EMPLOYMENT ========== */}
        {/* Employment history + gap verification + declarations */}
        <TabsContent value="employment">
          <div className="space-y-6">
            {profileMode === 'applicant' && (
              <ApplicantTabReadinessBanner
                readiness={employmentTabReadiness}
                completeMessage="Recruitment Review tab complete — you can continue to References"
                incompleteMessage="Recruitment Review tab incomplete"
                systemCheckPending={employmentSystemOnlyPending}
              />
            )}

            {profileMode === 'applicant' && !employmentSystemOnlyPending && (
              <ApplicantNextActionPanel action={applicantNextAction} />
            )}

            <EmploymentReviewCockpit
              snapshot={snap}
              onOpenEvidence={openEmploymentReviewEvidence}
              onRetry={handleRetryEmploymentReview}
              retrying={employmentReviewRetryLoading}
              state={employmentDecisionState}
              signedOffBy={employmentSignedOffBy}
              signedOffAt={employmentSignedOffAt}
              items={employmentCockpitItems}
              nextAction={employmentCockpitNextAction}
              blockers={employmentCockpitBlockers}
              warnings={employmentStatusWarnings}
              canSignOff={employmentReadyForSignOff && !employmentSignedOff}
              onSignOff={handleSignOffEmploymentReview}
              signingOff={employmentSignOffLoading}
              isAuditor={isAuditor()}
              attentionCount={employmentAttentionCount}
              systemCheckPending={employmentSystemOnlyPending}
            />
            {!isAuditor() && String(snapIntegrity?.state || '').toLowerCase() === 'error' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950"
                data-testid="admin-employment-integrity-recheck">
                <p className="font-semibold">CareDock still needs to finish comparing the applicant's CV and official history.</p>
                <p className="mt-1 text-xs text-blue-900">
                  This is a system check, not an applicant confirmation. The signed-off CV and declarations are preserved.
                </p>
                <Button type="button" size="sm" variant="outline" className="mt-2"
                  disabled={employmentIntegrityRecheckLoading}
                  onClick={handleAdminRecheckEmploymentIntegrity}>
                  {employmentIntegrityRecheckLoading
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <RefreshCw className="mr-2 h-4 w-4" />}
                  Recheck current history
                </Button>
              </div>
            )}
            {!isAuditor() && snapIntegrity?.state === 'confirmation_required' && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
                data-testid="admin-employment-confirmation-needed">
                <strong>Waiting for the applicant to confirm their work history.</strong>
                <p className="mt-1 text-xs">
                  Send a private, single-use access link. It opens the applicant's work-history confirmation.
                  They review their own jobs and dates and press Confirm. No admin can confirm for them.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={Boolean(employmentConfirmationSending)}
                    data-testid="send-employment-confirmation-whatsapp"
                    onClick={() => handleEmploymentConfirmationReminder('whatsapp')}>
                    {employmentConfirmationSending === 'whatsapp'
                      ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      : <MessageCircle className="mr-1.5 h-4 w-4" />}
                    Open WhatsApp with link
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    disabled={Boolean(employmentConfirmationSending)}
                    data-testid="send-employment-confirmation-email"
                    onClick={() => handleEmploymentConfirmationReminder('email')}>
                    {employmentConfirmationSending === 'email'
                      ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      : <Mail className="mr-1.5 h-4 w-4" />}
                    Email confirmation link
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-amber-900">
                  WhatsApp opens a ready-written message for you to review and send. Email sends immediately
                  when clicked. The secure link expires and must be used by the intended applicant.
                </p>
              </div>
            )}

            {snapIntegrity && !employmentSignedOff && (
              <Card
                className={
                  integritySourceState.mode !== 'assessable'
                    ? 'border-slate-200 bg-slate-50 shadow-sm'
                    : integrityState === 'awaiting_admin_review'
                    ? 'border-blue-200 bg-blue-50 shadow-sm'
                    : integrityState === 'approved'
                      ? 'border-green-200 bg-green-50 shadow-sm'
                      : integrityState === 'action_required' || integrityState === 'confirmation_required'
                        ? 'border-amber-200 bg-amber-50 shadow-sm'
                        : 'border-slate-200 bg-slate-50 shadow-sm'
                }
                data-testid="employment-integrity-admin-card"
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">CV & work history check</CardTitle>
                      <p className="mt-1 text-sm text-slate-600">
                        CareOps compares the CV, the applicant&apos;s work history and any gaps. Staff only need to act on the clear next step shown below.
                      </p>
                    </div>
                    <Badge
                      className={
                        integritySourceState.mode !== 'assessable'
                          ? 'bg-slate-200 text-slate-700'
                          : integrityState === 'awaiting_admin_review'
                          ? 'bg-blue-100 text-blue-800'
                          : integrityState === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : integrityState === 'checking'
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-amber-100 text-amber-800'
                      }
                    >
                      {integritySourceState.mode !== 'assessable'
                        ? 'Not yet assessed'
                        : integrityState === 'awaiting_admin_review'
                        ? 'Ready for Osabea'
                        : integrityState === 'checking'
                          ? 'Needs refresh'
                          : integrityState === 'approved' && integritySourceState.mode === 'assessable'
                            ? 'Approved'
                            : 'Action needed'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {integritySourceState.mode !== 'assessable' ? (
                    <EmploymentComparisonPendingNotice status={integritySourceState} />
                  ) : integrityState === 'checking' ? (
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-900">Work history comparison needs refreshing</p>
                      <p className="mt-1 text-sm text-slate-600">
                        {employmentSystemOnlyPending
                          ? 'The application, CV, work history, verified gaps and declarations are saved. The stored comparison is not current. Select Recheck system comparison before sign-off; this is not missing applicant evidence.'
                          : 'This comparison is not current. Use Recheck system comparison to run it again. Employment Review remains blocked until the result is current.'}
                      </p>
                      {!isAuditor() && (
                        <Button type="button" size="sm" variant="outline" className="mt-3"
                          disabled={employmentIntegrityRecheckLoading}
                          onClick={handleAdminRecheckEmploymentIntegrity}
                          data-testid="recheck-employment-comparison">
                          {employmentIntegrityRecheckLoading ? 'Rechecking…' : 'Recheck system comparison'}
                        </Button>
                      )}
                    </div>
                  ) : integrityUnresolvedDifferences.length > 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-white p-4">
                      <p className="text-sm font-semibold text-amber-900">
                        Applicant action needed: {integrityUnresolvedDifferences.length} CV/work-history difference{integrityUnresolvedDifferences.length === 1 ? '' : 's'}
                      </p>
                      <p className="mt-1 text-sm text-amber-800">
                        The applicant should confirm or correct these items in CareOps. Osabea does not need to guess or rewrite their history.
                      </p>
                      <ul className="mt-3 space-y-1 text-sm text-slate-700">
                        {integrityUnresolvedDifferences.slice(0, 3).map((item) => (
                          <li key={item.id || item.description || item.type} className="flex gap-2">
                            <span aria-hidden="true">•</span>
                            <span>{item.description || String(item.type || 'CV/work-history difference').replace(/_/g, ' ')}</span>
                          </li>
                        ))}
                      </ul>
                      {integrityUnresolvedDifferences.length > 3 ? (
                        <p className="mt-2 text-xs text-slate-500">
                          +{integrityUnresolvedDifferences.length - 3} more item{integrityUnresolvedDifferences.length - 3 === 1 ? '' : 's'}
                        </p>
                      ) : null}
                    </div>
                  ) : integrityApplicantGapActions > 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-white p-4">
                      <p className="text-sm font-semibold text-amber-900">
                        Applicant needs to explain {integrityApplicantGapActions} employment gap{integrityApplicantGapActions === 1 ? '' : 's'}
                      </p>
                      <p className="mt-1 text-sm text-amber-800">
                        The applicant completes this in CareOps. Staff can review it afterwards.
                      </p>
                    </div>
                  ) : !snapIntegrity.confirmation_current && integrityState === 'confirmation_required' ? (
                    <div className="rounded-lg border border-amber-200 bg-white p-4">
                      <p className="text-sm font-semibold text-amber-900">Applicant confirmation needed</p>
                      <p className="mt-1 text-sm text-amber-800">
                        The checks are settled. The applicant now needs to confirm that the current work history is complete and accurate.
                      </p>
                    </div>
                  ) : integrityAdminGapReviews > 0 || integrityState === 'awaiting_admin_review' ? (
                    <div className="rounded-lg border border-blue-200 bg-white p-4">
                      <p className="text-sm font-semibold text-blue-900">Ready for Osabea review</p>
                      <p className="mt-1 text-sm text-blue-800">
                        Applicant-side checks are complete. Review any submitted explanations and finish the remaining Employment Review checks.
                      </p>
                    </div>
                  ) : integrityState === 'approved' ? (
                    <div className="rounded-lg border border-green-200 bg-white p-4">
                      <p className="text-sm font-semibold text-green-900">Employment Review approved</p>
                      <p className="mt-1 text-sm text-green-800">
                        Employment Review has been signed off for the current records. Recruitment approval is a separate decision.
                      </p>
                    </div>
                  ) : (
                    <EmploymentComparisonPendingNotice />
                  )}

                  <details className="rounded-lg border border-slate-200 bg-white">
                    <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700">
                      Show check details
                    </summary>
                    <div className="border-t border-slate-100 p-4">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-lg border bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Work history</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">
                            {integritySourceState.mode !== 'assessable' || integrityState === 'checking'
                              ? (integrityState === 'checking' ? 'Comparison needs refresh' : 'Not yet assessed')
                              : snapIntegrity.invalid_entry_count > 0
                              ? snapIntegrity.invalid_entry_count + ' row(s) need correction'
                              : 'Structured history checked'}
                          </p>
                        </div>
                        <div className="rounded-lg border bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Gaps</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">
                            {integritySourceState.mode !== 'assessable' || integrityState === 'checking'
                              ? (integrityState === 'checking' ? 'Comparison needs refresh' : 'Not yet assessed')
                              : integrityApplicantGapActions > 0
                              ? integrityApplicantGapActions + ' with applicant'
                              : integrityAdminGapReviews > 0
                                ? integrityAdminGapReviews + ' ready for Osabea'
                                : 'No applicant gap action'}
                          </p>
                        </div>
                        <div className="rounded-lg border bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500">CV differences</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">
                            {integritySourceState.mode !== 'assessable' || integrityState === 'checking'
                              ? (integrityState === 'checking' ? 'Comparison needs refresh' : 'Not yet assessed')
                              : integrityUnresolvedDifferences.length > 0
                              ? integrityUnresolvedDifferences.length + ' unresolved'
                              : integrityResolvedDifferences.length > 0
                                ? integrityResolvedDifferences.length + ' resolved'
                                : 'None outstanding'}
                          </p>
                        </div>
                        <div className="rounded-lg border bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Applicant confirmation</p>
                          <p className="mt-1 text-sm font-medium text-slate-900">
                            {snapIntegrity.confirmation_current ? 'Confirmed' : 'Not yet confirmed'}
                          </p>
                        </div>
                      </div>

                      {integrityResolvedDifferences.length > 0 ? (
                        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
                          <p className="text-sm font-medium text-slate-900">Resolved CV/work-history differences</p>
                          <div className="mt-2 space-y-2">
                            {integrityResolvedDifferences.map((item) => (
                              <div key={item.id || item.description || item.type} className="text-sm text-slate-700">
                                <p className="font-medium">{item.description || item.type}</p>
                                {item.resolution?.explanation ? (
                                  <p className="mt-1 text-slate-600">{item.resolution.explanation}</p>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </details>
                </CardContent>
              </Card>
            )}

            {employmentSignedOff && employmentCoverage && coverageAssessed ? (
              <p className="text-xs text-green-700 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                10-year coverage {coverageDisplayPercent}% — signed off. Open gap records below only if you need to amend a decision.
              </p>
            ) : null}
            {/* 10-Year Coverage Card */}
            {!employmentSignedOff && employmentCoverage && coverageAssessed && (
              <Card className={`shadow-sm ${coverageMet ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    {coverageMet ? (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 space-y-2">
                      <p className={`text-sm font-medium ${coverageMet ? 'text-green-800' : 'text-amber-800'}`}>
                        10-year history accounted for: {coverageDisplayPercent}%
                      </p>
                      <p className="text-xs text-slate-600">
                        Review period: {formatEmploymentBoundary(employmentCoverage.coverage_start, { fallback: 'Start unavailable' })} — {formatEmploymentBoundary(employmentCoverage.coverage_end, { fallback: 'Current review date' })}
                        {' · '}Status: {coverageMet ? 'Period accounted for' : 'Period not fully accounted for'}
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${coverageMet ? 'bg-green-500' : coverageDisplayPercent >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(coverageDisplayPercent, 100)}%` }}
                        />
                      </div>
                      {coverageInformationalOnly && coverageInfoMessage && (
                        <p className="text-xs text-slate-500">{coverageInfoMessage}</p>
                      )}
                      <p className="text-xs text-slate-600">
                        The percentage includes documented work and reviewed explanations of employment gaps. It is not the percentage of time employed and does not mean recruitment is approved.
                      </p>
                      <p className="text-xs text-slate-500">
Recorded employment only: {Number.isFinite(directCoveragePercent) ? `${directCoveragePercent}%` : 'Unavailable'}
                      </p>
                      {Number.isFinite(coverageTotalDaysExplained) && coverageTotalDaysExplained > 0 && (
                        <p className="text-xs text-slate-500">
                          Verified gap explanations accounted for {coverageTotalDaysExplained} days
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        Required review window: {employmentCoverage.total_days_required} days
                        {employmentCoverage.earliest_entry_date && ` · Earliest entry: ${new Date(employmentCoverage.earliest_entry_date + 'T00:00:00Z').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {!employmentSignedOff && employmentCoverage && !coverageAssessed && (
              <Card className="border-red-200 bg-red-50 shadow-sm">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Unable to confirm 10-year coverage</p>
                      <p className="mt-1 text-xs text-red-700">
                        {coverageLooksStaleOrUnusable
                          ? 'Dated employment history is present, but the stored coverage summary did not count any dated coverage.'
                          : 'The stored coverage summary is missing usable coverage totals.'}
                        {' '}Re-run gap analysis or save the employment history again before sign-off.
                      </p>
                      <p className="mt-1 text-xs text-red-700">
                        Applicant-submitted explanations are visible below as supporting notes but do not count as dated employment coverage.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {!employmentSignedOff && !employmentCoverage && employmentHistoryExists && (
              <Card className="border-red-200 bg-red-50 shadow-sm">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Unable to confirm 10-year coverage</p>
                      <p className="mt-1 text-xs text-red-700">
                        Employment history exists, but no usable coverage summary is available yet. Re-run gap analysis to generate coverage data.
                      </p>
                      <p className="mt-1 text-xs text-red-700">
                        Applicant-submitted explanations are supporting notes only and do not count as dated employment coverage.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <details className="rounded-xl border border-slate-200 bg-white"
              open={!employmentCoreEvidenceOnFile}
              data-testid="employment-evidence-disclosure">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-800">
                Application, CV &amp; declarations · {employmentCoreEvidenceOnFile ? 'On file — view details' : 'Open for review'}
              </summary>
              <Card className="border-0 shadow-none" data-testid="section-employment-evidence">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Application & CV Evidence</CardTitle>
                <p className="text-xs text-text-muted">
                  Source evidence available on this page for employment-history review.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-[#E4E8EB] bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-medium text-gray-800">Application Record</h4>
                        <p className="mt-1 text-xs text-text-muted">Application submission and PDF export where already available.</p>
                      </div>
                      <Badge variant="outline" className={applicationAvailable ? 'bg-green-100 text-green-700 border-green-200' : (snapApp && ['recoverable', 'possible_historical', 'confirmation_required'].includes(snapApp.state) ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200')}>
                        {snapApp ? snapApp.label : (applicationAvailable ? 'Evidence on file' : 'Missing')}
                      </Badge>
                    </div>

                    {applicationAvailable ? (
                      <div className="mt-4 space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs text-text-muted">Submitted</p>
                            <p className="text-sm font-medium text-gray-800">
                              {applicationSubmission?.submitted_at || applicationSubmission?.updated_at
                                ? formatBackendDateTime(applicationSubmission?.submitted_at || applicationSubmission?.created_at || applicationSubmission?.updated_at)
                                : 'Date unavailable'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Source</p>
                            <p className="text-sm font-medium text-gray-800">
                              {applicationSubmission
                                ? 'Online application'
                                : applicationPdfDocument
                                  ? 'Uploaded PDF'
                                  : applicationEvidenceRow
                                    ? 'Compliance record'
                                    : applicationEmbeddedRecord
                                      ? 'Employee record'
                                      : 'Unavailable'}
                            </p>
                          </div>
                        </div>

                        {snapApp ? (
                          snapApp.view_available ? (
                            // View Application always opens the canonical STRUCTURED
                            // application (never PDF-dependent). The PDF evidence is
                            // offered separately so a broken PDF can never block the
                            // structured view.
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  if (applicationSubmission?.id) {
                                    setViewFormSubmission({
                                      isOpen: true,
                                      formType: 'application_form',
                                      formName: 'Application Form',
                                      submissionId: applicationSubmission.id,
                                      data: pickSubmissionData(applicationSubmission),
                                    });
                                  } else {
                                    handleViewApplication();
                                  }
                                }}
                                data-testid="view-application-btn"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Application
                              </Button>
                              {applicationSubmission?.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewFormPDF(applicationSubmission.id)}
                                    data-testid="view-application-pdf-btn"
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    View PDF
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownloadFormPDF(applicationSubmission.id)}
                                    data-testid="download-application-pdf-btn"
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    Download PDF
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          ) : applicationSubmission?.id ? (
                            // CS-R2 PART 4: the canonical application is on file with
                            // structured content but no PDF evidence artifact is
                            // attached. Reuse the existing application PDF generator
                            // (view-pdf / download-pdf generate + store on demand) and
                            // the structured viewer — do NOT dead-end the admin.
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setViewFormSubmission({
                                  isOpen: true,
                                  formType: 'application_form',
                                  formName: 'Application Form',
                                  submissionId: applicationSubmission.id,
                                  data: pickSubmissionData(applicationSubmission),
                                })}
                                data-testid="view-application-btn"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Application
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewFormPDF(applicationSubmission.id)}
                                data-testid="view-application-pdf-btn"
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadFormPDF(applicationSubmission.id)}
                                data-testid="download-application-pdf-btn"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download PDF
                              </Button>
                              <p className="w-full text-[11px] text-text-muted">
                                The PDF is generated from the submitted application on first view. The structured form remains the source of truth.
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-text-muted">
                              {snapApp.state === 'on_file' || snapApp.state === 'recovered'
                                ? 'This application is on file but no PDF evidence is attached and no structured submission is available to generate one. Detailed content remains in Forms & Interview.'
                                : 'No viewable application document is attached yet.'}
                            </p>
                          )
                        ) : applicationSubmission?.id ? (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewFormSubmission({
                                isOpen: true,
                                formType: 'application_form',
                                formName: 'Application Form',
                                submissionId: applicationSubmission.id,
                                data: pickSubmissionData(applicationSubmission),
                              })}
                              data-testid="view-application-btn"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Application
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewFormPDF(applicationSubmission.id)}
                              data-testid="view-application-pdf-btn"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadFormPDF(applicationSubmission.id)}
                              data-testid="download-application-pdf-btn"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download PDF
                            </Button>
                          </div>
                        ) : applicationEvidenceRow?.file_url || applicationEvidenceRow?.document_url || applicationEvidenceRow?.download_url || applicationEvidenceRow?.view_url ? (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const rawUrl = applicationEvidenceRow?.file_url || applicationEvidenceRow?.view_url || applicationEvidenceRow?.download_url || applicationEvidenceRow?.document_url;
                                if (!rawUrl) return;
                                const resolvedUrl = rawUrl.startsWith('/api/') ? `${API}${rawUrl.substring(4)}` : rawUrl;
                                setInlineViewerUrl(resolvedUrl);
                                setInlineViewerTitle('Application Record');
                                setInlineViewerFilename('application_form.pdf');
                                setInlineViewerFallback(null);
                                setInlineViewerOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Application
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const rawUrl = applicationEvidenceRow?.download_url || applicationEvidenceRow?.file_url || applicationEvidenceRow?.document_url || applicationEvidenceRow?.view_url;
                                if (!rawUrl) return;
                                const resolvedUrl = rawUrl.startsWith('/api/') ? `${API}${rawUrl.substring(4)}` : rawUrl;
                                window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download PDF
                            </Button>
                          </div>
                        ) : (
                          <p className="text-xs text-text-muted">
                            Detailed viewing remains available from Forms & Interview when a structured application submission is stored.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white p-4">
                        <p className="text-sm font-medium text-gray-700">
                          {snapApp ? snapApp.label : 'No application form on file'}
                        </p>
                        <p className="mt-1 text-xs text-text-muted">
                          {snapApp?.reason
                            || 'This profile does not currently have a structured application record or uploaded application PDF.'}
                        </p>
                        {!isAuditor() && snapApp?.admin_action === 'recover_application' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-3 text-blue-700 border-blue-200 hover:bg-blue-50"
                            onClick={handleRecoverApplication}
                            data-testid="recover-application-btn"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Use saved application
                          </Button>
                        ) : null}
                        {!isAuditor() && snapApp?.admin_action === 'review_possible_historical_application' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-3 text-amber-700 border-amber-200 hover:bg-amber-50"
                            onClick={handleOpenApplicationCandidates}
                            data-testid="review-possible-historical-btn"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review possible match
                          </Button>
                        ) : null}
                      </div>
                    )}

                    <div className="mt-4 border-t pt-4" data-testid="section-employment-declarations">
                      <h4 className="font-medium text-gray-800 mb-1">Applicant Declarations</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Applicant confirmations are reviewed here. If they are missing, the applicant needs to complete them first.
                      </p>
                      <div className="p-4 rounded-lg border bg-gray-50 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-800">Declarations and consent</p>
                          <p className="text-xs text-gray-600 mt-1">
                            Includes criminal/health declarations, DBS consent, right-to-work restrictions, and professional declarations.
                          </p>
                          <Badge
                            variant="outline"
                            className={`mt-2 ${
                              declarationDisplayState.tone === 'success'
                                ? 'bg-green-100 text-green-700 border-green-200'
                                : declarationDisplayState.tone === 'warning'
                                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                                  : declarationDisplayState.tone === 'danger'
                                    ? 'bg-red-100 text-red-700 border-red-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-200'
                            }`}
                          >
                            {declarationDisplayState.label}
                          </Badge>
                          {declarationDisplayState.submittedAt && (
                            <p className="mt-1 text-xs text-gray-500">
                              Submitted {formatBackendDateTime(declarationDisplayState.submittedAt)}
                            </p>
                          )}
                          {declarationDisplayState.missingFields?.length > 0 && (
                            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                              <p className="text-xs font-medium text-red-800">
                                Missing from declaration submission:
                              </p>
                              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-red-700">
                                {declarationDisplayState.missingFields.map((item) => (
                                  <li key={item.key}>{item.label}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {declarationsAdequatelyReviewed && (
                            <p className="text-xs text-green-700 mt-2">
                              Reviewed
                              {(employee?.declarations_reviewed_by_name || employee?.declarations_reviewed_by) && (
                                <> by {employee.declarations_reviewed_by_name || employee.declarations_reviewed_by}</>
                              )}
                              {employee?.declarations_reviewed_at && (
                                <> · {new Date(employee.declarations_reviewed_at).toLocaleDateString('en-GB')}</>
                              )}
                            </p>
                          )}
                        </div>
                        {!isAuditor() && declarationsReadyForReview && (
                          <Button
                            size="sm"
                            variant={declarationsAdequatelyReviewed ? 'outline' : 'default'}
                            className={declarationsAdequatelyReviewed ? 'border-green-200 text-green-800 hover:bg-green-50' : ''}
                            onClick={() => setReviewDeclarationsOpen(true)}
                            data-testid={declarationsAdequatelyReviewed ? 'open-declarations-view' : 'open-declarations-verify'}
                          >
                            {declarationsAdequatelyReviewed ? (
                              <>
                                <Eye className="h-4 w-4 mr-1" />
                                View declarations
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Review declarations
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      {declarationSummaryOnFile ? (
                        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                          {[
                            ['Criminal convictions', employee.declarations?.has_criminal_convictions],
                            ['Health conditions', employee.declarations?.has_health_conditions],
                            ['DBS consent', employee.declarations?.dbs_consent_given],
                            ['RTW restrictions', employee.declarations?.has_rtw_restrictions],
                            ['Driving licence', employee.declarations?.has_driving_licence],
                          ].map(([label, val]) => {
                            const consent = label === 'DBS consent'
                              ? getEnhancedDbsConsentDisplay(val)
                              : null;
                            return (
                              <div key={label} className="flex items-center justify-between py-0.5 border-b border-gray-100">
                                <span className="text-gray-500">{label}</span>
                                <span className={consent
                                  ? `${consent.className} font-medium`
                                  : val ? 'text-amber-700 font-medium' : 'text-green-700'}>
                                  {consent ? consent.label : val ? 'Declared' : 'None declared'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className={`mt-2 text-xs ${
                          declarationDisplayState.tone === 'danger'
                            ? 'text-red-700'
                            : declarationDisplayState.tone === 'warning'
                              ? 'text-amber-700'
                              : 'text-gray-600'
                        }`}>
                          {declarationDisplayState.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E4E8EB] bg-gray-50 p-4" data-testid="section-employment-cv">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-medium text-gray-800">CV / Resume</h4>
                        <p className="mt-1 text-xs text-text-muted">Supporting evidence for the official work history below.</p>
                      </div>
                      <Badge variant="outline" className={cvStatusBadgeClass}>
                        {cvStatusLabel}
                      </Badge>
                    </div>

                    {cvReviewReady ? (
                      <div className="mt-4 space-y-3">
                        {cvApproved && (
                          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-800">
                            <span className="font-medium">CV approved and locked</span>
                            <p className="mt-1">
                              This CV is accepted for recruitment. Review and replacement requests are disabled to prevent accidental changes.
                            </p>
                            {employee?.cv_approval_note ? (
                              <p className="mt-1 text-green-700">{employee.cv_approval_note}</p>
                            ) : null}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {cvViewAvailable !== false && (
                            cvInlinePreviewable ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleViewCv}
                                data-testid="view-cv-btn"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View CV
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleOpenCvDocument}
                                data-testid="open-cv-btn"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Open CV
                              </Button>
                            )
                          )}
                          {!isAuditor() && !cvApproved && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                onClick={handleReviewCv}
                                disabled={cvReviewLoading}
                                data-testid="employment-review-cv-btn"
                              >
                                {cvReviewLoading ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <FileSearch className="h-4 w-4 mr-1" />
                                )}
                                Review CV
                              </Button>
                            </>
                          )}
                        </div>
                        {(cvFileFormat && cvFileFormat !== 'pdf') && (
                          <p className="text-xs text-text-muted" data-testid="cv-format-hint">
                            {cvFileFormat === 'doc' ? 'Legacy Word (.doc)' : 'Word (.docx)'} CV on file
                            {cvInlinePreviewable
                              ? ' — a PDF preview is available for inline viewing.'
                              : ' — open/download to review. You can still approve or reject it.'}
                          </p>
                        )}
                        {cvReplacementPending && (employee?.cv_replacement_reason || employee?.cv_reupload_reason || employee?.cv_rejection_reason) && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                            <span className="font-medium">Outstanding request:</span>{' '}
                            {employee?.cv_replacement_reason || employee?.cv_reupload_reason || employee?.cv_rejection_reason}
                          </div>
                        )}
                        <CvEmploymentAlignmentPanel
                          alignment={employmentMismatch?.alignment || employee?.cv_employment_alignment}
                          employee={employee}
                          employmentMismatch={employmentMismatch}
                          onReviewCv={undefined}
                          onViewMismatch={() => setMismatchDialogOpen(true)}
                          onRequestReplacement={undefined}
                          reviewLoading={cvReviewLoading}
                          compact
                        />
                      </div>
                    ) : (snapCv?.state === 'replacement_required' || (snapCv?.state === 'repair_required' && snapCv?.admin_action === 'request_cv_replacement')) ? (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-medium text-amber-900">A usable CV is needed</p>
                        <p className="mt-1 text-xs text-amber-800">
                          The current CV cannot be used for review. Ask the applicant for a replacement; CareOps will keep the old record for audit.
                        </p>
                        {!isAuditor() && (
                          <Button
                            size="sm"
                            className="mt-3"
                            onClick={() => openCvRequestDialog('replacement')}
                            data-testid="employment-request-cv-replacement-btn"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Request replacement CV
                          </Button>
                        )}
                      </div>
                    ) : (cvCanViewFromFallback || cvLinkRecoveryAvailable || cvServerLinkRecoveryAvailable || cvLegacyNonPdfOnly || cvFileExists) ? (
                      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <p className="text-sm font-medium text-blue-900">CV is ready to check</p>
                        <p className="mt-1 text-xs text-blue-800">
                          CareOps will prepare the correct CV file automatically, then open the normal review. You do not need to link or recover files yourself.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {snapCv?.view_available && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleViewCv}
                              data-testid="view-cv-fallback-btn"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View CV
                            </Button>
                          )}
                          {!isAuditor() && (
                            <Button
                              size="sm"
                              onClick={handleReviewCvSimple}
                              disabled={cvReviewLoading}
                              data-testid="review-cv-simple-btn"
                            >
                              {cvReviewLoading ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <FileCheck className="h-4 w-4 mr-1" />
                              )}
                              Review CV
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white p-4">
                        <p className="text-sm font-medium text-gray-800">CV needed</p>
                        <p className="mt-1 text-xs text-text-muted">
                          No usable CV is currently available for this Employment Review.
                        </p>
                        {!isAuditor() && (
                          <Button
                            size="sm"
                            className="mt-3"
                            onClick={() => openCvRequestDialog('upload')}
                            data-testid="employment-request-cv-upload-btn"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Request CV
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
              </Card>
            </details>

            <Card className="border-[#E4E8EB] shadow-sm" data-testid="section-employment-cv">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Employment History & Gap Review</CardTitle>
                  <p className="text-xs text-text-muted">
                    Official work history used for 10-year coverage, gap checks and sign-off. The CV above is supporting evidence.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!isAuditor() && (employmentGapsCannotAssess || gapAnalysisFailed || employmentReviewError) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRebuildEmploymentReview}
                      disabled={employmentReviewRebuildLoading}
                      data-testid="rebuild-employment-review-btn"
                    >
                      {employmentReviewRebuildLoading
                        ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        : <RefreshCw className="h-4 w-4 mr-1" />}
                      Refresh checks
                    </Button>
                  )}
                  {!isAuditor() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditEmploymentOpen(true)}
                      data-testid="edit-employment-btn"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit work history
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
              
              {/* An invalid source row is a data correction, not a processing failure. */}
              {employmentGapsCannotAssess && (
                <div className={`mb-4 p-4 border rounded-lg flex items-start gap-3 ${employmentHistoryNeedsCorrection
                  ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}
                  data-testid="employment-gap-assessment-notice">
                  <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${employmentHistoryNeedsCorrection ? 'text-amber-700' : 'text-red-600'}`} />
                  <div className="flex-1">
                    <p className={`font-medium ${employmentHistoryNeedsCorrection ? 'text-amber-900' : 'text-red-800'}`}>
                      {employmentHistoryNeedsCorrection
                        ? `${invalidEmploymentEntries.length} work history ${invalidEmploymentEntries.length === 1 ? 'entry needs' : 'entries need'} correction`
                        : 'Unable to confirm gaps from the current history'}
                    </p>
                    <p className={`text-sm mt-1 ${employmentHistoryNeedsCorrection ? 'text-amber-800' : 'text-red-600'}`}>
                      {employmentHistoryNeedsCorrection
                        ? 'The gap calculation ran on valid employment rows only. The gaps below are provisional until the excluded record is corrected. You do not need to extract the CV again.'
                        : gapAnalysisFailed
                          ? 'The gap analysis could not finish. Try re-running the check.'
                          : 'Gap analysis has not run yet. Run it using the saved employment history.'}
                    </p>
                    {!employmentHistoryNeedsCorrection && gapAnalysisError ? (
                      <p className="mt-1 text-xs text-red-600">{gapAnalysisError}</p>
                    ) : null}
                    {!isAuditor() && employmentHistoryNeedsCorrection ? (
                      <Button size="sm" variant="outline" className="mt-3 border-amber-300 text-amber-900"
                        data-testid="fix-invalid-employment-history" onClick={() => setEditEmploymentOpen(true)}>
                        <Edit className="h-4 w-4 mr-1" /> Correct flagged record
                      </Button>
                    ) : null}
                    {!isAuditor() && !employmentHistoryNeedsCorrection && employmentHistoryExists ? (
                      <Button size="sm" variant="outline" className="mt-3 border-red-300 text-red-700"
                        disabled={gapRerunLoading}
                        onClick={async () => {
                          setGapRerunLoading(true);
                          try {
                            await axios.post(`${API}/employees/${employeeId}/detect-employment-gaps`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            await Promise.all([
                              fetchEmployee(), fetchCompliance(), fetchComplianceFile(),
                              fetchEmploymentReview({ rebuild: true })
                            ]);
                            toast.success('Gap analysis refreshed.');
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Gap analysis failed');
                          } finally {
                            setGapRerunLoading(false);
                          }
                        }}>
                        {gapRerunLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                        Re-run Gap Analysis
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}

              {employmentHistoryExists && employmentHistoryGapRow && !showEmploymentGapPanel && employmentGapEvaluation && gapAnalysisRun && coverageMet && (employmentCoverage?.coverage_percent ?? 0) > 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">{employmentHistoryGapRow.status_summary || 'No detected gaps — 10-year coverage met'}</p>
                    <p className="text-sm text-green-600 mt-1">Gap analysis has run and the dated employment history meets the required 10-year period. Admin sign-off is still required to complete the review.</p>
                  </div>
                </div>
              )}
              {employmentHistoryExists && employmentHistoryGapRow && !showEmploymentGapPanel && employmentGapEvaluation && gapAnalysisRun && coverageAssessed && !coverageMet && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">No gaps detected, but 10-year coverage is incomplete</p>
                    <p className="text-sm text-amber-600 mt-1">No gaps were detected in the dated employment history, but sign-off is blocked until the required 10-year coverage period is met.</p>
                  </div>
                </div>
              )}

              {/* Employment Gap Panel */}
              {showEmploymentGapPanel && (
                <div data-testid="section-employment-gaps">
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-800">10-Year Gap Verification</h4>
                    <p className="mt-1 text-xs text-text-muted">
                      {hasPersistedEmploymentReview
                        ? 'Gap statuses below come from the saved work history review for this applicant.'
                        : 'Gap statuses below come from the current employment-gap evaluation stored on the compliance file.'}
                    </p>
                  </div>
                  <EmploymentGapPanel
                    employeeId={employeeId}
                    employeeName={`${employee?.first_name} ${employee?.last_name}`}
                    initialData={{
                      has_gaps: true,
                      gaps: canonicalEmploymentGaps,
                      evaluation: employmentHistoryGapRow.gap_evaluation
                    }}
                    isAdmin={!isAuditor() && isAdmin()}
                    canVerifyGaps={!isAuditor() && isManagerOrAdmin() && !employmentHistoryNeedsCorrection}
                    onGapUpdate={() => {
                      fetchEmployee();
                      fetchCompliance();
                      fetchComplianceFile();
                      fetchEmploymentReview();
                    }}
                  />
                </div>
              )}
              
              {/* Matched Applicant Gap Explanations — linked to detected gaps */}
              {matchedApplicantGapExplanations.length > 0 && (
                <details className="mt-4 mb-4 rounded-lg border border-slate-200 bg-white"
                  data-testid="section-matched-gap-explanations">
                  <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-slate-700">
                    Applicant gap explanations ({matchedApplicantGapExplanations.length} matched) · Supporting notes
                  </summary>
                  <p className="px-3 pt-2 text-xs text-slate-500">
                    Applicant-submitted explanations linked to verified gaps. Review decisions are in the gap record above.
                  </p>
                  <div className="space-y-2">
                    {matchedApplicantGapExplanations.map((expl, idx) => {
                      const hasDateRange = Boolean((expl.gap_start || expl.start_date) && (expl.gap_end || expl.end_date));
                      const gapStart = expl.gap_start || expl.start_date;
                      const gapEnd = expl.gap_end || expl.end_date;
                      const durationDays = expl.duration_days || expl.gap_duration_days;
                      const durationMonths = expl.duration_months || expl.gap_duration_months;
                      const fmtDate = (d) => formatEmploymentReviewDate(d);
                      const explanationType = expl.reason_type || expl.type || expl.category || 'Applicant declaration';
                      return (
                        <div key={expl.gap_id || idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {hasDateRange ? (
                              <span className="text-xs font-medium text-green-800">
                                {fmtDate(gapStart)} — {fmtDate(gapEnd)}
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-green-800">
                                Undated applicant gap explanation
                              </span>
                            )}
                            {(durationDays || durationMonths) && (
                              <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-200">
                                {durationDays ? `${durationDays} days` : `${durationMonths} months`}
                              </Badge>
                            )}
                            {explanationType && (
                              <Badge variant="outline" className="text-[10px] bg-green-100 text-green-600 border-green-200 capitalize">
                                {explanationType.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-200">
                              Matched to {expl.matched_gap_id || 'detected gap'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700">{expl.explanation || 'No explanation text provided by applicant'}</p>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* Unmatched Supporting Notes — NOT linked to any detected gap */}
              {unmatchedApplicantGapExplanations.length > 0 && (
                <div className="mt-4 mb-4" data-testid="section-unmatched-supporting-notes">
                  <h4 className="font-medium text-slate-700 mb-2">Unmatched Supporting Notes</h4>
                  <p className="mb-3 text-xs text-slate-500">
                    These applicant-submitted notes did not match any detected employment gap. They are preserved as supporting context but do not count as verified gap explanations or dated coverage.
                  </p>
                  <div className="space-y-2">
                    {unmatchedApplicantGapExplanations.map((expl, idx) => {
                      const hasDateRange = Boolean((expl.gap_start || expl.start_date) && (expl.gap_end || expl.end_date));
                      const gapStart = expl.gap_start || expl.start_date;
                      const gapEnd = expl.gap_end || expl.end_date;
                      const durationDays = expl.duration_days || expl.gap_duration_days;
                      const durationMonths = expl.duration_months || expl.gap_duration_months;
                      const fmtDate = (d) => formatEmploymentReviewDate(d);
                      const explanationType = expl.reason_type || expl.type || expl.category || 'Applicant note';
                      return (
                        <div key={expl.gap_id || idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {hasDateRange ? (
                              <span className="text-xs font-medium text-slate-700">
                                {fmtDate(gapStart)} — {fmtDate(gapEnd)}
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-slate-700">
                                Undated applicant note
                              </span>
                            )}
                            {(durationDays || durationMonths) && (
                              <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">
                                {durationDays ? `${durationDays} days` : `${durationMonths} months`}
                              </Badge>
                            )}
                            {explanationType && (
                              <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200 capitalize">
                                {explanationType.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">
                              Not attached to detected gap
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{expl.explanation || 'No explanation text provided by applicant'}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            This note is preserved as applicant evidence only. It does not prove dated 10-year coverage.
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Employment Records */}
              {canonicalEmploymentRecords.length > 0 && (
                <details className="mt-6 rounded-lg border border-slate-200"
                  open={!employmentCoreEvidenceOnFile || employmentHistoryNeedsCorrection}
                  data-testid="section-employment-history">
                  <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-gray-800">
                    Employment records ({canonicalEmploymentRecords.length} saved roles) · View history
                  </summary>
                  <p className="px-3 pb-2 text-xs text-text-muted">
                    Only validated saved roles are shown and counted here. {employmentHistoryNeedsCorrection
                      ? 'An excluded saved row is listed below and needs correcting.' : ''}
                  </p>
                  <div className="space-y-3">
                    {canonicalEmploymentRecords.map((job, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-800">{job.employer || job.company || job.employer_name}</p>
                            <p className="text-sm text-gray-600">{job.job_title || job.position}</p>
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatBackendDate(job.start_date, { format: 'short' })} - {job.end_date ? formatBackendDate(job.end_date, { format: 'short' }) : 'Present'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Excluded rows are still present in the saved official source. */}
              {invalidEmploymentEntries.length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="section-employment-invalid-entries">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h4 className="font-medium text-red-800">Saved work history needing correction</h4>
                    {!isAuditor() ? (
                      <Button size="sm" variant="outline" data-testid="edit-invalid-employment-entries"
                        onClick={() => setEditEmploymentOpen(true)}>
                        <Edit className="h-4 w-4 mr-1" /> Correct work history
                      </Button>
                    ) : null}
                  </div>
                  <p className="text-xs text-red-700 mb-3">
                    These records are preserved but are excluded from the dated timeline.
                    Correct the dates or current-employment flags before final review.
                  </p>
                  <div className="space-y-2">
                    {invalidEmploymentEntries.map((entry, idx) => (
                      <div key={idx} className="rounded-md bg-white border border-red-200 p-3 text-xs text-red-800">
                        <p className="font-semibold">{entry?.employer || `Row ${Number.isFinite(entry?.index) ? entry.index + 1 : idx + 1}`}</p>
                        <p className="mt-1">{entry?.reason || 'Invalid dates or current-employment status'}</p>
                        <p className="mt-1 text-red-600">
                          Start: {entry?.start_date || 'Missing'} · End: {entry?.end_date || 'Not set'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!canonicalEmploymentRecords.length && !employmentHistoryGapRow?.has_gaps && employmentHistoryEmptyState && (
                <div className="text-center py-8 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{employmentHistoryEmptyState.title}</p>
                  <p className="mt-1 text-xs text-gray-500">{employmentHistoryEmptyState.detail}</p>
                </div>
              )}

              {/* Qualifications & Education — supporting context, shown last */}
              {(employee?.qualifications || employee?.education) && (
                <div className="mt-6 pt-6 border-t border-gray-200" data-testid="section-employment-qualifications">
                  <h4 className="font-medium text-gray-800 mb-3">Qualifications & Education</h4>
                  <div className="space-y-3">
                    {employee?.qualifications && (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-1">Qualifications</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{employee.qualifications}</p>
                      </div>
                    )}
                    {employee?.education && (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-1">Education</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{employee.education}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </CardContent>
            </Card>
            {profileMode === 'applicant' && (
              <ApplicantRecruitmentTabFooter
                currentTab="employment"
                employeeStatus={employee?.status}
                onContinue={handleTabChange}
                canContinue={employmentTabReadiness?.can_continue}
                blockers={employmentSystemOnlyPending ? [] : employmentTabReadiness?.blockers}
              />
            )}
          </div>
        </TabsContent>
        <TabsContent value="checklist">
          {/* The actual recruitment-check workflows and audited sign-off below
              own this stage status. No second top-of-tab warning banner. */}
          <Card className="border-[#E4E8EB] shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">
                    {profileMode === 'applicant' ? 'Recruitment Checks' : 'Compliance File'}
                  </CardTitle>
                  <p className="text-xs text-text-muted mt-1">
                    {profileMode === 'applicant'
                      ? 'Recruitment-check evidence and verification for this stage. Training and employment documents are handled in the following stages.'
                      : 'Operational workflow for compliance. Upload evidence, verify checks, manage agreements.'}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* ============================================== */}
              {/* COMPLIANCE FILE - Linear Workflow                */}
              {/* All actions live INSIDE each requirement card    */}
              {/* No global actions - see issue â†’ scroll â†’ fix     */}
              {/* ============================================== */}

              {/* Conditional Items - Keep minimal info about items not required */}
              {complianceRequirements?.conditional_not_required?.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Some items not required for this employee:</p>
                      <ul className="mt-1 space-y-0.5">
                        {complianceRequirements.conditional_not_required.map((item, idx) => (
                          <li key={idx} className="text-xs text-gray-600">
                            {item.name} — {item.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {!complianceRequirements ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Employment Gap Verification moved to Employment History tab only */}

                  {/* Employment pack — contract + handbook placeholder-free preparation */}
                  {profileMode !== 'applicant' && (
                    <div className="mb-6">
                      <ComplianceTabErrorBoundary>
                        <EmploymentPackPanel
                          employeeId={employeeId}
                          isAuditor={isAuditor()}
                          onRefresh={refreshChecksAfterMutation}
                        />
                      </ComplianceTabErrorBoundary>
                    </div>
                  )}

                  {/* ============================================ */}
                  {/* DUAL-ROW COMPLIANCE SECTION (Phase 4A) */}
                  {/* Separates evidence from employer checks */}
                  {/* ============================================ */}
                  <div className="mb-6">
                    
                    <ComplianceTabErrorBoundary>
                      <DualRowComplianceSection
                        surface={profileMode === 'applicant' ? 'recruitment_checks' : 'full'}
                        employeeId={employeeId}
                        employeeEmail={employee?.email}
                        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
                        employeeData={employee}
                        complianceFile={complianceFile}
                        readinessSummary={complianceReadinessSummary}
                        parentManagesFetch={true}
                        onUpload={(key) => {
                          if (key === 'cv') {
                            toast.info('CV must be uploaded by the applicant from their worker dashboard.');
                            return;
                          }
                          setSelectedRequirement(key);
                          setUploadDialogOpen(true);
                        }}
                        onReviewCv={!cvApproved && !isAuditor() ? handleReviewCv : undefined}
                        onRequestCv={handleRequestCvFromApplicant}
                        onRequestCvReplacement={!cvApproved && !isAuditor() ? handleRequestCvReplacement : undefined}
                        cvAlignment={employmentMismatch?.alignment || employee?.cv_employment_alignment}
                        onRequest={(key, title) => {
                          setRequestingRequirement({ id: key, name: title || key });
                          setRequestDocMessage('');
                          setRequestDocDialogOpen(true);
                        }}
                        onPreviewFile={handleCompliancePreviewFile}
                        onExtractReview={(docId) => {
                          setDocExtractionDocumentId(docId);
                          setDocExtractionReviewOpen(true);
                        }}
                        onRecordCheck={(checkType) => {
                          setRecordCheckType(checkType);
                          setRecordCheckDialogOpen(true);
                        }}
                        onReissueContract={(contract) => {
                          setCurrentContract(contract || null);
                          setSupersedeContractOpen(true);
                        }}
                        employeeData={employee}
                        isAuditor={isAuditor()}
                        onRefresh={refreshChecksAfterMutation}
                      />
                    </ComplianceTabErrorBoundary>
                  </div>

                  {/* TRAINING SUMMARY CARD - Phase 4A */}
                </div>
              )}
            </CardContent>
          </Card>
          {profileMode === 'applicant' && (
            <ApplicantRecruitmentTabFooter
              currentTab="checklist"
              employeeStatus={employee?.status}
              onContinue={handleTabChange}
              canContinue={checklistTabReadiness?.can_continue}
              blockers={checklistTabReadiness?.blockers}
              signoff={recruitmentTabSignoffs.checklist}
              signoffRequired
              onSignOff={handleSignOffRecruitmentTab}
              signingOff={recruitmentTabSigningOff === 'checklist'}
              isAuditor={isAuditor()}
            />
          )}
        </TabsContent>

        {/* Policies Tab - Extracted to PoliciesTabContent */}
        {profileMode === 'employee' && (
        <TabsContent value="policies">
          <PoliciesTabContent
            policies={policies}
            token={token}
            isAuditor={isAuditor()}
            isAdmin={isAdmin()}
            onRefresh={fetchData}
          />
        </TabsContent>
        )}

        {/* Training Tab */}
        <TabsContent value="training" ref={trainingSectionRef} data-testid="section-training-root">
          {profileMode === 'applicant' && (
            <div className="mb-6">
              <ApplicantTabReadinessBanner
                readiness={trainingTabReadiness}
                completeMessage="Mandatory training complete — ready for sign-off"
                incompleteMessage="Mandatory training incomplete"
              />
            </div>
          )}
          {/* Canonical Training & Audit Hub: the live employee-profile path must
              mount this surface directly. TrainingTabContent is not used here. */}
          <div className="mb-6" data-testid="employee-profile-training-hub">
            <TrainingAuditHub employeeId={employeeId} isAuditor={isAuditor()} key={employeeId}
              onEvidenceUpdated={() => {
                setTrainingEvidenceRefreshKey(value => value + 1);
                fetchTrainingEvaluation();
                fetchComplianceFile();
              }} />
          </div>
          {/* One work area at a time. The hub above remains visible; choosing
              Records never opens induction as a second enormous page.
              Query deep links to a training review open the records area. */}
          <TrainingAdminWorkspaceSwitcher
            key={employeeId}
            focusRecords={queueTrainingFocus || String(searchParams.get('requirement') || '').startsWith('training_')}
            records={(
              <>
                {queueTrainingFocus && (
                  <div role="status" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
                    data-testid="training-queue-evidence-hint">
                    Review the original source certificate before approval. If an old document
                    is missing or cannot be opened, request a replacement or investigate storage.
                    Do not approve unseen evidence.
                  </div>
                )}
                <AuditReadyTrainingMatrix
                  key={employeeId}
                  employeeId={employeeId}
                  employeeName={`${employee?.first_name} ${employee?.last_name}`}
                  role={employee?.role}
                  onUploadCertificate={() => {
                    setTrainingIntakeOpen(true);
                  }}
                  onViewCertificate={(recordId) => {
                    handleViewTrainingCertificate(recordId, 'training_certificate');
                  }}
                  onRefresh={() => {
                    fetchTrainingEvaluation();
                    fetchProposedTrainingItems();
                    fetchOnboardingSync();
                  }}
                />
              </>
            )}
            induction={(
              <div data-testid="section-training-induction">
                <InductionChecklistPanel
                  key={`induction-${employeeId}-${trainingEvidenceRefreshKey}`}
                  employeeId={employeeId}
                  employeeName={`${employee?.first_name} ${employee?.last_name}`}
                  isAuditor={isAuditor()}
                  onStatusChange={() => {
                    fetchTrainingEvaluation();
                    fetchComplianceFile();
                  }}
                />
              </div>
            )}
          />
          {profileMode === 'applicant' && (
            <ApplicantRecruitmentTabFooter
              currentTab="training"
              employeeStatus={employee?.status}
              onContinue={handleTabChange}
              canContinue={trainingTabReadiness?.can_continue}
              blockers={trainingTabReadiness?.blockers}
              signoff={
                recruitmentTabSignoffs.training?.signed_off && trainingTabReadiness?.can_continue === false
                  ? {
                      ...recruitmentTabSignoffs.training,
                      signed_off: false,
                      status: 'invalidated',
                      invalidation_reason: 'Training or Care Certificate readiness is incomplete',
                    }
                  : recruitmentTabSignoffs.training
              }
              signoffRequired
              onSignOff={handleSignOffRecruitmentTab}
              signingOff={recruitmentTabSigningOff === 'training'}
              isAuditor={isAuditor()}
            />
          )}
        </TabsContent>

        {/* Audit Log Tab - Extracted to AuditTabContent */}
        <TabsContent value="audit">
          <AuditTabContent employeeId={employeeId} />
        </TabsContent>

        {/* ========== TAB: COMPETENCIES ========== */}
        {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
        <TabsContent value="competencies" data-testid="section-competencies-root">
          <CompetencyAssessmentsPanel
            employeeId={employeeId}
            employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
            onRefresh={() => {
              fetchComplianceFile();
              fetchRecruitmentStatus();
            }}
          />
        </TabsContent>
        )}

        {/* ========== TAB: SPOT CHECKS (admin only — management QA records) ========== */}
        {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
          <TabsContent value="spot_checks" data-testid="section-spot-checks-root">
            <SpotChecksPanel
              employeeId={employeeId}
              employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
              onRefresh={() => {
                fetchComplianceFile();
                fetchRecruitmentStatus();
              }}
            />
          </TabsContent>
        )}

        {/* ========== TAB: SUPERVISIONS ========== */}
        {profileMode === 'employee' && lifecycleStage === 'active' && isAdmin() && (
          <TabsContent value="supervisions" data-testid="section-supervisions-root">
            <SupervisionsPanel
              employeeId={employeeId}
              employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
              employeeRole={employee?.role || employee?.job_title || ''}
            />
          </TabsContent>
        )}

        {profileMode === 'employee' && lifecycleStage === 'active' && (
          <TabsContent value="appraisals" data-testid="section-appraisals-root">
            <AppraisalsPanel
              employeeId={employeeId}
              employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
            />
          </TabsContent>
        )}

        {/* References Tab - Extracted to ReferencesTabContent */}
        <TabsContent value="references" data-testid="section-references-root">
          {profileMode === 'applicant' && (
            <div className="mb-6">
              <ApplicantTabReadinessBanner
                readiness={referencesTabReadiness}
                completeMessage="References tab complete — you can continue to Application & Interview"
                incompleteMessage="References tab incomplete"
              />
            </div>
          )}
          <ReferencesTabContent 
            key={`references-${employeeId}-${referencesTabRefreshKey}`}
            employeeId={employeeId}
            employee={employee}
            onRefresh={() => {
              fetchComplianceFile();
              fetchRecruitmentStatus();
              fetchApplicantTabReadiness();
              fetchOnboardingSync();
            }}
            onEditReference={(refNum, refData) => {
              setSelectedReferenceId(refNum);
              setSelectedReferenceData({
                ...refData,
                referee_name: refData.name,
                referee_email: refData.email,
                referee_phone: refData.phone,
                referee_organisation: refData.organisation,
                referee_position: refData.job_title || refData.position,
                referee_relationship: refData.relationship
              });
              setEditReferenceOpen(true);
            }}
          />
          {profileMode === 'applicant' && (
            <ApplicantRecruitmentTabFooter
              currentTab="references"
              employeeStatus={employee?.status}
              onContinue={handleTabChange}
              canContinue={referencesTabReadiness?.can_continue}
              blockers={referencesTabReadiness?.blockers}
              signoff={recruitmentTabSignoffs.references}
              signoffRequired
              onSignOff={handleSignOffRecruitmentTab}
              signingOff={recruitmentTabSigningOff === 'references'}
              isAuditor={isAuditor()}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <Edit className="h-5 w-5 text-teal-600" />
              Edit Employee Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">First Name *</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                  className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Last Name *</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                  className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Phone</Label>
              <Input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Role *</Label>
                <Select value={editForm.role} onValueChange={(value) => setEditForm({...editForm, role: value})}>
                  <SelectTrigger className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-teal-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {roles.map((role) => (
                      <SelectItem key={role} value={role} className="text-gray-900">{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({...editForm, status: value})}>
                  <SelectTrigger className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-teal-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {statuses.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-gray-900">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Onboarding Status</Label>
                <Select value={editForm.onboarding_status} onValueChange={(value) => setEditForm({...editForm, onboarding_status: value})}>
                  <SelectTrigger className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-teal-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {onboardingStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="text-gray-900">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Start Date</Label>
                <Input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm({...editForm, start_date: e.target.value})}
                  className="rounded-xl bg-gray-50 border-gray-300 text-gray-900 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Internal notes about this employee..."
                className="rounded-xl min-h-[80px] bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-600 focus:border-teal-600"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-xl border-gray-300 text-gray-700 hover:bg-gray-50">
              Cancel
            </Button>
            <Button onClick={handleSaveEmployee} disabled={isSaving} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <Archive className="h-5 w-5 text-amber-500" />
              Archive Employee
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Are you sure you want to archive <strong className="text-gray-900">{employee?.first_name} {employee?.last_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <p className="text-sm text-gray-600">This will:</p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Hide employee from the active employees list</li>
              <li>Retain all documents, forms, and audit history</li>
              <li>Allow restoration at any time</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)} className="rounded-xl border-gray-300 text-gray-700 hover:bg-gray-50">
              Cancel
            </Button>
            <Button onClick={handleArchiveEmployee} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
              <Archive className="h-4 w-4 mr-2" />
              Archive Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Permanent Deletion
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Are you sure you want to <strong className="text-gray-900">permanently delete</strong> {employee?.first_name} {employee?.last_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 bg-red-50 p-4 rounded-xl border border-red-200">
            <p className="text-sm font-medium text-red-600">This action cannot be undone!</p>
            <p className="text-sm text-gray-600">All of the following will be permanently deleted:</p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Employee record</li>
              <li>All uploaded documents</li>
              <li>All compliance forms</li>
              <li>Training records</li>
              <li>Policy assignments</li>
            </ul>
            <p className="text-xs text-gray-500 mt-2">Only use this for duplicate records, test data, or incorrect entries.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-xl border-gray-300 text-gray-700 hover:bg-gray-50">
              Cancel
            </Button>
            <Button onClick={handlePermanentDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Completion Dialog */}
      <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-text-primary">
              <GraduationCap className="h-5 w-5 text-primary" />
              Mark Training Complete
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              Mark this training requirement as completed for the employee.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTrainingReq && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-[#F8FAFA] rounded-xl">
                <p className="font-medium text-text-primary">{selectedTrainingReq.name}</p>
                <p className="text-sm text-text-muted mt-1">
                  Category: {selectedTrainingReq.category?.replace(/_/g, ' ').replace(/^[A-Z]_/, '')}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-text-primary">Expiry Date (Optional)</Label>
                <Input
                  type="date"
                  value={trainingExpiryDate}
                  onChange={(e) => setTrainingExpiryDate(e.target.value)}
                  className="rounded-xl"
                  placeholder="Leave empty if no expiry"
                />
                <p className="text-xs text-text-muted">
                  Set an expiry date if this training needs to be renewed
                </p>
              </div>
              
              <div className="bg-info/10 border border-info/20 rounded-xl p-3">
                <p className="text-sm text-info font-medium">What happens:</p>
                <ul className="text-xs text-text-muted mt-1 space-y-1">
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Training record created or updated
                  </li>
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Compliance requirement marked complete
                  </li>
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Compliance score updates immediately
                  </li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setTrainingDialogOpen(false);
                setSelectedTrainingReq(null);
                setTrainingExpiryDate('');
              }} 
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteTraining}
              disabled={isCompletingTraining}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl"
              data-testid="confirm-complete-training"
            >
              {isCompletingTraining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Complete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Certificate Upload Dialog */}
      <Dialog open={trainingCertDialogOpen} onOpenChange={setTrainingCertDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-text-primary">
              <Upload className="h-5 w-5 text-primary" />
              Upload Training Certificate
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              Upload a certificate as evidence for this training requirement.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTrainingReq && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-[#F8FAFA] rounded-xl">
                <p className="font-medium text-text-primary">{selectedTrainingReq.name}</p>
                <p className="text-sm text-text-muted mt-1">
                  Category: {selectedTrainingReq.category?.replace(/_/g, ' ').replace(/^[A-Z]_/, '')}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-text-primary">Certificate File *</Label>
                <FileUploaderInline
                  onFileSelect={(file) => setTrainingCertFile(file)}
                  selectedFile={trainingCertFile}
                  onClear={() => setTrainingCertFile(null)}
                  acceptedTypes={['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                  placeholder="Drop certificate here or click to browse"
                  data-testid="training-cert-file-input"
                />
                <p className="text-xs text-text-muted">
                  Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-text-primary">Certificate Expiry Date (Optional)</Label>
                <Input
                  type="date"
                  value={trainingExpiryDate}
                  onChange={(e) => setTrainingExpiryDate(e.target.value)}
                  className="rounded-xl"
                />
                <p className="text-xs text-text-muted">
                  Set an expiry date if this certificate needs to be renewed
                </p>
              </div>
              
              <div className="bg-success/10 border border-success/20 rounded-xl p-3">
                <p className="text-sm text-success font-medium">Audit-Ready Evidence:</p>
                <ul className="text-xs text-text-muted mt-1 space-y-1">
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Certificate stored with audit trail
                  </li>
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Training marked as complete with evidence
                  </li>
                  <li className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-success" />
                    Certificate can be viewed and downloaded
                  </li>
                  <li className="flex items-center gap-1">
                    <Shield className="h-3 w-3 text-success" />
                    Ready for verification
                  </li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setTrainingCertDialogOpen(false);
                setSelectedTrainingReq(null);
                setTrainingCertFile(null);
                setTrainingExpiryDate('');
              }} 
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUploadTrainingCertificate}
              disabled={isUploadingCert || !trainingCertFile}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl"
              data-testid="confirm-upload-training-cert"
            >
              {isUploadingCert ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Certificate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Evidence Details Modal */}
      <Dialog open={editEvidenceOpen} onOpenChange={setEditEvidenceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Document Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-text-muted">
              Update document metadata. A reason is required for audit trail purposes.
            </p>
            
            <div className="space-y-2">
              <Label>Document Label</Label>
              <Input
                value={editForm.file_label}
                onChange={(e) => setEditForm(prev => ({ ...prev, file_label: e.target.value }))}
                placeholder="e.g., DBS Certificate 2024"
                className="rounded-xl"
              />
            </div>
            
            {/* DBS Update Service Check - Special labels and auto-calculation */}
            {editEvidenceData?.requirementId === 'dbs_check' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Last DBS Check Date</Label>
                  <Input
                    type="date"
                    value={editForm.issue_date}
                    onChange={(e) => {
                      const checkDate = e.target.value;
                      // Auto-calculate Next Review Due = Check Date + 12 months
                      let nextReviewDate = '';
                      if (checkDate) {
                        const date = new Date(checkDate);
                        date.setFullYear(date.getFullYear() + 1);
                        nextReviewDate = date.toISOString().split('T')[0];
                      }
                      setEditForm(prev => ({ 
                        ...prev, 
                        issue_date: checkDate,
                        expiry_date: nextReviewDate
                      }));
                    }}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next DBS Review Due</Label>
                  <Input
                    type="date"
                    value={editForm.expiry_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                    className="rounded-xl bg-gray-50"
                    title="Auto-calculated as 12 months from Last DBS Check Date"
                  />
                  <p className="text-xs text-text-muted">Auto-calculated (+12 months)</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Issue Date</Label>
                  <Input
                    type="date"
                    value={editForm.issue_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, issue_date: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={editForm.expiry_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about this document..."
                className="rounded-xl"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-warning">Reason for Change *</Label>
              <Textarea
                value={editForm.reason}
                onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Wrong expiry year entered, Corrected issue date from certificate..."
                className="rounded-xl border-warning/50 focus:border-warning"
                rows={2}
              />
              <p className="text-xs text-text-muted">
                This will be recorded in the audit trail.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setEditEvidenceOpen(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEvidenceEdit}
              disabled={isEditingEvidence || !editForm.reason}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl"
            >
              {isEditingEvidence ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Change History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {editHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="h-10 w-10 mx-auto text-text-muted/50 mb-2" />
                <p className="text-text-muted">No changes recorded</p>
                <p className="text-xs text-text-muted mt-1">
                  Document details have not been modified since upload.
                </p>
              </div>
            ) : (
              editHistory.map((log) => (
                <div 
                  key={log.id} 
                  className="p-3 bg-[#F8FAFA] rounded-xl border border-[#E4E8EB]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      <span className="font-medium text-text-primary text-sm">
                        {log.changed_by_name}
                      </span>
                    </div>
                    <span className="text-xs text-text-muted">
                      {formatBackendDateTime(log.changed_at)}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-text-muted">Field:</span>
                      <span className="font-medium text-text-primary capitalize">
                        {log.field_changed.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-muted">From:</span>
                      <span className="text-error line-through">
                        {log.old_value || '(empty)'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-muted">To:</span>
                      <span className="text-success">
                        {log.new_value || '(empty)'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 mt-2 pt-2 border-t border-[#E4E8EB]">
                      <span className="text-text-muted">Reason:</span>
                      <span className="text-text-primary italic">
                        "{log.reason}"
                      </span>
                    </div>
                    {log.was_verified_before_edit && (
                      <div className="mt-2 px-2 py-1 bg-warning/10 text-warning text-xs rounded-lg inline-block">
                        Changed after approval
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setHistoryOpen(false)}
              className="rounded-xl"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete File
            </DialogTitle>
            <DialogDescription>
              This will permanently remove the file from active use. The file will no longer count towards compliance. An audit record will be kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedFileForAction && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800">{selectedFileForAction.file_label || selectedFileForAction.original_filename || 'File'}</p>
                {selectedFileForAction.uploaded_at && (
                  <p className="text-xs text-red-600 mt-1">
                    Uploaded: {formatBackendDate(selectedFileForAction.uploaded_at)}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="delete-reason">Reason for deletion (optional)</Label>
              <Textarea
                id="delete-reason"
                placeholder="Enter an optional reason for deleting this file"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                className="min-h-[80px] rounded-xl"
              />
              <p className="text-xs text-text-muted">This reason will be recorded in the audit trail if provided.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteFile}
              disabled={isRemoving}
              className="rounded-xl"
              data-testid="confirm-delete-file"
            >
              {isRemoving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace File Dialog */}
      <Dialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              Replace File
            </DialogTitle>
            <DialogDescription>
              Uploading a new file will replace the existing one. The old file will be kept in history for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedFileForAction && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Replacing:</p>
                <p className="text-sm font-medium">{selectedFileForAction.file_label || selectedFileForAction.original_filename}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="replace-file">New File <span className="text-error">*</span></Label>
              <FileUploaderInline
                onFileSelect={(file) => setReplaceFile(file)}
                selectedFile={replaceFile}
                onClear={() => setReplaceFile(null)}
                acceptedTypes={['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp']}
                placeholder="Drop replacement file here or click to browse"
              />
              <p className="text-xs text-muted-foreground">Upload PDF or photo (JPG, PNG)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="replace-reason">Reason for replacement <span className="text-error">*</span></Label>
              <Textarea
                id="replace-reason"
                placeholder="Why is this file being replaced? (e.g. clearer scan, updated document)"
                value={replaceReason}
                onChange={(e) => setReplaceReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReplaceFile}
              disabled={isReplacing || !replaceReason.trim() || !replaceFile}
              className="bg-primary hover:bg-primary-hover"
            >
              {isReplacing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Replace File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Correction Dialog - Step 8 */}
      <Dialog open={docCorrectionDialogOpen} onOpenChange={setDocCorrectionDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {docCorrectionType === 'uploaded_in_error' && <FileWarning className="h-5 w-5 text-amber-500" />}
              {docCorrectionType === 'supersede' && <FileArchive className="h-5 w-5 text-blue-500" />}
              {docCorrectionType === 'move_category' && <FormInput className="h-5 w-5 text-purple-500" />}
              {docCorrectionType === 'reopen_review' && <RotateCcw className="h-5 w-5 text-green-500" />}
              {docCorrectionType === 'uploaded_in_error' && 'Mark as Uploaded in Error'}
              {docCorrectionType === 'supersede' && 'Mark as Superseded'}
              {docCorrectionType === 'move_category' && 'Move to Different Category'}
              {docCorrectionType === 'reopen_review' && 'Reopen for Review'}
            </DialogTitle>
            <DialogDescription>
              {docCorrectionType === 'uploaded_in_error' && 'This document will be marked as uploaded in error. It will not count toward requirements but is preserved for audit.'}
              {docCorrectionType === 'supersede' && 'Mark this document as superseded by a newer version. The original is preserved for audit trail.'}
              {docCorrectionType === 'move_category' && 'Move this document to a different requirement category if it was filed incorrectly.'}
              {docCorrectionType === 'reopen_review' && 'Reopen this document for review, undoing any previous verification or rejection.'}
            </DialogDescription>
          </DialogHeader>
          
          {docCorrectionTarget && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-sm">{docCorrectionTarget.file_label || docCorrectionTarget.original_filename || 'Document'}</p>
                {docCorrectionTarget.uploaded_at && (
                  <p className="text-xs text-muted-foreground">
                    Uploaded: {formatBackendDate(docCorrectionTarget.uploaded_at, { format: 'medium' })}
                  </p>
                )}
              </div>

              {docCorrectionType === 'move_category' && (
                <div className="space-y-2">
                  <Label>New Category *</Label>
                  <Select 
                    value={docCorrectionNewCategory} 
                    onValueChange={setDocCorrectionNewCategory}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select new category" />
                    </SelectTrigger>
                    <SelectContent>
                      {complianceRequirements?.requirements
                        .filter(r => r.type === 'document' && r.id !== selectedRequirementForAction)
                        .map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>
                  Reason for Change *
                  <span className="text-xs text-muted-foreground ml-2">
                    ({docCorrectionType === 'move_category' ? 'min 5' : 'min 10'} characters)
                  </span>
                </Label>
                <Textarea
                  placeholder="Explain why this correction is being made..."
                  value={docCorrectionReason}
                  onChange={(e) => setDocCorrectionReason(e.target.value)}
                  className="min-h-[80px] rounded-xl"
                  data-testid="doc-correction-reason"
                />
                <p className="text-xs text-muted-foreground">
                  This reason will be permanently recorded in the audit trail.
                </p>
              </div>

              {docCorrectionTarget.verified && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    This document is currently verified. This action will be flagged in the audit log.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDocCorrectionDialogOpen(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitDocCorrection}
              disabled={
                isSubmittingDocCorrection || 
                !docCorrectionReason.trim() ||
                docCorrectionReason.trim().length < (docCorrectionType === 'move_category' ? 5 : 10) ||
                (docCorrectionType === 'move_category' && !docCorrectionNewCategory)
              }
              className={`rounded-xl ${
                docCorrectionType === 'uploaded_in_error' ? 'bg-amber-600 hover:bg-amber-700' :
                docCorrectionType === 'reopen_review' ? 'bg-green-600 hover:bg-green-700' :
                'bg-primary hover:bg-primary-hover'
              }`}
              data-testid="submit-doc-correction"
            >
              {isSubmittingDocCorrection && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Extraction Review Modal - Phase 2: DBS, RTW, ID, POA */}
      {docExtractionReviewOpen && docExtractionDocumentId && (
        <DocumentExtractionReview
          documentId={docExtractionDocumentId}
          onClose={() => {
            setDocExtractionReviewOpen(false);
            setDocExtractionDocumentId(null);
            setDocExtractionContext(null);
          }}
          onApproved={handleDocExtractionComplete}
          documentName={docExtractionDocumentName}
          documentContext={docExtractionContext}
        />
      )}

      {/* Training Intake Wizard (Step 10) */}
      <TrainingIntakeWizard
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        open={trainingIntakeOpen}
        onClose={() => setTrainingIntakeOpen(false)}
        onComplete={() => {
          setTrainingIntakeOpen(false);
          fetchData();
          fetchCompliance();
          fetchProposedTrainingItems();
          fetchTrainingEvaluation();
        }}
      />

      {/* Training Request Dialog (Step 10) */}
      <TrainingRequestDialog
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        employeeEmail={employee?.email}
        open={trainingRequestOpen}
        onClose={() => setTrainingRequestOpen(false)}
        onComplete={() => {
          setTrainingRequestOpen(false);
          toast.success('Training certificate request sent');
        }}
      />

      {/* Requirement History Dialog */}
      <Dialog open={requirementHistoryOpen} onOpenChange={setRequirementHistoryOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              File History
            </DialogTitle>
            <DialogDescription>
              Complete timeline of all file operations for this requirement.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : requirementHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No history recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requirementHistory.map((entry, idx) => (
                  <div key={entry.id || idx} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {entry.action === 'replace_evidence' && <RefreshCw className="h-4 w-4 text-blue-500" />}
                        {entry.action === 'remove_evidence' && <Trash2 className="h-4 w-4 text-red-500" />}
                        {entry.action === 'edit_evidence' && <Edit className="h-4 w-4 text-amber-500" />}
                        {entry.action === 'upload_evidence' && <Upload className="h-4 w-4 text-green-500" />}
                        {entry.action === 'verify_evidence' && <Shield className="h-4 w-4 text-green-600" />}
                        {!['replace_evidence', 'remove_evidence', 'edit_evidence', 'upload_evidence', 'verify_evidence'].includes(entry.action) && (
                          <FileText className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-medium text-sm capitalize">
                          {entry.action?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {entry.timestamp ? formatBackendDateTime(entry.timestamp) : 'Unknown'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      By: {entry.user_name || 'Unknown'}
                    </p>
                    {entry.reason && (
                      <p className="text-sm mt-2 p-2 bg-muted rounded">
                        <span className="font-medium">Reason:</span> {entry.reason}
                      </p>
                    )}
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2 space-y-1">
                        {entry.details.old_filename && (
                          <p>Old file: {entry.details.old_filename}</p>
                        )}
                        {entry.details.new_filename && (
                          <p>New file: {entry.details.new_filename}</p>
                        )}
                        {entry.details.filename && (
                          <p>File: {entry.details.filename}</p>
                        )}
                        {entry.details.field && (
                          <p>Changed: {entry.details.field} from "{entry.details.old_value || 'empty'}" to "{entry.details.new_value}"</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequirementHistoryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal - supports multi-file navigation */}
      <DocumentPreviewModal
        isOpen={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFiles([]); }}
        fileUrl={previewFile?.url}
        fileName={previewFile?.name || previewFile?.filename}
        stampedFileUrl={previewFile?.stampedFileUrl}
        verificationStampByName={previewFile?.verificationStampByName}
        verificationStampAt={previewFile?.verificationStampAt}
        token={token}
        files={previewFiles}
        onDownload={previewFile ? async () => {
          try {
            const downloadUrl = previewFile.url.replace('/view', '/download');
            const response = await axios.get(downloadUrl, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
            const blob = new Blob([response.data]);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = previewFile.filename || 'document';
            link.click();
            URL.revokeObjectURL(url);
            toast.success('Document downloaded');
          } catch (error) {
            toast.error('Failed to download');
          }
        } : undefined}
      />
      
      {/* Training Correction Dialog */}
      <Dialog open={trainingCorrectionDialogOpen} onOpenChange={setTrainingCorrectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Training Record</DialogTitle>
            <DialogDescription>
              Make a correction to this training record. All changes require a reason and are logged for audit purposes.
            </DialogDescription>
          </DialogHeader>
          {editingTrainingRecord && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#F8FAFA] rounded-lg border border-[#E4E8EB]">
                <p className="font-medium text-text-primary">{editingTrainingRecord.training_name}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Field to Edit</Label>
                <Select value={trainingCorrectionField} onValueChange={(value) => {
                  setTrainingCorrectionField(value);
                  setTrainingCorrectionValue(editingTrainingRecord[value]?.split?.('T')?.[0] || editingTrainingRecord[value] || '');
                }}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expiry_date">Expiry Date</SelectItem>
                    <SelectItem value="completion_date">Completion Date</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Current Value</Label>
                <Input 
                  value={editingTrainingRecord[trainingCorrectionField] || '(not set)'} 
                  disabled 
                  className="rounded-xl bg-gray-100"
                />
              </div>
              
              <div className="space-y-2">
                <Label>New Value *</Label>
                {trainingCorrectionField === 'status' ? (
                  <Select value={trainingCorrectionValue} onValueChange={setTrainingCorrectionValue}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select new status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="expiring">Expiring</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    type="date" 
                    value={trainingCorrectionValue?.split?.('T')?.[0] || trainingCorrectionValue || ''} 
                    onChange={(e) => setTrainingCorrectionValue(e.target.value)}
                    className="rounded-xl"
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Change *</Label>
                <Textarea 
                  placeholder="Explain why this correction is being made (required for audit trail)"
                  value={trainingCorrectionReason}
                  onChange={(e) => setTrainingCorrectionReason(e.target.value)}
                  className="rounded-xl min-h-[80px]"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setTrainingCorrectionDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleTrainingCorrection} 
              disabled={!trainingCorrectionReason || !trainingCorrectionValue}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl"
            >
              Save Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Training History Dialog */}
      <Dialog open={trainingHistoryDialogOpen} onOpenChange={setTrainingHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Training Record History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto mt-4">
            {trainingHistory.length === 0 ? (
              <div className="text-center py-8 text-text-muted">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No correction history</p>
              </div>
            ) : (
              trainingHistory.map((entry, idx) => (
                <div key={entry.id || idx} className="p-3 bg-white rounded-lg border border-[#E4E8EB]">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-text-primary">
                        {entry.action === 'training_correction' ? 'Correction' : entry.action?.replace('_', ' ')}
                      </p>
                      {entry.field_changed && (
                        <p className="text-sm text-text-muted">
                          <span className="font-medium">{entry.field_changed}</span>: {entry.old_value || '(empty)'} â†’ {entry.new_value}
                        </p>
                      )}
                      {entry.reason && (
                        <p className="text-sm text-text-muted mt-1">
                          <span className="font-medium">Reason:</span> {entry.reason}
                        </p>
                      )}
                    </div>
                    <div className="text-right text-xs text-text-muted">
                      <p>{entry.changed_by_name || 'System'}</p>
                      <p>{entry.created_at ? formatBackendDateTime(entry.created_at) : ''}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Training Record Dialog */}
      <Dialog open={deleteTrainingDialogOpen} onOpenChange={setDeleteTrainingDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Training Record
            </DialogTitle>
            <DialogDescription>
              This will permanently remove this training record. An audit trail will be kept.
            </DialogDescription>
          </DialogHeader>
          {deletingTrainingRecord && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium text-red-800">{deletingTrainingRecord.training_name}</p>
                <p className="text-sm text-red-600 mt-1">
                  Status: {deletingTrainingRecord.status?.replace('_', ' ')}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delete-training-reason">Reason for deletion (optional)</Label>
                <Textarea
                  id="delete-training-reason"
                  placeholder="Enter an optional reason for deleting this record"
                  value={deleteTrainingReason}
                  onChange={(e) => setDeleteTrainingReason(e.target.value)}
                  className="min-h-[80px] rounded-xl"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTrainingDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteTrainingRecord}
              disabled={isDeletingTraining}
              className="rounded-xl"
            >
              {isDeletingTraining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Acknowledgement Dialog - For Contract/Handbook acknowledgements */}
      <Dialog open={acknowledgementDialogOpen} onOpenChange={(open) => {
        setAcknowledgementDialogOpen(open);
        if (!open) {
          setAcknowledgementConfirmed(false);
          setAcknowledgingRequirement(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Confirm & Complete
            </DialogTitle>
            <DialogDescription>
              Please confirm that this employee has received and understood the document.
            </DialogDescription>
          </DialogHeader>
          {acknowledgingRequirement && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="font-semibold text-green-800">{acknowledgingRequirement.name}</p>
                <p className="text-sm text-green-600 mt-2">
                  {acknowledgingRequirement.description}
                </p>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <Checkbox 
                  id="acknowledgement-confirm"
                  checked={acknowledgementConfirmed}
                  onCheckedChange={setAcknowledgementConfirmed}
                  className="mt-0.5"
                  data-testid="acknowledgement-checkbox"
                />
                <label htmlFor="acknowledgement-confirm" className="text-sm cursor-pointer">
                  {acknowledgingRequirement.acknowledgement_text || 
                    `I confirm that this employee has received, read, and understood the ${acknowledgingRequirement.name.replace(' Acknowledgement', '')}.`}
                </label>
              </div>
              
              <p className="text-xs text-text-muted">
                This acknowledgement will be logged with your name and timestamp for audit purposes.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAcknowledgementDialogOpen(false)} 
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAcknowledgeRequirement}
              disabled={!acknowledgementConfirmed || isAcknowledging}
              className="rounded-xl bg-green-600 hover:bg-green-700"
              data-testid="submit-acknowledgement-btn"
            >
              {isAcknowledging ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirm & Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Submission Modal - Structured forms with sections */}
      <Dialog open={formModalOpen} onOpenChange={(open) => {
        setFormModalOpen(open);
        if (!open) {
          setFormTemplate(null);
          setFormData({});
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Branded Header for Staff Health Questionnaire */}
          {formTemplate?.branding?.show_logo && (
            <div className="bg-[#2E7D32] text-white p-4 -m-6 mb-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <span className="text-[#2E7D32] font-bold text-xl">O</span>
                </div>
                <div>
                  <h2 className="text-lg font-bold">{formTemplate?.branding?.company_name || 'Workspace'}</h2>
                  <p className="text-sm opacity-90">{formTemplate?.name}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogHeader className={formTemplate?.branding?.show_logo ? 'pt-0' : ''}>
            {!formTemplate?.branding?.show_logo && (
              <DialogTitle className="font-heading flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                {formTemplate?.name || 'Complete Form'}
              </DialogTitle>
            )}
            {formTemplate?.description && (
              <DialogDescription className="text-sm text-text-muted">
                {formTemplate.description}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {formTemplate && (
            <div className="space-y-6 mt-4">
              {/* Optional form notice */}
              {formTemplate.is_optional && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700 flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Optional</span>
                    This form does not affect compliance percentage or work readiness status.
                  </p>
                </div>
              )}
              
              {/* Auto-fill notice */}
              {formTemplate.auto_fill_fields?.length > 0 && Object.keys(formData).length > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Some fields have been pre-filled from the employee profile. Please review and update as needed.
                  </p>
                </div>
              )}
              
              {/* Profile update notice */}
              {formTemplate.updates_profile && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    This form can update the employee's profile data when submitted.
                  </p>
                </div>
              )}
              
              {/* Render sections if available, otherwise fallback to flat fields */}
              {formTemplate.sections?.length > 0 ? (
                <div className="space-y-6">
                  {formTemplate.sections.map((section) => {
                    // Skip admin-only sections for non-admins
                    if (section.admin_only && !isAdmin()) return null;
                    
                    // Use green header style if form has branding
                    const sectionHeaderClass = formTemplate?.branding?.header_color 
                      ? 'bg-[#2E7D32] text-white px-4 py-3 border-b border-[#2E7D32]'
                      : 'bg-gray-50 px-4 py-3 border-b border-gray-200';
                    
                    return (
                      <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className={sectionHeaderClass}>
                          <h4 className={`font-medium ${formTemplate?.branding?.header_color ? 'text-white' : 'text-text-primary'}`}>
                            {section.title}
                          </h4>
                          {section.description && (
                            <p className={`text-xs mt-0.5 ${formTemplate?.branding?.header_color ? 'text-white/80' : 'text-text-muted'}`}>
                              {section.description}
                            </p>
                          )}
                        </div>
                        <div className="p-4 space-y-4">
                          {section.fields.map((field) => {
                            // Handle conditional fields
                            if (field.conditional_on) {
                              const conditionValue = formData[field.conditional_on];
                              if (conditionValue !== field.conditional_value) {
                                return null;
                              }
                            }
                            
                            return (
                              <div key={field.id} className="space-y-1.5">
                                {field.type === 'info' ? (
                                  <p className="text-sm text-text-muted italic bg-[#F8FAFA] p-3 rounded-lg">
                                    {field.label}
                                  </p>
                                ) : (
                                  <>
                                    <Label className="text-sm font-medium flex items-center gap-2">
                                      {field.label}
                                      {field.required && <span className="text-error">*</span>}
                                      {field.auto_fill && formData[field.id] && (
                                        <span className="text-xs text-green-600 font-normal">(auto-filled)</span>
                                      )}
                                    </Label>
                                    
                                    {field.type === 'text' && (
                                      <Input
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                                        placeholder={field.placeholder || ''}
                                        className="rounded-xl"
                                      />
                                    )}
                                    
                                    {field.type === 'number' && (
                                      <Input
                                        type="number"
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                                        placeholder={field.placeholder || ''}
                                        className="rounded-xl"
                                      />
                                    )}
                                    
                                    {field.type === 'textarea' && (
                                      <Textarea
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                                        placeholder={field.placeholder || ''}
                                        className="rounded-xl"
                                        rows={3}
                                      />
                                    )}
                                    
                                    {field.type === 'date' && (
                                      <Input
                                        type="date"
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                                        className="rounded-xl"
                                      />
                                    )}
                                    
                                    {field.type === 'checkbox' && (
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          id={field.id}
                                          checked={formData[field.id] || false}
                                          onCheckedChange={(checked) => setFormData({...formData, [field.id]: checked})}
                                        />
                                        <label htmlFor={field.id} className="text-sm cursor-pointer">Yes</label>
                                      </div>
                                    )}
                                    
                                    {field.type === 'select' && (
                                      <Select 
                                        value={formData[field.id] || ''} 
                                        onValueChange={(v) => setFormData({...formData, [field.id]: v})}
                                      >
                                        <SelectTrigger className="rounded-xl">
                                          <SelectValue placeholder="Select..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {field.options?.map((opt) => (
                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    
                                    {field.type === 'multi_select' && (
                                      <div className="flex flex-wrap gap-2">
                                        {field.options?.map((opt) => (
                                          <label key={opt} className="flex items-center gap-1.5 text-sm">
                                            <Checkbox
                                              checked={(formData[field.id] || []).includes(opt)}
                                              onCheckedChange={(checked) => {
                                                const current = formData[field.id] || [];
                                                if (checked) {
                                                  setFormData({...formData, [field.id]: [...current, opt]});
                                                } else {
                                                  setFormData({...formData, [field.id]: current.filter(v => v !== opt)});
                                                }
                                              }}
                                            />
                                            {opt}
                                          </label>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Fallback to flat fields for backward compatibility */
                <div className="grid gap-4">
                  {formTemplate.fields?.map((field) => (
                    <div key={field.id} className="space-y-1.5">
                      {field.type === 'info' ? (
                        <p className="text-sm text-text-muted italic bg-[#F8FAFA] p-3 rounded-lg">
                          {field.label}
                        </p>
                      ) : (
                        <>
                          <Label className="text-sm font-medium">
                            {field.label}
                            {field.required && <span className="text-error ml-1">*</span>}
                          </Label>
                          
                          {field.type === 'text' && (
                            <Input
                              value={formData[field.id] || ''}
                              onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                              placeholder={field.placeholder || ''}
                              className="rounded-xl"
                            />
                          )}
                          
                          {field.type === 'textarea' && (
                            <Textarea
                              value={formData[field.id] || ''}
                              onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                              placeholder={field.placeholder || ''}
                              className="rounded-xl"
                              rows={3}
                            />
                          )}
                          
                          {field.type === 'date' && (
                            <Input
                              type="date"
                              value={formData[field.id] || ''}
                              onChange={(e) => setFormData({...formData, [field.id]: e.target.value})}
                              className="rounded-xl"
                            />
                          )}
                          
                          {field.type === 'checkbox' && (
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={field.id}
                                checked={formData[field.id] || false}
                                onCheckedChange={(checked) => setFormData({...formData, [field.id]: checked})}
                              />
                              <label htmlFor={field.id} className="text-sm cursor-pointer">Yes</label>
                            </div>
                          )}
                          
                          {field.type === 'select' && (
                            <Select 
                              value={formData[field.id] || ''} 
                              onValueChange={(v) => setFormData({...formData, [field.id]: v})}
                            >
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <DialogFooter className="pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setFormModalOpen(false)} 
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleFormSubmit}
                  disabled={isSubmittingForm}
                  className="rounded-xl bg-primary hover:bg-primary/90"
                  data-testid="submit-form-btn"
                >
                  {isSubmittingForm ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
                  ) : (
                    'Submit Form'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Form Modal - Display submitted form data */}
      <Dialog open={viewFormOpen} onOpenChange={setViewFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              {viewFormData?.requirementName || 'Form Submission'}
            </DialogTitle>
          </DialogHeader>
          
          {viewFormData && (
            <div className="space-y-4 mt-4">
              {/* Status badges */}
              <div className="flex items-center gap-3 p-3 bg-[#F8FAFA] rounded-xl">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    viewFormData.verified 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {viewFormData.verified ? 'Verified' : 'Submitted'}
                  </span>
                </div>
                <div className="text-xs text-text-muted">
                  Submitted: {viewFormData.submitted_at ? formatBackendDateTime(viewFormData.submitted_at) : 'Unknown'}
                </div>
                {viewFormData.submitted_by_name && (
                  <div className="text-xs text-text-muted">
                    By: {viewFormData.submitted_by_name}
                  </div>
                )}
              </div>
              
              {/* Form data display */}
              <div className="space-y-3">
                {Object.entries(viewFormData.data || {}).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3 p-2 border-b border-[#E4E8EB]">
                    <span className="text-sm font-medium text-text-primary min-w-[180px] capitalize">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="text-sm text-text-muted flex-1">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || '-')}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Verification info if verified */}
              {viewFormData.verified && viewFormData.verified_by_name && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-sm text-green-700">
                    <CheckCircle className="h-4 w-4 inline mr-2" />
                    Verified by {viewFormData.verified_by_name} on {formatBackendDateTime(viewFormData.verified_at)}
                  </p>
                </div>
              )}
              
              <DialogFooter className="pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setViewFormOpen(false)} 
                  className="rounded-xl"
                >
                  Close
                </Button>
                {!viewFormData.verified && isAdmin() && (
                  <Button 
                    onClick={() => handleVerifyFormSubmission(viewFormData.id)}
                    className="rounded-xl bg-green-600 hover:bg-green-700"
                    data-testid="verify-form-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Form
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Extraction Review Dialog */}
      <Dialog open={extractionDialogOpen} onOpenChange={(open) => {
        if (!open && !isApplyingExtraction) {
          handleDiscardExtraction();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {extractionFailed ? 'Extraction Options' : 'Review Extracted Data'}
            </DialogTitle>
            <DialogDescription>
              {extractionFailed ? (
                extractionFailed.message
              ) : (
                <>
                  Review the extracted values below. Select which fields to apply to the employee profile.
                  <span className="block mt-2 text-amber-600 font-medium">
                    Note: This updates profile data only. Compliance evidence requirements remain unchanged.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {isExtracting ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-text-muted">Extracting data from application form...</p>
              <p className="text-xs text-text-muted mt-1">This may take a few seconds</p>
            </div>
          ) : extractionFailed ? (
            /* Extraction Failed - Show Options */
            <div className="space-y-4">
              {/* Friendly Message */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Don't worry - you can still proceed!</p>
                    <p>Automatic extraction didn't work for this document, but you have options to continue.</p>
                    {extractionFailed.extraction_log && (
                      <p className="text-xs mt-2 text-amber-600">
                        Details: {extractionFailed.extraction_log.file_type} ({Math.round((extractionFailed.extraction_log.file_size_bytes || 0) / 1024)} KB)
                        {extractionFailed.extraction_log.failure_reason && (
                          <span className="block">Reason: {extractionFailed.extraction_log.failure_reason}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Options Buttons */}
              <div className="space-y-3">
                {extractionFailed.options?.map((option) => (
                  <button
                    key={option.action}
                    onClick={() => handleExtractionOption(option.action)}
                    className="w-full flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
                    data-testid={`extraction-option-${option.action}`}
                  >
                    <div className="flex-shrink-0">
                      {option.action === 'fill_manually' && (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Edit className="h-5 w-5 text-blue-600" />
                        </div>
                      )}
                      {option.action === 'view_document' && (
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Eye className="h-5 w-5 text-purple-600" />
                        </div>
                      )}
                      {option.action === 'retry' && (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <RefreshCw className="h-5 w-5 text-green-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-text-primary">{option.label}</p>
                      <p className="text-sm text-text-muted">{option.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
              
              <DialogFooter className="pt-4">
                <Button
                  variant="outline"
                  onClick={handleDiscardExtraction}
                  data-testid="close-extraction-dialog"
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : extractionResult ? (
            <div className="space-y-4">
              {/* Extraction Method & Low Confidence Warning */}
              {extractionResult.low_confidence_fields?.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium">Low Confidence Fields Detected</p>
                      <p>Please review highlighted fields carefully: {extractionResult.low_confidence_fields.map(f => FIELD_LABELS[f] || f).join(', ')}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Extraction Method Badge */}
              {extractionResult.extraction_method && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-text-muted">Extraction method:</span>
                  <span className={`px-2 py-0.5 rounded font-medium ${
                    extractionResult.extraction_method === 'ai' ? 'bg-blue-100 text-blue-700' :
                    extractionResult.extraction_method === 'ai+ocr' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {extractionResult.extraction_method === 'ai' ? 'AI Vision' :
                     extractionResult.extraction_method === 'ai+ocr' ? 'AI + OCR' :
                     extractionResult.extraction_method === 'ocr' ? 'OCR' : extractionResult.extraction_method}
                  </span>
                </div>
              )}
              
              {/* Compliance Note */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Profile Data Only</p>
                    <p>Extracted values will populate profile fields (e.g., NI Number field). They do NOT complete compliance requirements (e.g., "Proof of NI Number" still needs evidence upload).</p>
                  </div>
                </div>
              </div>
              
              {/* Fields Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Apply</th>
                      <th className="text-left p-3 font-medium">Field</th>
                      <th className="text-left p-3 font-medium">Extracted Value</th>
                      <th className="text-left p-3 font-medium">Current Value</th>
                      <th className="text-left p-3 font-medium">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractionResult.fields.map((field, idx) => {
                      // Handle both numeric confidence and string confidence_label
                      const confidenceScore = typeof field.confidence === 'number' ? field.confidence : null;
                      const confidenceLabel = field.confidence_label || 
                        (typeof field.confidence === 'string' ? field.confidence : 
                         confidenceScore >= 0.8 ? 'high' : confidenceScore >= 0.5 ? 'medium' : 'low');
                      const isLowConfidence = confidenceLabel === 'low' || (confidenceScore !== null && confidenceScore < 0.5);
                      
                      return (
                        <tr 
                          key={field.field_name} 
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isLowConfidence ? 'bg-red-50/50' : ''}`}
                        >
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={fieldsToApply[field.field_name] || false}
                              onChange={() => toggleFieldToApply(field.field_name)}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              data-testid={`apply-field-${field.field_name}`}
                            />
                          </td>
                          <td className="p-3 font-medium text-text-primary">
                            {FIELD_LABELS[field.field_name] || field.field_name}
                            {isLowConfidence && (
                              <span className="ml-2 text-red-500" title="Low confidence - please verify">âš </span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className={`${field.extracted_value ? 'text-text-primary' : 'text-text-muted italic'} ${isLowConfidence ? 'text-red-700' : ''}`}>
                              {field.extracted_value || 'Not found'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`${field.current_value ? 'text-text-primary' : 'text-text-muted italic'}`}>
                              {field.current_value || 'Empty'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                confidenceLabel === 'high' ? 'bg-green-100 text-green-700' :
                                confidenceLabel === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {confidenceLabel}
                              </span>
                              {confidenceScore !== null && (
                                <span className="text-xs text-text-muted">
                                  {Math.round(confidenceScore * 100)}%
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2 text-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const allSelected = {};
                    extractionResult.fields.forEach(f => { allSelected[f.field_name] = true; });
                    setFieldsToApply(allSelected);
                  }}
                >
                  Select All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const emptyOnly = {};
                    extractionResult.fields.forEach(f => {
                      emptyOnly[f.field_name] = !f.current_value && !!f.extracted_value;
                    });
                    setFieldsToApply(emptyOnly);
                  }}
                >
                  Select Empty Only
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFieldsToApply({})}
                >
                  Clear All
                </Button>
              </div>
              
              <DialogFooter className="pt-4">
                <Button
                  variant="outline"
                  onClick={handleDiscardExtraction}
                  disabled={isApplyingExtraction}
                >
                  Discard
                </Button>
                <Button
                  onClick={handleApplyExtraction}
                  disabled={isApplyingExtraction || Object.values(fieldsToApply).filter(Boolean).length === 0}
                  data-testid="apply-extraction-btn"
                >
                  {isApplyingExtraction ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Applying...</>
                  ) : (
                    <>Apply {Object.values(fieldsToApply).filter(Boolean).length} Field(s)</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="text-center py-8 text-text-muted">
              <p>No extraction data available.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Reference Dialog */}
      <Dialog open={verifyRefDialogOpen} onOpenChange={setVerifyRefDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Verify Reference {selectedRefNum}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Reference Integrity Rule:</strong> References must match the applicant's CV, 
                or you must document why they differ.
              </p>
            </div>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium">Does this reference match the CV?</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="fromCv" 
                    checked={refFromCv === true}
                    onChange={() => setRefFromCv(true)}
                    className="h-4 w-4 text-primary"
                  />
                  <span>Yes, matches CV</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="fromCv" 
                    checked={refFromCv === false}
                    onChange={() => setRefFromCv(false)}
                    className="h-4 w-4 text-primary"
                  />
                  <span>No, different from CV</span>
                </label>
              </div>
            </div>
            
            {refFromCv === false && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-red-700">
                  âš ï¸ Justification Required
                </label>
                <Textarea
                  value={refOverrideReason}
                  onChange={(e) => setRefOverrideReason(e.target.value)}
                  placeholder="Explain why this reference differs from the CV (min 10 characters)..."
                  className="min-h-[100px] rounded-xl"
                />
                <p className="text-xs text-text-muted">
                  {refOverrideReason.length}/10 characters minimum
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setVerifyRefDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyReference}
              disabled={isVerifyingRef || (!refFromCv && refOverrideReason.length < 10)}
              className="rounded-xl bg-primary hover:bg-primary-hover"
            >
              {isVerifyingRef ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify Reference
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Explain CV Gap Dialog */}
      <Dialog open={explainGapDialogOpen} onOpenChange={setExplainGapDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-purple-600" />
              Explain Employment Gap
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedGap && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-700">Gap Duration: {selectedGap.gap_duration_days} days</p>
                <p className="text-sm text-amber-600 mt-1">
                  From: {selectedGap.previous_job?.company} ({selectedGap.gap_start})
                </p>
                <p className="text-sm text-amber-600">
                  To: {selectedGap.next_job?.company} ({selectedGap.gap_end})
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Explanation for this gap <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={gapExplanation}
                onChange={(e) => setGapExplanation(e.target.value)}
                placeholder="E.g., Career break for family care, further education, travel, etc. (min 10 characters)..."
                className="min-h-[120px] rounded-xl"
              />
              <p className="text-xs text-text-muted">
                {gapExplanation.length}/10 characters minimum
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setExplainGapDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleExplainGap}
              disabled={isExplainingGap || gapExplanation.length < 10}
              className="rounded-xl bg-purple-600 hover:bg-purple-700"
            >
              {isExplainingGap ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Explanation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Request Dialog */}
      <Dialog open={requestDocDialogOpen} onOpenChange={(open) => {
        setRequestDocDialogOpen(open);
        if (!open) {
          setDuplicateBlockedInfo(null);
          setIsResendMode(false);
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <Send className="h-5 w-5 text-blue-600" />
              {isResendMode ? 'Resend Document Request' : 'Request Document'}
            </DialogTitle>
            <DialogDescription>
              Send an email request to {employee?.first_name} for {requestingRequirement?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Duplicate blocked warning */}
            {duplicateBlockedInfo && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Active Request Exists</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {duplicateBlockedInfo.message}
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Click "Resend" to supersede the previous request and send a new email.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {!duplicateBlockedInfo && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">
                  An email will be sent to <strong>{employee?.email}</strong> requesting them to upload this document.
                  {isResendMode && <span className="block mt-1 text-blue-600">This will supersede any previous active request.</span>}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Additional Message (Optional)</Label>
              <Textarea
                value={requestDocMessage}
                onChange={(e) => setRequestDocMessage(e.target.value)}
                placeholder="Add any specific instructions or notes..."
                rows={3}
                className="bg-white border-[#E4E8EB]"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setRequestDocDialogOpen(false);
                setDuplicateBlockedInfo(null);
                setIsResendMode(false);
              }}
              className="border-[#E4E8EB]"
            >
              Cancel
            </Button>
            
            {duplicateBlockedInfo ? (
              <Button 
                onClick={handleResendRequest}
                disabled={isRequestingDoc}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="resend-request-btn"
              >
                {isRequestingDoc ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Resend Request
              </Button>
            ) : (
              <Button 
                onClick={() => handleRequestDocument(isResendMode)}
                disabled={isRequestingDoc}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-request-btn"
              >
                {isRequestingDoc ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {isResendMode ? 'Resend Request' : 'Send Request'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Form Dialog */}
      <Dialog open={sendFormDialogOpen} onOpenChange={setSendFormDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <FileText className="h-5 w-5 text-primary" />
              Send Form to Employee
            </DialogTitle>
            <DialogDescription>
              Send a form request to {employee?.first_name} via email. They can complete it without logging in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                An email will be sent to <strong>{employee?.email}</strong> with a secure link to complete the form.
              </p>
            </div>
            {sendFormSetupGate && (
              <TenantSetupGateNotice
                gate={sendFormSetupGate}
                title="Workspace setup must be complete before sending forms"
                compact
              />
            )}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Form Type</Label>
              <Select value={selectedFormType} onValueChange={setSelectedFormType}>
                <SelectTrigger className="bg-white border-[#E4E8EB]">
                  <SelectValue placeholder="Select form to send..." />
                </SelectTrigger>
                <SelectContent>
                  {FORM_OPTIONS.map((form) => (
                    <SelectItem key={form.value} value={form.value}>{form.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Additional Message (Optional)</Label>
              <Textarea
                value={sendFormMessage}
                onChange={(e) => setSendFormMessage(e.target.value)}
                placeholder="Add any specific instructions or context..."
                rows={3}
                className="bg-white border-[#E4E8EB]"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setSendFormDialogOpen(false)}
              className="border-[#E4E8EB]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendForm}
              disabled={isSendingForm || !selectedFormType}
              className="bg-primary hover:bg-primary-hover text-white"
              data-testid="send-form-btn"
            >
              {isSendingForm ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reference Request Dialog (NHS-Level Workflow Step 1) */}
      <Dialog open={requestReferenceDialogOpen} onOpenChange={setRequestReferenceDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <Mail className="h-5 w-5 text-primary" />
              Request Reference from Referee
            </DialogTitle>
            <DialogDescription>
              Send a reference request email directly to the referee. They can complete a secure form without logging in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedRefForRequest && (
              <>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>Referee Details (from application):</strong>
                  </p>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p><strong>Name:</strong> {selectedRefForRequest.declared?.name || 'Not provided'}</p>
                    <p><strong>Email:</strong> {selectedRefForRequest.declared?.email || 'Not provided'}</p>
                    <p><strong>Company:</strong> {selectedRefForRequest.declared?.company || 'Not provided'}</p>
                  </div>
                </div>
                {!selectedRefForRequest.declared?.email && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Referee email is required. Please update the employee profile first.
                    </p>
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Additional Message (Optional)</Label>
              <Textarea
                value={referenceRequestMessage}
                onChange={(e) => setReferenceRequestMessage(e.target.value)}
                placeholder="Add any specific instructions or context for the referee..."
                rows={3}
                className="bg-white border-[#E4E8EB]"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setRequestReferenceDialogOpen(false)}
              className="border-[#E4E8EB]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendReferenceRequest}
              disabled={isRequestingReference || !selectedRefForRequest?.declared?.email}
              className="bg-primary hover:bg-primary-hover text-white"
              data-testid="send-reference-request-btn"
            >
              {isRequestingReference ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Reference Dialog (NHS-Level Workflow Step 2) */}
      <Dialog open={reviewReferenceDialogOpen} onOpenChange={setReviewReferenceDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Review Reference {selectedRefForReview?.reference_num}
            </DialogTitle>
            <DialogDescription>
              Compare declared details with returned response. Document any mismatches before verification.
            </DialogDescription>
          </DialogHeader>
          {selectedRefForReview && (
            <div className="space-y-4 mt-4">
              {/* Mismatch Warning */}
              {selectedRefForReview.mismatch_detected && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800 flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Mismatch Detected - Details in returned response differ from application
                  </p>
                </div>
              )}
              
              {/* Side-by-side comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Declared (From Application) */}
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm border-b pb-1">Declared (Application)</h4>
                  <div className="text-sm space-y-1.5">
                    <p><span className="text-gray-500">Name:</span> {selectedRefForReview.declared?.name || '-'}</p>
                    <p><span className="text-gray-500">Company:</span> {selectedRefForReview.declared?.company || '-'}</p>
                    <p><span className="text-gray-500">Email:</span> {selectedRefForReview.declared?.email || '-'}</p>
                    <p><span className="text-gray-500">Phone:</span> {selectedRefForReview.declared?.phone || '-'}</p>
                    <p><span className="text-gray-500">Job Title:</span> {selectedRefForReview.declared?.job_title || '-'}</p>
                    <p><span className="text-gray-500">Relationship:</span> {selectedRefForReview.declared?.relationship || '-'}</p>
                  </div>
                </div>
                
                {/* Returned (From Referee) */}
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm border-b pb-1">Returned (Referee Response)</h4>
                  <div className="text-sm space-y-1.5">
                    <p className={selectedRefForReview.mismatch_detected && selectedRefForReview.declared?.name?.toLowerCase() !== selectedRefForReview.returned?.name?.toLowerCase() ? 'text-amber-600 font-medium' : ''}>
                      <span className="text-gray-500">Name:</span> {selectedRefForReview.returned?.name || '-'}
                    </p>
                    <p className={selectedRefForReview.mismatch_detected && selectedRefForReview.declared?.company?.toLowerCase() !== selectedRefForReview.returned?.company?.toLowerCase() ? 'text-amber-600 font-medium' : ''}>
                      <span className="text-gray-500">Company:</span> {selectedRefForReview.returned?.company || '-'}
                    </p>
                    <p><span className="text-gray-500">Email:</span> {selectedRefForReview.returned?.email || '-'}</p>
                    <p><span className="text-gray-500">Phone:</span> {selectedRefForReview.returned?.phone || '-'}</p>
                    <p><span className="text-gray-500">Job Title:</span> {selectedRefForReview.returned?.job_title || '-'}</p>
                    <p><span className="text-gray-500">Relationship:</span> {selectedRefForReview.returned?.relationship || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Full Response Summary */}
              {selectedRefForReview.response_data && (
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <h4 className="font-medium text-gray-900 text-sm">Reference Assessment Summary</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-gray-500">Performance:</span> {selectedRefForReview.response_data.performance_rating || '-'}</p>
                    <p><span className="text-gray-500">Reliability:</span> {selectedRefForReview.response_data.reliability || '-'}</p>
                    <p><span className="text-gray-500">Professionalism:</span> {selectedRefForReview.response_data.professionalism || '-'}</p>
                    <p><span className="text-gray-500">Care Suitable:</span> {selectedRefForReview.response_data.care_vulnerable_suitable || '-'}</p>
                    <p><span className="text-gray-500">Would Re-employ:</span> {selectedRefForReview.response_data.would_re_employ || '-'}</p>
                    <p><span className="text-gray-500">Safeguarding:</span> {selectedRefForReview.response_data.safeguarding_concerns || '-'}</p>
                  </div>
                </div>
              )}
              
              {/* Mismatch Notes (Required if mismatch detected) */}
              {selectedRefForReview.mismatch_detected && (
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <Label className="text-gray-700 font-medium flex items-center gap-1">
                    Mismatch Explanation <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Document the discrepancy and explain why it is acceptable for verification.
                  </p>
                  <Textarea
                    value={reviewMismatchNotes}
                    onChange={(e) => setReviewMismatchNotes(e.target.value)}
                    placeholder="Explain the mismatch (e.g., 'Name variation is maiden name vs married name, confirmed via phone call with referee on [date]')"
                    rows={3}
                    className="bg-white border-[#E4E8EB]"
                    required
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setReviewReferenceDialogOpen(false)}
              className="border-[#E4E8EB]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReviewReference}
              disabled={isReviewingReference || (selectedRefForReview?.mismatch_detected && reviewMismatchNotes.length < 10)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="review-reference-btn"
            >
              {isReviewingReference ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Mark as Reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employment History Mismatch Details Dialog */}
      <Dialog open={mismatchDialogOpen} onOpenChange={(v) => { setMismatchDialogOpen(v); if (!v) { setSelectedCvRolesForApply({}); setReconcileReason(''); } }}>
        <DialogContent className="sm:max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-gray-900">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Check CV against work history
            </DialogTitle>
            <DialogDescription>
              Official work history is the record used for sign-off. Use the CV as evidence to check, add confirmed missing roles, or request an updated CV.
            </DialogDescription>
          </DialogHeader>
          
          {employmentMismatch && (
            <div className="space-y-6 mt-4">
              {(employmentMismatch.employment_mismatch_reviewed || cvApproved) && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">
                    {cvApproved ? 'CV approved' : 'Mismatch reviewed and accepted'}
                  </p>
                  {(employmentMismatch.cv_approval_note || employmentMismatch.employment_mismatch_review_notes?.length > 0) && (
                    <p className="text-xs text-green-700 mt-1">
                      {employmentMismatch.cv_approval_note
                        || employmentMismatch.employment_mismatch_review_notes[employmentMismatch.employment_mismatch_review_notes.length - 1]?.note}
                    </p>
                  )}
                  {employmentMismatch.employment_mismatch_reviewed_at && (
                    <p className="text-xs text-green-600 mt-1">
                      Reviewed {new Date(employmentMismatch.employment_mismatch_reviewed_at).toLocaleString()}
                      {employmentMismatch.employment_mismatch_reviewed_by
                        ? ` by ${employmentMismatch.employment_mismatch_reviewed_by}`
                        : ''}
                    </p>
                  )}
                </div>
              )}
              {/* Summary */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-800">
                  <strong>{employmentMismatch.mismatch_count}</strong> item{employmentMismatch.mismatch_count !== 1 ? 's' : ''} to check |
                  <span className="ml-2">Official roles: {employmentMismatch.structured_history?.length || 0}</span> |
                  <span className="ml-2">CV roles: {employmentMismatch.cv_extracted_roles?.length || 0}</span>
                </p>
                {employmentMismatch.compared_at && (
                  <p className="text-xs text-amber-600 mt-1">
                    Last compared: {new Date(employmentMismatch.compared_at).toLocaleString()}
                  </p>
                )}
              </div>
              
              {/* Mismatch List */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Items to check</h4>
                {employmentMismatch.mismatch_summary?.map((mismatch, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    mismatch.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className={`text-sm font-medium ${
                      mismatch.severity === 'critical' ? 'text-red-800' : 'text-amber-800'
                    }`}>
                      {mismatch.type === 'missing_in_structured' && 'CV role is not in official work history'}
                      {mismatch.type === 'missing_in_cv' && 'Official work history role is not in the CV'}
                      {mismatch.type === 'date_inconsistency' && 'Dates need checking'}
                      {mismatch.type === 'overlap_inconsistency' && 'Dates overlap'}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">{mismatch.description}</p>
                    
                    {/* Show data comparison */}
                    <div className="grid grid-cols-2 gap-4 mt-2 text-xs">
                      {mismatch.structured_data && (
                        <div className="p-2 bg-white rounded border">
                          <p className="font-medium text-gray-600">Official work history:</p>
                          <p>{mismatch.structured_data.employer_name || mismatch.structured_data.company}</p>
                          <p className="text-gray-500">{mismatch.structured_data.start_date} - {mismatch.structured_data.end_date || 'Present'}</p>
                        </div>
                      )}
                      {mismatch.cv_data && (
                        <div className="p-2 bg-white rounded border">
                          <p className="font-medium text-gray-600">CV:</p>
                          <p>{mismatch.cv_data.employer}</p>
                          <p className="text-gray-500">{mismatch.cv_data.start_date} - {mismatch.cv_data.end_date || 'Present'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Side-by-side View */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Official work history</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {employmentMismatch.structured_history?.length > 0 ? (
                      employmentMismatch.structured_history.map((job, idx) => (
                        <div key={idx} className="p-2 bg-green-50 rounded border border-green-200 text-sm">
                          <p className="font-medium">{job.employer_name || job.company || job.employer}</p>
                          <p className="text-gray-600">{job.job_title || job.role}</p>
                          <p className="text-xs text-gray-500">{job.start_date} - {job.end_date || 'Present'}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No official work history has been saved yet</p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Roles found in the CV</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {employmentMismatch.cv_extracted_roles?.length > 0 ? (
                      employmentMismatch.cv_extracted_roles.map((role, idx) => (
                        <div key={idx} className="p-2 bg-blue-50 rounded border border-blue-200 text-sm">
                          <p className="font-medium">{role.employer_name || role.employer || role.company || 'Employer not identified'}</p>
                          <p className="text-gray-600">{role.job_title || role.position || role.role}</p>
                          <p className="text-xs text-gray-500">{role.start_date} - {role.end_date || 'Present'}</p>
                          {role.employer_inferred_from_application && (
                            <p className="text-xs text-blue-600 mt-0.5">Matched from application dates</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No roles extracted from CV</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Apply selected CV roles to history */}
              {!isAuditor() && employmentMismatch.cv_extracted_roles?.length > 0 && cvReconcileBlocked && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    Work history cannot be changed from the CV after the CV is approved or Employment Review is signed off.
                    Request an updated CV if the official record still needs correction.
                  </p>
                </div>
              )}
              {!isAuditor() && employmentMismatch.cv_extracted_roles?.length > 0 && !cvReconcileBlocked && (
                <div className="pt-4 border-t space-y-3" data-testid="apply-cv-roles-section">
                  <h4 className="font-medium text-gray-900">Add confirmed CV roles to work history</h4>
                  <p className="text-xs text-gray-500">
                    Only add roles you have checked.
                    Existing work history is never overwritten; selected CV roles are added as new entries.
                    Gaps and coverage will recalculate automatically.
                  </p>
                  <div className="space-y-2">
                    {employmentMismatch.cv_extracted_roles.map((role, idx) => (
                      <label key={idx} className="flex items-start gap-3 p-2 rounded border border-gray-200 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={!!selectedCvRolesForApply[idx]}
                          onChange={(e) => setSelectedCvRolesForApply(prev => ({ ...prev, [idx]: e.target.checked }))}
                        />
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">{role.employer_name || role.employer || role.company}</p>
                          <p className="text-gray-600">{role.job_title || role.position || role.role}</p>
                          <p className="text-xs text-gray-500">{role.start_date} - {role.end_date || 'Present'}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {Object.values(selectedCvRolesForApply).some(Boolean) && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Reason for applying (required) *</label>
                      <Textarea
                        value={reconcileReason}
                        onChange={(e) => setReconcileReason(e.target.value)}
                        placeholder="e.g. CV confirms this missing role and the dates have been checked"
                        rows={2}
                        className="bg-white border-[#E4E8EB] text-sm"
                      />
                      <Button
                        onClick={handleApplyReconcile}
                        disabled={isApplyingReconcile || reconcileReason.trim().length < 5}
                        className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                      >
                        {isApplyingReconcile ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Add selected roles to work history
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Add Review Note */}
              {!isAuditor() && (
                <div className="pt-4 border-t space-y-3">
                  <h4 className="font-medium text-gray-900">Record review note</h4>
                  <p className="text-xs text-gray-500">Use this when you have checked the CV and do not need to add roles from it.</p>
                  <Textarea
                    value={mismatchReviewNote}
                    onChange={(e) => setMismatchReviewNote(e.target.value)}
                    placeholder="Record what you checked and what action was taken..."
                    rows={3}
                    className="bg-white border-[#E4E8EB]"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setMismatchDialogOpen(false)}
              className="border-[#E4E8EB]"
            >
              Close
            </Button>
            {!isAuditor() && (
              <Button 
                onClick={handleAddMismatchNote}
                disabled={isSubmittingMismatchNote || mismatchReviewNote.length < 5}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="add-mismatch-note-btn"
              >
                {isSubmittingMismatchNote ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Save review note
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ========== EVIDENCE UPLOAD DIALOG (SUPER ADMIN) ========== */}
      {/* This dialog allows admins to upload evidence for any compliance requirement */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
        setUploadDialogOpen(open);
        if (!open) {
          setSelectedRequirement('');
          setUploadFile(null);
          setDocumentLabel('');
        }
      }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Upload Document Evidence
            </DialogTitle>
            <DialogDescription>
              Upload evidence for the selected compliance requirement. This will be available for verification.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUploadDocument} className="space-y-4 mt-4">
            {/* Requirement Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Requirement <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedRequirement} onValueChange={setSelectedRequirement}>
                <SelectTrigger className="rounded-xl" data-testid="upload-requirement-select">
                  <SelectValue placeholder="Select requirement type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="right_to_work_documents">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Right to Work Documents
                    </div>
                  </SelectItem>
                  <SelectItem value="dbs_certificate">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      DBS Certificate
                    </div>
                  </SelectItem>
                  <SelectItem value="identity_documents">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      Identity (Passport/ID)
                    </div>
                  </SelectItem>
                  <SelectItem value="proof_of_address">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      Proof of Address
                    </div>
                  </SelectItem>

                  <SelectItem value="application_form">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-pink-500" />
                      Application Form (PDF)
                    </div>
                  </SelectItem>
                  <SelectItem value="right_to_work_check">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-teal-500" />
                      Right to Work Check (Internal)
                    </div>
                  </SelectItem>
                  <SelectItem value="dbs_check">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-500" />
                      DBS Update Service Check (Internal)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Optional File Label */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">File Label (optional)</Label>
              <Input
                value={documentLabel}
                onChange={(e) => setDocumentLabel(e.target.value)}
                placeholder="e.g., BRP Card Front, DBS Certificate 2024"
                className="rounded-xl"
                data-testid="upload-file-label"
              />
            </div>
            
            {/* File Picker */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                File <span className="text-red-500">*</span>
              </Label>
              <FileUploaderInline
                onFileSelect={(file) => {
                  if (selectedRequirement === 'cv' && file) {
                    const isPdfFile = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
                    if (!isPdfFile) {
                      toast.error('Only PDF CV files are supported. Please upload the CV as a PDF. Word documents (.doc, .docx) are not accepted.');
                      return;
                    }
                  }
                  setUploadFile(file);
                }}
                selectedFile={uploadFile}
                onClear={() => setUploadFile(null)}
                acceptedTypes={selectedRequirement === 'cv'
                  ? ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
                  : ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp']}
                maxSizeBytes={10 * 1024 * 1024}
                placeholder="Choose file or drag here"
                className="border-2 border-dashed rounded-xl"
              />
              <p className="text-xs text-gray-500">
                {selectedRequirement === 'cv'
                  ? 'Accepted: PDF or Word (.docx) — max 10MB. Word is converted to PDF automatically.'
                  : 'Accepted: PDF, JPG, PNG, WebP (max 10MB)'}
              </p>
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
                className="border-gray-200"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!selectedRequirement || !uploadFile || isUploading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="upload-submit-btn"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload Evidence
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* ========== DUAL-ROW COMPLIANCE MODEL DIALOGS (STEP 11) ========== */}
      
      {/* Record Check Dialog */}
      <RecordCheckDialog
        open={recordCheckDialogOpen}
        onClose={() => {
          setRecordCheckDialogOpen(false);
          setRecordCheckType(null);
        }}
        employeeId={employeeId}
        checkType={recordCheckType}
        onComplete={refreshChecksAfterMutation}
        // Evidence status props - computed from complianceFile
        hasAcceptedEvidence={(() => {
          if (!complianceFile || !recordCheckType) return false;
          const sectionKey = recordCheckType?.replace('_check', '').replace('_verification', '');
          const section = complianceFile?.requirements?.[sectionKey];
          if (!section?.rows) return false;
          const evidenceRow = section.rows.find(r => r.row_type === 'evidence');
          return (evidenceRow?.counts?.verified || 0) > 0;
        })()}
        hasStampedEvidence={(() => {
          if (!complianceFile || !recordCheckType) return false;
          const sectionKey = recordCheckType?.replace('_check', '').replace('_verification', '');
          const section = complianceFile?.requirements?.[sectionKey];
          if (!section?.rows) return false;
          const evidenceRow = section.rows.find(r => r.row_type === 'evidence');
          const docs = evidenceRow?.documents_preview || [];
          return docs.some(d => d.verification_stamp);
        })()}
        acceptedEvidenceCount={(() => {
          if (!complianceFile || !recordCheckType) return 0;
          const sectionKey = recordCheckType?.replace('_check', '').replace('_verification', '');
          const section = complianceFile?.requirements?.[sectionKey];
          if (!section?.rows) return 0;
          const evidenceRow = section.rows.find(r => r.row_type === 'evidence');
          return evidenceRow?.counts?.verified || 0;
        })()}
      />

      {/* ========== EDIT DIALOGS FOR UNIVERSAL EDITABILITY ========== */}

      <Dialog open={workApprovalDialogOpen} onOpenChange={setWorkApprovalDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve for Work</DialogTitle>
            <DialogDescription>
              Record the fit-for-work decision after readiness checks have passed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="work-approval-outcome">Outcome</Label>
              <Select value={workApprovalOutcome} onValueChange={setWorkApprovalOutcome}>
                <SelectTrigger id="work-approval-outcome">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ready">Ready for work</SelectItem>
                  <SelectItem value="ready_with_conditions">Ready with conditions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="work-approval-rationale">Rationale</Label>
              <Textarea
                id="work-approval-rationale"
                value={workApprovalRationale}
                onChange={(event) => setWorkApprovalRationale(event.target.value)}
                placeholder="Summarise why this employee is approved for work."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="work-approval-conditions">Conditions (optional)</Label>
              <Textarea
                id="work-approval-conditions"
                value={workApprovalConditions}
                onChange={(event) => setWorkApprovalConditions(event.target.value)}
                placeholder="Add one condition per line if needed."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWorkApprovalDialogOpen(false)} disabled={isSavingWorkApproval}>
              Cancel
            </Button>
            <Button
              onClick={profileMode === 'applicant' ? handleApproveToOnboarding : handleApproveForWork}
              disabled={(profileMode === 'applicant' ? !applicantReadyForRecruitmentApproval : !canRecordFitForWorkNow) || isSavingWorkApproval}
            >
              {isSavingWorkApproval ? 'Saving...' : 'Record approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Personal Details Dialog */}
      <EditPersonalDetailsDialog
        open={editPersonalOpen}
        onClose={() => setEditPersonalOpen(false)}
        employeeId={employeeId}
        currentData={employee}
        onSuccess={() => {
          fetchEmployee();
          fetchComplianceFile();
          fetchEmploymentReview();
        }}
      />
      
      {/* Edit Employment History Dialog */}
      <EditEmploymentHistoryDialog
        open={editEmploymentOpen}
        onClose={() => setEditEmploymentOpen(false)}
        employeeId={employeeId}
        currentHistory={editableEmploymentHistory}
        onSuccess={async () => {
          // Refresh from the saved source before rebuilding its persisted review.
          await fetchEmployee();
          await fetchEmploymentReview({ rebuild: true, refreshReadiness: false });
          await Promise.allSettled([
            fetchComplianceFile({ refreshReadiness: false }),
            fetchCompliance(),
            fetchApplicantTabReadiness(),
            fetchOnboardingSync(),
          ]);
        }}
      />
      
      {/* Edit Reference Dialog */}
      <EditReferenceDialog
        open={editReferenceOpen}
        onClose={() => {
          setEditReferenceOpen(false);
          setSelectedReferenceId(null);
          setSelectedReferenceData(null);
        }}
        employeeId={employeeId}
        referenceId={selectedReferenceId}
        currentData={selectedReferenceData}
        onSuccess={async () => {
          await Promise.allSettled([
            fetchEmployee(),
            fetchCompliance(),
            fetchComplianceFile(),
            fetchReferenceStatus(),
            fetchRecruitmentStatus(),
            fetchOnboardingSync(),
          ]);
          setReferencesTabRefreshKey((current) => current + 1);
        }}
      />
      
      {/* Supersede Contract Dialog */}
      <SupersedeContractDialog
        open={supersedeContractOpen}
        onClose={() => {
          setSupersedeContractOpen(false);
          setCurrentContract(null);
        }}
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        currentContract={currentContract}
        onSuccess={() => {
          fetchEmployee();
          fetchComplianceFile();
          fetchCompliance();
          fetchData();
        }}
      />
      
      {/* Form Submission View Dialog — rendered at component root so it is mounted
          regardless of the active tab (previously nested inside a TabsContent, which
          made "View Application" a dead action on the Recruitment Review tab). */}
      <Dialog open={viewFormSubmission?.isOpen} onOpenChange={(open) => !open && setViewFormSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewFormSubmission?.formName} - Submission</DialogTitle>
            <DialogDescription>
              Review the worker's submitted answers
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {viewFormSubmission?.data && Object.keys(viewFormSubmission.data).length > 0 ? (
              <SubmissionObjectView data={viewFormSubmission.data} />
            ) : (
              <p className="text-gray-500 text-center py-8">No submission data available</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            {viewFormSubmission?.submissionId && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    let url, filename;
                    if (viewFormSubmission.formType === 'pre_interview_questionnaire') {
                      url = `${API}/employees/${employee?.id}/pre-interview-questionnaire/download-pdf`;
                      filename = `pre_interview_questionnaire_${employee?.first_name || ''}_${employee?.last_name || ''}.pdf`;
                    } else {
                      url = `${API}/form-submissions/${viewFormSubmission.submissionId}/download-pdf`;
                      filename = `${viewFormSubmission.formType || 'form'}_${employee?.first_name || ''}_${employee?.last_name || ''}.pdf`;
                    }
                    const resp = await axios.get(url, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const blob = new Blob([resp.data], { type: 'application/pdf' });
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    toast.success('PDF downloaded');
                  } catch { toast.error('Failed to download PDF'); }
                }}
                className="mr-auto"
              >
                <Download className="h-4 w-4 mr-1" />
                Download PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewFormSubmission(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReviewDeclarationsDialog
        open={reviewDeclarationsOpen}
        onClose={() => setReviewDeclarationsOpen(false)}
        employeeId={employeeId}
        alreadyVerified={declarationsAdequatelyReviewed}
        verifiedAt={employee?.declarations_reviewed_at}
        verifiedByName={employee?.declarations_reviewed_by_name || employee?.declarations_reviewed_by}
        onRequestAmend={() => setEditDeclarationsOpen(true)}
        onSuccess={() => {
          fetchEmployee();
          fetchComplianceFile();
          fetchEmploymentReview();
        }}
      />

      <EditDeclarationsDialog
        open={editDeclarationsOpen}
        onClose={() => setEditDeclarationsOpen(false)}
        employeeId={employeeId}
        currentData={employee}
        onSuccess={() => {
          fetchEmployee();
          fetchComplianceFile();
          fetchEmploymentReview();
        }}
      />
      
      {/* Document Verification Modal (CQC P0) */}
      <DocumentVerificationModal
        open={verificationModalOpen}
        onClose={() => {
          setVerificationModalOpen(false);
          setVerificationDocument(null);
        }}
        document={verificationDocument}
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        onVerified={handleVerificationComplete}
      />
      
      {/* Document Viewer Modal (CQC P0) */}
      <DocumentViewerModal
        open={viewerModalOpen}
        onClose={() => {
          setViewerModalOpen(false);
          setViewerDocument(null);
        }}
        document={viewerDocument}
        employeeId={employeeId}
        employeeName={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        onVerify={(doc) => {
          setViewerModalOpen(false);
          handleOpenVerificationModal(doc);
        }}
      />
      
      {/* ========== CV REVIEW DIALOG ========== */}
      <Dialog open={cvReviewDialogOpen} onOpenChange={setCvReviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-purple-600" />
              CV review
            </DialogTitle>
            <DialogDescription>
              Check the CV against the official work history. If work history is missing, add or request it before approving the CV.
            </DialogDescription>
          </DialogHeader>
          
          {cvExtractionResult && (
            <div className="space-y-4 py-4">
              <CvEmploymentAlignmentPanel
                alignment={employmentMismatch?.alignment || employee?.cv_employment_alignment}
                employee={employee}
                employmentMismatch={employmentMismatch}
                onViewMismatch={() => {
                  setCvReviewDialogOpen(false);
                  setMismatchDialogOpen(true);
                }}
                onRequestReplacement={!cvApproved ? () => {
                  setCvReviewDialogOpen(false);
                  openCvRequestDialog('replacement');
                } : undefined}
                compact
              />
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-sm font-medium text-slate-900">CV compared with the official work history</p>
                <p className="mt-1 text-xs text-slate-600">
                  CareOps uses the CV as supporting evidence. Official employment gaps and dates still come from the structured work history above.
                </p>
              </div>

              <details className="rounded-lg border border-slate-200 bg-white">
                <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700">
                  Show extracted CV details
                </summary>
                <div className="border-t border-slate-100 p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg bg-blue-50 p-3 text-center">
                      <p className="text-xl font-bold text-blue-700">{cvExtractionResult.jobs_found}</p>
                      <p className="text-xs text-blue-600">Jobs found</p>
                    </div>
                    <div className="rounded-lg bg-purple-50 p-3 text-center">
                      <p className="text-xl font-bold text-purple-700">{cvExtractionResult.education_found}</p>
                      <p className="text-xs text-purple-600">Education</p>
                    </div>
                    <div className="rounded-lg bg-green-50 p-3 text-center">
                      <p className="text-xl font-bold text-green-700">{cvExtractionResult.skills_found}</p>
                      <p className="text-xs text-green-600">Skills</p>
                    </div>
                  </div>

                  {cvExtractionResult.employment_history?.length > 0 ? (
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        Employment found on CV
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {cvExtractionResult.employment_history.map((job, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                            <div className="flex justify-between gap-3">
                              <div>
                                <p className="font-medium text-gray-800">
                                  {job.employer_name || job.employer || job.company || 'Employer not identified'}
                                </p>
                                <p className="text-gray-600">{job.job_title || job.position || job.role}</p>
                              </div>
                              <p className="text-xs text-gray-500">
                                {formatEmploymentBoundary(job.start_date, { fallback: 'Start unknown' })} – {formatEmploymentBoundary(job.end_date || 'present', { fallback: 'End unknown' })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {cvExtractionResult.gaps?.length > 0 ? (
                    <p className="text-xs text-slate-500">
                      CareOps noticed {cvExtractionResult.gaps.length} interval{cvExtractionResult.gaps.length === 1 ? '' : 's'} in the CV timeline. These are comparison notes only and do not create official employment gaps.
                    </p>
                  ) : null}
                </div>
              </details>
              {cvApproveWithNoteBlocked ? (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="cv-approve-blocked-gaps"
                >
                  <p className="font-medium">Finish work history gaps first</p>
                  <p className="mt-1 text-amber-800">{cvApproveWithNoteBlockedReason}</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-3 border-amber-300 text-amber-900 hover:bg-amber-100"
                    onClick={() => {
                      setCvReviewDialogOpen(false);
                      setActiveTab('employment');
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('tab', 'employment');
                        return next;
                      });
                      window.requestAnimationFrame(() => {
                        document.querySelector('[data-testid="section-employment-gaps"]')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }}
                  >
                    Open work history &amp; gaps
                  </Button>
                </div>
              ) : null}
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => setCvRejectDialogOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50">
              <XCircle className="h-4 w-4 mr-1" />
              Reject with Reason
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCvReviewDialogOpen(false)}>
                Close
              </Button>
              <Button
                onClick={handleApproveCvClick}
                disabled={cvReviewLoading || cvApproveWithNoteBlocked}
                className={cvApproveNeedsAcknowledgment ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                data-testid="approve-cv-btn"
                title={cvApproveWithNoteBlocked ? cvApproveWithNoteBlockedReason : undefined}
              >
                {cvReviewLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                {cvApproveWithNoteBlocked ? 'Locked until gaps are checked' : (cvApproveNeedsAcknowledgment ? 'Approve with note...' : 'Approve CV')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={cvApproveConfirmOpen} onOpenChange={setCvApproveConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Approve CV after checking differences?
            </DialogTitle>
            <DialogDescription>
              The applicant's gaps are verified. Add a short note if you are happy to accept this CV,
              or ask the applicant for an updated CV.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {activeCvAlignment?.summary && (
              <p className="text-xs text-amber-900 rounded-lg border border-amber-200 bg-amber-50 p-3">
                {activeCvAlignment.summary}
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-gray-600">Approval note (required, min 10 characters)</Label>
              <Textarea
                value={cvApprovalNote}
                onChange={(e) => setCvApprovalNote(e.target.value)}
                placeholder="e.g. Checked CV PDF — missing roles are agency placements named differently on the CV. Accepting for now while applicant updates CV."
                rows={4}
                className="text-sm"
                data-testid="cv-approval-note-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={() => handleApproveCv(cvApprovalNote)}
              disabled={cvReviewLoading || cvApprovalNote.trim().length < 10}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-approve-cv-btn"
            >
              {cvReviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Approve CV with this note
            </Button>
            <div className="flex w-full gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setCvApproveConfirmOpen(false);
                  setCvReviewDialogOpen(false);
                  setMismatchDialogOpen(true);
                }}
              >
                Check details
              </Button>
              <Button
                variant="outline"
                className="flex-1 text-amber-800 border-amber-200"
                onClick={() => {
                  setCvApproveConfirmOpen(false);
                  setCvReviewDialogOpen(false);
                  openCvRequestDialog('replacement');
                }}
              >
                Ask for updated CV
              </Button>
            </div>
            <Button variant="ghost" className="w-full" onClick={() => setCvApproveConfirmOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CV Request Dialog (upload or replacement — applicant action) */}
      <Dialog open={cvRequestDialogOpen} onOpenChange={setCvRequestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              {cvRequestMode === 'replacement' ? 'Request replacement CV' : 'Request CV upload'}
            </DialogTitle>
            <DialogDescription>
              The applicant will be notified and can upload from their worker dashboard. You cannot upload on their behalf.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <CvRequestReasonPicker
              mode={cvRequestMode}
              reason={cvRequestReason}
              onReasonChange={setCvRequestReason}
              alignment={employmentMismatch?.alignment || employee?.cv_employment_alignment}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCvRequestDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCvRequest}
              disabled={cvRequestLoading || cvRequestReason.trim().length < 10}
              data-testid="employment-cv-request-submit"
            >
              {cvRequestLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CV Reject Dialog */}
      <Dialog open={cvRejectDialogOpen} onOpenChange={setCvRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reject CV
            </DialogTitle>
            <DialogDescription>
              Provide a clear reason for rejection. The worker will be notified and asked to either explain gaps or upload a new CV.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Rejection Reason</Label>
            <Textarea
              value={cvRejectReason}
              onChange={(e) => setCvRejectReason(e.target.value)}
              placeholder="e.g., Please explain the employment gap between March 2019 and January 2021, or provide documentation for this period."
              className="mt-2 min-h-[100px]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Be specific about what the worker needs to do (explain gap, provide documentation, upload new CV)
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCvRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRejectCv} 
              disabled={cvRejectLoading || cvRejectReason.length < 10}
              className="bg-red-600 hover:bg-red-700"
            >
              {cvRejectLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline Document Viewer (PDF / image) — replaces window.open */}
      <InlineDocumentViewer
        open={inlineViewerOpen}
        onClose={() => {
          setInlineViewerOpen(false);
          setInlineViewerFallback(null);
        }}
        fetchUrl={inlineViewerUrl}
        title={inlineViewerTitle}
        token={token}
        filename={inlineViewerFilename}
        fallbackLabel={inlineViewerFallback ? 'View submitted answers' : undefined}
        onFallback={inlineViewerFallback ? () => {
          setInlineViewerOpen(false);
          setViewFormSubmission(inlineViewerFallback);
          setInlineViewerFallback(null);
        } : undefined}
      />

      {/* ER-S1: Possible historical application review — admin explicitly selects
          the correct candidate; never auto-selected, never cross-tenant. */}
      <Dialog open={applicationCandidatesOpen} onOpenChange={setApplicationCandidatesOpen}>
        <DialogContent className="max-w-lg" data-testid="application-candidates-dialog">
          <DialogHeader>
            <DialogTitle>Possible historical application found</DialogTitle>
            <DialogDescription>
              We found more than one earlier application that could belong to this person.
              Please check the details and choose the correct one. Nothing is attached until you select it.
            </DialogDescription>
          </DialogHeader>
          {applicationCandidatesLoading ? (
            <p className="py-6 text-center text-sm text-text-muted">Loading possible matches…</p>
          ) : applicationCandidates.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-muted">No possible historical applications to review.</p>
          ) : (
            <div className="space-y-3">
              {applicationCandidates.map((cand) => (
                <div key={cand.application_id} className="rounded-lg border border-gray-200 p-3" data-testid="application-candidate-row">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div><span className="text-text-muted">Reference:</span> {cand.application_reference || '—'}</div>
                    <div><span className="text-text-muted">Submitted:</span> {cand.submitted_at ? new Date(cand.submitted_at).toLocaleDateString() : '—'}</div>
                    <div><span className="text-text-muted">Name:</span> {cand.applicant_name || '—'}</div>
                    <div><span className="text-text-muted">Email:</span> {cand.email_masked || '—'}</div>
                    <div><span className="text-text-muted">Phone:</span> {cand.phone_masked || '—'}</div>
                    <div><span className="text-text-muted">Source:</span> {cand.source_collection || '—'}</div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-text-muted">
                      Match: {(cand.match_signals || []).join(', ') || '—'}
                      {cand.confirmation_present ? ' · confirmed' : ' · not confirmed'}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => handleSelectApplicationCandidate(cand.application_id)}
                      data-testid={`use-application-${cand.application_id}`}
                    >
                      Use this application
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplicationCandidatesOpen(false)}>
              None of these — leave as missing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <LifecycleReasonDialog
        open={lifecycleReasonDialogOpen}
        onOpenChange={(open) => {
          setLifecycleReasonDialogOpen(open);
          if (!open && !isLifecycleSaving) {
            setLifecycleNextStatus('');
            setLifecycleActionLabel('');
          }
        }}
        actionLabel={lifecycleActionLabel || 'Update lifecycle status'}
        subjectLabel={employee ? `${employee.first_name} ${employee.last_name}` : ''}
        minLength={5}
        isSubmitting={isLifecycleSaving}
        onConfirm={submitLifecycleStatusChange}
      />
    </div>
  );
}




